import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link, useSearchParams, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home, User, Plus, Pencil, Trash2, LogOut, Check, X, Upload, Eye, EyeOff,
  Clock, Calendar, ChevronDown, ShieldCheck, ExternalLink, ArrowRight, ArrowLeft, Building2, Copy, Link2, Ban, Newspaper, Mail, Heart, Coins, Rocket, Info, Sparkles, Users, BarChart3, TrendingUp, Globe, Palette, Wand2, GripVertical, Loader2, Monitor, Smartphone, RefreshCw, FileText, Mic, Phone, MapPin, Contact, Handshake, Send, Search, Menu, LayoutGrid, ChevronRight, MoreHorizontal, Download, Bell, Share2, Link as LinkIcon, Megaphone, BadgeCheck, Facebook, Linkedin, Twitter, MessageCircle, Instagram, Move, Image as ImageIcon, HelpCircle, MousePointerClick, Inbox, ShieldAlert, FileSignature, KeyRound, PenLine, Flame, Filter, CalendarClock, List as ListIcon,
} from "lucide-react";
import { PlatformLayout, WhatsAppIcon } from "./PlatformLayout";
import { Brand } from "./Brand";
import { useAuth, authApi, uploadMemberFile } from "./authApi";
import { fmtPrice, API, PUBLIC_SITE, LOGO_URL } from "./api";
import { ShareButtons } from "./ShareButtons";
import { ImageCropper, CROP_PRESETS } from "./ImageCropper";
import { STATUS as EVO_STATUS, TIMELINE as EVO_TIMELINE, LAST_UPDATE as EVO_LAST } from "./private/changelogData";
import ComandatSection from "./ComandatSection";
import AuditTab, { AuditBanner } from "./AuditTab";
import AdminListingsPage from "./admin/Dashboard";
import AdminFinancePage from "./admin/AdminFinance";
import AdminEmailPreview from "./admin/AdminEmailPreview";
import AffiliationsPage from "./admin/Affiliations";
import AdminSubmissionsPage from "./admin/Submissions";
import AdminSupervisionPage from "./admin/AdminSupervision";
import AdminAccessRequests from "./admin/AdminAccessRequests";
import MemberComandats from "./MemberComandats";
import MemberPaymentsHistory from "./MemberPaymentsHistory";
import { adminApi } from "./admin/adminApi";
import AdminAcquereursPage from "./admin/AdminAcquereurs";
import AdminOffersPage from "./admin/AdminOffers";
import AdminStatsPage from "./admin/Stats";
import AdminROIPage from "./admin/AdminROI";
import AdminCreditsPage from "./admin/AdminCredits";
import AdminAgentPage from "./admin/AdminAgent";
import { InstallBackOfficeBanner, useInstallPrompt } from "./InstallApp";
import { VoiceImportModal } from "./VoiceImportModal";
import { ACQ_ORIGINS, originMeta, OriginBadge } from "./acquereurOrigins";
import { WalletBar, BuyCreditsModal, PublishModal, PreviewModal, StagingStudioModal, CommissionsTab, ChangeTierModal } from "./Credits";
import { AgentPresenter } from "./AIAssist";
import NetworkTab from "./NetworkTab";
import OffersTab from "./OffersTab";
import ProspectionTerrain from "./ProspectionTerrain";
import GenerationProgress from "./GenerationProgress";
import { OneClickGenerate } from "./OneClickGenerate";
import { PublishWizard } from "./PublishWizard";
import { NETWORK_META, networkOf } from "./PartnerSite";
import DecouvrirAgentIA from "./DecouvrirAgentIA";
import { EmmaWorkBlock, WhatsAppDraftModal, BoostReportPanel } from "./EmmaWork";

const inp = "w-full bg-white/5 border border-white/15 px-4 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-[#C19A6B] transition-colors";
const TYPES = ["Conseiller immobilier", "Agent immobilier", "Marchand de biens", "Apporteur d'affaires", "Propriétaire vendeur", "Autre"];
const PROP_TYPES = ["Appartement", "Maison", "Villa", "Terrain", "Immeuble", "Local commercial", "Autre"];
const COLLECTIONS = ["Habitat", "Prestige"];
const EMAIL_LANGS = [
  { code: "fr", name: "Français" }, { code: "en", name: "English" }, { code: "es", name: "Español" },
  { code: "de", name: "Deutsch" }, { code: "pt", name: "Português" }, { code: "zh-CN", name: "简体中文" },
];

const STATUS_META = {
  pending: { label: "En attente de validation", color: "text-amber-400", bg: "bg-amber-400/10 border-amber-400/30", icon: Clock,
    text: "Votre demande d'affiliation est en cours d'examen par Vincent Marette. Vous pouvez déjà préparer vos annonces ; elles seront visibles publiquement dès validation." },
  approved: { label: "Compte validé", color: "text-green-400", bg: "bg-green-400/10 border-green-400/30", icon: ShieldCheck,
    text: "Votre compte est actif. Vos annonces publiées sont visibles sur OFF MARKETTE et sur votre page conseiller." },
  rejected: { label: "Demande refusée", color: "text-red-400", bg: "bg-red-400/10 border-red-400/30", icon: X,
    text: "Votre demande n'a pas été retenue pour le moment. Contactez Vincent Marette pour plus d'informations." },
  blocked: { label: "Compte suspendu", color: "text-zinc-300", bg: "bg-zinc-500/10 border-zinc-400/30", icon: Ban,
    text: "Votre compte est actuellement suspendu. Vos annonces ne sont plus visibles publiquement. Contactez Vincent Marette pour rétablir votre accès." },
};

// ---------- Back Office SaaS shell (sidebar + topbar + mobile) ----------
const UNIVERSES_DEF = [
  { key: "reseau", label: "Mon Réseau", icon: Users, modules: [
    { id: "accueil", label: "Accueil", icon: LayoutGrid, roles: ["admin", "agent"] },
    { id: "network", label: "Réseau d'agents", icon: Users, roles: ["admin", "agent"] },
    { id: "prospection", label: "Prospection Terrain", icon: MapPin, roles: ["admin", "agent"] },
    { id: "matching", label: "Agents recommandés", icon: Sparkles, roles: ["admin", "agent"] },
    { id: "opportunites", label: "Opportunités IA", icon: TrendingUp, roles: ["admin", "agent"] },
    { id: "acquereurs", label: "Mes acquéreurs", icon: Contact, roles: ["admin", "agent"] },
    { id: "vendeurs", label: "Mes vendeurs", icon: Building2, roles: ["admin", "agent"] },
    { id: "comandat", label: "Co-mandats", icon: Handshake, roles: ["admin", "agent"] },
    { id: "filleuls", label: "Mes filleuls", icon: Users, roles: ["admin", "agent"] },
  ] },
  { key: "annonces", label: "Mes Annonces", icon: Home, modules: [
    { id: "listings", label: "Mes annonces", icon: Home, roles: ["admin", "agent"] },
    { id: "audit", label: "Audit annonces", icon: BadgeCheck, roles: ["admin", "agent"] },
    { id: "matching_bien", label: "Matching IA (biens ⇄ acquéreurs)", icon: Sparkles, roles: ["admin", "agent"] },
    { id: "listings_network", label: "Annonces de mon réseau", icon: Building2, roles: ["admin", "agent"] },
    { id: "match_annonces", label: "Annonces à matcher", icon: Sparkles, roles: ["admin", "agent"] },
  ] },
  { key: "agentia", label: "Mon Agent IA", icon: Wand2, modules: [
    { id: "agent_decouvrir", label: "Découvrir Emma", icon: Sparkles, roles: ["admin", "agent"] },
    { id: "agent_prospects", label: "Prospects", icon: Contact, roles: ["admin", "agent"] },
    { id: "agent_emails", label: "Emails envoyés", icon: Mail, roles: ["admin", "agent"] },
    { id: "agent_settings", label: "Paramètres", icon: Wand2, roles: ["admin", "agent"] },
  ] },
  { key: "activite", label: "Mon Activité", icon: BarChart3, modules: [
    { id: "stats", label: "Tableau de bord", icon: BarChart3, roles: ["admin", "agent"] },
    { id: "activity", label: "Historique d'activité", icon: Clock, roles: ["admin", "agent"] },
    { id: "offers", label: "Offres reçues", icon: FileText, roles: ["admin", "agent"] },
    { id: "apporteur", label: "Tableau apporteur", icon: Handshake, roles: ["admin", "agent"], apporteurOnly: true },
    { id: "commissions", label: "Mes commissions", icon: Coins, roles: ["admin", "agent"] },
    { id: "documents", label: "Mes documents", icon: FileText, roles: ["admin", "agent"] },
  ] },
  { key: "monsite", label: "Mon Site", icon: Globe, modules: [
    { id: "site", label: "Mon site", icon: Globe, roles: ["admin", "agent"] },
  ] },
  { key: "mapage", label: "Ma Page", icon: User, modules: [
    { id: "profile", label: "Modifier ma page", icon: User, roles: ["admin", "agent"] },
  ] },
  { key: "social", label: "Réseaux Sociaux", icon: Send, modules: [
    { id: "social_reco", label: "Annonces à publier", icon: Sparkles, roles: ["admin", "agent"] },
    { id: "social_published", label: "Annonces publiées", icon: Share2, roles: ["admin", "agent"] },
    { id: "social_connect", label: "Connecter mes réseaux", icon: LinkIcon, roles: ["admin", "agent"] },
    { id: "social_report", label: "Rapport de mon réseau", icon: BarChart3, roles: ["admin", "agent"] },
    { id: "social_ads", label: "Mes pubs parrainages", icon: Megaphone, roles: ["admin", "agent"] },
  ] },
  { key: "visites", label: "Mes Visites", icon: Wand2, modules: [
    { id: "agenda", label: "Agenda", icon: Clock, roles: ["admin", "agent"] },
    { id: "visite_live", label: "Visite Live IA", icon: Wand2, action: "/visite-ia", roles: ["admin", "agent"] },
    { id: "rapports", label: "Rapports", icon: Newspaper, roles: ["admin", "agent"] },
  ] },
  { key: "admin", label: "Administration", icon: ShieldCheck, roles: ["admin"], modules: [
    { id: "admin_listings", label: "Annonces (réseau)", icon: Building2, roles: ["admin"] },
    { id: "admin_submissions", label: "Biens proposés", icon: Inbox, roles: ["admin"] },
    { id: "admin_affiliations", label: "Membres & affiliations", icon: Users, roles: ["admin"] },
    { id: "admin_supervision", label: "Supervision", icon: ShieldAlert, roles: ["admin"] },
    { id: "admin_access", label: "Demandes de code", icon: KeyRound, roles: ["admin"] },
    { id: "admin_acquereurs", label: "Acquéreurs", icon: Search, roles: ["admin"] },
    { id: "admin_offers", label: "Offres d'achat", icon: FileSignature, roles: ["admin"] },
    { id: "admin_stats", label: "Statistiques", icon: BarChart3, roles: ["admin"] },
    { id: "admin_roi", label: "Rentabilité", icon: TrendingUp, roles: ["admin"] },
    { id: "admin_finance", label: "Finances & associés", icon: Coins, roles: ["admin"] },
    { id: "admin_credits", label: "Crédits & promos", icon: Sparkles, roles: ["admin"] },
    { id: "admin_emails", label: "Aperçu e-mails", icon: Mail, roles: ["admin"] },
    { id: "admin_agent", label: "Agent IA (réseau)", icon: Wand2, roles: ["admin"] },
    { id: "bo", label: "Back-Office complet", icon: ShieldCheck, action: "/admin", roles: ["admin"] },
  ] },
];
const moduleLabel = (id) => { for (const u of UNIVERSES_DEF) { const mm = u.modules.find((x) => x.id === id); if (mm) return mm.label; } return "Espace conseiller"; };
const universeOfTab = (id) => UNIVERSES_DEF.find((u) => u.modules.some((m) => m.id === id)) || UNIVERSES_DEF[0];
// Onglets réellement rendus (pour valider le deep-linking /mon-compte/{slug}).
const VALID_TABS = new Set(["accueil", "acquereurs", "activity", "admin_access", "admin_acquereurs", "admin_affiliations", "admin_agent", "admin_credits", "admin_emails", "admin_finance", "admin_listings", "admin_offers", "admin_roi", "admin_stats", "admin_submissions", "admin_supervision", "agenda", "agent_decouvrir", "agent_emails", "agent_prospects", "agent_settings", "apporteur", "audit", "comandat", "commissions", "connexions", "documents", "evolution", "filleuls", "listings", "listings_network", "match_annonces", "matching", "matching_bien", "network", "offers", "opportunites", "partenaires", "profile", "prospection", "rapports", "site", "stats", "vendeurs"]);
const firstModuleOf = (u, role, isApporteur = false) => (u.modules.filter((m) => m.roles.includes(role) && !m.action && (!m.apporteurOnly || isApporteur))[0] || u.modules[0]);

function SidebarNav({ role, tab, onSelect, isApporteur = false }) {
  const activeUniverse = universeOfTab(tab);
  return (
    <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-2.5" data-testid="bo-sidebar-nav">
      {UNIVERSES_DEF.filter((u) => !u.roles || u.roles.includes(role)).map((u) => {
        const mods = u.modules.filter((t) => t.roles.includes(role) && (!t.apporteurOnly || isApporteur));
        if (!mods.length) return null;
        const Ic = u.icon || LayoutGrid;
        const active = activeUniverse.key === u.key;
        return (
          <button key={u.key} onClick={() => onSelect(firstModuleOf(u, role, isApporteur))} data-testid={`bo-cat-${u.key}`}
            className={`group w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-[15px] font-semibold border transition-all duration-200 hover:translate-x-1 ${active ? "bg-[#C19A6B] text-white border-[#C19A6B] shadow-md" : "bg-white text-[#111827] border-[#E5E7EB] shadow-sm hover:bg-[#C19A6B] hover:text-white hover:border-[#C19A6B]"}`}>
            <Ic className={`w-5 h-5 shrink-0 ${active ? "text-white" : "text-[#C19A6B] group-hover:text-white"}`} strokeWidth={2} />
            <span className="truncate">{u.label}</span>
            {active && <ChevronRight className="ml-auto w-4 h-4 text-white" />}
          </button>
        );
      })}
    </nav>
  );
}

function SubTabBar({ role, tab, onSelect, isApporteur = false }) {
  const u = universeOfTab(tab);
  const mods = u.modules.filter((t) => t.roles.includes(role) && (!t.apporteurOnly || isApporteur));
  const [pendingAccess, setPendingAccess] = useState(0);
  const [matchNew, setMatchNew] = useState(0);
  useEffect(() => {
    if (u.key !== "admin") return;
    fetch(`${API}/admin/access-requests`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null).then((d) => d && setPendingAccess(d.pending || 0)).catch(() => {});
  }, [u.key, tab]);
  useEffect(() => {
    if (!mods.some((t) => t.id === "matching_bien")) return;
    if (tab === "matching_bien") {
      setMatchNew(0);
      fetch(`${API}/member/matching/seen`, { method: "POST", credentials: "include" }).catch(() => {});
    } else {
      fetch(`${API}/member/matching/count`, { credentials: "include" })
        .then((r) => r.ok ? r.json() : null).then((d) => d && setMatchNew(d.new || 0)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  if (mods.length < 2) return null;
  return (
    <div className="sticky top-16 z-20 bg-[#F1F3F5] border-b border-[#E5E7EB]" data-testid="bo-subtabs">
      <div className="flex flex-wrap items-center gap-1 px-4 sm:px-6 py-2">
        {mods.map((t) => {
          const Ic = t.icon; const active = tab === t.id;
          const badge = t.id === "admin_access" && pendingAccess > 0 ? pendingAccess : t.id === "matching_bien" ? matchNew : 0;
          return (
            <button key={t.id} onClick={() => onSelect(t)} data-testid={`bo-subtab-${t.id}`}
              className={`relative inline-flex items-center gap-1.5 whitespace-nowrap text-[12px] font-medium rounded-lg px-2.5 py-1.5 transition-all ${active ? "bg-white text-[#111827] shadow-sm" : "text-[#6B7280] hover:text-[#111827] hover:bg-white/60"}`}>
              <Ic className={`w-3.5 h-3.5 ${active ? "text-[#C19A6B]" : ""}`} strokeWidth={1.9} /> {t.label}
              {badge > 0 && <span data-testid={t.id === "matching_bien" ? "matching-tab-badge" : "access-tab-badge"} className="ml-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-[#C19A6B] text-white text-[10px] font-bold flex items-center justify-center animate-pulse">{badge > 9 ? "9+" : badge}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CommandPalette({ role, open, onClose, onSelect }) {
  const [q, setQ] = useState("");
  const inputRef = useRef(null);
  useEffect(() => { if (open) { setQ(""); setTimeout(() => inputRef.current?.focus(), 50); } }, [open]);
  if (!open) return null;
  const all = UNIVERSES_DEF.filter((u) => !u.roles || u.roles.includes(role)).flatMap((u) => u.modules.filter((t) => t.roles.includes(role)).map((t) => ({ ...t, universe: u.label })));
  const filtered = q ? all.filter((t) => (t.label + " " + t.universe).toLowerCase().includes(q.toLowerCase())) : all;
  return (
    <div className="fixed inset-0 z-[90] flex items-start justify-center pt-[12vh] px-4" data-testid="bo-command-palette">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-xl shadow-[0_16px_40px_rgba(0,0,0,0.18)] border border-[#E5E7EB] overflow-hidden">
        <div className="flex items-center gap-3 px-4 border-b border-[#F3F4F6]">
          <Search className="w-4 h-4 text-[#9CA3AF]" />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Escape") onClose(); if (e.key === "Enter" && filtered[0]) onSelect(filtered[0]); }}
            placeholder="Rechercher un module, une action…" data-testid="bo-command-input" className="flex-1 py-3.5 text-sm text-[#111827] outline-none bg-transparent placeholder-[#9CA3AF]" />
          <kbd className="text-[10px] text-[#9CA3AF] border border-[#E5E7EB] rounded px-1.5 py-0.5">ESC</kbd>
        </div>
        <div className="max-h-[50vh] overflow-y-auto py-2">
          {filtered.length === 0 && <p className="px-4 py-6 text-sm text-[#9CA3AF] text-center">Aucun résultat</p>}
          {filtered.map((t) => { const Ic = t.icon; return (
            <button key={t.id} onClick={() => onSelect(t)} data-testid={`bo-cmd-${t.id}`} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#374151] hover:bg-[#FBF8F4] transition-colors">
              <Ic className="w-4 h-4 text-[#C19A6B]" strokeWidth={1.75} /> {t.label}
              <span className="ml-auto text-[11px] text-[#9CA3AF]">{t.universe}</span>
            </button>
          ); })}
        </div>
      </div>
    </div>
  );
}

function FloatingWidgets({ m, wallet, onBuy }) {
  const [openRef, setOpenRef] = useState(false);
  const { available, installed, ios, promptInstall } = useInstallPrompt();
  const [instDismissed, setInstDismissed] = useState(false);
  const [hidden, setHidden] = useState(false);
  const showInstall = !installed && !instDismissed && (available || ios);
  const code = m?.referral_code || "";
  const link = `${PUBLIC_SITE}/rejoindre?ref=${code}`;
  const msg = `Rejoignez le réseau OFF MARKETTE (parrainé par ${m?.display_name || "un conseiller"}) : ${link}`;
  const shares = [
    { key: "wa", label: "WhatsApp", href: `https://wa.me/?text=${encodeURIComponent(msg)}`, cls: "bg-[#25D366] text-black" },
    { key: "fb", label: "Facebook / Messenger", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`, cls: "bg-[#1877F2] text-white" },
    { key: "x", label: "X", href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(msg)}`, cls: "bg-white text-black" },
    { key: "mail", label: "E-mail", href: `mailto:?subject=${encodeURIComponent("Rejoignez OFF MARKETTE")}&body=${encodeURIComponent(msg)}`, cls: "bg-white/10 text-white" },
  ];
  return (
    <div className="hidden lg:flex fixed bottom-6 right-4 z-20 flex-col items-stretch gap-1.5 w-[120px]" data-testid="floating-widgets">
      {hidden ? (
        <button onClick={() => setHidden(false)} data-testid="floating-reopen" className="self-end w-8 h-8 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center shadow-lg border border-[#C19A6B]/40 hover:border-[#C19A6B]" title="Afficher les widgets"><Coins className="w-4 h-4 text-[#C19A6B]" /></button>
      ) : (
        <>
          {/* Fermer tous les widgets */}
          <button onClick={() => setHidden(true)} data-testid="floating-hide-all" className="self-end -mb-0.5 w-5 h-5 rounded-full bg-[#0A0A0A]/80 text-white/80 hover:text-white flex items-center justify-center" title="Masquer les widgets"><X className="w-3 h-3" /></button>

          {/* Widget installation */}
          {showInstall && (
            <div data-testid="floating-install" className="flex items-center gap-1.5 bg-white border border-[#E5E7EB] pl-2 pr-1.5 py-1.5 rounded-xl shadow-[0_6px_20px_rgba(0,0,0,0.10)]">
              <button onClick={() => promptInstall()} data-testid="floating-install-btn" className="flex-1 flex items-center justify-center gap-1.5 text-[9px] font-semibold uppercase tracking-wider text-[#111827] hover:text-[#A88358] transition-colors">
                <span className="w-5 h-5 rounded-md bg-[#0A0A0A] flex items-center justify-center shrink-0"><Download className="w-3 h-3 text-[#C19A6B]" /></span>
                Installer
              </button>
              <button onClick={() => setInstDismissed(true)} data-testid="floating-install-dismiss" className="text-[#9CA3AF] hover:text-[#111827] ml-auto"><X className="w-3 h-3" /></button>
            </div>
          )}

          {/* Widget crédits */}
          <div data-testid="floating-credits" className="flex flex-col items-center gap-0 bg-white border border-[#E5E7EB] px-2.5 py-1.5 rounded-xl shadow-[0_6px_20px_rgba(0,0,0,0.10)]">
            <span className="text-[8px] uppercase tracking-[0.14em] text-[#9CA3AF] flex items-center gap-1"><Coins className="w-2.5 h-2.5 text-[#C19A6B]" /> Crédits</span>
            <span className="text-xl font-bold text-[#111827] leading-tight">{wallet?.credits ?? 0}</span>
          </div>
          <button onClick={onBuy} data-testid="floating-buy-credits" className="flex items-center justify-center whitespace-nowrap bg-[#C19A6B] hover:bg-[#A88358] text-white text-[9px] font-semibold uppercase tracking-wider px-2.5 py-1.5 rounded-xl shadow-[0_6px_18px_rgba(193,154,107,0.32)] hover:-translate-y-0.5 transition-all">
            Acheter crédits
          </button>

          {/* Encart parrainage */}
          {code && (
            <div data-testid="floating-referral">
              {openRef ? (
                <div className="bg-[#0A0A0A] text-white p-3.5 rounded-xl shadow-2xl border border-white/10 w-60 -ml-[144px]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-white flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-white" /> Parrainer</span>
                    <button onClick={() => setOpenRef(false)} data-testid="floating-referral-close" className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
                  </div>
                  <p className="text-[10px] text-white/60 mb-2.5">Partagez votre lien en 1 clic.</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {shares.map((s) => (
                      <a key={s.key} href={s.href} target="_blank" rel="noopener noreferrer" data-testid={`floating-share-${s.key}`} className={`text-[10px] font-semibold text-center rounded-lg px-2 py-1.5 transition-transform hover:scale-[1.03] ${s.cls}`}>{s.label}</a>
                    ))}
                  </div>
                  <button onClick={() => { try { navigator.clipboard.writeText(link); } catch (_) {} }} data-testid="floating-referral-copy" className="mt-1.5 w-full flex items-center justify-center gap-1.5 text-[10px] text-white/70 hover:text-white border border-white/15 rounded-lg px-3 py-1.5 transition-colors"><Copy className="w-3.5 h-3.5" /> Copier le lien</button>
                </div>
              ) : (
                <motion.button onClick={() => setOpenRef(true)} data-testid="floating-referral-open"
                  animate={{ scale: [1, 1.05, 1], y: [0, -2, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                  whileHover={{ scale: 1.06 }}
                  className="w-full flex items-center justify-center gap-1.5 bg-[#0A0A0A] text-white px-2.5 py-2 rounded-xl border border-[#C19A6B] transition-colors"
                  style={{ boxShadow: "0 0 14px rgba(193,154,107,0.45)" }}>
                  <Users className="w-3.5 h-3.5 text-white shrink-0" />
                  <span className="text-[9px] font-semibold uppercase tracking-wider text-white">Parrainer</span>
                </motion.button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function NotifBell({ onOpenTab }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const load = () => {
    fetch(`${API}/member/notifications`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null).then((d) => { if (d) { setItems(d.items || []); setUnread(d.unread || 0); } }).catch(() => {});
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  const openPanel = () => {
    setOpen((v) => !v);
    if (!open && unread > 0) {
      fetch(`${API}/member/notifications/read`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: "{}" })
        .then(() => setUnread(0)).catch(() => {});
    }
  };
  const iconFor = (t) => t === "collaboration" ? Handshake : t === "mise_en_relation" ? Contact
    : t === "match_listing" ? Sparkles : t === "offer" ? Inbox : t === "comandat" ? Handshake
    : (t === "apport_commission" || t === "apporteur_threshold") ? Coins : Bell;
  const tabFor = (t) => t === "collaboration" ? "apporteur" : t === "match_listing" ? "matching_bien"
    : t === "offer" ? "offers" : t === "mise_en_relation" ? "acquereurs" : null;
  return (
    <div className="relative">
      <button onClick={openPanel} data-testid="bo-notif-bell" className="relative w-9 h-9 rounded-full bg-white ring-1 ring-white/40 text-[#C19A6B] flex items-center justify-center hover:bg-gray-100 transition-colors">
        <Bell className="w-4 h-4" />
        {unread > 0 && <span data-testid="bo-notif-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C19A6B] text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-[#0A0A0A]">{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 bg-white border border-[#E5E7EB] rounded-xl shadow-[0_12px_32px_rgba(0,0,0,0.14)] z-50 overflow-hidden" data-testid="bo-notif-panel">
            <div className="px-4 py-3 border-b border-[#F3F4F6] flex items-center justify-between">
              <p className="text-sm font-bold text-[#111827] flex items-center gap-1.5"><Bell className="w-4 h-4 text-[#C19A6B]" /> Notifications</p>
              <span className="text-[11px] text-[#9CA3AF]">{items.length}</span>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {items.length === 0 ? (
                <p className="px-4 py-8 text-sm text-[#9CA3AF] text-center">Aucune notification pour le moment.</p>
              ) : items.map((n) => { const Ic = iconFor(n.type); return (
                <button key={n.id} onClick={() => { setOpen(false); const t = tabFor(n.type); if (t) onOpenTab && onOpenTab(t); }}
                  className="w-full text-left flex items-start gap-2.5 px-4 py-3 border-b border-[#F5F6F7] last:border-0 hover:bg-[#F9FAFB] transition-colors">
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${n.read ? "bg-[#F1F3F5]" : "bg-[#C19A6B]/15"}`}><Ic className="w-4 h-4 text-[#C19A6B]" /></span>
                  <span className="min-w-0">
                    <span className="block text-[13px] font-semibold text-[#111827] flex items-center gap-1.5">{!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#C19A6B] shrink-0" />}{n.title}</span>
                    <span className="block text-xs text-[#6B7280] line-clamp-2">{n.message}</span>
                  </span>
                </button>
              ); })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function BOShell({ m, user, role, tab, setTab, wallet, onBuy, onLogout, nav, children }) {
  const isApporteur = (m.account_type || "").trim().toLowerCase() === "apporteur d'affaires";
  const [drawer, setDrawer] = useState(false);
  const [palette, setPalette] = useState(false);
  const [menu, setMenu] = useState(false);
  useEffect(() => {
    const h = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setPalette((v) => !v); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);
  const select = (t) => { setPalette(false); setDrawer(false); if (t.action) nav(t.action); else setTab(t.id); };
  const initials = (m.display_name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const BrandMini = () => (
    <Link to="/" className="flex items-center justify-center px-3 h-16 shrink-0 bg-[#0A0A0A] border-b border-white/10 overflow-hidden" data-testid="bo-brand">
      <div className="overflow-hidden flex items-start justify-center" style={{ height: "36px", width: "215px" }}>
        <img src={LOGO_URL} alt="OFF MARKETTE" style={{ width: "215px", display: "block" }} />
      </div>
    </Link>
  );
  const bottomItems = [
    { id: "network", label: "Réseau", icon: Users },
    { id: "listings", label: "Annonces", icon: Home },
  ];

  return (
    <div className="bo min-h-screen" data-testid="member-dashboard">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-64 bg-white border-r border-[#E5E7EB] z-40">
        <BrandMini />
        <SidebarNav role={role} tab={tab} onSelect={select} isApporteur={isApporteur} />
        <div className="p-3 border-t border-[#F3F4F6] space-y-2">
          <button onClick={() => select({ id: "evolution" })} data-testid="bo-see-evolutions" className="w-full rounded-lg px-3 py-2.5 text-sm font-semibold uppercase tracking-wider border bg-[#C19A6B] text-white border-[#C19A6B] hover:bg-[#A88358] transition-colors">
            <span className="flex items-center justify-center gap-2"><Sparkles className="w-4 h-4" /> Voir les évolutions</span>
          </button>
          <p className="text-[10px] text-center text-[#9CA3AF] -mt-1">Depuis juin 2026</p>
          <button onClick={onBuy} data-testid="bo-credits-chip" className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-[#FBF8F4] border border-[#C19A6B]/20 text-sm">
            <span className="flex items-center gap-2 text-[#A88358] font-medium"><Coins className="w-4 h-4" /> {wallet?.credits ?? 0} crédits</span>
            <Plus className="w-4 h-4 text-[#C19A6B]" />
          </button>
          <button onClick={onLogout} data-testid="bo-logout" className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#6B7280] hover:bg-[#F3F4F6] hover:text-[#111827] transition-colors"><LogOut className="w-4 h-4" /> Déconnexion</button>
        </div>
      </aside>

      {/* Drawer mobile */}
      <AnimatePresence>
        {drawer && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setDrawer(false)} className="lg:hidden fixed inset-0 bg-black/40 backdrop-blur-sm z-50" data-testid="bo-drawer-overlay" />
            <motion.aside initial={{ x: -300 }} animate={{ x: 0 }} exit={{ x: -300 }} transition={{ type: "tween", duration: 0.25, ease: "easeOut" }} className="lg:hidden fixed inset-y-0 left-0 w-72 bg-white z-[55] flex flex-col shadow-2xl" data-testid="bo-drawer">
              <div className="flex items-center justify-between h-16 px-4 border-b border-[#F3F4F6]"><BrandMini /><button onClick={() => setDrawer(false)} data-testid="bo-drawer-close" className="text-[#6B7280]"><X className="w-5 h-5" /></button></div>
              <SidebarNav role={role} tab={tab} onSelect={select} isApporteur={isApporteur} />
              <div className="p-3 border-t border-[#F3F4F6]"><button onClick={onLogout} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#6B7280]"><LogOut className="w-4 h-4" /> Déconnexion</button></div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Zone principale */}
      <div className="lg:pl-64 min-h-screen flex flex-col">
        {/* Topbar premium noire */}
        <header className="sticky top-0 z-30 h-16 bg-[#0A0A0A] text-white border-b border-white/10 flex items-center gap-2 px-4 sm:px-6" data-testid="bo-topbar">
          <button onClick={() => setDrawer(true)} data-testid="bo-hamburger" className="lg:hidden text-white hover:text-white p-1"><Menu className="w-5 h-5" /></button>
          {tab === "accueil" ? (
            <h1 className="truncate shrink" data-testid="bo-title"><span style={{ fontFamily: "'Dancing Script', cursive", fontSize: "1.3rem", fontWeight: 700, lineHeight: 1, color: "#C19A6B", textTransform: "none", letterSpacing: 0 }}>Votre nouvel outil préféré</span></h1>
          ) : (
            <h1 className="truncate shrink" data-testid="bo-title"><span style={{ fontFamily: "'Dancing Script', cursive", fontSize: "1.3rem", fontWeight: 700, lineHeight: 1, color: "#C19A6B", textTransform: "none", letterSpacing: 0 }}>{moduleLabel(tab)}</span></h1>
          )}
          {m.status === "approved" && (
            <div className="ml-auto hidden md:flex items-center gap-1.5 shrink-0">
              <a href="/" target="_blank" rel="noopener noreferrer" data-testid="bo-topbar-national" style={{ borderRadius: 0 }} className="inline-flex items-center gap-1 rounded-none text-[10px] font-semibold uppercase tracking-wider text-white bg-[#111827] border border-white/20 hover:bg-[#C19A6B] hover:text-white hover:border-[#C19A6B] px-2 py-1 transition-colors"><Home className="w-3.5 h-3.5" /> Voir site OFF MARKETTE</a>
              <a href={`/${m.slug}/site`} target="_blank" rel="noopener noreferrer" data-testid="bo-topbar-mysite" style={{ borderRadius: 0 }} className="inline-flex items-center gap-1 rounded-none text-[10px] font-semibold uppercase tracking-wider text-white bg-[#C19A6B] border border-transparent hover:bg-white hover:text-[#111827] hover:border-white px-2 py-1 transition-colors"><Globe className="w-3.5 h-3.5" /> Voir mon site</a>
              <a href={`/${m.slug}`} target="_blank" rel="noopener noreferrer" data-testid="bo-topbar-mypage" style={{ borderRadius: 0 }} className="inline-flex items-center gap-1 rounded-none text-[10px] font-semibold uppercase tracking-wider text-[#111827] bg-white hover:bg-gray-100 px-2 py-1 transition-colors"><ExternalLink className="w-3.5 h-3.5 text-[#111827]" /> Voir ma page</a>
              <button onClick={() => setTab("filleuls")} data-testid="bo-topbar-filleuls" style={{ borderRadius: 0 }} className="inline-flex items-center gap-1 rounded-none text-[10px] font-semibold uppercase tracking-wider text-[#C19A6B] bg-white hover:bg-gray-100 px-2 py-1 transition-colors"><Users className="w-3.5 h-3.5 text-[#C19A6B]" /> Mes filleuls</button>
            </div>
          )}
          <button onClick={() => setPalette(true)} data-testid="bo-search-btn" className={`${m.status === "approved" ? "" : "ml-auto"} hidden lg:flex items-center gap-2 text-[10px] uppercase tracking-wider font-semibold text-[#374151] bg-white/85 backdrop-blur-md hover:bg-white border border-white/50 rounded-none px-2.5 py-1.5 transition-colors w-32 shrink-0`}>
            <Search className="w-3.5 h-3.5 text-[#6B7280]" /> Rechercher… <kbd className="ml-auto text-[9px] border border-[#D1D5DB] rounded-none px-1 py-0.5 text-[#6B7280]">⌘K</kbd>
          </button>
          <button onClick={() => setPalette(true)} data-testid="bo-search-btn-mobile" className="lg:hidden text-white p-1 ml-auto"><Search className="w-5 h-5" /></button>
          <button onClick={() => nav("/visite-ia")} data-testid="bo-topbar-visiteia" className="hidden sm:inline-flex items-center gap-1.5 rounded-none bg-[#C19A6B] hover:bg-[#A88358] text-white text-xs font-semibold px-3 py-1.5 transition-colors shrink-0"><Wand2 className="w-3.5 h-3.5" /> Visite IA</button>
          <NotifBell onOpenTab={setTab} />
          <div className="relative">
            <button onClick={() => setMenu((v) => !v)} data-testid="bo-avatar" className="w-9 h-9 rounded-full bg-[#D1D5DB] ring-1 ring-white/30 text-[#C19A6B] text-xs font-bold flex items-center justify-center overflow-hidden">{m.photo ? <img src={m.photo} alt="" className="w-full h-full object-cover" /> : initials}</button>
            {menu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMenu(false)} />
                <div className="absolute right-0 mt-2 w-60 bg-white border border-[#E5E7EB] rounded-xl shadow-[0_12px_32px_rgba(0,0,0,0.12)] py-1.5 z-50" data-testid="bo-avatar-menu">
                  <div className="px-3 py-2 border-b border-[#F3F4F6]"><p className="text-sm font-semibold text-[#111827] truncate">{m.display_name}</p><p className="text-xs text-[#9CA3AF] truncate">{m.account_type}</p></div>
                  {m.status === "approved" && <a href={`/${m.slug}/site`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 text-sm text-[#374151] hover:bg-[#F9FAFB]"><Globe className="w-4 h-4 text-[#C19A6B]" /> Voir mon site</a>}
                  {m.status === "approved" && <a href={`/${m.slug}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 text-sm text-[#374151] hover:bg-[#F9FAFB]"><ExternalLink className="w-4 h-4 text-[#C19A6B]" /> Ma page publique</a>}
                  <button onClick={() => { setMenu(false); setTab("filleuls"); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#374151] hover:bg-[#F9FAFB]"><Users className="w-4 h-4 text-[#C19A6B]" /> Mes filleuls</button>
                  <button onClick={() => { setMenu(false); setTab("profile"); }} data-testid="bo-menu-infos" className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#374151] hover:bg-[#F9FAFB]"><User className="w-4 h-4 text-[#C19A6B]" /> Mes infos perso</button>
                  <Link to="/favoris" className="flex items-center gap-2 px-3 py-2 text-sm text-[#374151] hover:bg-[#F9FAFB]"><Heart className="w-4 h-4 text-[#C19A6B]" /> Mes favoris</Link>
                  {(user?.role === "admin" || user?.role === "super_admin") && <Link to="/admin" className="flex items-center gap-2 px-3 py-2 text-sm text-[#374151] hover:bg-[#F9FAFB]"><ShieldCheck className="w-4 h-4 text-[#C19A6B]" /> Back-Office complet</Link>}
                  <button onClick={onLogout} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#EF4444] hover:bg-[#FEF2F2]"><LogOut className="w-4 h-4" /> Déconnexion</button>
                </div>
              </>
            )}
          </div>
        </header>

        {/* Barre de sous-onglets (par catégorie) */}
        <SubTabBar role={role} tab={tab} onSelect={select} isApporteur={isApporteur} />

        {/* Contenu */}
        <main className="flex-1 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-28 lg:pb-8" data-testid="bo-content">
          <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, ease: "easeOut" }}>
            {children}
          </motion.div>
        </main>
      </div>

      {/* Bottom nav mobile + FAB Visite IA */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 h-[68px] bg-white/90 backdrop-blur-md border-t border-[#E5E7EB] shadow-[0_-4px_24px_rgba(0,0,0,0.05)] grid grid-cols-5 items-center px-1" data-testid="bo-bottom-nav">
        {bottomItems.map((b) => { const Ic = b.icon; const active = tab === b.id; return (
          <button key={b.id} onClick={() => setTab(b.id)} data-testid={`bo-bottom-${b.id}`} className={`flex flex-col items-center gap-1 py-2 text-[10px] font-medium ${active ? "text-[#C19A6B]" : "text-[#9CA3AF]"}`}><Ic className="w-5 h-5" strokeWidth={1.75} /> {b.label}</button>
        ); })}
        <div className="flex justify-center">
          <button onClick={() => nav("/visite-ia")} data-testid="bo-fab-visiteia" className="-mt-8 w-16 h-16 rounded-full bg-[#C19A6B] text-white flex flex-col items-center justify-center shadow-[0_8px_24px_rgba(193,154,107,0.5)] active:scale-95 transition-transform border-4 border-white">
            <Wand2 className="w-5 h-5" /><span className="text-[8px] font-bold mt-0.5">VISITE IA</span>
          </button>
        </div>
        <button onClick={() => setTab("agenda")} data-testid="bo-bottom-agenda" className={`flex flex-col items-center gap-1 py-2 text-[10px] font-medium ${tab === "agenda" ? "text-[#C19A6B]" : "text-[#9CA3AF]"}`}><Clock className="w-5 h-5" strokeWidth={1.75} /> Agenda</button>
        <button onClick={() => setDrawer(true)} data-testid="bo-bottom-more" className="flex flex-col items-center gap-1 py-2 text-[10px] font-medium text-[#9CA3AF]"><MoreHorizontal className="w-5 h-5" strokeWidth={1.75} /> Menu</button>
      </nav>

      <CommandPalette role={role} open={palette} onClose={() => setPalette(false)} onSelect={select} />
      <FloatingWidgets m={m} wallet={wallet} onBuy={onBuy} />
    </div>
  );
}


// ---------- Formulaire de complétion de profil (après Google) ----------
function CompleteProfile({ onDone }) {
  const [f, setF] = useState({ account_type: "", account_type_other: "", first_name: "", last_name: "", phone: "", company: "", city: "", sector: "", website: "", description: "", vision: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.account_type) { setErr("Choisissez votre profil."); return; }
    setErr(""); setLoading(true);
    try {
      let ref = "";
      try { ref = sessionStorage.getItem("om_ref") || ""; } catch (_) {}
      await authApi.completeProfile({ ...f, ref });
      try { sessionStorage.removeItem("om_ref"); } catch (_) {}
      onDone();
    }
    catch (e) { setErr(e.message || "Erreur."); }
    setLoading(false);
  };
  return (
    <div className="max-w-2xl mx-auto" data-testid="complete-profile">
      <div className="text-center space-y-2 mb-8">
        <div className="w-12 h-12 mx-auto rounded-full bg-[#C19A6B]/15 flex items-center justify-center"><Building2 className="w-5 h-5 text-[#C19A6B]" /></div>
        <h1 className="text-2xl font-light text-white">Finalisez votre affiliation <Brand /></h1>
        <p className="text-sm text-zinc-400">Complétez vos informations professionnelles pour rejoindre le réseau.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        {TYPES.map((t) => (
          <button key={t} onClick={() => set("account_type", t)} className={`text-left p-3 border text-sm transition-colors ${f.account_type === t ? "border-[#C19A6B] bg-[#C19A6B]/10 text-white" : "border-white/10 text-zinc-300 hover:border-white/30"}`}>{t}</button>
        ))}
      </div>
      {f.account_type === "Autre" && <input className={`${inp} mb-3`} placeholder="Précisez" value={f.account_type_other} onChange={(e) => set("account_type_other", e.target.value)} />}
      <div className="grid sm:grid-cols-2 gap-3">
        <input className={inp} placeholder="Prénom" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} />
        <input className={inp} placeholder="Nom" value={f.last_name} onChange={(e) => set("last_name", e.target.value)} />
        <input className={inp} placeholder="Téléphone" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
        <input className={inp} placeholder="Société" value={f.company} onChange={(e) => set("company", e.target.value)} />
        <input className={inp} placeholder="Ville principale" value={f.city} onChange={(e) => set("city", e.target.value)} />
        <input className={inp} placeholder="Secteur" value={f.sector} onChange={(e) => set("sector", e.target.value)} />
      </div>
      <textarea className={`${inp} mt-3 min-h-[80px]`} placeholder="Présentation" value={f.description} onChange={(e) => set("description", e.target.value)} />
      {err && <p className="text-sm text-red-400 mt-3" data-testid="complete-error">{err}</p>}
      <button onClick={submit} disabled={loading} data-testid="complete-submit" className="mt-6 inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-60 text-white font-medium uppercase tracking-wider text-sm px-6 py-3 transition-colors">
        {loading ? "Envoi…" : "Envoyer ma demande"} <Check className="w-4 h-4" />
      </button>
    </div>
  );
}

// ---------- Éditeur d'annonce ----------
const emptyListing = { title: "", price: 0, type: "Maison", collection: "Habitat", city: "", department: "Oise (60)", address: "", surface: 0, land: 0, rooms: 0, bedrooms: 0, dpe: "", summary: "", description: "", cover: "", images: [], highlights: [], video_url: "" };


function ListingTextGenerate({ slug, onApply }) {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState("");
  const run = async () => {
    setBusy(true); setErr("");
    try {
      const d = await authApi.generateListingContent(slug);
      setData(d);
      onApply && onApply(d);
    } catch (e) { setErr(e.detail?.message || e.message || "Génération impossible"); }
    setBusy(false);
  };
  const copy = (k, t) => { navigator.clipboard.writeText(t || ""); setCopied(k); setTimeout(() => setCopied(""), 1500); };
  const social = data?.social || {};
  return (
    <div className="border border-[#C19A6B]/30 bg-[#C19A6B]/5 p-4 space-y-3 rounded-[5px]" data-testid="listing-text-generate">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#C19A6B]" /><p className="text-sm font-medium text-white">Étape 1 · Générer les textes (description + réseaux)</p></div>
        <button type="button" onClick={run} disabled={busy} data-testid="gen-texts-btn" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 text-white text-xs font-semibold uppercase tracking-wider px-4 py-2 rounded-[5px] transition-colors">{busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Génération…</> : <><Wand2 className="w-4 h-4" /> Générer les textes IA</>}</button>
      </div>
      <p className="text-xs text-zinc-400 -mt-1">À partir des infos récupérées (lien ou saisie), l'IA rédige la description commerciale et les publications réseaux — à faire avant le home staging.</p>
      {err && <p className="text-xs text-red-400" data-testid="gen-texts-err">{err}</p>}
      {data && (
        <div className="space-y-2" data-testid="gen-texts-result">
          <p className="text-xs text-green-400 flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Description & points forts appliqués à l'annonce.</p>
          {["instagram", "facebook", "linkedin"].filter((k) => social[k]).map((k) => (
            <div key={k} className="bg-white/[0.04] border border-white/10 rounded-[5px] p-2.5">
              <div className="flex items-center justify-between mb-1"><span className="text-[10px] uppercase tracking-wider text-[#C19A6B]">{k}</span><button type="button" onClick={() => copy(k, social[k])} className="text-[10px] text-zinc-300 hover:text-white inline-flex items-center gap-1">{copied === k ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} Copier</button></div>
              <p className="text-xs text-zinc-300 whitespace-pre-wrap">{social[k]}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function ListingEditor({ listing, onClose, onSaved, onCreated }) {
  const [f, setF] = useState(() => ({ ...emptyListing, ...(listing || {}), highlights: (listing?.highlights || []).join(", ") }));
  const [loading, setLoading] = useState(false);
  const [hlBusy, setHlBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProg, setUploadProg] = useState(null);
  const [err, setErr] = useState("");
  const [protect, setProtect] = useState(!!(listing && listing.access_password));
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const moveImg = (i, dir) => setF((p) => {
    const a = [...p.images];
    const j = i + dir;
    if (j < 0 || j >= a.length) return p;
    [a[i], a[j]] = [a[j], a[i]];
    return { ...p, images: a };
  });

  const doUpload = async (file, target) => {
    setUploading(true);
    try {
      const d = await uploadMemberFile(file);
      if (target === "cover") set("cover", d.url);
      else if (target === "video") set("video_url", d.url);
      else setF((p) => ({ ...p, images: [...p.images, d.url] }));
    } catch (e) { setErr("Upload impossible"); }
    setUploading(false);
  };

  // Multi-upload : ajoute plusieurs photos en une seule opération (sans supprimer l'existant)
  const doUploadMulti = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true); setUploadProg({ done: 0, total: list.length });
    const added = [];
    for (const file of list) {
      try { const d = await uploadMemberFile(file); if (d?.url) added.push(d.url); } catch (_) {}
      setUploadProg((p) => ({ done: (p?.done || 0) + 1, total: list.length }));
    }
    if (added.length) setF((p) => ({ ...p, images: [...p.images, ...added], cover: p.cover || added[0] }));
    else setErr("Aucune photo n'a pu être envoyée.");
    setUploading(false); setUploadProg(null);
  };
  const clearGallery = () => {
    if (!f.images.length) return;
    if (window.confirm(`Vider toute la galerie (${f.images.length} photo${f.images.length > 1 ? "s" : ""}) ? Les nouvelles photos que vous ajouterez la remplaceront. (Le home staging éventuel n'est pas supprimé.)`)) {
      setF((p) => ({ ...p, images: [], cover: "" }));
    }
  };

  const save = async () => {
    if (!f.title.trim()) { setErr("Le titre est requis."); return; }
    setErr(""); setLoading(true);
    const payload = {
      ...f,
      price: Number(f.price) || 0, surface: Number(f.surface) || 0, land: Number(f.land) || 0,
      rooms: Number(f.rooms) || 0, bedrooms: Number(f.bedrooms) || 0,
      highlights: (typeof f.highlights === "string" ? f.highlights.split(",") : f.highlights).map((s) => s.trim()).filter(Boolean),
      cover: f.cover || f.images[0] || "",
    };
    payload.access_password = protect ? (f.access_password || "").trim() : "";
    payload.confidential_mode = protect ? "password" : "classic";
    payload.visibility_mode = f.visibility_mode || "national";
    payload.network_also_national = payload.visibility_mode === "network" ? !!f.network_also_national : false;
    payload.commission_rate = (f.commission_rate !== "" && f.commission_rate != null) ? Number(f.commission_rate) : null;
    delete payload.published;
    try {
      if (listing?.slug) { await authApi.updateListing(listing.slug, payload); onSaved(); }
      else { const created = await authApi.createListing(payload); (onCreated || onSaved)(created?.slug); }
    } catch (e) {
      const msg = e.status === 402 && e.detail?.message ? e.detail.message : (e.message || "Enregistrement impossible");
      setErr(msg); setLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm overflow-y-auto py-10 px-4" data-testid="listing-editor">
      <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="max-w-3xl mx-auto bg-[#0d0d10] border border-white/10 p-6 sm:p-8 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-light text-white">{listing ? "Modifier l'annonce" : "Nouvelle annonce"}</h3>
          <div className="flex items-center gap-3">
            {listing && listing.slug && (
              <button type="button" onClick={async () => { try { const d = await authApi.sellerLink(listing.slug); const url = PUBLIC_SITE + d.path; await navigator.clipboard.writeText(url); alert("Lien espace vendeur copié :\n" + url); } catch (_) { alert("Impossible de générer le lien."); } }} data-testid="seller-link-btn" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider border border-[#C19A6B]/50 text-[#C19A6B] hover:bg-[#C19A6B]/10 px-3 py-1.5 transition-colors"><Link2 className="w-3.5 h-3.5" /> Lien vendeur</button>
            )}
            <button onClick={onClose} data-testid="editor-close" className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <input className={inp} placeholder="Titre du bien *" value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="ed-title" />
        <textarea className={`${inp} min-h-[60px]`} placeholder="Résumé (1-2 lignes)" value={f.summary} onChange={(e) => set("summary", e.target.value)} data-testid="ed-summary" />
        <div className="grid sm:grid-cols-3 gap-3">
          <input type="number" className={inp} placeholder="Prix (€)" value={f.price || ""} onChange={(e) => set("price", e.target.value)} data-testid="ed-price" />
          <select className={inp} value={f.type} onChange={(e) => set("type", e.target.value)} data-testid="ed-type">{PROP_TYPES.map((t) => <option key={t} value={t} className="bg-[#0d0d10]">{t}</option>)}</select>
          <select className={inp} value={f.collection} onChange={(e) => set("collection", e.target.value)}>{COLLECTIONS.map((t) => <option key={t} value={t} className="bg-[#0d0d10]">{t}</option>)}</select>
          <input className={inp} placeholder="Ville" value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="ed-city" />
          <input className={inp} placeholder="Département" value={f.department} onChange={(e) => set("department", e.target.value)} />
          <input className={inp} placeholder="Adresse" value={f.address} onChange={(e) => set("address", e.target.value)} />
          <input type="number" className={inp} placeholder="Surface (m²)" value={f.surface || ""} onChange={(e) => set("surface", e.target.value)} />
          <input type="number" className={inp} placeholder="Terrain (m²)" value={f.land || ""} onChange={(e) => set("land", e.target.value)} />
          <input className={inp} placeholder="DPE (A-G)" value={f.dpe} onChange={(e) => set("dpe", e.target.value)} />
          <input type="number" className={inp} placeholder="Pièces" value={f.rooms || ""} onChange={(e) => set("rooms", e.target.value)} />
          <input type="number" className={inp} placeholder="Chambres" value={f.bedrooms || ""} onChange={(e) => set("bedrooms", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <label className="text-xs uppercase tracking-wider text-zinc-500">Points forts (séparés par des virgules)</label>
            {listing?.slug && (
              <button type="button" onClick={async () => {
                setHlBusy(true);
                try {
                  const res = await authApi.auditRegenHighlights(listing.slug);
                  if (res.highlights) set("highlights", res.highlights.join(", "));
                } catch (e) { /* noop */ } finally { setHlBusy(false); }
              }} disabled={hlBusy} data-testid="editor-regen-highlights"
                className="inline-flex items-center gap-1 text-[11px] text-[#C19A6B] border border-[#C19A6B]/40 hover:bg-[#C19A6B]/10 px-2 py-1 transition-colors disabled:opacity-40">
                {hlBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Régénérer les points forts
              </button>
            )}
          </div>
          <input className={inp} placeholder="Ex. Grand jardin 1 300 m², Vue dégagée, Garage double" value={f.highlights} onChange={(e) => set("highlights", e.target.value)} data-testid="editor-highlights" />
          <p className="text-[10px] text-zinc-500">Étiquettes courtes de 3 à 6 mots (4 à 8 idéalement).</p>
        </div>
        <textarea className={`${inp} min-h-[100px]`} placeholder="Description détaillée" value={f.description} onChange={(e) => set("description", e.target.value)} />

        <div>
          <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Photo de couverture</p>
          <div className="flex items-center gap-3">
            {f.cover && <img src={f.cover} alt="" className="w-24 h-16 object-cover border border-white/10" />}
            <label className="flex items-center gap-2 border border-dashed border-white/20 px-4 py-3 cursor-pointer text-sm text-zinc-300 hover:border-[#C19A6B]/50 transition-colors" data-testid="ed-upload-cover">
              <Upload className="w-4 h-4 text-[#C19A6B]" /> Choisir
              <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && doUpload(e.target.files[0], "cover")} />
            </label>
          </div>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Galerie ({f.images.length}) <span className="text-zinc-600 normal-case">— déplacer / définir en couverture / supprimer</span></p>
          <div className="flex flex-wrap gap-2 mb-2">
            {f.images.map((im, i) => (
              <div key={i} className="relative group">
                <img src={im} alt="" className={`w-20 h-14 object-cover border ${f.cover === im ? "border-[#C19A6B]" : "border-white/10"}`} />
                {f.cover === im && <span className="absolute top-0.5 left-0.5 bg-[#C19A6B] text-white text-[7px] font-bold uppercase px-1">Couv.</span>}
                <button onClick={() => setF((p) => ({ ...p, images: p.images.filter((_, j) => j !== i) }))} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5" title="Supprimer"><X className="w-3 h-3" /></button>
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button type="button" onClick={() => moveImg(i, -1)} disabled={i === 0} className="text-white px-1 disabled:opacity-30" title="Déplacer avant"><ArrowLeft className="w-3 h-3" /></button>
                  <button type="button" onClick={() => set("cover", im)} className="text-[#C19A6B] text-[8px] font-bold uppercase px-0.5" title="Définir comme couverture">Couv.</button>
                  <button type="button" onClick={() => moveImg(i, 1)} disabled={i === f.images.length - 1} className="text-white px-1 disabled:opacity-30" title="Déplacer après"><ArrowRight className="w-3 h-3" /></button>
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label className={`inline-flex items-center gap-2 border border-dashed px-4 py-3 cursor-pointer text-sm transition-colors ${uploading ? "border-[#C19A6B]/40 text-zinc-500" : "border-white/20 text-zinc-300 hover:border-[#C19A6B]/50"}`} data-testid="ed-upload-image">
              {uploading ? <><Loader2 className="w-4 h-4 text-[#C19A6B] animate-spin" /> Envoi {uploadProg ? `${uploadProg.done}/${uploadProg.total}` : ""}…</> : <><Upload className="w-4 h-4 text-[#C19A6B]" /> Ajouter des photos (plusieurs à la fois)</>}
              <input type="file" accept="image/*" multiple disabled={uploading} className="hidden" onChange={(e) => { if (e.target.files.length) doUploadMulti(e.target.files); e.target.value = ""; }} />
            </label>
            {f.images.length > 0 && <button type="button" onClick={clearGallery} disabled={uploading} data-testid="ed-clear-gallery" className="inline-flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 border border-red-500/30 hover:border-red-500/50 px-3 py-2 transition-colors disabled:opacity-40"><Trash2 className="w-3.5 h-3.5" /> Remplacer la galerie</button>}
          </div>
          <p className="text-[11px] text-zinc-500 mt-1.5">Sélectionnez plusieurs fichiers d'un coup (ou glissez-les depuis votre ordinateur). Les nouvelles photos s'ajoutent à la suite — rien n'est supprimé sans votre confirmation.</p>
        </div>

        <div className="border border-white/10 p-3 space-y-2">
          <p className="text-xs uppercase tracking-wider text-zinc-500">Vidéo du bien (prioritaire dans le hero)</p>
          <input className={inp} placeholder="Lien YouTube, Vimeo ou URL .mp4" value={f.video_url || ""} onChange={(e) => set("video_url", e.target.value)} data-testid="ed-video-url" />
          <div className="flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-2 border border-dashed border-white/20 px-4 py-2.5 cursor-pointer text-sm text-zinc-300 hover:border-[#C19A6B]/50 transition-colors" data-testid="ed-upload-video">
              <Upload className="w-4 h-4 text-[#C19A6B]" /> Téléverser une vidéo
              <input type="file" accept="video/*" className="hidden" onChange={(e) => e.target.files[0] && doUpload(e.target.files[0], "video")} />
            </label>
            {f.video_url && <button type="button" onClick={() => set("video_url", "")} className="text-xs text-red-400 hover:underline" data-testid="ed-video-remove">Retirer</button>}
          </div>
          {f.video_url && <p className="text-[11px] text-[#C19A6B]">Vidéo active — elle remplacera les photos dans le hero.</p>}
        </div>

        {/* Réseau Privé B2B — Commission d'agence (base des calculs d'apport d'acquéreur) */}
        <div className="border border-white/10 p-4 space-y-3" data-testid="ed-commission">
          <p className="text-xs uppercase tracking-wider text-zinc-500">Votre commission d'agence</p>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <input type="number" step="0.1" min="0" className={`${inp} w-40 pr-8`} placeholder="Taux (%)" value={f.commission_rate ?? ""} onChange={(e) => set("commission_rate", e.target.value)} data-testid="ed-commission-rate" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">%</span>
            </div>
            {(() => {
              const rate = Number(f.commission_rate) || 0; const price = Number(f.price) || 0;
              if (!rate || !price) return <span className="text-xs text-zinc-500">Renseignez le prix et le taux pour estimer les honoraires.</span>;
              const fees = Math.round(price * rate / 100);
              const app = Math.round(fees * 8) / 100; const plat = Math.round(fees * 2) / 100;
              const fmt = (n) => (Math.round(n)).toLocaleString("fr-FR") + " €";
              return (
                <div className="text-xs text-zinc-300 leading-relaxed" data-testid="ed-commission-preview">
                  Honoraires estimés : <b className="text-white">{fmt(fees)}</b>
                  <span className="text-zinc-500"> · si apport d'acquéreur → apporteur <b className="text-[#C19A6B]">{fmt(app)}</b> (8%) · plateforme {fmt(plat)} (2%) · vous conservez {fmt(fees - app - plat)}</span>
                </div>
              );
            })()}
          </div>
          <p className="text-[11px] text-zinc-500">Sert de base au calcul automatique de la commission d'un apporteur d'acquéreur (8% apporteur / 2% plateforme). Le co-mandat reste un accord distinct.</p>
        </div>

        {/* Réseau Privé B2B — Visibilité de l'annonce (source unique de vérité) */}
        <div className="border border-white/10 p-4 space-y-3" data-testid="ed-visibility">
          <p className="text-xs uppercase tracking-wider text-zinc-500">Visibilité de l'annonce</p>
          <div className="grid sm:grid-cols-3 gap-2">
            {[
              { id: "national", label: "National", icon: <Globe className="w-4 h-4 text-[#C19A6B]" />, desc: "Visible par tous : portail national, votre page/site et vos partenaires validés." },
              { id: "network", label: "Réseau partenaires", icon: <Users className="w-4 h-4 text-[#C19A6B]" />, desc: "Votre page/site + partenaires validés uniquement. Option de diffusion nationale." },
              { id: "private", label: "Personnel", icon: <EyeOff className="w-4 h-4 text-[#C19A6B]" />, desc: "Uniquement votre compte/page. Jamais sur le portail national ni chez les partenaires." },
            ].map((o) => (
              <button key={o.id} type="button" onClick={() => set("visibility_mode", o.id)} data-testid={`ed-vis-${o.id}`}
                className={`text-left p-3 border transition-colors ${(f.visibility_mode || "national") === o.id ? "border-[#C19A6B] bg-[#C19A6B]/10" : "border-white/10 hover:border-white/25"}`}>
                <div className="flex items-center gap-2 text-white text-sm font-medium">{o.icon}{o.label}</div>
                <p className="text-[11px] text-zinc-400 mt-1 leading-snug">{o.desc}</p>
              </button>
            ))}
          </div>
          {(f.visibility_mode || "national") === "network" && (
            <label className="flex items-center gap-2 text-sm text-zinc-200 cursor-pointer select-none pt-1" data-testid="ed-also-national-label">
              <input type="checkbox" checked={!!f.network_also_national} onChange={(e) => set("network_also_national", e.target.checked)} data-testid="ed-also-national" className="accent-[#C19A6B] w-4 h-4" />
              Diffuser aussi cette annonce sur le portail national public
            </label>
          )}
        </div>

        {/* Lot E — Confidentialité : mot de passe indépendant par annonce */}
        <div className="border border-white/10 p-3 space-y-2" data-testid="ed-protection">
          <label className="flex items-center gap-2 text-sm text-zinc-200 cursor-pointer select-none">
            <input type="checkbox" checked={protect} onChange={(e) => setProtect(e.target.checked)} data-testid="ed-protect-toggle" className="accent-[#C19A6B] w-4 h-4" />
            <ShieldCheck className="w-4 h-4 text-[#C19A6B]" /> Protéger cette annonce par un mot de passe
          </label>
          <p className="text-[11px] text-zinc-500">Idéal pour vos biens confidentiels (profils Personnel / Privé / Réseau). Seuls les visiteurs disposant du mot de passe pourront ouvrir la fiche. Vous, les conseillers approuvés et l'administrateur en êtes dispensés.</p>
          {protect && (
            <input className={inp} type="text" placeholder="Mot de passe de l'annonce (ex : PRIVE2026)" value={f.access_password || ""} onChange={(e) => set("access_password", e.target.value)} data-testid="ed-access-password" />
          )}
        </div>

        <div className="flex items-start gap-3 text-sm text-zinc-300 border border-[#C19A6B]/20 bg-[#C19A6B]/5 px-4 py-3">
          <Info className="w-4 h-4 text-[#C19A6B] mt-0.5 shrink-0" />
          <span>Votre annonce est enregistrée en <b className="text-white">prévisualisation</b>. Utilisez le bouton <b className="text-[#C19A6B]">« Publier »</b> depuis vos annonces pour la mettre en ligne (crédits requis selon le niveau).</span>
        </div>

        {uploading && <p className="text-xs text-[#C19A6B]">Téléversement en cours…</p>}
        {err && <p className="text-sm text-red-400" data-testid="editor-error">{err}</p>}
        <div className="flex items-center justify-end gap-3 pt-2">
          <button onClick={onClose} className="text-sm text-zinc-400 hover:text-white uppercase tracking-wider">Annuler</button>
          <button onClick={save} disabled={loading} data-testid="editor-save" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-60 text-white font-medium uppercase tracking-wider text-sm px-6 py-3 transition-colors">
            {loading ? "Enregistrement…" : "Enregistrer"} <Check className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function MatchProposalsSection() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const reload = () => authApi.matchProposals().then(setData).catch(() => setData({ received: [], sent: [] }));
  useEffect(() => { reload(); }, []);
  const act = async (id, kind) => {
    setBusy(id + kind);
    try { kind === "accept" ? await authApi.matchProposalAccept(id) : await authApi.matchProposalDecline(id); await reload(); } catch (_) {}
    setBusy("");
  };
  const received = (data?.received || []).filter((p) => p.status === "pending");
  const pastRecv = (data?.received || []).filter((p) => p.status !== "pending");
  const sent = data?.sent || [];
  const eur = (n) => (typeof n === "number" && n) ? `${Math.round(n).toLocaleString("fr-FR")} €` : "";
  const terms = (p) => p.kind === "comandat"
    ? `Co-mandat ${p.owner_pct}/${(p.commission?.buyer_side_pct) || 25} — ${p.partner_pct}% pour vous (+${p.site_pct || 2}% OFF MARKETTE)`
    : `Partenariat — rétro ${p.commission?.retro_pct || 10}% (${p.partner_pct}% pour vous, ${p.site_pct || 2}% OFF MARKETTE)`;
  if (!data) return null;
  if (!received.length && !pastRecv.length && !sent.length) return null;
  const Card = ({ p, role }) => (
    <div className="border border-[#E5E7EB] bg-white rounded-xl overflow-hidden flex flex-col sm:flex-row" data-testid={`match-proposal-${p.id}`}>
      {p.cover && <img src={p.cover} alt="" className="w-full sm:w-40 h-32 sm:h-auto object-cover" />}
      <div className="p-4 flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[#111827] truncate">{p.listing_title}{p.listing_city ? ` · ${p.listing_city}` : ""}</p>
            <p className="text-xs text-zinc-500">{eur(p.listing_price)} · {role === "received" ? `de ${p.counterparty?.display_name}` : `à ${p.counterparty?.display_name}`}{p.acq_label ? ` · pour ${p.acq_label}` : ""}</p>
          </div>
          {p.status !== "pending" && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${p.status === "accepted" ? "bg-[#10B981]/10 text-[#10B981]" : "bg-zinc-100 text-zinc-500"}`}>{p.status === "accepted" ? "Accepté" : "Décliné"}</span>}
        </div>
        <p className="text-[11px] text-[#A88358] mt-1.5 font-medium">{terms(p)}</p>
        {role === "received" && p.status === "pending" && (
          <div className="flex items-center gap-2 mt-3">
            <button onClick={() => act(p.id, "accept")} disabled={!!busy} className="inline-flex items-center gap-1 bg-[#10B981] hover:bg-[#0e9f74] disabled:opacity-50 text-white text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors" data-testid={`match-proposal-accept-${p.id}`}>{busy === p.id + "accept" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Accepter (créer le co-mandat)</button>
            <button onClick={() => act(p.id, "decline")} disabled={!!busy} className="inline-flex items-center gap-1 bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50 disabled:opacity-50 text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors" data-testid={`match-proposal-decline-${p.id}`}><X className="w-3.5 h-3.5" /> Décliner</button>
          </div>
        )}
        {role === "sent" && p.status === "pending" && <p className="text-[11px] text-zinc-400 mt-2">En attente de réponse du partenaire…</p>}
      </div>
    </div>
  );
  return (
    <div className="space-y-3" data-testid="match-proposals-section">
      <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Send className="w-4 h-4 text-[#C19A6B]" /> Propositions de match</h3>
      {received.length > 0 && <div className="space-y-2">{received.map((p) => <Card key={p.id} p={p} role="received" />)}</div>}
      {sent.length > 0 && <div className="space-y-2"><p className="text-[11px] uppercase tracking-wider text-zinc-500">Envoyées ({sent.length})</p>{sent.map((p) => <Card key={p.id} p={p} role="sent" />)}</div>}
      {pastRecv.length > 0 && <div className="space-y-2 opacity-70"><p className="text-[11px] uppercase tracking-wider text-zinc-500">Reçues traitées</p>{pastRecv.map((p) => <Card key={p.id} p={p} role="received" />)}</div>}
    </div>
  );
}

function ComandatTab() {
  const [accepted, setAccepted] = useState([]);
  useEffect(() => {
    authApi.netPartnerships().then((d) => setAccepted(d?.accepted || [])).catch(() => {});
  }, []);
  return (
    <div className="space-y-6" data-testid="comandat-tab">
      <div>
        <h1 className="calli text-3xl sm:text-4xl leading-none text-[#C19A6B]">Co-mandats &amp; rétrocommissions</h1>
        <p className="text-sm text-zinc-400 mt-1.5">Partagez un mandat avec un partenaire de votre réseau et suivez vos rétrocommissions en toute transparence.</p>
      </div>
      <MatchProposalsSection />
      <ComandatSection partners={accepted} hideTitle />
      <MemberComandats />
      <MemberPaymentsHistory />
    </div>
  );
}


// ---------- Onglet profil ----------
function ProfileTab({ member, onSaved }) {
  const { logout } = useAuth();
  const isPaused = member.status === "paused";
  const [acctBusy, setAcctBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const togglePause = async () => {
    setAcctBusy(true);
    try { await authApi.pauseAccount(!isPaused); onSaved(); } catch (_) {}
    setAcctBusy(false);
  };
  const deleteAcct = async () => {
    setAcctBusy(true);
    try { await authApi.deleteAccount(); await logout(); window.location.href = "/"; } catch (_) { setAcctBusy(false); }
  };
  const [f, setF] = useState({ first_name: member.first_name || "", last_name: member.last_name || "", phone: member.phone || "", company: member.company || "", city: member.city || "", sector: member.sector || "", website: member.website || "", description: member.description || "", vision: member.vision || "", logo: member.logo || "", photo: member.photo || "", hero_video: member.hero_video || "", gallery: member.gallery || [], email_tone: member.email_tone || "conseiller", preferred_lang: member.preferred_lang || "fr" });
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");
  const [dirty, setDirty] = useState(false);
  const [galErr, setGalErr] = useState("");
  const [uploading, setUploading] = useState(""); // clé en cours d'upload
  const [upErr, setUpErr] = useState("");
  const [cropData, setCropData] = useState(null); // { src, key, aspect, presets }
  const pickAndCrop = (file, key, opts) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCropData({ src: reader.result, key, ...opts });
    reader.readAsDataURL(file);
  };
  const onCropDone = async (blob) => {
    const key = cropData?.key;
    setCropData(null);
    if (!key || !blob) return;
    const file = new File([blob], `${key}.jpg`, { type: "image/jpeg" });
    await doUpload(file, key);
  };
  const [tones, setTones] = useState([]);
  const [testBusy, setTestBusy] = useState(false);
  const [testMsg, setTestMsg] = useState("");
  useEffect(() => { authApi.emailTones().then((d) => setTones(d.tones || [])).catch(() => setTones([])); }, []);
  const sendTest = async () => {
    setTestBusy(true); setTestMsg("");
    try {
      await authApi.updateProfile({ email_tone: f.email_tone });
      const r = await authApi.sendEmailTest(f.email_tone);
      setTestMsg(r.configured ? `3 e-mails d'aperçu envoyés à ${r.to} ✓` : "E-mail non configuré côté serveur (aucun envoi).");
    } catch (_) { setTestMsg("Envoi impossible pour le moment."); }
    setTestBusy(false);
  };
  const set = (k, v) => { setF((p) => ({ ...p, [k]: v })); setDirty(true); setSaved(false); };
  // Persiste immédiatement un champ (photos/logo/vidéo/galerie) pour éviter toute perte de données.
  const persist = async (patch) => {
    try { await authApi.updateProfile(patch); onSaved(); return true; }
    catch (_) { setUpErr("Enregistrement impossible pour le moment. Vérifiez votre connexion et réessayez."); return false; }
  };
  const doUpload = async (file, key) => {
    setUpErr(""); setUploading(key);
    try {
      const d = await uploadMemberFile(file);
      setF((p) => ({ ...p, [key]: d.url }));
      await persist({ [key]: d.url }); // auto-save immédiat
    } catch (e) {
      setUpErr(e.message === "Fichier trop volumineux" ? "Fichier trop volumineux (max 15 Mo). Réduisez la taille et réessayez." : (e.message || "Import impossible. Vérifiez le format et la taille du fichier."));
    }
    setUploading("");
  };
  const removeMedia = async (key) => {
    setUpErr("");
    setF((p) => ({ ...p, [key]: "" }));
    await persist({ [key]: "" });
  };
  const doUploadGallery = async (files) => {
    setGalErr(""); setUpErr("");
    const list = Array.from(files || []);
    const room = 10 - f.gallery.length;
    if (room <= 0) { setGalErr("Maximum 10 photos."); return; }
    const toAdd = list.slice(0, room);
    if (list.length > room) setGalErr(`Seules ${room} photo(s) ont été ajoutées (max 10).`);
    setUploading("gallery");
    try {
      const urls = await Promise.all(toAdd.map((file) => uploadMemberFile(file).then((d) => d.url)));
      const next = [...f.gallery, ...urls].slice(0, 10);
      setF((p) => ({ ...p, gallery: next }));
      await persist({ gallery: next }); // auto-save immédiat
    } catch (_) { setGalErr("Upload impossible pour certaines photos (max 15 Mo par photo)."); }
    setUploading("");
  };
  const removeGallery = async (idx) => {
    const next = f.gallery.filter((_, i) => i !== idx);
    setF((p) => ({ ...p, gallery: next }));
    await persist({ gallery: next }); // auto-save immédiat
  };
  const save = async () => {
    setLoading(true); setSaved(false); setErr("");
    try {
      await authApi.updateProfile(f);
      setSaved(true); setDirty(false); onSaved();
    } catch (e) {
      setErr(e.message || "Enregistrement impossible. Vérifiez votre connexion et réessayez.");
    }
    setLoading(false);
  };
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);
  return (
    <div className="max-w-2xl space-y-4" data-testid="profile-tab">
      <h1 className="calli text-3xl sm:text-4xl leading-none text-[#C19A6B]" data-testid="profile-page-title">Configurer ma page</h1>
      <div className="grid sm:grid-cols-2 gap-3">
        <input className={inp} placeholder="Prénom" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="pf-first" />
        <input className={inp} placeholder="Nom" value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="pf-last" />
        <input className={inp} placeholder="Téléphone" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
        <input className={inp} placeholder="Société" value={f.company} onChange={(e) => set("company", e.target.value)} />
        <input className={inp} placeholder="Ville" value={f.city} onChange={(e) => set("city", e.target.value)} />
        <input className={inp} placeholder="Secteur" value={f.sector} onChange={(e) => set("sector", e.target.value)} />
        <input className={`${inp} sm:col-span-2`} placeholder="Site internet" value={f.website} onChange={(e) => set("website", e.target.value)} />
      </div>
      <AgentPresenter
        context={{ name: `${f.first_name} ${f.last_name}`.trim(), company: f.company, city: f.city, sector: f.sector }}
        description={f.description}
        vision={f.vision}
        onGenerated={({ description, vision }) => { set("description", description); set("vision", vision); }}
      />
      <textarea className={`${inp} min-h-[80px]`} placeholder="Présentation" value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="profile-description" />
      <textarea className={`${inp} min-h-[70px]`} placeholder="Votre vision du métier" value={f.vision} onChange={(e) => set("vision", e.target.value)} data-testid="profile-vision" />
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="border border-white/10 p-3 space-y-2" data-testid="pf-logo-block">
          <p className="text-xs uppercase tracking-wider text-zinc-500">Logo société</p>
          {f.logo ? (
            <div className="relative group w-full h-28 bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden">
              <img src={f.logo} alt="Logo" className="max-h-full max-w-full object-contain" data-testid="pf-logo-preview" />
              <button type="button" onClick={() => removeMedia("logo")} data-testid="pf-logo-remove" className="absolute top-1 right-1 bg-black/70 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3.5 h-3.5" /></button>
            </div>
          ) : (
            <label className="flex items-center justify-center gap-2 border border-dashed border-white/20 h-28 cursor-pointer text-sm text-zinc-300 hover:border-[#C19A6B]/50 transition-colors">
              {uploading === "logo" ? <><Loader2 className="w-4 h-4 text-[#C19A6B] animate-spin" /> Envoi…</> : <><Upload className="w-4 h-4 text-[#C19A6B]" /> Ajouter un logo</>}
              <input type="file" accept="image/*" className="hidden" disabled={uploading === "logo"} onChange={(e) => { if (e.target.files[0]) doUpload(e.target.files[0], "logo"); e.target.value = ""; }} />
            </label>
          )}
        </div>
        <div className="border border-white/10 p-3 space-y-2" data-testid="pf-photo-block">
          <p className="text-xs uppercase tracking-wider text-zinc-500">Photo de profil</p>
          {f.photo ? (
            <div className="relative group w-full h-28 bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden">
              <img src={f.photo} alt="Profil" className="w-full h-full object-cover" data-testid="pf-photo-preview" />
              <button type="button" onClick={() => removeMedia("photo")} data-testid="pf-photo-remove" className="absolute top-1 right-1 bg-black/70 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3.5 h-3.5" /></button>
            </div>
          ) : (
            <label className="flex items-center justify-center gap-2 border border-dashed border-white/20 h-28 cursor-pointer text-sm text-zinc-300 hover:border-[#C19A6B]/50 transition-colors">
              {uploading === "photo" ? <><Loader2 className="w-4 h-4 text-[#C19A6B] animate-spin" /> Envoi…</> : <><Upload className="w-4 h-4 text-[#C19A6B]" /> Ajouter une photo</>}
              <input type="file" accept="image/*" className="hidden" disabled={uploading === "photo"} onChange={(e) => { pickAndCrop(e.target.files[0], "photo", { aspect: 1, title: "Recadrer la photo de profil" }); e.target.value = ""; }} />
            </label>
          )}
        </div>
      </div>
      {upErr && <p className="text-[11px] text-amber-400" data-testid="pf-upload-error">{upErr}</p>}
      <div className="border border-white/10 p-4 space-y-2" data-testid="pf-video-block">
        <p className="text-xs uppercase tracking-wider text-zinc-500">Vidéo de présentation (Hero plein écran, prioritaire sur les photos)</p>
        <input className={inp} placeholder="Lien YouTube, Vimeo ou URL .mp4" value={f.hero_video} onChange={(e) => set("hero_video", e.target.value)} data-testid="pf-hero-video" />
        <div className="flex items-center gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2 border border-dashed border-white/20 px-4 py-2.5 cursor-pointer text-sm text-zinc-300 hover:border-[#C19A6B]/50 transition-colors" data-testid="pf-upload-video">
            <Upload className="w-4 h-4 text-[#C19A6B]" /> Téléverser une vidéo
            <input type="file" accept="video/*" className="hidden" onChange={(e) => e.target.files[0] && doUpload(e.target.files[0], "hero_video")} />
          </label>
          {f.hero_video && <button type="button" onClick={() => removeMedia("hero_video")} className="text-xs text-red-400 hover:underline" data-testid="pf-video-remove">Retirer</button>}
        </div>
        {f.hero_video && <p className="text-[11px] text-[#C19A6B]">Vidéo active — votre hero l'affichera en priorité (le cadre photo de profil sera masqué).</p>}
      </div>
      <div className="border border-white/10 p-4 space-y-3" data-testid="pf-gallery-block">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-wider text-zinc-500">Galerie photos (max 10) — affichée en mosaïque sur votre page</p>
          <span className="text-[11px] text-[#C19A6B]">{f.gallery.length}/10</span>
        </div>
        {f.gallery.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="pf-gallery-grid">
            {f.gallery.map((src, i) => (
              <div key={i} className="relative group aspect-square overflow-hidden border border-white/10">
                <img src={src} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeGallery(i)} data-testid={`pf-gallery-remove-${i}`} className="absolute top-1 right-1 bg-black/70 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        )}
        {f.gallery.length < 10 && (
          <label className="inline-flex items-center gap-2 border border-dashed border-white/20 px-4 py-2.5 cursor-pointer text-sm text-zinc-300 hover:border-[#C19A6B]/50 transition-colors" data-testid="pf-upload-gallery">
            {uploading === "gallery" ? <><Loader2 className="w-4 h-4 text-[#C19A6B] animate-spin" /> Envoi en cours…</> : <><Upload className="w-4 h-4 text-[#C19A6B]" /> Ajouter des photos (plusieurs à la fois)</>}
            <input type="file" accept="image/*" multiple className="hidden" disabled={uploading === "gallery"} onChange={(e) => e.target.files.length && doUploadGallery(e.target.files)} />
          </label>
        )}
        {galErr && <p className="text-[11px] text-amber-400">{galErr}</p>}
        <p className="text-[11px] text-zinc-500">Les photos, le logo et la vidéo sont enregistrés automatiquement dès l'ajout.</p>
      </div>
      <div className="bg-white border border-[#E5E7EB] rounded-xl shadow-sm p-5 space-y-3" data-testid="email-tone-block">
        <div>
          <p className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] flex items-center gap-2"><Mail className="w-5 h-5 text-[#C19A6B]" /> Ton de vos e-mails clients</p>
          <p className="text-xs text-zinc-400 mt-1.5">Choisissez le pack de modèles adapté à votre clientèle. Le ton s'applique aux e-mails automatiques envoyés à vos vendeurs et acquéreurs (offre reçue, acceptée, compromis…).</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {tones.map((t) => (
            <button key={t.key} type="button" onClick={() => set("email_tone", t.key)} data-testid={`tone-${t.key}`}
              className={`text-left border p-3 transition-colors ${f.email_tone === t.key ? "border-[#C19A6B] bg-[#C19A6B]/10" : "border-white/15 hover:border-white/30"}`}>
              <span className={`text-sm font-medium ${f.email_tone === t.key ? "text-[#C19A6B]" : "text-white"}`}>{t.label}</span>
              <span className="block text-[11px] text-zinc-400 mt-0.5">{t.desc}</span>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={sendTest} disabled={testBusy} data-testid="tone-send-test" className="inline-flex items-center gap-2 border border-[#C19A6B]/50 text-[#C19A6B] hover:bg-[#C19A6B]/10 disabled:opacity-50 text-xs uppercase tracking-wider px-4 py-2.5 transition-colors">
            {testBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} M'envoyer un aperçu
          </button>
          {testMsg && <span className="text-xs text-zinc-300">{testMsg}</span>}
        </div>
      </div>
      <div className="bg-white border border-[#E5E7EB] rounded-xl shadow-sm p-5 space-y-3" data-testid="email-lang-block">
        <div>
          <p className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] flex items-center gap-2"><Globe className="w-5 h-5 text-[#C19A6B]" /> Langue de mes e-mails</p>
          <p className="text-xs text-zinc-400 mt-1.5">Les e-mails automatiques d'OFF MARKETTE (bienvenue, offres, co-mandats, rappels…) vous seront envoyés dans cette langue.</p>
        </div>
        <select className="w-full bg-white border border-[#E5E7EB] rounded-[6px] px-4 py-3 text-sm text-[#1a1a1a] focus:outline-none focus:border-[#C19A6B]"
          value={f.preferred_lang} onChange={(e) => set("preferred_lang", e.target.value)} data-testid="pf-preferred-lang">
          {EMAIL_LANGS.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-4 flex-wrap sticky bottom-0 bg-transparent py-2">
        <button onClick={save} disabled={loading} data-testid="profile-save" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-60 text-white font-medium uppercase tracking-wider text-sm px-6 py-3 transition-colors">{loading ? "Enregistrement…" : "Enregistrer"} <Check className="w-4 h-4" /></button>
        {saved && <span className="text-sm text-green-400" data-testid="profile-saved">Profil mis à jour ✓</span>}
        {dirty && !saved && <span className="text-sm text-amber-400" data-testid="profile-dirty">Modifications non enregistrées</span>}
        {err && <span className="text-sm text-red-400" data-testid="profile-error">{err}</span>}
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl shadow-sm p-5 space-y-3" data-testid="account-management">
        <h4 className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B]">Gestion du compte</h4>
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={togglePause} disabled={acctBusy} data-testid="account-pause" className="inline-flex items-center gap-2 border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 disabled:opacity-50 text-xs uppercase tracking-wider px-4 py-2.5 transition-colors">
            {isPaused ? <><Eye className="w-4 h-4" /> Réactiver mon compte</> : <><EyeOff className="w-4 h-4" /> Mettre en veille</>}
          </button>
          <button onClick={() => setConfirmDel(true)} disabled={acctBusy} data-testid="account-delete" className="inline-flex items-center gap-2 border border-red-400/40 text-red-400 hover:bg-red-400/10 disabled:opacity-50 text-xs uppercase tracking-wider px-4 py-2.5 transition-colors">
            <Trash2 className="w-4 h-4" /> Supprimer mon compte
          </button>
        </div>
        <p className="text-xs text-zinc-500">
          {isPaused
            ? "Votre compte est en veille : vos annonces et votre page publique sont masquées. Réactivez quand vous le souhaitez."
            : "La mise en veille masque temporairement vos annonces et votre page publique. La suppression est définitive."}
        </p>
        {confirmDel && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setConfirmDel(false)}>
            <div className="bg-[#0d0d10] border border-white/10 p-6 max-w-sm w-full space-y-4" onClick={(e) => e.stopPropagation()} data-testid="account-delete-confirm">
              <h3 className="text-lg font-light text-white">Supprimer définitivement votre compte ?</h3>
              <p className="text-sm text-zinc-400">Vos annonces seront retirées et votre page publique désactivée. Cette action est irréversible.</p>
              <div className="flex justify-end gap-3">
                <button onClick={() => setConfirmDel(false)} className="px-4 py-2 text-sm border border-white/15 text-zinc-300 hover:bg-white/5">Annuler</button>
                <button onClick={deleteAcct} disabled={acctBusy} data-testid="account-delete-confirm-btn" className="px-4 py-2 text-sm bg-red-500/90 hover:bg-red-500 text-white disabled:opacity-50">Supprimer définitivement</button>
              </div>
            </div>
          </div>
        )}
      </div>
      {cropData && (
        <ImageCropper src={cropData.src} aspect={cropData.aspect} presets={cropData.presets} title={cropData.title}
          onCancel={() => setCropData(null)} onDone={onCropDone} />
      )}
    </div>
  );
}

function InviteBlock({ code, name }) {
  const [copied, setCopied] = useState(false);
  const [emails, setEmails] = useState("");
  const [pmsg, setPmsg] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState("");
  const [parsing, setParsing] = useState(false);
  const [invites, setInvites] = useState(null);
  const [remindingId, setRemindingId] = useState("");
  const fileRef = useRef(null);
  const link = `${PUBLIC_SITE}/rejoindre?ref=${code}`;
  const shareOg = `${PUBLIC_SITE}/api/referral/${code}/share`;
  const copy = async () => { try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (_) {} };
  const msg = `Bonjour, je vous invite à rejoindre le réseau OFF MARKETTE${name ? ` (parrainé par ${name})` : ""}. Inscrivez-vous ici : ${shareOg}`;
  const wa = `https://wa.me/?text=${encodeURIComponent(msg)}`;
  const mail = `mailto:?subject=${encodeURIComponent("Invitation à rejoindre OFF MARKETTE")}&body=${encodeURIComponent(msg)}`;

  const loadInvites = () => authApi.netInvitations().then(setInvites).catch(() => setInvites({ invitations: [], stats: {} }));
  useEffect(() => { loadInvites(); }, []);

  const sendInvites = async () => {
    setSending(true); setResult("");
    try {
      const r = await authApi.netInvite(emails, pmsg);
      setResult(r.configured ? `✅ ${r.sent}/${r.total} invitation(s) envoyée(s) avec votre lien de parrainage.` : "⚠️ Envoi d'e-mails non configuré pour le moment.");
      if (r.sent) { setEmails(""); setPmsg(""); loadInvites(); }
    } catch (e) { setResult("❌ " + (e.message || "Erreur")); }
    setSending(false);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setParsing(true); setResult("");
    try {
      const r = await authApi.netInviteParse(f);
      const existing = emails.split(/[,;\s]+/).map((x) => x.trim().toLowerCase()).filter(Boolean);
      const merged = Array.from(new Set([...existing, ...r.emails]));
      setEmails(merged.join(", "));
      setResult(`📄 ${r.count} adresse(s) trouvée(s) dans « ${r.filename} ». Vérifiez puis envoyez.`);
    } catch (err) { setResult("❌ " + (err.message || "Impossible de lire ce fichier")); }
    setParsing(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const remind = async (id) => {
    setRemindingId(id);
    try { const r = await authApi.netRemind(id); setResult(r.sent ? "✅ Relance envoyée." : "⚠️ Relance non envoyée."); loadInvites(); }
    catch (e) { setResult("❌ " + (e.message || "Erreur")); }
    setRemindingId("");
  };

  const STATUS = {
    registered: { label: "Inscrit ✓", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
    sent: { label: "Envoyé", cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
    pending: { label: "En attente", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  };
  const fmtD = (s) => { try { return new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }); } catch { return ""; } };

  return (
    <div className="border border-white/10 bg-[#0d0d10] px-5 py-4 mb-8" data-testid="invite-block">
      <div className="flex items-center gap-2 text-sm text-zinc-300 mb-3"><Link2 className="w-4 h-4 text-[#C19A6B]" /> Parrainez un conseiller</div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <input readOnly value={link} data-testid="invite-link" onFocus={(e) => e.target.select()} className="flex-1 bg-white/5 border border-white/10 px-3 py-2 text-xs text-zinc-300 focus:outline-none focus:border-[#C19A6B]" />
        <div className="flex items-center gap-2">
          <button onClick={copy} data-testid="invite-copy" className="inline-flex items-center justify-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-medium uppercase tracking-wider px-3 py-2 transition-colors">
            {copied ? <><Check className="w-3.5 h-3.5" /> Copié</> : <><Copy className="w-3.5 h-3.5" /> Copier</>}
          </button>
          <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="invite-whatsapp" title="Partager par WhatsApp" className="inline-flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1eb857] text-black text-xs font-medium uppercase tracking-wider px-3 py-2 transition-colors"><WhatsAppIcon className="w-4 h-4" /> WhatsApp</a>
          <a href={mail} data-testid="invite-email" title="Partager par e-mail (client)" className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-medium uppercase tracking-wider px-3 py-2 transition-colors"><Mail className="w-4 h-4" /> Mail perso</a>
        </div>
      </div>
      {/* Envoi d'invitations premium (branded) directement depuis la plateforme */}
      <div className="mt-4 pt-4 border-t border-white/10">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div className="text-xs uppercase tracking-wider text-[#C19A6B] flex items-center gap-2"><Send className="w-3.5 h-3.5" /> Inviter par e-mail (message premium OFF MARKETTE)</div>
          <button onClick={() => fileRef.current?.click()} disabled={parsing} data-testid="invite-import"
            className="inline-flex items-center gap-1.5 border border-white/15 text-zinc-300 hover:bg-white/5 text-[11px] uppercase tracking-wider px-3 py-1.5 transition-colors disabled:opacity-50">
            <FileText className="w-3.5 h-3.5" /> {parsing ? "Lecture…" : "Importer un fichier (Excel, CSV, Word)"}
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.xlsm,.csv,.tsv,.txt,.docx" onChange={onFile} className="hidden" data-testid="invite-file-input" />
        </div>
        <textarea value={emails} onChange={(e) => setEmails(e.target.value)} data-testid="invite-emails" rows={2}
          placeholder="Adresses e-mail séparées par une virgule ou un espace… (ou importez un fichier)"
          className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-[#C19A6B] mb-2" />
        <input value={pmsg} onChange={(e) => setPmsg(e.target.value)} data-testid="invite-message"
          placeholder="Message personnel (facultatif)"
          className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-[#C19A6B] mb-2" />
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={sendInvites} disabled={sending || !emails.trim()} data-testid="invite-send"
            className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 text-white text-xs font-semibold uppercase tracking-wider px-4 py-2 transition-colors">
            <Send className="w-3.5 h-3.5" /> {sending ? "Envoi…" : "Envoyer l'invitation"}
          </button>
          {result && <span data-testid="invite-result" className="text-xs text-zinc-300">{result}</span>}
        </div>
        <div className="text-[11px] text-zinc-500 mt-2">Astuce : dans Google Contacts, cliquez sur « Exporter → CSV », puis importez le fichier ici pour inviter tous vos contacts d'un coup.</div>
      </div>

      {/* Suivi des invitations envoyées */}
      {invites && invites.invitations.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/10" data-testid="invite-tracking">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-wider text-zinc-400">Mes invitations</div>
            <div className="text-[11px] text-zinc-500">{invites.stats?.registered || 0} inscrit(s) · {invites.invitations.length} envoyée(s)</div>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {invites.invitations.map((iv) => {
              const st = STATUS[iv.status] || STATUS.pending;
              return (
                <div key={iv.id} data-testid={`invite-row-${iv.id}`} className="flex items-center gap-3 bg-white/[0.03] border border-white/10 px-3 py-2">
                  <span className="flex-1 min-w-0 truncate text-sm text-zinc-200">{iv.email}</span>
                  {iv.reminder_count > 0 && <span className="text-[10px] text-zinc-500">relancé {iv.reminder_count}×</span>}
                  <span className="text-[10px] text-zinc-500 hidden sm:inline">{fmtD(iv.created_at)}</span>
                  <span className={`text-[10px] uppercase tracking-wider border px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                  {iv.status !== "registered" && (
                    <button onClick={() => remind(iv.id)} disabled={remindingId === iv.id} data-testid={`remind-${iv.id}`}
                      title="Relancer" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#C19A6B] hover:text-[#d1af84] border border-[#C19A6B]/40 px-2 py-1 transition-colors disabled:opacity-50">
                      <RefreshCw className={`w-3 h-3 ${remindingId === iv.id ? "animate-spin" : ""}`} /> Relancer
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function FilleulsTab({ code, name }) {
  const [stats, setStats] = useState(null);
  useEffect(() => { authApi.netInvitations().then((d) => setStats(d.stats || {})).catch(() => setStats({})); }, []);
  const kpis = [
    { icon: Users, v: stats?.registered ?? "—", l: "Filleuls inscrits", sub: "ont rejoint via votre lien" },
    { icon: Send, v: stats?.total ?? "—", l: "Invitations envoyées", sub: "au total" },
    { icon: Coins, v: 0, l: "Crédits gagnés", sub: "récompenses de parrainage" },
    { icon: TrendingUp, v: 0, l: "CA généré", sub: "par votre réseau" },
  ];
  return (
    <div className="space-y-8" data-testid="filleuls-tab">
      <div>
        <h2 className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B]">Mes filleuls</h2>
        <p className="text-sm text-[#6B7280] mt-1">Développez et suivez votre réseau de conseillers parrainés.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((c, i) => { const Ic = c.icon; return (
          <div key={i} data-testid={`filleul-kpi-${i}`} className="bg-[#F1F3F5] rounded-xl p-5 transition-all hover:shadow-md hover:-translate-y-0.5">
            <Ic className="w-5 h-5 text-[#C19A6B] mb-3" />
            <p className="text-3xl font-bold text-[#111827] leading-none">{c.v}</p>
            <p className="text-xs uppercase tracking-wider text-[#6B7280] mt-2">{c.l}</p>
            <p className="text-[11px] text-[#9CA3AF] mt-0.5">{c.sub}</p>
          </div>
        ); })}
      </div>
      <div className="bg-[#C19A6B]/10 border border-[#C19A6B]/25 rounded-xl px-5 py-4 flex items-start gap-3" data-testid="filleuls-soon">
        <Sparkles className="w-5 h-5 text-[#C19A6B] mt-0.5 shrink-0" />
        <p className="text-sm text-[#4B5563]">Le tableau de bord complet arrive bientôt : classements des meilleurs vendeurs et apporteurs d'affaires, évolution du réseau, et vos filleuls de niveau 2. En attendant, invitez de nouveaux conseillers ci-dessous.</p>
      </div>
      {code && <InviteBlock code={code} name={name} />}
    </div>
  );
}

function Sparkline({ data }) {
  const max = Math.max(1, ...data.map((d) => d.views));
  return (
    <div className="flex items-end gap-[3px] h-20" data-testid="stats-trend">
      {data.map((d, i) => (
        <div key={i} className="flex-1 group relative flex items-end" style={{ height: "100%" }}>
          <div className="w-full rounded-t-sm bg-[#C19A6B]/70 group-hover:bg-[#C19A6B] transition-all" style={{ height: `${Math.max(2, (d.views / max) * 100)}%` }} title={`${d.date} · ${d.views} vue(s)`} />
        </div>
      ))}
    </div>
  );
}

function StatsTab({ onOpenTab }) {
  const [s, setS] = useState(undefined);
  const [recap, setRecap] = useState(null); // {html, subject}
  const [recapLoading, setRecapLoading] = useState(false);
  useEffect(() => { authApi.memberStats().then(setS).catch(() => setS(null)); }, []);
  const openRecap = async () => {
    setRecapLoading(true);
    try { const d = await authApi.weeklyRecapPreview(); setRecap({ html: d.html, subject: d.subject }); }
    catch (_) { setRecap({ html: "", subject: "Aperçu indisponible" }); }
    setRecapLoading(false);
  };
  if (s === undefined) return <div className="py-12 text-center text-sm text-[#C19A6B] uppercase tracking-[0.25em] animate-pulse" data-testid="stats-loading">Chargement des statistiques…</div>;
  if (s === null) return <div className="py-12 text-center text-zinc-500">Statistiques indisponibles pour le moment.</div>;
  const comp = s.completeness || { avg: 0, incomplete: 0 };
  const offers = s.offers || { total: 0, pending: 0 };
  const trend = s.trend || [];
  const trendTotal = trend.reduce((a, d) => a + d.views, 0);
  const cards = [
    { icon: Home, v: s.listings.total, l: "Annonces", sub: `${s.listings.online} en ligne · ${s.listings.offline} en prévisualisation`, accent: "#C19A6B" },
    { icon: Eye, v: s.views, l: "Vues cumulées", sub: "depuis la publication (tout l'historique)", accent: "#C19A6B" },
    { icon: Users, v: s.unique_visitors, l: "Visiteurs uniques", sub: "sur vos annonces", accent: "#C19A6B" },
    { icon: Heart, v: s.favorites, l: "Mises en favori", sub: "par les acquéreurs", accent: "#C19A6B" },
    { icon: Inbox, v: offers.total, l: "Offres reçues", sub: offers.pending ? `${offers.pending} en attente de réponse` : "aucune en attente", accent: "#10B981" },
    { icon: BadgeCheck, v: `${comp.avg}%`, l: "Complétude moyenne", sub: comp.incomplete ? `${comp.incomplete} annonce(s) à compléter` : "toutes vos fiches sont complètes", accent: comp.avg >= 90 ? "#10B981" : "#EAB308" },
  ];
  return (
    <div className="space-y-8" data-testid="stats-tab">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c, i) => {
          const Ic = c.icon;
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="relative border border-white/10 bg-[#0d0d10] p-5 overflow-hidden group" data-testid={`stat-card-${i}`}>
              <div className="absolute -right-6 -top-6 w-20 h-20 rounded-full opacity-10 group-hover:opacity-20 transition-opacity" style={{ background: c.accent }} />
              <Ic className="w-5 h-5 mb-3" style={{ color: c.accent }} />
              <p className="text-3xl font-bold text-white">{c.v}</p>
              <p className="text-xs uppercase tracking-wider text-zinc-400 mt-1">{c.l}</p>
              <p className="text-[11px] text-zinc-600 mt-1">{c.sub}</p>
            </motion.div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-white/10 bg-[#0d0d10] p-5" data-testid="stats-trend-card">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#C19A6B]" /> Vues — 30 derniers jours</p>
            <div className="flex items-center gap-3">
              <button onClick={openRecap} disabled={recapLoading} className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#C19A6B] border border-[#C19A6B]/40 hover:bg-[#C19A6B]/10 rounded px-2.5 py-1.5 transition-colors" data-testid="weekly-recap-btn">
                <Mail className="w-3.5 h-3.5" /> {recapLoading ? "…" : "Voir mon récap de la semaine"}
              </button>
              <span className="text-sm font-bold text-white">{trendTotal} <span className="text-[11px] font-normal text-zinc-500">vues</span></span>
            </div>
          </div>
          {trendTotal === 0 ? <p className="text-sm text-zinc-600 py-6 text-center">Pas encore de vues sur cette période.</p> : <Sparkline data={trend} />}
        </div>
        <button onClick={() => onOpenTab && onOpenTab("matching_bien")} className="border border-[#C19A6B]/30 bg-gradient-to-br from-[#C19A6B]/15 to-transparent p-5 text-left hover:border-[#C19A6B]/60 transition-colors group" data-testid="stats-matching-cta">
          <Sparkles className="w-5 h-5 text-[#C19A6B] mb-3" />
          <p className="text-sm font-semibold text-white">Matching IA</p>
          <p className="text-[11px] text-zinc-400 mt-1">Découvrez les acquéreurs du réseau compatibles avec vos biens et les biens qui correspondent à vos acquéreurs.</p>
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-[#C19A6B] mt-3 group-hover:gap-2 transition-all">Ouvrir <ArrowRight className="w-3 h-3" /></span>
        </button>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl shadow-sm p-5">
        <p className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-[#C19A6B]" /> Performance par annonce</p>
        {s.per_listing.length === 0 ? (
          <p className="text-zinc-500 text-sm py-8 text-center border border-dashed border-[#E5E7EB] rounded-lg">Aucune annonce à analyser pour le moment.</p>
        ) : (
          <div className="divide-y divide-[#F3F4F6]" data-testid="stats-per-listing">
            {s.per_listing.map((l) => (
              <div key={l.slug} className="flex items-center gap-4 p-3">
                <div className="w-14 h-14 bg-black/5 overflow-hidden shrink-0 rounded-md">{l.cover && <img src={l.cover} alt="" className="w-full h-full object-cover" />}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#111827] line-clamp-1 font-medium">{l.title}</p>
                  <span className={`text-[10px] uppercase tracking-wider ${l.published ? "text-green-600" : "text-amber-600"}`}>{l.published ? "En ligne" : "Prévisualisation"}</span>
                </div>
                <div className="flex items-center gap-5 text-sm">
                  <span className="flex items-center gap-1.5 text-zinc-600" title="Vues"><Eye className="w-4 h-4 text-[#C19A6B]" /> {l.views}</span>
                  <span className="flex items-center gap-1.5 text-zinc-600" title="Favoris"><Heart className="w-4 h-4 text-[#C19A6B]" /> {l.favorites}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {recap && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setRecap(null)} data-testid="weekly-recap-modal">
            <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
              onClick={(e) => e.stopPropagation()} className="w-full max-w-xl bg-white rounded-[10px] shadow-2xl overflow-hidden flex flex-col" style={{ maxHeight: "88vh" }}>
              <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between gap-2 shrink-0">
                <p className="text-sm font-semibold text-[#111827] flex items-center gap-2"><Mail className="w-4 h-4 text-[#C19A6B]" /> Aperçu — récap de la semaine</p>
                <button onClick={() => setRecap(null)} className="text-zinc-400 hover:text-zinc-700 transition-colors" data-testid="weekly-recap-close"><X className="w-5 h-5" /></button>
              </div>
              <p className="px-5 pt-3 text-[11px] text-zinc-500 shrink-0">Objet : <span className="text-[#111827] font-medium">{recap.subject}</span> — envoyé automatiquement chaque lundi.</p>
              <div className="p-4 overflow-auto flex-1">
                <iframe title="recap" srcDoc={recap.html} className="w-full rounded-md border border-zinc-100" style={{ height: "60vh" }} data-testid="weekly-recap-iframe" />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const PALETTE = ["#C19A6B", "#EB6402", "#0f766e", "#7c3aed", "#b91c1c", "#1d4ed8", "#b45309", "#065f46", "#9d174d"];
const PARTNER_CATS = ["Courtier", "Notaire", "Diagnostiqueur", "Architecte", "Décorateur", "Artisan", "Déménageur", "Photographe", "Home stager", "Autre"];

function LiveSitePreview({ slug, site, onClose }) {
  const ref = useRef(null);
  const [device, setDevice] = useState("desktop");
  const [ready, setReady] = useState(false);
  const [nonce, setNonce] = useState(0);
  const post = useCallback(() => {
    try { ref.current?.contentWindow?.postMessage({ type: "om-preview-site", site }, "*"); } catch (_) {}
  }, [site]);
  useEffect(() => {
    const onMsg = (e) => { if (e.data && e.data.type === "om-preview-ready") setReady(true); };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);
  useEffect(() => { if (ready) post(); }, [ready, post]);
  const w = device === "mobile" ? 390 : "100%";
  return (
    <div className="hidden xl:flex fixed top-0 right-0 h-screen w-[460px] z-40 bg-[#0A0A0C] border-l border-white/10 flex-col" data-testid="site-live-preview">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0">
        <span className="text-xs uppercase tracking-widest text-zinc-300 flex items-center gap-2"><Eye className="w-4 h-4 text-[#C19A6B]" /> Aperçu en direct</span>
        <div className="flex items-center gap-1">
          <button onClick={() => setDevice("desktop")} title="Bureau" className={`p-1.5 border ${device === "desktop" ? "border-[#C19A6B] text-[#C19A6B]" : "border-white/10 text-zinc-500"}`} data-testid="preview-device-desktop"><Monitor className="w-4 h-4" /></button>
          <button onClick={() => setDevice("mobile")} title="Mobile" className={`p-1.5 border ${device === "mobile" ? "border-[#C19A6B] text-[#C19A6B]" : "border-white/10 text-zinc-500"}`} data-testid="preview-device-mobile"><Smartphone className="w-4 h-4" /></button>
          <button onClick={() => { setReady(false); setNonce((n) => n + 1); }} title="Rafraîchir" className="p-1.5 border border-white/10 text-zinc-500 hover:text-white" data-testid="preview-refresh"><RefreshCw className="w-4 h-4" /></button>
          <a href={`/${slug}/site`} target="_blank" rel="noopener noreferrer" title="Ouvrir dans un onglet" className="p-1.5 border border-white/10 text-zinc-500 hover:text-white"><ExternalLink className="w-4 h-4" /></a>
          <button onClick={onClose} title="Fermer" className="p-1.5 border border-white/10 text-zinc-500 hover:text-white" data-testid="preview-close"><X className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="flex-1 overflow-hidden bg-black flex justify-center">
        <iframe
          key={nonce}
          ref={ref}
          title="preview"
          src={`/${slug}/site?preview=1`}
          onLoad={post}
          className="h-full border-0 bg-black transition-all"
          style={{ width: w, maxWidth: "100%" }}
        />
      </div>
    </div>
  );
}

const PublicUrlBox = ({ url }) => {
  const [ok, setOk] = useState(false);
  return (
    <div className="flex items-center gap-3 flex-wrap bg-[#F4F5F7] rounded-lg border border-[#E5E7EB] px-4 py-3" data-testid="site-public-url-box">
      <ExternalLink className="w-5 h-5 text-[#C19A6B] shrink-0" />
      <a href={url} target="_blank" rel="noopener noreferrer" data-testid="site-public-url" className="text-base sm:text-lg font-semibold text-[#111827] hover:text-[#C19A6B] transition-colors break-all">{url}</a>
      <button onClick={() => { navigator.clipboard.writeText(url); setOk(true); setTimeout(() => setOk(false), 1800); }} data-testid="site-url-copy" className="ml-auto inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-white bg-[#C19A6B] hover:bg-[#d1af84] px-3 py-2 rounded-md transition-colors shrink-0">{ok ? <><Check className="w-3.5 h-3.5" /> Copié</> : <><Copy className="w-3.5 h-3.5" /> Copier</>}</button>
    </div>
  );
};


function SiteEditorTab({ member }) {  const [data, setData] = useState(undefined);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [gen, setGen] = useState("");
  const [newVideo, setNewVideo] = useState("");
  const [newsTopic, setNewsTopic] = useState("");
  const [newsCity, setNewsCity] = useState("");
  const [genNews, setGenNews] = useState(false);
  const [genCover, setGenCover] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [sub, setSub] = useState(null);
  const [pubBusy, setPubBusy] = useState(false);
  const [domainInput, setDomainInput] = useState("");
  const [domBusy, setDomBusy] = useState(false);
  const [coverCrop, setCoverCrop] = useState(null); // { src, key }

  useEffect(() => { authApi.mySite().then((d) => { setData(d); setDomainInput(d?.site?.domain || ""); }).catch(() => setData(null)); }, []);
  useEffect(() => { authApi.subStatus().then(setSub).catch(() => {}); }, []);
  if (data === undefined) return <div className="py-12 text-center text-sm text-[#C19A6B] uppercase tracking-[0.25em] animate-pulse">Chargement…</div>;
  if (data === null) return <div className="py-12 text-center text-zinc-500">Site indisponible.</div>;

  const site = data.site;
  const setTheme = (patch) => setData((d) => ({ ...d, site: { ...d.site, theme: { ...d.site.theme, ...patch } } }));
  const setPages = (pages) => setData((d) => ({ ...d, site: { ...d.site, pages } }));
  const setContent = (key, val) => setData((d) => ({ ...d, site: { ...d.site, content: { ...d.site.content, [key]: val } } }));
  const setPartners = (partners) => setData((d) => ({ ...d, site: { ...d.site, partners } }));
  const setMedia = (media) => setData((d) => ({ ...d, site: { ...d.site, media } }));
  const primary = site.theme.primary;
  const netKey = networkOf(member);
  const netMeta = NETWORK_META[netKey];
  const netPresets = netMeta?.presets || [];

  const extractLogoColor = () => {
    if (!member.logo) return;
    const img = new Image(); img.crossOrigin = "anonymous"; img.src = member.logo;
    img.onload = () => {
      try {
        const c = document.createElement("canvas"); c.width = 24; c.height = 24;
        const ctx = c.getContext("2d"); ctx.drawImage(img, 0, 0, 24, 24);
        const d = ctx.getImageData(0, 0, 24, 24).data;
        let r = 0, g = 0, b = 0, n = 0;
        for (let i = 0; i < d.length; i += 4) {
          if (d[i + 3] < 128) continue;
          const lum = 0.3 * d[i] + 0.59 * d[i + 1] + 0.11 * d[i + 2];
          if (lum > 235 || lum < 20) continue;
          r += d[i]; g += d[i + 1]; b += d[i + 2]; n++;
        }
        if (n) { const hex = "#" + [r, g, b].map((x) => Math.round(x / n).toString(16).padStart(2, "0")).join(""); setTheme({ primary: hex }); }
      } catch (_) { alert("Impossible d'extraire la couleur du logo."); }
    };
  };

  const movePage = (i, dir) => {
    const pages = [...site.pages].sort((a, b) => a.order - b.order);
    const j = i + dir; if (j < 0 || j >= pages.length) return;
    [pages[i], pages[j]] = [pages[j], pages[i]];
    setPages(pages.map((p, idx) => ({ ...p, order: idx })));
  };
  const reorderPages = (from, to) => {
    if (from == null || to == null || from === to) return;
    const pages = [...site.pages].sort((a, b) => a.order - b.order);
    const [moved] = pages.splice(from, 1);
    pages.splice(to, 0, moved);
    setPages(pages.map((p, idx) => ({ ...p, order: idx })));
  };
  const addPage = () => {
    const label = window.prompt("Nom de la nouvelle page ?"); if (!label) return;
    const key = "p_" + label.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 20) + "-" + Math.random().toString(36).slice(2, 5);
    setPages([...site.pages, { key, label, visible: true, order: site.pages.length, removable: true, custom: true }]);
  };
  const removePage = (key) => setPages(site.pages.filter((p) => p.key !== key));
  const patchPage = (key, patch) => setPages(site.pages.map((p) => p.key === key ? { ...p, ...patch } : p));

  const generate = async (page, action = "generate", current = "") => {
    setGen(page);
    try {
      const r = await authApi.generateSite({ page, action, current });
      if (r.data) setContent(page, r.data); else setContent(page, { ...(site.content[page] || {}), text: r.text });
    } catch (_) { alert("Génération IA indisponible."); }
    setGen("");
  };

  const save = async () => {
    setSaving(true);
    try { const r = await authApi.saveSite(site); setData((d) => ({ ...d, site: r })); setSaved(true); setTimeout(() => setSaved(false), 2000); } catch (_) { alert("Enregistrement impossible."); }
    setSaving(false);
  };

  const startCheckout = async () => { try { const r = await authApi.subCheckout(); window.location.href = r.checkout_url; } catch (_) { alert("Abonnement indisponible, réessayez."); } };
  const setPublished = async (v) => {
    setPubBusy(true);
    try { const r = await authApi.publishSite(v); setData((d) => ({ ...d, site: { ...d.site, published: r.published } })); }
    catch (e) { if (e.status === 402) startCheckout(); else alert("Action impossible."); }
    setPubBusy(false);
  };
  const saveDomain = async () => {
    setDomBusy(true);
    try { const r = await authApi.setDomain(domainInput); setData((d) => ({ ...d, site: { ...d.site, domain: r.domain, domain_token: r.domain_token, domain_verified: r.domain_verified } })); }
    catch (e) { alert(e.detail || e.message || "Impossible."); }
    setDomBusy(false);
  };
  const doVerifyDomain = async () => {
    setDomBusy(true);
    try { const r = await authApi.verifyDomain(); setData((d) => ({ ...d, site: { ...d.site, domain_verified: r.domain_verified } })); if (!r.domain_verified) alert("Vérification échouée : l'enregistrement DNS n'est pas encore visible. La propagation peut prendre jusqu'à 24-48h."); }
    catch (_) { alert("Vérification impossible."); }
    setDomBusy(false);
  };

  const orderedPages = [...site.pages].sort((a, b) => a.order - b.order);
  const inp = "w-full bg-white/5 border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-[#C19A6B] transition-colors";
  const acc = { color: primary };
  const GenBtn = ({ page, action = "generate", current, label }) => (
    <button onClick={() => generate(page, action, current)} disabled={gen === page} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider border border-[#C19A6B]/40 text-[#C19A6B] hover:bg-[#C19A6B]/10 disabled:opacity-50 px-3 py-1.5 transition-colors">
      {gen === page ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} {label || "Générer par IA"}
    </button>
  );

  const content = site.content;
  const expert = content.expertises?.items || [];
  const partners = site.partners || [];
  const media = site.media || { photos: [], videos: [] };
  const news = site.news || [];
  const setNews = (n) => setData((d) => ({ ...d, site: { ...d.site, news: n } }));
  const patchNews = (id, patch) => setNews(news.map((a) => a.id === id ? { ...a, ...patch } : a));
  const uploadNewsCover = async (id, file) => {
    const a = news.find((x) => x.id === id);
    if ((a?.cover_ai_edits || 0) >= 1) return;
    try { const d = await uploadMemberFile(file); patchNews(id, { cover: d.url, cover_ai_edits: (a?.cover_ai_edits || 0) + 1 }); } catch (_) {}
  };
  const generateNewsCover = async (id, consume) => {
    const a = news.find((x) => x.id === id);
    if (!a) return;
    if (consume && (a.cover_ai_edits || 0) >= 1) return;
    setGenCover(id);
    try {
      const d = await authApi.generateNewsCover({ title: a.title, tag: a.tag, topic: a.title });
      patchNews(id, { cover: d.url, ...(consume ? { cover_ai_edits: (a.cover_ai_edits || 0) + 1 } : {}) });
    } catch (_) { alert("Génération de l'image impossible, réessayez."); }
    setGenCover(null);
  };
  const addGeneratedNews = async () => {
    setGenNews(true);
    try {
      const r = await authApi.generateNews({ topic: newsTopic, city: newsCity });
      const slugBase = (r.title || "article").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);
      const newId = "n_" + Math.random().toString(36).slice(2, 9);
      setNews([{ id: newId, slug: slugBase + "-" + Math.random().toString(36).slice(2, 5), title: r.title, excerpt: r.excerpt, body: r.body, tag: r.tag, cover: "", cover_ai_edits: 0, date: new Date().toISOString().slice(0, 10) }, ...news]);
      setNewsTopic(""); setNewsCity("");
      // Génération automatique d'une photo IA représentative (n'entame pas l'unique modification autorisée)
      (async () => {
        setGenCover(newId);
        try {
          const d = await authApi.generateNewsCover({ title: r.title, tag: r.tag, topic: r.title });
          setData((prev) => ({ ...prev, site: { ...prev.site, news: (prev.site.news || []).map((a) => a.id === newId ? { ...a, cover: d.url } : a) } }));
        } catch (_) {}
        setGenCover(null);
      })();
    } catch (_) { alert("Génération de l'article impossible."); }
    setGenNews(false);
  };
  const patchPartner = (id, patch) => setPartners(partners.map((p) => p.id === id ? { ...p, ...patch } : p));
  const addPartner = () => setPartners([...partners, { id: "pt_" + Math.random().toString(36).slice(2, 9), name: "", category: "Notaire", description: "", phone: "", website: "", logo: "" }]);
  const uploadPartnerLogo = async (id, file) => { try { const d = await uploadMemberFile(file); patchPartner(id, { logo: d.url }); } catch (_) {} };
  const uploadMediaPhotos = async (files) => {
    const list = Array.from(files || []); const room = 20 - media.photos.length;
    if (room <= 0) return;
    try { const urls = await Promise.all(list.slice(0, room).map((f) => uploadMemberFile(f).then((d) => d.url))); setMedia({ ...media, photos: [...media.photos, ...urls].slice(0, 20) }); } catch (_) {}
  };
  const join = content.join || {};
  const setJoin = (patch) => setContent("join", { ...join, ...patch });
  const uploadJoinCover = async (file) => { try { const d = await uploadMemberFile(file); setJoin({ cover: d.url }); } catch (_) {} };

  const covers = content.covers || {};
  const setCover = (key, patch) => setContent("covers", { ...covers, [key]: { ...(covers[key] || {}), ...patch } });
  const uploadPageCover = async (key, file) => { try { const d = await uploadMemberFile(file); setCover(key, { image: d.url }); } catch (_) {} };
  const pickCover = (key, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCoverCrop({ src: reader.result, key });
    reader.readAsDataURL(file);
  };
  const onCoverCropDone = async (blob) => {
    const key = coverCrop?.key;
    setCoverCrop(null);
    if (!key || !blob) return;
    try {
      const file = new File([blob], "cover.jpg", { type: "image/jpeg" });
      const d = await uploadMemberFile(file);
      setCover(key, { image: d.url });
    } catch (_) {}
  };
  const BANNER_PAGES = [
    { key: "annonces", label: "Annonces", ph: "Nos annonces" },
    { key: "presentation", label: "Présentation", ph: "Présentation" },
    { key: "expertises", label: "Nos expertises", ph: "Nos expertises" },
    { key: "partenaires", label: "Partenaires locaux", ph: "Partenaires locaux" },
    { key: "news", label: "News", ph: "News" },
    { key: "contact", label: "Contact", ph: "Parlons de votre projet" },
  ];

  return (
    <div className={`space-y-8 transition-all ${showPreview ? "xl:pr-[460px]" : ""}`} data-testid="site-editor">
      {/* Bouton flottant Publier */}
      <motion.button onClick={save} disabled={saving} data-testid="site-save-floating"
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ scale: 1.05 }}
        className="fixed bottom-20 lg:bottom-6 right-4 lg:right-6 z-40 inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#A88358] disabled:opacity-60 text-white font-semibold uppercase tracking-wider text-sm px-5 py-3 rounded-full shadow-[0_10px_30px_rgba(193,154,107,0.45)]">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {saved ? "Publié ✓" : "Publier"}
      </motion.button>
      <h1 className="calli text-3xl sm:text-4xl leading-none text-[#C19A6B]" data-testid="site-page-title">Configurer mon site</h1>

      {showPreview && <LiveSitePreview slug={member.slug} site={site} onClose={() => setShowPreview(false)} />}

      {/* Abonnement & mise en ligne */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl shadow-sm p-5 space-y-4" data-testid="site-subscription">
        <div className="flex items-center justify-between flex-wrap gap-3 pb-4 border-b border-[#F3F4F6]">
          <p className="text-sm text-zinc-500">Personnalisez votre site puis <a href={`/${member.slug}/site`} target="_blank" rel="noopener noreferrer" className="underline" style={acc}>prévisualisez-le</a>.</p>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setShowPreview((v) => !v)} data-testid="site-preview-toggle" className={`inline-flex items-center gap-2 border text-sm uppercase tracking-wider px-4 py-2.5 rounded-md transition-colors ${showPreview ? "border-[#C19A6B] text-[#C19A6B] bg-[#C19A6B]/10" : "border-zinc-300 text-zinc-600 hover:border-[#C19A6B] hover:text-[#C19A6B]"}`}><Eye className="w-4 h-4" /> Aperçu en direct</button>
            <button onClick={save} disabled={saving} data-testid="site-save" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-60 text-white font-medium uppercase tracking-wider text-sm px-6 py-2.5 rounded-md transition-colors">{saved ? "Enregistré ✓" : saving ? "…" : "Publier les modifications"} <Check className="w-4 h-4" /></button>
          </div>
        </div>
        {sub === null ? (
          <p className="text-sm text-zinc-500">Chargement de l'abonnement…</p>
        ) : sub.active ? (
          <>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="calli text-2xl sm:text-3xl text-[#C19A6B] flex items-center gap-2 leading-none"><Rocket className="w-5 h-5" /> Site {site.published ? "en ligne" : "hors ligne"}</p>
                <p className="text-xs text-zinc-500 mt-1.5">{sub.is_founder ? "Accès fondateur (abonnement offert)." : sub.cancel_at_period_end ? "Abonnement actif jusqu'à la fin de la période en cours." : "Abonnement Site personnalisé actif (39 €/mois)."}</p>
              </div>
              <div className="flex items-center gap-2">
                {site.published
                  ? <button onClick={() => setPublished(false)} disabled={pubBusy} data-testid="site-unpublish" className="inline-flex items-center gap-2 border border-zinc-300 text-zinc-600 text-sm uppercase tracking-wider px-4 py-2.5 rounded-md hover:border-red-400 hover:text-red-400 transition-colors"><EyeOff className="w-4 h-4" /> Mettre hors ligne</button>
                  : <button onClick={() => setPublished(true)} disabled={pubBusy} data-testid="site-publish" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-sm uppercase tracking-wider px-4 py-2.5 rounded-md transition-colors"><Rocket className="w-4 h-4" /> Mettre en ligne</button>}
              </div>
            </div>
            {site.published && <PublicUrlBox url={`${PUBLIC_SITE}/${member.slug}/site`} />}
            {!sub.is_founder && !sub.cancel_at_period_end && <div><button onClick={async () => { if (window.confirm("Résilier votre abonnement en fin de période ?")) { try { await authApi.subCancel(); setSub(await authApi.subStatus()); } catch (_) {} } }} data-testid="sub-cancel" className="text-[11px] text-zinc-500 hover:text-red-400 underline">Résilier l'abonnement</button></div>}
          </>
        ) : (
          <div className="text-center py-4 space-y-3" data-testid="site-subscribe-cta">
            <Rocket className="w-8 h-8 text-[#C19A6B] mx-auto" />
            <p className="text-lg font-light text-white">Mettez votre site personnalisé en ligne</p>
            <p className="text-sm text-zinc-400 max-w-md mx-auto">Abonnez-vous pour publier votre site sur le web et accéder à la configuration d'un nom de domaine personnalisé.</p>
            <button onClick={startCheckout} data-testid="site-subscribe" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white font-medium uppercase tracking-wider text-sm px-6 py-3 transition-colors">S'abonner — 39 €/mois <ArrowRight className="w-4 h-4" /></button>
          </div>
        )}
      </div>

      {/* Domaine personnalisé (abonnés) */}
      {sub?.active && (
        <div className="border border-white/10 p-5 space-y-4" data-testid="site-domain">
          <p className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] flex items-center gap-2"><Globe className="w-4 h-4" style={acc} /> Nom de domaine personnalisé</p>
          <div className="flex gap-2 flex-wrap">
            <input value={domainInput} onChange={(e) => setDomainInput(e.target.value)} placeholder="www.mon-agence.fr" data-testid="domain-input" className={`${inp} flex-1 min-w-[220px]`} />
            <button onClick={saveDomain} disabled={domBusy} data-testid="domain-save" className="inline-flex items-center gap-2 border border-[#C19A6B]/50 text-[#C19A6B] hover:bg-[#C19A6B]/10 text-sm uppercase tracking-wider px-4 py-2.5">{domBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Enregistrer</button>
          </div>
          {site.domain && (
            <div className="space-y-3 text-sm">
              {site.domain_verified
                ? <span className="inline-flex items-center gap-1.5 text-emerald-400 text-xs uppercase tracking-wider" data-testid="domain-verified"><Check className="w-4 h-4" /> Domaine vérifié</span>
                : <span className="inline-flex items-center gap-1.5 text-amber-400 text-xs uppercase tracking-wider"><Info className="w-4 h-4" /> En attente de vérification</span>}
              <div className="bg-white/5 border border-white/10 p-4 space-y-3 text-xs">
                <p className="text-zinc-400">Ajoutez ces enregistrements chez votre registrar (OVH, Gandi, IONOS…) :</p>
                <div className="font-mono text-zinc-200 space-y-2">
                  <div><span className="text-zinc-500">1. CNAME</span><br />{site.domain} → <span className="text-[#C19A6B]">{window.location.host}</span></div>
                  <div><span className="text-zinc-500">2. TXT (vérification de propriété)</span><br />_offmarkette.{site.domain} → <span className="text-[#C19A6B] break-all">{site.domain_token}</span></div>
                </div>
                <p className="text-zinc-500">La propagation DNS peut prendre 24-48h. Le raccordement HTTPS final est activé par l'équipe OFF MARKETTE une fois le domaine vérifié.</p>
              </div>
              <button onClick={doVerifyDomain} disabled={domBusy} data-testid="domain-verify" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-sm uppercase tracking-wider px-4 py-2.5">{domBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Vérifier le domaine</button>
            </div>
          )}
        </div>
      )}

      {/* Couleurs */}
      <div className="border border-white/10 p-5">
        <p className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] mb-4 flex items-center gap-2"><Palette className="w-4 h-4" style={acc} /> Identité visuelle</p>
        {netKey && (
          <div className="mb-4 rounded-[6px] border border-[#C19A6B]/20 bg-[#C19A6B]/5 p-3 text-xs" data-testid="site-network-detected">
            <p className="text-zinc-300">Réseau détecté : <b className="text-white">{netMeta?.alt || netKey}</b></p>
            <p className="text-zinc-400 mt-0.5">Template actif : <b className="text-[#C19A6B]">{netMeta?.alt} · Off Markette</b></p>
          </div>
        )}
        {netPresets.length > 0 && (
          <div className="mb-4" data-testid="site-network-presets">
            <p className="text-[11px] uppercase tracking-wider text-zinc-400 mb-2">Couleurs {netMeta?.alt || "réseau"} — choisissez la dominante</p>
            <div className="flex flex-wrap gap-2">
              {netPresets.map((p) => {
                const active = (site.theme.primary || "").toLowerCase() === p.primary.toLowerCase() && (site.theme.accent || "").toLowerCase() === (p.accent || "").toLowerCase();
                return (
                  <button key={p.label} onClick={() => setTheme({ primary: p.primary, accent: p.accent || "" })}
                    data-testid={`site-preset-${p.label.replace(/\s+/g, "-").toLowerCase()}`}
                    className={`inline-flex items-center gap-2 rounded-[5px] border px-3 py-2 text-xs uppercase tracking-wider transition-colors ${active ? "border-[#C19A6B] bg-[#C19A6B]/15 text-white" : "border-white/15 text-zinc-300 hover:border-[#C19A6B]/60"}`}>
                    <span className="flex items-center -space-x-1">
                      <span className="w-4 h-4 rounded-full border border-white/30" style={{ backgroundColor: p.primary }} />
                      {p.accent && <span className="w-4 h-4 rounded-full border border-white/30" style={{ backgroundColor: p.accent }} />}
                    </span>
                    {p.label}
                    {active && <Check className="w-3.5 h-3.5 text-[#C19A6B]" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div className="flex items-center gap-4 flex-wrap">
          <input type="color" value={primary} onChange={(e) => setTheme({ primary: e.target.value })} data-testid="site-color" className="w-12 h-10 bg-transparent border border-white/15 cursor-pointer" />
          <div className="flex items-center gap-2">{PALETTE.map((c) => <button key={c} onClick={() => setTheme({ primary: c })} className="w-7 h-7 rounded-full border-2" style={{ backgroundColor: c, borderColor: primary === c ? "#fff" : "transparent" }} />)}</div>
          {member.logo && <button onClick={extractLogoColor} data-testid="site-logo-color" className="text-xs uppercase tracking-wider border border-white/15 px-3 py-2 hover:border-[#C19A6B] transition-colors">Couleur du logo</button>}
        </div>
      </div>

      {/* Pages / menu */}
      <div className="border border-white/10 p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B]">Menu & pages</p>
          <button onClick={addPage} data-testid="site-add-page" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider border border-white/15 px-3 py-1.5 hover:border-[#C19A6B] hover:text-[#C19A6B] transition-colors"><Plus className="w-3.5 h-3.5" /> Nouvelle page</button>
        </div>
        <div className="space-y-2" data-testid="site-pages">
          {orderedPages.map((p, i) => (
            <div
              key={p.key}
              draggable
              onDragStart={() => setDragIdx(i)}
              onDragOver={(e) => { e.preventDefault(); if (overIdx !== i) setOverIdx(i); }}
              onDrop={(e) => { e.preventDefault(); reorderPages(dragIdx, i); setDragIdx(null); setOverIdx(null); }}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
              className={`flex items-center gap-3 border px-3 py-2 transition-colors ${dragIdx === i ? "opacity-40" : ""} ${overIdx === i && dragIdx !== null && dragIdx !== i ? "border-[#C19A6B] bg-[#C19A6B]/5" : "border-white/10"}`}
              data-testid={`site-page-row-${p.key}`}
            >
              <GripVertical className="w-4 h-4 text-zinc-500 cursor-grab active:cursor-grabbing" data-testid={`site-page-drag-${p.key}`} />
              <div className="flex flex-col gap-0.5">
                <button onClick={() => movePage(i, -1)} className="text-zinc-500 hover:text-white text-xs leading-none">▲</button>
                <button onClick={() => movePage(i, 1)} className="text-zinc-500 hover:text-white text-xs leading-none">▼</button>
              </div>
              <input value={p.label} onChange={(e) => patchPage(p.key, { label: e.target.value })} className="flex-1 bg-transparent text-sm text-white outline-none border-b border-transparent focus:border-white/20" data-testid={`site-page-label-${p.key}`} />
              <button onClick={() => patchPage(p.key, { visible: !p.visible })} title={p.visible ? "Masquer" : "Afficher"} className={`text-xs uppercase tracking-wider px-2 py-1 border ${p.visible ? "border-[#C19A6B]/40 text-[#C19A6B]" : "border-white/10 text-zinc-500"}`} data-testid={`site-page-toggle-${p.key}`}>{p.visible ? "Visible" : "Masquée"}</button>
              {p.removable && <button onClick={() => removePage(p.key)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>}
            </div>
          ))}
        </div>
        <p className="text-[11px] text-zinc-600 mt-3 flex items-center gap-1.5"><GripVertical className="w-3.5 h-3.5" /> Glissez-déposez pour réordonner les pages, ou utilisez les flèches.</p>
      </div>

      {/* Contenu IA */}
      <div className="border border-white/10 p-5 space-y-6">
        <p className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] flex items-center gap-2"><Sparkles className="w-4 h-4" style={acc} /> Contenus (générés par IA)</p>

        {/* Accueil */}
        <div>
          <div className="flex items-center justify-between mb-2"><span className="text-xs uppercase tracking-wider text-zinc-500">Accueil (accroche)</span><GenBtn page="accueil" /></div>
          <div className="grid sm:grid-cols-3 gap-2">
            <input className={inp} placeholder="Titre" value={content.accueil?.hero_title || ""} onChange={(e) => setContent("accueil", { ...content.accueil, hero_title: e.target.value })} />
            <input className={inp} placeholder="Sous-titre" value={content.accueil?.hero_subtitle || ""} onChange={(e) => setContent("accueil", { ...content.accueil, hero_subtitle: e.target.value })} />
            <input className={inp} placeholder="Bouton" value={content.accueil?.cta || ""} onChange={(e) => setContent("accueil", { ...content.accueil, cta: e.target.value })} />
          </div>
        </div>

        {/* Présentation */}
        <div>
          <div className="flex items-center justify-between mb-2"><span className="text-xs uppercase tracking-wider text-zinc-500">Présentation</span><div className="flex gap-2"><GenBtn page="presentation" /><GenBtn page="presentation" action="improve" current={content.presentation?.text} label="Améliorer" /></div></div>
          <textarea className={`${inp} min-h-[120px]`} placeholder="Votre présentation…" value={content.presentation?.text || ""} onChange={(e) => setContent("presentation", { text: e.target.value })} data-testid="site-presentation" />
        </div>

        {/* Secteur */}
        <div>
          <div className="flex items-center justify-between mb-2"><span className="text-xs uppercase tracking-wider text-zinc-500">Secteur (atouts, prix, rentabilité)</span><div className="flex gap-2"><GenBtn page="secteur" /><GenBtn page="secteur" action="improve" current={content.secteur?.text} label="Améliorer" /></div></div>
          <textarea className={`${inp} min-h-[100px]`} placeholder="Présentation de votre secteur : atouts immobiliers, prix, rentabilité d'investissement…" value={content.secteur?.text || ""} onChange={(e) => setContent("secteur", { text: e.target.value })} data-testid="site-secteur-edit" />
        </div>

        {/* Expertises */}
        <div>
          <div className="flex items-center justify-between mb-2"><span className="text-xs uppercase tracking-wider text-zinc-500">Nos expertises</span><GenBtn page="expertises" /></div>
          <div className="space-y-2" data-testid="site-expertises-editor">
            {expert.map((e, i) => (
              <div key={i} className="grid sm:grid-cols-[1fr_2fr_auto] gap-2 items-start">
                <input className={inp} value={e.title} onChange={(ev) => { const items = [...expert]; items[i] = { ...e, title: ev.target.value }; setContent("expertises", { items }); }} />
                <input className={inp} value={e.text} onChange={(ev) => { const items = [...expert]; items[i] = { ...e, text: ev.target.value }; setContent("expertises", { items }); }} />
                <button onClick={() => setContent("expertises", { items: expert.filter((_, j) => j !== i) })} className="text-zinc-500 hover:text-red-400 pt-2"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={() => setContent("expertises", { items: [...expert, { title: "Nouvelle expertise", text: "" }] })} className="text-[11px] uppercase tracking-wider text-zinc-400 hover:text-[#C19A6B]">+ Ajouter</button>
          </div>
        </div>

        {/* SEO */}
        <div>
          <div className="flex items-center justify-between mb-2"><span className="text-xs uppercase tracking-wider text-zinc-500">SEO (référencement)</span><GenBtn page="seo" /></div>
          <div className="grid sm:grid-cols-2 gap-2">
            <input className={inp} placeholder="Titre SEO" value={content.seo?.meta_title || ""} onChange={(e) => setContent("seo", { ...content.seo, meta_title: e.target.value })} />
            <input className={inp} placeholder="Description SEO" value={content.seo?.meta_description || ""} onChange={(e) => setContent("seo", { ...content.seo, meta_description: e.target.value })} />
          </div>
        </div>
      </div>

      {/* Bannières de couverture des pages */}
      <div className="border border-white/10 p-5 space-y-4" data-testid="site-banners-editor">
        <p className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] flex items-center gap-2"><Upload className="w-4 h-4" style={acc} /> Bannières des pages</p>
        <p className="text-[11px] text-zinc-500">Personnalisez l'image de couverture (bandeau pleine largeur) et le titre affichés en haut de chaque page. Laissez vide pour utiliser les visuels OFF MARKETTE par défaut.</p>
        <div className="grid md:grid-cols-2 gap-4">
          {BANNER_PAGES.map((bp) => {
            const c = covers[bp.key] || {};
            return (
              <div key={bp.key} className="border border-white/10 p-4 space-y-3" data-testid={`banner-editor-${bp.key}`}>
                <p className="text-xs uppercase tracking-wider text-[#C19A6B]">{bp.label}</p>
                <div className="relative h-28 w-full overflow-hidden border border-white/10 bg-black/40 flex items-end justify-center text-center" data-testid={`banner-preview-${bp.key}`}>
                  {c.image ? <img src={c.image} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 flex items-center justify-center text-[10px] uppercase tracking-widest text-zinc-600">Visuel par défaut</div>}
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,5,5,0.25) 0%, rgba(5,5,5,0.72) 100%)" }} />
                  <div className="relative z-10 px-3 pb-2 w-full">
                    {c.subtitle && <p className="text-white text-[11px] leading-snug line-clamp-2">{c.subtitle}</p>}
                    <p className="mt-1 text-[9px] lowercase tracking-[0.18em] text-[#C19A6B] inline-flex items-center gap-1.5"><span className="w-4 h-px bg-[#C19A6B] inline-block" />{c.title || bp.ph}<span className="w-4 h-px bg-[#C19A6B] inline-block" /></p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="inline-flex items-center gap-2 border border-dashed border-white/20 px-3 py-2 cursor-pointer text-xs text-zinc-300 hover:border-[#C19A6B]/50 transition-colors" data-testid={`banner-upload-${bp.key}`}>
                    <Upload className="w-3.5 h-3.5 text-[#C19A6B]" /> {c.image ? "Changer" : "Image"}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => { pickCover(bp.key, e.target.files[0]); e.target.value = ""; }} />
                  </label>
                  {c.image && <button onClick={() => setCover(bp.key, { image: "" })} className="text-[11px] text-red-400 hover:underline" data-testid={`banner-remove-${bp.key}`}>Retirer</button>}
                </div>
                <input className={inp} placeholder={`Titre (défaut : ${bp.ph})`} value={c.title || ""} onChange={(e) => setCover(bp.key, { title: e.target.value.slice(0, 80) })} data-testid={`banner-title-${bp.key}`} />
                <input className={inp} placeholder="Sous-titre (optionnel)" value={c.subtitle || ""} onChange={(e) => setCover(bp.key, { subtitle: e.target.value.slice(0, 160) })} data-testid={`banner-subtitle-${bp.key}`} />
              </div>
            );
          })}
        </div>
      </div>

      {coverCrop && (
        <ImageCropper src={coverCrop.src} aspect={16 / 7} presets={CROP_PRESETS.cover} title="Recadrer la bannière (déplacez pour ajuster verticalement)"
          onCancel={() => setCoverCrop(null)} onDone={onCoverCropDone} />
      )}

      {/* Page « Rejoindre le réseau » */}
      <div className="border border-white/10 p-5 space-y-4" data-testid="site-join-editor">
        <p className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] flex items-center gap-2"><Users className="w-4 h-4" style={acc} /> Page « Rejoindre le réseau »</p>
        <p className="text-[11px] text-zinc-500">Personnalisez la page de recrutement de votre site (accroche, texte et image de couverture). Laissez vide pour utiliser les valeurs OFF MARKETTE par défaut. Toute inscription depuis votre site vous désigne automatiquement comme parrain.</p>
        <div>
          <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1.5">Accroche (titre)</label>
          <input className={inp} placeholder="Le réseau immobilier collaboratif augmenté par l'IA" value={join.headline || ""} onChange={(e) => setJoin({ headline: e.target.value.slice(0, 160) })} data-testid="join-edit-headline" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1.5">Sous-titre</label>
          <textarea className={`${inp} min-h-[80px]`} placeholder="Développez votre activité à mes côtés et profitez de la puissance d'un réseau national…" value={join.subtitle || ""} onChange={(e) => setJoin({ subtitle: e.target.value.slice(0, 400) })} data-testid="join-edit-subtitle" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Image de couverture</label>
          <div className="flex items-center gap-3 flex-wrap">
            {join.cover && <img src={join.cover} alt="" className="w-32 h-20 object-cover border border-white/10" data-testid="join-edit-cover-preview" />}
            <label className="inline-flex items-center gap-2 border border-dashed border-white/20 px-4 py-3 cursor-pointer text-sm text-zinc-300 hover:border-[#C19A6B]/50 transition-colors" data-testid="join-edit-cover-upload">
              <Upload className="w-4 h-4 text-[#C19A6B]" /> {join.cover ? "Changer l'image" : "Choisir une image"}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadJoinCover(e.target.files[0])} />
            </label>
            {join.cover && <button onClick={() => setJoin({ cover: "" })} className="text-xs text-red-400 hover:underline" data-testid="join-edit-cover-remove">Retirer</button>}
          </div>
        </div>
      </div>

      {/* Partenaires locaux */}
      <div className="border border-white/10 p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] flex items-center gap-2"><Users className="w-4 h-4" style={acc} /> Partenaires locaux</p>
          <button onClick={addPartner} data-testid="site-add-partner" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider border border-white/15 px-3 py-1.5 hover:border-[#C19A6B] hover:text-[#C19A6B] transition-colors"><Plus className="w-3.5 h-3.5" /> Ajouter</button>
        </div>
        <div className="space-y-3" data-testid="site-partners-editor">
          {partners.length === 0 && <p className="text-xs text-zinc-500">Ajoutez vos notaires, courtiers, artisans, photographes… pour valoriser votre réseau local.</p>}
          {partners.map((p) => (
            <div key={p.id} className="border border-white/10 p-3 grid sm:grid-cols-[auto_1fr] gap-3">
              <label className="w-16 h-16 border border-dashed border-white/20 flex items-center justify-center cursor-pointer overflow-hidden shrink-0 hover:border-[#C19A6B]/50">
                {p.logo ? <img src={p.logo} alt="" className="w-full h-full object-contain" /> : <Upload className="w-4 h-4 text-[#C19A6B]" />}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadPartnerLogo(p.id, e.target.files[0])} />
              </label>
              <div className="grid sm:grid-cols-2 gap-2">
                <input className={inp} placeholder="Nom" value={p.name} onChange={(e) => patchPartner(p.id, { name: e.target.value })} />
                <select className={inp} value={p.category} onChange={(e) => patchPartner(p.id, { category: e.target.value })}>{PARTNER_CATS.map((c) => <option key={c} value={c} className="bg-[#0d0d10]">{c}</option>)}</select>
                <input className={inp} placeholder="Téléphone" value={p.phone} onChange={(e) => patchPartner(p.id, { phone: e.target.value })} />
                <input className={inp} placeholder="Site web" value={p.website} onChange={(e) => patchPartner(p.id, { website: e.target.value })} />
                <textarea className={`${inp} sm:col-span-2`} placeholder="Description" value={p.description} onChange={(e) => patchPartner(p.id, { description: e.target.value })} />
                <button onClick={() => setPartners(partners.filter((x) => x.id !== p.id))} className="sm:col-span-2 text-left text-xs text-red-400 hover:text-red-300 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Supprimer</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Galeries photos & vidéos */}
      <div className="border border-white/10 p-5 space-y-5">
        <p className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] flex items-center gap-2"><Palette className="w-4 h-4" style={acc} /> Galerie photos & vidéos</p>
        <div>
          <div className="flex items-center justify-between mb-2"><span className="text-xs uppercase tracking-wider text-zinc-500">Photos ({media.photos.length}/20)</span></div>
          {media.photos.length > 0 && (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-3" data-testid="site-media-photos">
              {media.photos.map((src, i) => (
                <div key={i} className="relative group aspect-square overflow-hidden border border-white/10">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setMedia({ ...media, photos: media.photos.filter((_, j) => j !== i) })} className="absolute top-1 right-1 bg-black/70 text-white p-1 opacity-0 group-hover:opacity-100"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
          {media.photos.length < 20 && (
            <label className="inline-flex items-center gap-2 border border-dashed border-white/20 px-4 py-2.5 cursor-pointer text-sm text-zinc-300 hover:border-[#C19A6B]/50 transition-colors" data-testid="site-media-upload">
              <Upload className="w-4 h-4 text-[#C19A6B]" /> Ajouter des photos
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => e.target.files.length && uploadMediaPhotos(e.target.files)} />
            </label>
          )}
        </div>
        <div>
          <span className="text-xs uppercase tracking-wider text-zinc-500 block mb-2">Vidéos (YouTube, Vimeo ou lien direct)</span>
          <div className="space-y-2 mb-2">
            {media.videos.map((v, i) => (
              <div key={i} className="flex items-center gap-2"><input className={inp} value={v} onChange={(e) => { const vids = [...media.videos]; vids[i] = e.target.value; setMedia({ ...media, videos: vids }); }} /><button onClick={() => setMedia({ ...media, videos: media.videos.filter((_, j) => j !== i) })} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button></div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input className={inp} placeholder="https://youtube.com/watch?v=…" value={newVideo} onChange={(e) => setNewVideo(e.target.value)} data-testid="site-video-input" />
            <button onClick={() => { if (newVideo.trim()) { setMedia({ ...media, videos: [...media.videos, newVideo.trim()].slice(0, 10) }); setNewVideo(""); } }} className="text-xs uppercase tracking-wider border border-white/15 px-3 py-2.5 hover:border-[#C19A6B] hover:text-[#C19A6B] transition-colors shrink-0">Ajouter</button>
          </div>
        </div>
      </div>

      {/* News (articles & pages SEO locales) */}
      <div className="border border-white/10 p-5 space-y-4" data-testid="site-news-editor">
        <p className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] flex items-center gap-2"><Newspaper className="w-4 h-4" style={acc} /> News (articles générés par IA)</p>
        <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-end bg-white/[0.02] border border-white/10 p-3">
          <div><label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Sujet d'article</label><input className={inp} placeholder="Ex : Bien préparer sa vente en 2026" value={newsTopic} onChange={(e) => setNewsTopic(e.target.value)} data-testid="news-topic" /></div>
          <div><label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Ou ville (page SEO locale)</label><input className={inp} placeholder="Ex : Chantilly" value={newsCity} onChange={(e) => setNewsCity(e.target.value)} data-testid="news-city" /></div>
          <button onClick={addGeneratedNews} disabled={genNews || (!newsTopic && !newsCity)} data-testid="news-generate" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 text-white text-xs uppercase tracking-wider px-4 py-2.5 transition-colors">{genNews ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Générer</button>
        </div>
        {news.length === 0 ? <p className="text-xs text-zinc-500">Générez des articles et des pages SEO locales (« Immobilier à Chantilly »…) pour booster votre référencement.</p> : (
          <div className="space-y-2">
            {news.map((a) => (
              <details key={a.id} className="border border-white/10">
                <summary className="flex items-center gap-3 px-3 py-2 cursor-pointer list-none">
                  <span className="text-[10px] uppercase tracking-widest text-[#C19A6B]">{a.tag}</span>
                  <span className="flex-1 text-sm text-white truncate">{a.title}</span>
                  <span className="text-[11px] text-zinc-600">{a.date}</span>
                  <button onClick={(e) => { e.preventDefault(); setNews(news.filter((x) => x.id !== a.id)); }} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </summary>
                <div className="p-3 border-t border-white/10 space-y-2">
                  <input className={inp} placeholder="Titre" value={a.title} onChange={(e) => patchNews(a.id, { title: e.target.value })} />
                  <input className={inp} placeholder="Résumé" value={a.excerpt} onChange={(e) => patchNews(a.id, { excerpt: e.target.value })} />
                  <textarea className={`${inp} min-h-[160px]`} placeholder="Contenu" value={a.body} onChange={(e) => patchNews(a.id, { body: e.target.value })} />
                  {(() => {
                    const edits = a.cover_ai_edits || 0;
                    const locked = edits >= 1;
                    const busy = genCover === a.id;
                    return (
                      <div className="space-y-2" data-testid={`news-cover-block-${a.id}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1"><Sparkles className="w-3 h-3 text-[#C19A6B]" /> Image IA {locked ? "(déjà modifiée)" : "· modifiable 1 fois"}</span>
                          <button onClick={() => generateNewsCover(a.id, true)} disabled={busy || locked} data-testid={`news-cover-regen-${a.id}`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider border border-[#C19A6B]/40 text-[#C19A6B] hover:bg-[#C19A6B]/10 disabled:opacity-40 px-3 py-1.5 transition-colors">
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} {a.cover ? "Régénérer l'image" : "Générer l'image IA"}
                          </button>
                          {!locked && <label className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider text-zinc-300 cursor-pointer border border-white/15 hover:border-[#C19A6B] px-3 py-1.5"><Upload className="w-3.5 h-3.5 text-[#C19A6B]" /> Importer<input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadNewsCover(a.id, e.target.files[0])} /></label>}
                        </div>
                        {busy && !a.cover && <p className="text-[11px] text-zinc-500 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Génération de l'image (jusqu'à 1 min)…</p>}
                        {a.cover && <img src={a.cover} alt="" className="w-40 aspect-[16/10] object-cover border border-white/10 rounded-[5px]" />}
                      </div>
                    );
                  })()}
                </div>
              </details>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MemberAcqCard({ a, onDelete }) {
  return (
    <div className="border border-white/10 bg-[#0d0d10] p-4 space-y-2" data-testid={`my-acq-${a.id}`}>
      <div className="flex items-center justify-between">
        <h4 className="text-white font-medium text-sm">{a.display_name || a.name || "Acquéreur"}</h4>
        <button onClick={() => onDelete(a.id)} className="text-zinc-500 hover:text-red-400" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="flex items-center gap-2">
        <OriginBadge origin={a.origin} />
        <button onClick={async () => { try { const d = await authApi.acqPortalLink(a.id); const url = PUBLIC_SITE + d.path; await navigator.clipboard.writeText(url); alert("Lien espace acquéreur copié :\n" + url); } catch (_) { alert("Impossible de générer le lien."); } }} className="text-[10px] uppercase tracking-wider text-[#C19A6B] border border-[#C19A6B]/40 rounded-full px-2 py-1 hover:bg-[#C19A6B]/10" title="Lien espace acquéreur" data-testid={`acq-link-${a.id}`}>Espace</button>
      </div>
      <div className="text-xs text-zinc-400 space-y-1 pt-1">
        {a.phone && <p className="flex items-center gap-1.5"><Phone className="w-3 h-3" /> {a.phone}</p>}
        {a.email && <p className="flex items-center gap-1.5"><Mail className="w-3 h-3" /> {a.email}</p>}
        {(a.secteurs || []).length > 0 && <p className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {a.secteurs.join(", ")}</p>}
        {a.budget && <p className="text-[#C19A6B]">Budget : {a.budget}</p>}
        {(a.type_bien || []).length > 0 && <p>Type : {a.type_bien.join(", ")}</p>}
        {(a.criteres || []).length > 0 && <p className="text-zinc-500">{a.criteres.join(" · ")}</p>}
      </div>
    </div>
  );
}

function AcquereursTab() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();
  const reload = () => authApi.memberAcquereurs().then(setItems).catch(() => setItems([]));
  useEffect(() => { reload(); }, []);
  const onImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true); setMsg("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${API}/member/acquereurs/import`, { method: "POST", body: fd, credentials: "include" });
      if (!res.ok) throw new Error();
      const d = await res.json();
      setMsg(`${d.imported} importé(s)${d.skipped ? `, ${d.skipped} ignoré(s)` : ""}.`); reload();
    } catch (_) { setMsg("Import impossible (CSV ou Excel .xlsx)."); }
    setImporting(false); e.target.value = "";
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer cet acquéreur ?")) return;
    try { await authApi.deleteMemberAcquereur(id); setItems((l) => l.filter((x) => x.id !== id)); } catch (_) {}
  };
  const counts = React.useMemo(() => { const c = {}; (items || []).forEach((a) => { const o = a.origin || "spontane"; c[o] = (c[o] || 0) + 1; }); return c; }, [items]);
  const filtered = (items || []).filter((a) => filter === "all" || (a.origin || "spontane") === filter);
  const present = Object.keys(ACQ_ORIGINS).filter((k) => counts[k]);

  return (
    <div data-testid="acquereurs-tab">
      <div className="border border-[#C19A6B]/30 bg-gradient-to-r from-[#C19A6B]/10 to-transparent p-4 mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] flex items-center gap-2"><Contact className="w-5 h-5 text-[#C19A6B]" /> Mon fichier acquéreurs</h3>
          <p className="text-xs text-zinc-400 mt-1">Importez votre fichier (Excel/CSV) ou dictez vos contacts. Ils vous sont rattachés automatiquement.</p>
          {msg && <p className="text-xs text-[#C19A6B] mt-2" data-testid="my-acq-msg">{msg}</p>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => fileRef.current?.click()} disabled={importing} className="inline-flex items-center gap-2 bg-white border border-[#C19A6B]/50 text-[#C19A6B] hover:bg-[#C19A6B]/5 disabled:opacity-60 text-sm font-semibold uppercase tracking-wider px-4 py-2.5 rounded-[5px] transition-colors" data-testid="my-acq-import-btn">{importing ? <><Loader2 className="w-4 h-4 animate-spin" /> Import…</> : <><Upload className="w-4 h-4" /> Fichier</>}</button>
          <motion.button onClick={() => setVoiceOpen(true)} data-testid="my-acq-voice-btn"
            animate={{ scale: [1, 1.06, 1] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }} whileHover={{ scale: 1.1 }}
            style={{ boxShadow: "0 0 16px rgba(193,154,107,0.5)" }}
            className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#A88358] text-white text-sm font-semibold uppercase tracking-wider px-4 py-2.5 rounded-[5px] transition-colors"><Mic className="w-4 h-4 text-white" /> Enregistrer un vocal</motion.button>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onImport} className="hidden" />
        </div>
      </div>

      {items && items.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5">
          <button onClick={() => setFilter("all")} className={`text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full border ${filter === "all" ? "bg-white text-black border-white" : "border-white/20 text-zinc-400 hover:text-white"}`}>Tous ({items.length})</button>
          {present.map((k) => { const mo = originMeta(k); const active = filter === k; return (
            <button key={k} onClick={() => setFilter(k)} className="text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full border" style={active ? { backgroundColor: mo.color, borderColor: mo.color, color: "#000" } : { borderColor: mo.color + "55", color: mo.color }}>{mo.emoji} {mo.label} ({counts[k]})</button>
          ); })}
        </div>
      )}

      {items === null ? (
        <div className="py-16 text-center text-zinc-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-zinc-500">Aucun acquéreur pour l'instant. Importez votre fichier ou dictez vos contacts pour démarrer.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((a) => <MemberAcqCard key={a.id} a={a} onDelete={del} />)}
        </div>
      )}

      {voiceOpen && <VoiceImportModal onClose={() => setVoiceOpen(false)} onDone={(m) => { setVoiceOpen(false); setMsg(m); reload(); }} />}
    </div>
  );
}

function VendeursTab() {
  const [items, setItems] = useState(null);
  const [active, setActive] = useState(null); // slug ouvert
  const [thread, setThread] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  useEffect(() => { authApi.memberSellers().then(setItems).catch(() => setItems([])); }, []);
  const open = async (l) => { setActive(l); setThread([]); try { setThread(await authApi.sellerThread(l.slug)); } catch (_) {} };
  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try { const m = await authApi.sellerReply(active.slug, reply); setThread((t) => [...t, m]); setReply(""); } catch (_) {}
    setSending(false);
  };
  const copy = async (l) => { try { await navigator.clipboard.writeText(PUBLIC_SITE + l.path); alert("Lien copié : " + PUBLIC_SITE + l.path); } catch (_) {} };
  return (
    <div data-testid="vendeurs-tab">
      <div className="mb-5"><h3 className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] flex items-center gap-2"><Building2 className="w-5 h-5 text-[#C19A6B]" /> Mes vendeurs</h3>
        <p className="text-sm text-zinc-400 mt-1">Les biens dont vous avez partagé un espace vendeur. Suivez et répondez aux échanges.</p></div>
      {items === null ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" /></div> : items.length === 0 ? (
        <p className="text-sm text-zinc-500">Aucun espace vendeur actif. Ouvrez une annonce → « Lien vendeur » pour en générer un.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((l) => (
            <div key={l.slug} className="border border-white/10 bg-[#0d0d10] overflow-hidden" data-testid={`vendeur-${l.slug}`}>
              {l.cover && <img src={l.cover} alt="" className="w-full aspect-[16/10] object-cover" />}
              <div className="p-4 space-y-2">
                <h4 className="text-white text-sm font-medium truncate">{l.title}</h4>
                <p className="text-xs text-zinc-500">{l.city} · {l.messages} message(s){l.unread ? <span className="text-[#C19A6B]"> · {l.unread} non lu(s)</span> : ""}</p>
                <div className="flex gap-2">
                  <button onClick={() => open(l)} className="flex-1 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-xs font-semibold uppercase tracking-wider py-2" data-testid={`vendeur-open-${l.slug}`}>Échanges</button>
                  <button onClick={() => copy(l)} className="border border-white/15 text-zinc-300 hover:text-white px-3" title="Copier le lien"><Link2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {active && (
        <div className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-sm flex items-center justify-center p-3" data-testid="vendeur-thread">
          <div className="max-w-md w-full bg-[#0d0d10] border border-white/10 p-5 space-y-3">
            <div className="flex items-center justify-between"><h3 className="text-white font-light">{active.title}</h3><button onClick={() => setActive(null)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button></div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {thread.length === 0 ? <p className="text-sm text-zinc-500">Aucun message pour l'instant.</p> :
                thread.map((m, i) => (
                  <div key={i} className={`text-sm p-2.5 max-w-[85%] ${m.from === "advisor" ? "ml-auto bg-[#C19A6B]/15 border border-[#C19A6B]/20" : "bg-white/5 border border-white/10"}`}>
                    <p className="text-zinc-200">{m.text}</p>
                    <p className="text-[10px] text-zinc-500 mt-1">{m.from === "advisor" ? "Vous" : "Vendeur"}</p>
                  </div>
                ))}
            </div>
            <div className="flex gap-2">
              <input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Répondre au vendeur…" className="flex-1 bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C19A6B]" data-testid="vendeur-reply-input" />
              <button onClick={send} disabled={sending || !reply.trim()} className="bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 text-white px-4" data-testid="vendeur-reply-send"><Send className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function AgendaTab() {
  const [items, setItems] = useState(null);
  const [acqs, setAcqs] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const emptyForm = { title: "", date: "", time: "", kind: "acquereur", address: "", slug: "", acquereur_id: "", contact: "", notes: "" };
  const [f, setF] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [genBusy, setGenBusy] = useState(null);
  const [editCR, setEditCR] = useState(null);
  const [crText, setCrText] = useState("");
  const [crBusy, setCrBusy] = useState(false);
  const openCR = (v) => { setEditCR(v.id); setCrText(v.report || ""); };
  const genReportInline = async (id) => {
    setGenBusy(id);
    try { const r = await authApi.generateVisitReport(id); setCrText(r.report || ""); }
    catch (_) { window.alert("Génération impossible : rattachez un bien (idéalement issu d'une Visite IA) ou ajoutez des notes."); }
    setGenBusy(null);
  };
  const genReport = async (id) => {
    setGenBusy(id);
    try { const r = await authApi.generateVisitReport(id); setItems((l) => l.map((x) => (x.id === id ? { ...x, report: r.report } : x))); }
    catch (_) { window.alert("Génération impossible : rattachez un bien (idéalement issu d'une Visite IA) ou ajoutez des notes."); }
    setGenBusy(null);
  };
  const reload = () => authApi.memberVisits().then(setItems).catch(() => setItems([]));
  useEffect(() => {
    reload();
    authApi.memberAcquereurs().then((d) => setAcqs(d || [])).catch(() => setAcqs([]));
    authApi.myListings().then((d) => setMyListings(d || [])).catch(() => setMyListings([]));
  }, []);
  // Auto-remplissage quand on choisit un acquéreur / un bien
  const pickAcq = (id) => {
    const a = acqs.find((x) => x.id === id);
    setF((p) => ({ ...p, acquereur_id: id, contact: a ? (a.display_name || a.name || p.contact) : p.contact }));
  };
  const pickListing = (slug) => {
    const l = myListings.find((x) => x.slug === slug);
    setF((p) => ({ ...p, slug, address: l && !p.address ? (l.address || l.city || "") : p.address, title: l && !p.title ? l.title : p.title }));
  };
  const add = async () => {
    if (!f.title || !f.date) return;
    setSaving(true);
    try { await authApi.createVisit(f); setF(emptyForm); reload(); } catch (_) {}
    setSaving(false);
  };
  const del = async (id) => { try { await authApi.deleteVisit(id); setItems((l) => l.filter((x) => x.id !== id)); } catch (_) {} };
  const patch = async (id, upd) => { try { await authApi.updateVisit(id, upd); setItems((l) => l.map((x) => (x.id === id ? { ...x, ...upd } : x))); } catch (_) {} };
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = (items || []).filter((v) => (v.date || "") >= today);
  const past = (items || []).filter((v) => (v.date || "") < today);
  const toConfirm = upcoming.filter((v) => v.status !== "confirmee" && v.status !== "terminee");
  const toFollowUp = past.filter((v) => v.status !== "terminee");
  const maps = (addr) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr || "")}`;
  const Row = ({ v, mode }) => (
    <div className="border border-white/10 bg-[#0d0d10] p-3" data-testid={`visit-${v.id}`}>
      <div className="flex items-center gap-3">
        <div className="text-center w-14 shrink-0">
          <div className="text-[#C19A6B] text-lg font-bold leading-none">{v.date ? v.date.slice(8, 10) : "--"}</div>
          <div className="text-[10px] text-zinc-500 uppercase">{v.date ? new Date(v.date).toLocaleDateString("fr-FR", { month: "short" }) : ""}</div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-medium truncate">{v.title} {v.time && <span className="text-zinc-500 font-normal">· {v.time}</span>}
            {v.status === "confirmee" && <span className="ml-2 text-[9px] uppercase text-[#10B981] border border-[#10B981]/40 rounded px-1.5 py-0.5">Confirmée</span>}
            {v.status === "terminee" && <span className="ml-2 text-[9px] uppercase text-zinc-400 border border-white/20 rounded px-1.5 py-0.5">Terminée</span>}
          </p>
          <p className="text-xs text-zinc-500 truncate">{v.kind === "vendeur" ? "Vendeur" : "Acquéreur"}{v.address ? ` · ${v.address}` : ""}{v.contact ? ` · ${v.contact}` : ""}</p>
          {(v.acquereur_name || v.listing_title) && (
            <p className="text-[11px] text-[#C19A6B]/90 truncate mt-0.5" data-testid={`visit-links-${v.id}`}>
              {v.acquereur_name && <span className="inline-flex items-center gap-1 mr-2"><Contact className="w-3 h-3" /> {v.acquereur_name}</span>}
              {v.listing_title && <span className="inline-flex items-center gap-1"><Home className="w-3 h-3" /> {v.listing_title}</span>}
            </p>
          )}
          {v.report && <p className="text-[11px] text-zinc-400 mt-1 line-clamp-2 italic" data-testid={`visit-cr-preview-${v.id}`}>“{v.report}”</p>}
        </div>
        <button onClick={() => del(v.id)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="flex flex-wrap gap-2 mt-2 pl-[68px]">
        {v.address && <a href={maps(v.address)} target="_blank" rel="noopener noreferrer" className="text-[11px] inline-flex items-center gap-1 border border-white/15 text-zinc-300 hover:text-white rounded-full px-2.5 py-1"><MapPin className="w-3 h-3" /> Itinéraire</a>}
        {mode === "up" && v.status !== "confirmee" && <button onClick={() => patch(v.id, { status: "confirmee" })} className="text-[11px] inline-flex items-center gap-1 border border-[#10B981]/40 text-[#10B981] hover:bg-[#10B981]/10 rounded-full px-2.5 py-1"><Check className="w-3 h-3" /> Confirmer</button>}
        {mode === "past" && v.status !== "terminee" && <button onClick={() => patch(v.id, { status: "terminee" })} className="text-[11px] inline-flex items-center gap-1 border border-white/15 text-zinc-300 hover:text-white rounded-full px-2.5 py-1"><Check className="w-3 h-3" /> Marquer terminée</button>}
        {(v.slug || v.acquereur_id) && <button onClick={() => genReport(v.id)} disabled={genBusy === v.id} data-testid={`visit-cr-ai-${v.id}`} className="text-[11px] inline-flex items-center gap-1 border border-[#C19A6B]/40 text-[#C19A6B] hover:bg-[#C19A6B]/10 disabled:opacity-50 rounded-full px-2.5 py-1">{genBusy === v.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} CR par IA</button>}
        {(v.slug || v.acquereur_id) && <button onClick={() => (editCR === v.id ? setEditCR(null) : openCR(v))} data-testid={`visit-cr-edit-${v.id}`} className="text-[11px] inline-flex items-center gap-1 border border-white/15 text-zinc-300 hover:text-[#C19A6B] hover:border-[#C19A6B] rounded-full px-2.5 py-1"><Pencil className="w-3 h-3" /> {v.report ? "Modifier le CR" : "Compte-rendu"}</button>}
        {(v.slug || v.acquereur_id) && <a href={`${API}/member/visits/${v.id}/debrief`} target="_blank" rel="noopener noreferrer" data-testid={`visit-cr-pdf-${v.id}`} className="text-[11px] inline-flex items-center gap-1 border border-[#C19A6B]/40 text-[#C19A6B] hover:bg-[#C19A6B]/10 rounded-full px-2.5 py-1"><FileText className="w-3 h-3" /> CR PDF</a>}
      </div>
      {editCR === v.id && (
        <div className="mt-3 ml-[68px] border border-[#C19A6B]/30 bg-[#C19A6B]/5 p-3 space-y-2" data-testid={`visit-cr-editor-${v.id}`}>
          <div className="flex items-center justify-between">
            <p className="text-[11px] uppercase tracking-wider text-[#C19A6B]">Compte-rendu de visite</p>
            <button onClick={() => genReportInline(v.id)} disabled={genBusy === v.id} data-testid={`visit-cr-editor-ai-${v.id}`} className="text-[11px] inline-flex items-center gap-1 border border-[#C19A6B]/40 text-[#C19A6B] hover:bg-[#C19A6B]/10 disabled:opacity-50 rounded-full px-2.5 py-1">{genBusy === v.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Générer par IA</button>
          </div>
          <textarea value={crText} onChange={(e) => setCrText(e.target.value)} placeholder="Feedback acquéreur, points de blocage, suite à donner…" className="w-full bg-white/5 border border-white/15 px-3 py-2 text-sm text-white min-h-[110px] outline-none focus:border-[#C19A6B]" data-testid={`visit-cr-textarea-${v.id}`} />
          <div className="flex items-center gap-2">
            <button onClick={async () => { setCrBusy(true); await patch(v.id, { report: crText }); setCrBusy(false); setEditCR(null); }} disabled={crBusy} data-testid={`visit-cr-save-${v.id}`} className="inline-flex items-center gap-1.5 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 text-white text-[11px] font-semibold uppercase tracking-wider px-3.5 py-1.5"><Check className="w-3.5 h-3.5" /> Enregistrer</button>
            <button onClick={() => setEditCR(null)} className="text-[11px] text-zinc-400 hover:text-white px-2 py-1.5">Annuler</button>
          </div>
        </div>
      )}
    </div>
  );
  return (
    <div data-testid="agenda-tab">
      {/* Rappels intelligents */}
      {(toConfirm.length > 0 || toFollowUp.length > 0) && (
        <div className="border border-[#C19A6B]/30 bg-[#C19A6B]/5 p-4 mb-5 space-y-2" data-testid="visit-reminders">
          <h3 className="text-white text-sm font-medium flex items-center gap-2"><Info className="w-4 h-4 text-[#C19A6B]" /> Rappels intelligents</h3>
          {toConfirm.map((v) => (
            <p key={v.id} className="text-xs text-zinc-300 flex items-start gap-1.5"><Bell className="w-3.5 h-3.5 text-[#C19A6B] mt-0.5 shrink-0" /> <span><b className="text-white">{v.title}</b> le {v.date} — pensez à confirmer le rendez-vous{v.address ? " et préparer l'itinéraire" : ""}.</span></p>
          ))}
          {toFollowUp.map((v) => (
            <p key={v.id} className="text-xs text-zinc-300 flex items-start gap-1.5"><Check className="w-3.5 h-3.5 text-[#C19A6B] mt-0.5 shrink-0" /> <span>Après « <b className="text-white">{v.title}</b> » : relancer {v.kind === "vendeur" ? "le vendeur" : "l'acquéreur"}{v.kind !== "vendeur" ? " et penser à la publication" : ""} — puis marquer la visite terminée.</span></p>
          ))}
        </div>
      )}
      <div className="border border-[#C19A6B]/30 bg-gradient-to-r from-[#C19A6B]/10 to-transparent p-4 mb-5 space-y-3">
        <h3 className="text-white text-sm font-medium flex items-center gap-2"><Clock className="w-4 h-4 text-[#C19A6B]" /> Programmer une visite</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Titre / bien" className="bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C19A6B]" data-testid="visit-title" />
          <input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C19A6B]" data-testid="visit-date" />
          <input type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} className="bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C19A6B]" />
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className="bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white outline-none">
            <option value="acquereur" className="bg-[#0d0d10]">Visite acquéreur</option>
            <option value="vendeur" className="bg-[#0d0d10]">Rendez-vous vendeur</option>
          </select>
          <input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} placeholder="Adresse" className="bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C19A6B]" />
          <input value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} placeholder="Contact" className="bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C19A6B]" />
          {/* Rattachement explicite : bien + acquéreur (suivi croisé portails) */}
          <select value={f.slug} onChange={(e) => pickListing(e.target.value)} className="bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C19A6B]" data-testid="visit-listing">
            <option value="" className="bg-[#0d0d10]">— Rattacher un bien —</option>
            {myListings.map((l) => <option key={l.slug} value={l.slug} className="bg-[#0d0d10]">{l.title}</option>)}
          </select>
          {f.kind === "acquereur" && (
            <select value={f.acquereur_id} onChange={(e) => pickAcq(e.target.value)} className="bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C19A6B]" data-testid="visit-acquereur">
              <option value="" className="bg-[#0d0d10]">— Rattacher un acquéreur —</option>
              {acqs.map((a) => <option key={a.id} value={a.id} className="bg-[#0d0d10]">{a.display_name || a.name || "Acquéreur"}</option>)}
            </select>
          )}
        </div>
        <button onClick={add} disabled={saving || !f.title || !f.date} className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 text-white text-sm font-semibold uppercase tracking-wider px-5 py-2.5" data-testid="visit-add"><Plus className="w-4 h-4" /> Ajouter au planning</button>
      </div>
      {items === null ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" /></div> : (
        <div className="space-y-5">
          <div><p className="text-[11px] uppercase tracking-wider text-[#C19A6B] mb-2">À venir ({upcoming.length})</p>
            <div className="space-y-2">{upcoming.length ? upcoming.map((v) => <Row key={v.id} v={v} mode="up" />) : <p className="text-sm text-zinc-500">Aucune visite programmée.</p>}</div></div>
          {past.length > 0 && <div><p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Passées ({past.length})</p><div className="space-y-2 opacity-70">{past.map((v) => <Row key={v.id} v={v} mode="past" />)}</div></div>}
        </div>
      )}
    </div>
  );
}

function RapportsTab() {
  const [items, setItems] = useState(null);
  useEffect(() => { authApi.myListings().then((d) => setItems((d || []).filter((l) => l.created_via === "visit_ia" || l.visit_transcript || (l.room_photos || []).length))).catch(() => setItems([])); }, []);
  return (
    <div data-testid="rapports-tab">
      <div className="mb-5"><h3 className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] flex items-center gap-2"><Newspaper className="w-5 h-5 text-[#C19A6B]" /> Rapports de visite</h3>
        <p className="text-sm text-zinc-400 mt-1">Chaque bien créé via la Visite IA génère un rapport : résumé, points forts, caractéristiques et transcription — exportable en PDF.</p></div>
      {items === null ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" /></div> : items.length === 0 ? (
        <p className="text-sm text-zinc-500">Aucun rapport pour l'instant. Lancez une <button onClick={() => (window.location.href = "/visite-ia")} className="text-[#C19A6B] underline">Visite Live IA</button> pour en générer un.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((l) => (
            <div key={l.slug} className="border border-white/10 bg-[#0d0d10] overflow-hidden" data-testid={`report-${l.slug}`}>
              {l.cover && <img src={l.cover} alt="" className="w-full aspect-[16/10] object-cover" />}
              <div className="p-4 space-y-2">
                <h4 className="text-white text-sm font-medium truncate">{l.title}</h4>
                <p className="text-xs text-zinc-500">{(l.room_photos || []).length} photos classées{l.visit_transcript ? " · transcription incluse" : ""}</p>
                <a href={`${API}/member/listings/${l.slug}/visit-report`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider border border-[#C19A6B]/50 text-[#C19A6B] hover:bg-[#C19A6B]/10 px-3 py-2 w-full justify-center transition-colors" data-testid={`report-pdf-${l.slug}`}><FileText className="w-4 h-4" /> Rapport PDF</a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function SoonTab({ title, desc, icon: Ic }) {
  return (
    <div className="border border-white/10 bg-[#0d0d10] p-10 text-center" data-testid="soon-tab">
      <div className="w-14 h-14 mx-auto rounded-full bg-[#C19A6B]/15 flex items-center justify-center mb-4"><Ic className="w-7 h-7 text-[#C19A6B]" /></div>
      <h3 className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] mb-2">{title}</h3>
      <p className="text-sm text-zinc-400 max-w-md mx-auto">{desc}</p>
      <span className="inline-block mt-4 text-[11px] uppercase tracking-wider text-[#C19A6B] border border-[#C19A6B]/40 rounded-full px-3 py-1">En préparation</span>
    </div>
  );
}

// Boost mon bien — mise en avant payante (Stripe one-off).
function BoostModal({ listing, onClose }) {
  const [packs, setPacks] = useState(null);
  const [features, setFeatures] = useState([]);
  const [active, setActive] = useState(null);
  const [sel, setSel] = useState("boost_30j");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    authApi.boostStatus(listing.slug)
      .then((d) => { setPacks(d.packs || []); setFeatures(d.features || []); setActive(d.active ? d : null); })
      .catch(() => setPacks([]));
  }, [listing.slug]);
  const go = async () => {
    setBusy(true);
    try {
      const r = await authApi.boostCheckout({ listing_slug: listing.slug, package: sel, origin_url: window.location.origin });
      if (r.checkout_url) window.location.href = r.checkout_url; else setBusy(false);
    } catch (_) { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3" onClick={onClose} data-testid="boost-modal">
      <motion.div initial={{ y: 18, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={(e) => e.stopPropagation()} className="max-w-md w-full bg-white rounded-[14px] shadow-2xl overflow-hidden">
        <div className="bg-[#C19A6B] p-5 flex items-center gap-3">
          <Rocket className="w-6 h-6 text-white" />
          <div className="min-w-0"><p className="text-white font-semibold text-base">Booster mon bien</p><p className="text-white/85 text-xs truncate">{listing.title}</p></div>
          <button onClick={onClose} className="ml-auto text-white/80 hover:text-white transition-colors" data-testid="boost-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {active && <div className="text-xs font-semibold text-[#10B981] bg-[#10B981]/10 rounded-lg px-3 py-2" data-testid="boost-active">Boost actif jusqu'au {new Date(active.expires_at).toLocaleDateString("fr-FR")} — vous pouvez le prolonger.</div>}
          <BoostReportPanel slug={listing.slug} />
          <ul className="space-y-1.5">
            {features.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm text-[#111827]"><Check className="w-4 h-4 text-[#10B981] mt-0.5 shrink-0" />{f}</li>)}
          </ul>
          <div className="space-y-2">
            {(packs || []).map((p) => (
              <button key={p.lookup_key} onClick={() => setSel(p.lookup_key)} data-testid={`boost-pack-${p.lookup_key}`} className={`w-full flex items-center justify-between rounded-lg border px-4 py-3 transition-colors ${sel === p.lookup_key ? "border-[#C19A6B] bg-[#C19A6B]/10" : "border-[#E5E7EB] hover:border-[#C19A6B]/50"}`}>
                <span className="flex items-center gap-2 text-sm font-semibold text-[#111827]">{p.label}{p.popular && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#C19A6B] text-white">Populaire</span>}</span>
                <span className="text-[#A88358] font-bold">{p.price_eur} €</span>
              </button>
            ))}
          </div>
          <button onClick={go} disabled={busy || !packs} className="w-full inline-flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 text-white text-sm font-semibold uppercase tracking-wider rounded-lg py-3 transition-colors" data-testid="boost-pay">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Payer &amp; booster</button>
          <p className="text-[11px] text-zinc-400 text-center">Paiement sécurisé Stripe · sans engagement.</p>
        </div>
      </motion.div>
    </div>
  );
}

function CollabModal({ agent, onClose, onDone }) {
  const comm = agent.commission;
  const custom = !!agent.custom;
  const [type, setType] = useState(custom || (comm && comm.mode === "choice") ? "comandat" : "mise_en_relation");
  const [remuneration, setRemuneration] = useState(
    custom ? "" : (comm && comm.comandat ? `${comm.comandat.owner_pct}/${comm.comandat.buyer_side_pct} · partenaire ${comm.comandat.partner_pct}% (+${comm.comandat.site_pct}% OFF MARKETTE)` : ""));
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await authApi.matchingPropose({ member_id: agent.member_id, type, remuneration, message }); onDone("Proposition envoyée à " + agent.display_name + "."); }
    catch (_) { onDone("Proposition enregistrée."); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-sm flex items-center justify-center p-3" data-testid="collab-modal">
      <div className="max-w-md w-full bg-[#0d0d10] border border-white/10 p-5 space-y-4">
        <div className="flex items-center justify-between"><h3 className="text-white font-light text-lg">{custom ? "Co-mandat personnalisé" : "Proposer une collaboration"}</h3><button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button></div>
        <p className="text-sm text-zinc-400">Avec <span className="text-white">{agent.display_name}</span></p>
        {custom && <p className="text-xs text-[#C19A6B]" data-testid="collab-custom-hint">Vous fixez librement le montant ou le partage proposé au partenaire (le 2 % OFF MARKETTE reste appliqué).</p>}
        <select value={type} onChange={(e) => setType(e.target.value)} className="w-full bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white outline-none">
          <option value="partage" className="bg-[#0d0d10]">Partage d'annonces</option>
          <option value="apport" className="bg-[#0d0d10]">Apport d'affaires</option>
          <option value="comandat" className="bg-[#0d0d10]">Co-mandat</option>
          <option value="mise_en_relation" className="bg-[#0d0d10]">Simple mise en relation</option>
        </select>
        <input value={remuneration} onChange={(e) => setRemuneration(e.target.value)} placeholder={custom ? "Montant / partage proposé (ex : 60/40, 30 %, 8 000 €…)" : "Rémunération proposée (ex : 50/50, 1 % du prix…)"} className="w-full bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C19A6B]" data-testid="collab-remuneration" />
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Message (facultatif)" className="w-full bg-white/5 border border-white/15 px-3 py-2 text-sm text-white min-h-[70px] outline-none focus:border-[#C19A6B]" />
        <button onClick={send} disabled={busy} className="w-full bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 text-white font-semibold uppercase tracking-wider py-2.5" data-testid="collab-send">{busy ? "Envoi…" : "Envoyer la proposition"}</button>
      </div>
    </div>
  );
}

function MatchingCard({ a }) {
  const [why, setWhy] = useState(null);
  const [loadingWhy, setLoadingWhy] = useState(false);
  const [collab, setCollab] = useState(false);
  const [msg, setMsg] = useState("");
  const loadWhy = async () => {
    if (why) { setWhy(null); return; }
    setLoadingWhy(true);
    try { setWhy(await authApi.matchingWhy(a.member_id)); } catch (_) {}
    setLoadingWhy(false);
  };
  const compat = a.compatibility;
  const color = compat >= 80 ? "#10B981" : compat >= 60 ? "#C19A6B" : "#71717A";
  return (
    <div className="border border-white/10 bg-[#0d0d10] overflow-hidden flex flex-col" data-testid={`match-card-${a.member_id}`}>
      <div className="p-4 flex items-center gap-4">
        {a.photo ? <img src={a.photo} alt="" className="w-16 h-16 rounded-full object-cover border border-white/15" /> : <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center text-xl text-[#C19A6B]">{(a.display_name || "?")[0]}</div>}
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-medium truncate">{a.display_name}</h4>
          <p className="text-xs text-zinc-400 truncate">{a.company} {a.account_type ? `· ${a.account_type}` : ""}</p>
          <p className="text-xs text-zinc-500 truncate">{a.sector}</p>
        </div>
        <div className="text-center shrink-0">
          <div className="text-2xl font-bold" style={{ color }}>{compat}%</div>
          <div className="text-[9px] uppercase tracking-wider text-zinc-500">compat.</div>
        </div>
      </div>
      <div className="px-4 pb-2 flex gap-4 text-xs text-zinc-400">
        <span><b className="text-white">{a.listings}</b> annonces</span>
        <span><b className="text-white">{a.acquereurs}</b> acquéreurs</span>
        {(a.mine_for_theirs + a.theirs_for_mine) > 0 && <span className="text-[#10B981]">{a.mine_for_theirs + a.theirs_for_mine} ventes croisées possibles</span>}
      </div>
      {why && (
        <div className="mx-4 mb-3 p-3 bg-[#C19A6B]/5 border border-[#C19A6B]/20 text-xs text-zinc-300 space-y-1" data-testid="match-why">
          <p className="text-[#C19A6B] font-semibold">Compatibilité : {why.compatibility} %</p>
          {why.bullets.map((b, i) => <p key={i}>• {b}</p>)}
          <p className="text-white pt-1">{why.conclusion}</p>
        </div>
      )}
      <div className="mt-auto p-3 pt-0 flex gap-2">
        <motion.button onClick={loadWhy}
          animate={{ scale: [1, 1.04, 1], y: [0, -1.5, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          whileHover={{ scale: 1.06 }}
          style={{ boxShadow: "0 0 12px rgba(193,154,107,0.4)" }}
          className="flex-1 bg-[#F1F3F5] border border-[#C19A6B]/40 text-[#A88358] hover:bg-[#E9ECEF] text-[10px] font-semibold uppercase tracking-wider py-2 transition-colors" data-testid={`why-${a.member_id}`}>{loadingWhy ? "…" : why ? "Masquer" : "Pourquoi l'ajouter ?"}</motion.button>
        <button onClick={() => setCollab(true)} className="flex-1 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-xs font-semibold uppercase tracking-wider py-2 transition-colors" data-testid={`collab-${a.member_id}`}>Collaborer</button>
      </div>
      {msg && <p className="px-3 pb-3 text-xs text-[#10B981]">{msg}</p>}
      {collab && <CollabModal agent={a} onClose={() => setCollab(false)} onDone={(m) => { setCollab(false); setMsg(m); }} />}
    </div>
  );
}

function MatchingTab() {
  const [agents, setAgents] = useState(null);
  useEffect(() => { authApi.matchingAgents().then((d) => setAgents(d.agents || [])).catch(() => setAgents([])); }, []);
  return (
    <div data-testid="matching-tab">
      <div className="mb-5">
        <h3 className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#C19A6B]" /> Agents recommandés par l'IA</h3>
        <p className="text-sm text-zinc-400 mt-1">Des partenaires classés selon la valeur commerciale réelle : croisement de vos annonces avec leurs acquéreurs (et inversement), secteurs et profils complémentaires.</p>
      </div>
      {agents === null ? (
        <div className="py-16 text-center text-zinc-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : agents.length === 0 ? (
        <p className="text-sm text-zinc-500">Aucun autre professionnel dans le réseau pour l'instant.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {agents.map((a) => <MatchingCard key={a.member_id} a={a} />)}
        </div>
      )}
    </div>
  );
}


function MatchScoreBadge({ score }) {
  const color = score >= 80 ? "#10B981" : score >= 60 ? "#C19A6B" : "#71717A";
  return <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ color, background: color + "1A" }} data-testid="match-score-badge">{score}%</span>;
}

// Mini-fiche d'un conseiller partenaire (Phase 2) — ajout en 1 clic + barème de commission.
// L'identité de l'acquéreur n'est JAMAIS révélée : on présente uniquement le conseiller à contacter.
function PartnerMiniFiche({ partner, slug, acqId, onClose, onCollab, onRel }) {
  const [rel, setRel] = useState(partner.relationship || "none");
  const [pid, setPid] = useState(partner.partnership_id || null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [proposed, setProposed] = useState(false);
  const [proposing, setProposing] = useState(false);
  const comm = partner.commission || { mode: "partnership", partnership: { retro_pct: 10, site_pct: 2, partner_pct: 8 } };
  const isChoice = comm.mode === "choice";
  const proposeMatch = async () => {
    if (proposing || proposed || !slug) return;
    setProposing(true); setErr("");
    try {
      await authApi.matchProposalCreate({ listing_slug: slug, partner_member_id: partner.member_id, acq_id: acqId || "", kind: isChoice ? "comandat" : "partenariat" });
      setProposed(true);
    } catch (e) { setErr("Impossible d'envoyer la proposition. Réessayez."); }
    setProposing(false);
  };
  const add = async () => {
    setBusy(true); setErr("");
    try {
      const r = await authApi.netRequest(partner.member_id);
      const st = r.status === "accepted" ? "accepted" : "pending_out";
      setRel(st); setPid(r.id || null); onRel && onRel(st, r.id || null);
    } catch (e) { setErr("Impossible d'envoyer la demande. Réessayez."); }
    setBusy(false);
  };
  const accept = async () => {
    if (!pid) return;
    setBusy(true); setErr("");
    try { await authApi.netAccept(pid); setRel("accepted"); onRel && onRel("accepted", pid); }
    catch (e) { setErr("Impossible d'accepter la demande. Réessayez."); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3" onClick={onClose} data-testid="partner-minifiche">
      <motion.div initial={{ y: 18, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={(e) => e.stopPropagation()} className="max-w-sm w-full bg-white rounded-[12px] shadow-2xl overflow-hidden">
        <div className="relative bg-gradient-to-br from-[#0d0d10] to-[#1a1712] p-5 flex items-center gap-3">
          {partner.photo
            ? <img src={partner.photo} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-[#C19A6B]" />
            : <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center text-2xl text-[#C19A6B]">{(partner.display_name || "?")[0]}</div>}
          <div className="min-w-0">
            <p className="text-white font-medium text-base truncate" data-testid="minifiche-name">{partner.display_name}</p>
            <p className="text-xs text-zinc-300 truncate flex items-center gap-1">{[partner.company, partner.city].filter(Boolean).join(" · ")}</p>
            {partner.account_type && <p className="text-[11px] text-[#C19A6B] truncate">{partner.account_type}</p>}
          </div>
          <button onClick={onClose} className="absolute top-3 right-3 text-zinc-400 hover:text-white transition-colors" data-testid="minifiche-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-2 text-sm text-[#111827]">
            <Users className="w-4 h-4 text-[#C19A6B] shrink-0" />
            <span data-testid="minifiche-buyercount"><b>{partner.buyer_count}</b> acquéreur{partner.buyer_count > 1 ? "s" : ""} potentiel{partner.buyer_count > 1 ? "s" : ""} sur ce bien</span>
          </div>
          <div className="rounded-lg border border-[#E5E7EB] divide-y divide-[#F1F3F5] overflow-hidden" data-testid="minifiche-commission">
            <div className="p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#A88358] flex items-center gap-1.5"><Handshake className="w-3.5 h-3.5" /> Partenariat réseau</p>
              <p className="text-xs text-zinc-600 mt-1 leading-relaxed">Rétro-commission <b>{comm.partnership.retro_pct} %</b> — dont <b>{comm.partnership.site_pct} % OFF MARKETTE</b>, soit <b>{comm.partnership.partner_pct} % net</b> pour le partenaire.</p>
            </div>
            {isChoice && (
              <div className="p-3 bg-[#C19A6B]/5">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[#A88358] flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Co-mandat (dès 3 acquéreurs)</p>
                <p className="text-xs text-zinc-600 mt-1 leading-relaxed">Partage <b>{comm.comandat.owner_pct} % / {comm.comandat.buyer_side_pct} %</b> — le partenaire touche <b>{comm.comandat.partner_pct} %</b> (+ {comm.comandat.site_pct} % OFF MARKETTE).</p>
              </div>
            )}
          </div>
          <div className="space-y-2">
            {slug && (proposed ? (
              <div className="flex items-center justify-center gap-2 text-sm font-semibold text-[#10B981] bg-[#10B981]/10 rounded-lg py-2.5" data-testid="minifiche-proposed"><BadgeCheck className="w-4 h-4" /> Match proposé au partenaire</div>
            ) : (
              <button onClick={proposeMatch} disabled={proposing} className="w-full inline-flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 text-white text-sm font-semibold uppercase tracking-wider rounded-lg py-2.5 transition-colors" data-testid="minifiche-propose-match">{proposing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Proposer le match</button>
            ))}
            {rel === "accepted" ? (
              <div className="flex items-center justify-center gap-2 text-sm font-semibold text-[#10B981] bg-[#10B981]/10 rounded-lg py-2.5" data-testid="minifiche-already"><BadgeCheck className="w-4 h-4" /> Déjà partenaire</div>
            ) : rel === "pending_out" ? (
              <div className="flex items-center justify-center gap-2 text-sm font-semibold text-[#A88358] bg-[#C19A6B]/10 rounded-lg py-2.5" data-testid="minifiche-pending">Demande envoyée</div>
            ) : rel === "pending_in" ? (
              <button onClick={accept} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#111827] hover:bg-black disabled:opacity-50 text-white text-sm font-semibold rounded-lg py-2.5 transition-colors" data-testid="minifiche-accept">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Accepter la demande</button>
            ) : (
              <button onClick={add} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#111827] hover:bg-black disabled:opacity-50 text-white text-sm font-semibold rounded-lg py-2.5 transition-colors" data-testid="minifiche-add">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Ajouter en 1 clic</button>
            )}
            {isChoice && (
              <button onClick={() => { onClose(); onCollab && onCollab({ ...partner, custom: true }); }} className="w-full inline-flex items-center justify-center gap-2 border border-[#111827]/25 text-[#111827] hover:bg-[#111827]/5 text-sm font-semibold rounded-lg py-2.5 transition-colors" data-testid="minifiche-comandat-custom"><Pencil className="w-4 h-4" /> Co-mandat personnalisé</button>
            )}
          </div>
          {err && <p className="text-xs text-red-500 text-center" data-testid="minifiche-error">{err}</p>}
          <p className="text-[11px] text-zinc-400 text-center leading-relaxed">« Proposer le match » envoie au partenaire une fiche (photo + home staging) et crée automatiquement le co-mandat à l'acceptation. L'identité de l'acquéreur reste protégée.</p>
        </div>
      </motion.div>
    </div>
  );
}

// Une ligne « match » (acquéreur pour un bien) avec explication IA + mini-fiche partenaire cliquable (Phase 2).
function BuyerMatchRow({ slug, match, onCollab }) {
  const [explain, setExplain] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fiche, setFiche] = useState(false);
  const [rel, setRel] = useState(match.partner?.relationship || "none");
  const isChoice = match.partner?.commission?.mode === "choice";
  const toggle = async () => {
    if (explain) { setExplain(null); return; }
    setLoading(true);
    try { const d = await authApi.matchingExplain(slug, match.acq_id); setExplain(d.explanation || "—"); }
    catch (_) { setExplain("Explication indisponible pour le moment."); }
    setLoading(false);
  };
  return (
    <div className="border border-[#E5E7EB] bg-white rounded-lg p-3" data-testid="buyer-match-row">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {match.mine
            ? <span className="w-8 h-8 rounded-full bg-[#C19A6B]/15 flex items-center justify-center shrink-0"><Contact className="w-4 h-4 text-[#C19A6B]" /></span>
            : <span className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center shrink-0" title="Client d'un partenaire (identité protégée)"><User className="w-4 h-4 text-zinc-400" /></span>}
          <div className="min-w-0">
            <p className="text-sm font-medium text-[#111827] truncate">{match.label}</p>
            <p className="text-[11px] text-zinc-500 truncate">
              {match.mine ? (match.budget ? `Budget ${match.budget}` : "Mon acquéreur")
                : match.partner ? <>via <button onClick={() => setFiche(true)} className="font-semibold text-[#A88358] hover:text-[#C19A6B] underline decoration-dotted underline-offset-2 transition-colors" data-testid="match-partner-link">{match.partner.short || match.partner.display_name}</button> <span className="text-zinc-400">(réseau)</span>{rel === "accepted" && <span className="ml-1 text-[#10B981]">· partenaire</span>}{rel === "pending_out" && <span className="ml-1 text-[#A88358]">· demande envoyée</span>}</>
                  : <>via <span className="font-semibold text-[#A88358]">votre Agent IA</span></>}
            </p>
          </div>
        </div>
        <MatchScoreBadge score={match.score} />
      </div>
      {(match.reasons || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {match.reasons.map((r, i) => <span key={i} className="text-[10px] px-2 py-0.5 bg-[#F1F3F5] text-zinc-600 rounded-full">{r}</span>)}
        </div>
      )}
      {explain && <p className="mt-2 text-xs text-zinc-700 bg-[#C19A6B]/5 border border-[#C19A6B]/20 rounded-md p-2 italic" data-testid="match-ai-explain">{explain}</p>}
      <div className="flex flex-wrap gap-2 mt-2.5">
        {match.acq_id && (
          <button onClick={toggle} disabled={loading} className="text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1.5 rounded border border-[#C19A6B]/40 text-[#A88358] hover:bg-[#C19A6B]/10 transition-colors inline-flex items-center gap-1" data-testid="match-explain-btn">
            <Sparkles className="w-3 h-3" /> {loading ? "…" : explain ? "Masquer" : "Explication IA"}
          </button>
        )}
        {!match.mine && match.partner && (
          <button onClick={() => setFiche(true)} className="text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1.5 rounded bg-[#C19A6B] text-white hover:bg-[#d1af84] transition-colors inline-flex items-center gap-1" data-testid="match-partner-btn">
            <Send className="w-3 h-3" /> {rel === "accepted" ? "Proposer le match" : "Proposer / Ajouter"}
          </button>
        )}
      </div>
      {fiche && match.partner && (
        <PartnerMiniFiche partner={{ ...match.partner, relationship: rel }} slug={slug} acqId={match.acq_id} onClose={() => setFiche(false)} onCollab={onCollab} onRel={(st) => setRel(st)} />
      )}
    </div>
  );
}

function MatchingBienAcqTab() {
  const [data, setData] = useState(null);
  const [dir, setDir] = useState("biens"); // biens | acquereurs
  const [collab, setCollab] = useState(null);
  const [msg, setMsg] = useState("");
  const [openKey, setOpenKey] = useState(null);
  const [prospect, setProspect] = useState(null); // {acqId, loading, subject, html, sending, sent}
  const openProspect = async (acqId, label) => {
    setProspect({ acqId, label, loading: true });
    try {
      const d = await authApi.prospectionPreview(acqId);
      if (d.has_content === false) setProspect({ acqId, label, empty: true });
      else setProspect({ acqId, label, subject: d.subject, html: d.html, coup: d.coup_de_coeur });
    } catch (_) { setProspect({ acqId, label, error: true }); }
  };
  const sendProspect = async () => {
    setProspect((p) => ({ ...p, sending: true }));
    try { const r = await authApi.prospectionSend(prospect.acqId); setProspect((p) => ({ ...p, sending: false, sent: r.sent, queued: r.queued, to: r.to })); }
    catch (e) { setProspect((p) => ({ ...p, sending: false, sendError: (e && e.message) || "Envoi impossible" })); }
  };
  useEffect(() => { authApi.matchingOverview().then(setData).catch(() => setData({ listings: [], buyers: [], totals: {} })); }, []);

  const tot = (data && data.totals) || {};
  return (
    <div data-testid="matching-bien-tab">
      <div className="mb-5">
        <h3 className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] flex items-center gap-2"><Sparkles className="w-5 h-5" /> Matching IA — biens ⇄ acquéreurs</h3>
        <p className="text-sm text-zinc-400 mt-1">Le moteur croise en continu vos biens et vos acquéreurs avec tout le réseau OFF MARKETTE. Les clients de vos partenaires restent <b>anonymes</b> : cliquez sur le conseiller pour l'<b>ajouter en 1 clic</b> ou proposer un co-mandat.</p>
      </div>

      <div className="flex gap-2 mb-4" data-testid="matching-dir-switch">
        <button onClick={() => setDir("biens")} className={`px-4 py-2 text-sm font-semibold rounded-full transition-colors inline-flex items-center gap-2 ${dir === "biens" ? "bg-[#C19A6B] text-white" : "bg-[#F1F3F5] text-zinc-600 hover:bg-[#E9ECEF]"}`} data-testid="dir-biens">
          <Home className="w-4 h-4" /> Mes biens → acquéreurs {tot.listing_matches ? `(${tot.listing_matches})` : ""}
        </button>
        <button onClick={() => setDir("acquereurs")} className={`px-4 py-2 text-sm font-semibold rounded-full transition-colors inline-flex items-center gap-2 ${dir === "acquereurs" ? "bg-[#C19A6B] text-white" : "bg-[#F1F3F5] text-zinc-600 hover:bg-[#E9ECEF]"}`} data-testid="dir-acquereurs">
          <Contact className="w-4 h-4" /> Mes acquéreurs → biens {tot.buyer_matches ? `(${tot.buyer_matches})` : ""}
        </button>
      </div>

      {msg && <p className="mb-3 text-sm text-[#10B981]" data-testid="matching-msg">{msg}</p>}

      {data === null ? (
        <div className="py-16 text-center text-zinc-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : dir === "biens" ? (
        (data.listings || []).length === 0 ? (
          <p className="text-sm text-zinc-500" data-testid="matching-empty-biens">Aucun acquéreur compatible pour vos biens pour l'instant. Ajoutez des annonces ou attendez que le réseau s'enrichisse.</p>
        ) : (
          <div className="space-y-3">
            {data.listings.map((l) => {
              const key = `l-${l.slug}`; const open = openKey === key;
              return (
                <div key={l.slug} className="border border-[#E5E7EB] rounded-xl bg-[#FBFBFC] overflow-hidden" data-testid={`matching-listing-${l.slug}`}>
                  <button onClick={() => setOpenKey(open ? null : key)} className="w-full flex items-center gap-3 p-3 text-left hover:bg-white transition-colors">
                    {l.cover ? <img src={l.cover} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" /> : <span className="w-14 h-14 rounded-lg bg-zinc-100 flex items-center justify-center shrink-0"><Home className="w-5 h-5 text-zinc-400" /></span>}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#111827] truncate">{l.title}</p>
                      <p className="text-[11px] text-zinc-500 truncate">{l.city} · {fmtPrice(l.price)}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0" data-testid={`matching-source-${l.slug}`}>
                      {(l.by_source?.reseau > 0) && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#C19A6B]/15 text-[#A88358]" title="Acquéreurs via votre réseau">{l.by_source.reseau} via réseau</span>}
                      {(l.by_source?.agent_ia > 0) && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#111827]/[0.06] text-[#111827]" title="Acquéreurs via votre Agent IA">{l.by_source.agent_ia} via Agent IA</span>}
                      {(l.by_source?.mine > 0) && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981]" title="Vos propres acquéreurs">{l.by_source.mine} à moi</span>}
                      {!l.by_source && <span className="text-xs font-bold text-[#C19A6B]">{l.match_count} acq.</span>}
                    </div>
                    <ChevronDown className={`w-4 h-4 text-zinc-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <div className="p-3 pt-0 space-y-2">
                      {l.matches.map((mm, i) => <BuyerMatchRow key={i} slug={l.slug} match={mm} onCollab={(p) => setCollab(p)} />)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      ) : (
        (data.buyers || []).length === 0 ? (
          <p className="text-sm text-zinc-500" data-testid="matching-empty-acq">Aucun bien du réseau ne correspond à vos acquéreurs pour l'instant. Enregistrez des acquéreurs pour lancer le matching.</p>
        ) : (
          <div className="space-y-3">
            {data.buyers.map((b) => {
              const key = `b-${b.acq_id}`; const open = openKey === key;
              return (
                <div key={b.acq_id} className="border border-[#E5E7EB] rounded-xl bg-[#FBFBFC] overflow-hidden" data-testid={`matching-buyer-${b.acq_id}`}>
                  <button onClick={() => setOpenKey(open ? null : key)} className="w-full flex items-center gap-3 p-3 text-left hover:bg-white transition-colors">
                    <span className="w-14 h-14 rounded-lg bg-[#C19A6B]/15 flex items-center justify-center shrink-0"><Contact className="w-5 h-5 text-[#C19A6B]" /></span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#111827] truncate">{b.label}</p>
                      <p className="text-[11px] text-zinc-500 truncate">{b.budget ? `Budget ${b.budget}` : ""}{(b.secteurs || []).length ? ` · ${b.secteurs.join(", ")}` : ""}</p>
                    </div>
                    <span className="text-xs font-bold text-[#C19A6B] shrink-0">{b.match_count} bien{b.match_count > 1 ? "s" : ""}</span>
                    <ChevronDown className={`w-4 h-4 text-zinc-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <div className="p-3 pt-0">
                      <div className="grid sm:grid-cols-2 gap-2">
                      {b.matches.map((l) => (
                        <a key={l.slug} href={`/bien/${l.slug}`} target="_blank" rel="noopener noreferrer" className="border border-[#E5E7EB] bg-white rounded-lg p-3 hover:border-[#C19A6B] transition-colors block" data-testid={`buyer-listing-${l.slug}`}>
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-medium text-[#111827] truncate">{l.title}</p>
                            <MatchScoreBadge score={l.match_score} />
                          </div>
                          <p className="text-[11px] text-zinc-500 mt-0.5">{l.city} · {fmtPrice(l.price)} {l.own ? "· votre bien" : l.owner ? `· ${l.owner.name || l.owner.display_name || ""}` : ""}</p>
                          {(l.match_reasons || []).length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {l.match_reasons.slice(0, 4).map((r, i) => <span key={i} className="text-[10px] px-2 py-0.5 bg-[#F1F3F5] text-zinc-600 rounded-full">{r}</span>)}
                            </div>
                          )}
                        </a>
                      ))}
                      </div>
                      <button onClick={() => openProspect(b.acq_id, b.label)} data-testid={`prospection-btn-${b.acq_id}`}
                        className="mt-3 inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-xs font-semibold uppercase tracking-wider rounded-lg px-3.5 py-2 transition-colors">
                        <Mail className="w-3.5 h-3.5" /> E-mail de prospection IA
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {collab && <CollabModal agent={collab} onClose={() => setCollab(null)} onDone={(m) => { setCollab(null); setMsg(m); }} />}

      <AnimatePresence>
        {prospect && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setProspect(null)} data-testid="prospection-modal">
            <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
              onClick={(e) => e.stopPropagation()} className="w-full max-w-xl bg-white rounded-[10px] shadow-2xl overflow-hidden flex flex-col" style={{ maxHeight: "90vh" }}>
              <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between gap-2 shrink-0">
                <p className="text-sm font-semibold text-[#111827] flex items-center gap-2"><Mail className="w-4 h-4 text-[#C19A6B]" /> E-mail de prospection IA — {prospect.label}</p>
                <button onClick={() => setProspect(null)} className="text-zinc-400 hover:text-zinc-700 transition-colors" data-testid="prospection-close"><X className="w-5 h-5" /></button>
              </div>
              {prospect.loading ? (
                <div className="py-16 text-center text-zinc-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /><p className="text-xs mt-2">L'Agent IA rédige l'e-mail…</p></div>
              ) : prospect.empty ? (
                <div className="p-8 text-center text-sm text-zinc-500" data-testid="prospection-empty">Aucun bien compatible à proposer à cet acquéreur pour l'instant.</div>
              ) : prospect.error ? (
                <div className="p-8 text-center text-sm text-red-500">Impossible de générer l'aperçu. Réessayez.</div>
              ) : (
                <>
                  <p className="px-5 pt-3 text-[11px] text-zinc-500 shrink-0">Objet : <span className="text-[#111827] font-medium">{prospect.subject}</span></p>
                  <div className="p-4 overflow-auto flex-1">
                    <iframe title="prospection" srcDoc={prospect.html} className="w-full rounded-md border border-zinc-100" style={{ height: "58vh" }} data-testid="prospection-iframe" />
                  </div>
                  <div className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between gap-2 shrink-0">
                    {prospect.queued ? (
                      <span className="text-sm text-[#C19A6B] font-medium" data-testid="prospection-queued">Ajouté à la file de validation. Validez-le dans « Agent IA — Emails envoyés ».</span>
                    ) : prospect.sent ? (
                      <span className="text-sm text-[#10B981] font-medium" data-testid="prospection-sent">E-mail envoyé à {prospect.to}.</span>
                    ) : prospect.sendError ? (
                      <span className="text-sm text-amber-600" data-testid="prospection-senderr">Envoi indisponible (config e-mail). L'aperçu reste valable.</span>
                    ) : <span className="text-[11px] text-zinc-400">Vérifiez l'aperçu avant l'envoi.</span>}
                    <button onClick={sendProspect} disabled={prospect.sending || prospect.sent || prospect.queued}
                      className="inline-flex items-center gap-2 bg-[#111827] hover:bg-black disabled:opacity-50 text-white text-sm font-semibold rounded-lg px-4 py-2 transition-colors" data-testid="prospection-send">
                      {prospect.sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</> : prospect.queued ? "En file d'attente" : prospect.sent ? "Envoyé" : <>Envoyer l'e-mail</>}
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


function AgentFeed() {
  const [feed, setFeed] = useState(null);
  useEffect(() => {
    const load = () => authApi.agentFeed().then((d) => setFeed(d.items || [])).catch(() => setFeed([]));
    load(); const iv = setInterval(load, 20000); return () => clearInterval(iv);
  }, []);
  const icon = (t) => t === "email" ? Mail : t === "open" ? Eye : t === "click" ? MousePointerClick : t === "match" ? Sparkles : Wand2;
  const ago = (iso) => { try { const s = Math.floor((Date.now() - new Date(iso).getTime()) / 60000); return s < 1 ? "à l'instant" : s < 60 ? `il y a ${s} min` : s < 1440 ? `il y a ${Math.floor(s / 60)} h` : `il y a ${Math.floor(s / 1440)} j`; } catch (_) { return ""; } };
  if (!feed || feed.length === 0) return null;
  return (
    <div className="mb-5 border border-[#C19A6B]/30 bg-gradient-to-br from-[#0d0d10] to-[#1a1712] rounded-xl p-4" data-testid="agent-feed">
      <p className="text-[11px] uppercase tracking-widest text-[#C19A6B] font-semibold flex items-center gap-2 mb-3">
        <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C19A6B] opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-[#C19A6B]" /></span>
        Votre agent IA travaille en ce moment
      </p>
      <div className="space-y-2 max-h-44 overflow-auto">
        {feed.map((f, i) => { const Ic = icon(f.type); return (
          <div key={i} className="flex items-center gap-2.5 text-[13px] text-zinc-300" data-testid="agent-feed-item">
            <span className="w-6 h-6 rounded-full bg-[#C19A6B]/15 flex items-center justify-center shrink-0"><Ic className="w-3 h-3 text-[#C19A6B]" /></span>
            <span className="flex-1 min-w-0 truncate">{f.text}</span>
            <span className="text-[10px] text-zinc-500 shrink-0">{ago(f.at)}</span>
          </div>
        ); })}
      </div>
    </div>
  );
}

const CHAN_SHORT = { whatsapp: "WhatsApp", email: "Email", facebook: "Facebook", instagram: "Instagram" };

const EMAIL_STAGE_META = {
  none: { label: "Aucun e-mail", cls: "bg-zinc-100 text-zinc-500", dot: "○" },
  pending: { label: "Prêt à partir", cls: "bg-amber-50 text-amber-600", dot: "🟡" },
  sent: { label: "Envoyé", cls: "bg-blue-50 text-blue-600", dot: "🟢" },
  opened: { label: "Ouvert", cls: "bg-green-50 text-green-600", dot: "👁" },
  clicked: { label: "Cliqué", cls: "bg-[#C19A6B]/15 text-[#A88358]", dot: "🔗" },
  rejected: { label: "Rejeté", cls: "bg-zinc-100 text-zinc-400", dot: "○" },
  failed: { label: "Échec", cls: "bg-red-50 text-red-500", dot: "!" },
};

function PScore({ score }) {
  const c = score >= 85 ? "#10B981" : score >= 60 ? "#C19A6B" : "#71717A";
  return <span className="inline-flex items-center gap-1 font-bold text-sm" style={{ color: c }}>{score >= 85 && <Flame className="w-3.5 h-3.5" />}{score}%</span>;
}

// Panneau latéral prospect (desktop) / plein écran (mobile) — mini-fiche actionnable.
function ProspectPanel({ acqId, onClose, onChanged, autoAction }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const [emailView, setEmailView] = useState(null); // {subject, html}
  const [showPartner, setShowPartner] = useState(false);
  const [showVisite, setShowVisite] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showWa, setShowWa] = useState(false);
  const [toast, setToast] = useState("");
  const autoFired = useRef(false);
  const load = useCallback(() => { authApi.prospectFiche(acqId).then(setD).catch(() => setD({ error: true })); }, [acqId]);
  useEffect(() => { setD(null); autoFired.current = false; load(); }, [load]);
  useEffect(() => {
    if (d && !d.error && autoAction === "visite" && !autoFired.current && !d.partner && d.best_match) {
      autoFired.current = true; setShowVisite(true);
    }
  }, [d, autoAction]);
  const fmt = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch (_) { return "—"; } };
  const tlIcon = (t) => t === "detection" ? Search : t === "analysis" ? Wand2 : t === "email" ? Mail : t === "open" ? Eye : t === "click" ? MousePointerClick : Clock;
  const openBien = (slug) => { if (slug) window.open(`/bien/${slug}`, "_blank"); };
  const viewEmail = async (id) => { if (!id) return; try { const e = await authApi.agentEmailDetail(id); setEmailView({ subject: e.subject, html: e.html }); } catch (_) {} };
  const setStatus = async (st) => { try { const r = await authApi.prospectSetStatus(acqId, st); setD((p) => ({ ...p, status: st, status_label: r.status_label })); onChanged && onChanged(); } catch (_) {} };
  const primary = async () => {
    if (busy || !d) return;
    const act = (d.next_action || {}).action;
    if (act === "propose_visite") { setShowVisite(true); return; }
    if (act === "validate_email" && d.pending_email) {
      setBusy("x"); try { await authApi.agentApprove(d.pending_email.id); setToast("E-mail validé et envoyé."); load(); onChanged && onChanged(); } catch (_) { setToast("Envoi indisponible (config e-mail)."); } setBusy(""); return;
    }
    if (["prepare_email", "relance"].includes(act)) {
      setBusy("x"); try { const r = await authApi.prospectionSend(acqId); setToast(r.queued ? "E-mail préparé — en attente de validation." : r.sent ? "E-mail envoyé." : "E-mail préparé."); load(); onChanged && onChanged(); } catch (e) { setToast((e && e.message) || "Action indisponible."); } setBusy(""); return;
    }
    setToast("Qualifiez le prospect ci-dessous.");
  };
  const primaryLabel = { validate_email: "Valider & envoyer l'e-mail", prepare_email: "Préparer l'e-mail IA", relance: "Envoyer une relance IA", propose_visite: "Proposer une visite", propose_match: "Proposer le match", wait: "En attente d'ouverture", qualify: "Qualifier le prospect" }[(d && d.next_action || {}).action] || "Agir";
  const stageMeta = EMAIL_STAGE_META[(d && d.email_stage) || "none"] || EMAIL_STAGE_META.none;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[75] bg-black/40 backdrop-blur-sm flex justify-end" onClick={onClose} data-testid="prospect-panel">
      <motion.div initial={{ x: 60, opacity: 0.6 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 60, opacity: 0 }} transition={{ type: "spring", stiffness: 260, damping: 30 }} onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-md bg-[#FAFAFA] h-full shadow-2xl flex flex-col" data-testid="prospect-panel-body">
        {/* Header */}
        <div className="px-5 py-4 bg-[#111827] bg-gradient-to-br from-[#0d0d10] to-[#1a1712] flex items-center gap-3 shrink-0">
          <div className="w-12 h-12 rounded-full bg-[#C19A6B]/20 flex items-center justify-center text-[#C19A6B] font-bold shrink-0">{d ? d.initials : "…"}</div>
          <div className="min-w-0 flex-1">
            <p className="text-white font-semibold truncate" data-testid="prospect-panel-name">{d ? d.name : "Chargement…"}</p>
            {d && d.partner ? <p className="text-[11px] text-zinc-300 truncate">via {d.partner.short} <span className="text-zinc-500">(réseau)</span></p>
              : d && d.attribution ? <p className="text-[11px] text-zinc-300 truncate">via {d.attribution} <span className="text-zinc-500">(réseau)</span></p>
              : d && <p className="text-[11px] text-zinc-400 truncate">{d.status_label}</p>}
          </div>
          {d && d.best_match && <PScore score={d.best_match.score} />}
          <button onClick={onClose} className="text-zinc-400 hover:text-white transition-colors ml-1" data-testid="prospect-panel-close"><X className="w-5 h-5" /></button>
        </div>

        {!d ? <div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C19A6B]" /></div> : d.error ? <div className="flex-1 flex items-center justify-center text-sm text-zinc-500">Fiche indisponible.</div> : (
          <div className="flex-1 overflow-auto p-4 space-y-4 pb-24 sm:pb-4">
            {toast && <div className="text-xs font-medium text-[#A88358] bg-[#C19A6B]/10 rounded-lg px-3 py-2" data-testid="prospect-panel-toast">{toast}</div>}

            {/* Prochaine action */}
            {d.next_action && !d.partner && (
              <div className="rounded-xl border border-[#C19A6B]/30 bg-[#C19A6B]/5 p-3" data-testid="prospect-next-action">
                <p className="text-[10px] uppercase tracking-wider text-[#A88358] font-semibold mb-1">Prochaine action conseillée</p>
                <p className="text-sm text-[#111827] mb-2">{d.next_action.label}{d.next_action.channel ? <span className="text-[#A88358] font-semibold"> · via {CHAN_SHORT[d.next_action.channel] || d.next_action.channel}</span> : ""}</p>
                {["validate_email", "prepare_email", "relance", "propose_visite"].includes(d.next_action.action) && (
                  <button onClick={primary} disabled={!!busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 text-white text-sm font-semibold rounded-lg py-2 transition-colors" data-testid="prospect-next-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {primaryLabel}</button>
                )}
              </div>
            )}

            {/* Contact (mes prospects) */}
            {!d.partner && d.contact && (
              <div className="bg-white rounded-xl border border-zinc-100 p-3" data-testid="prospect-contact">
                <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2 flex items-center gap-1.5"><Contact className="w-3.5 h-3.5" /> Contact {d.contact.registered && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-600">Inscrit</span>}</p>
                <div className="space-y-1.5 text-sm">
                  <p className="flex items-center gap-2 text-[#111827]"><Phone className="w-3.5 h-3.5 text-[#C19A6B]" /> {d.contact.phone_display || "—"} {d.contact.visible && d.contact.phone && <a href={`tel:${d.contact.phone}`} className="ml-auto text-[11px] text-[#C19A6B] hover:underline">Appeler</a>}</p>
                  <p className="flex items-center gap-2 text-[#111827]"><Mail className="w-3.5 h-3.5 text-[#C19A6B]" /> {d.contact.email_display || "—"} {d.contact.visible && d.contact.email && <a href={`mailto:${d.contact.email}`} className="ml-auto text-[11px] text-[#C19A6B] hover:underline">Écrire</a>}</p>
                </div>
                {!d.contact.visible && <p className="text-[10px] text-zinc-400 mt-1.5">Coordonnées masquées (RGPD) — non collectées avec consentement.</p>}
              </div>
            )}

            {/* Canaux de contact (Radar multicanal) */}
            {!d.partner && (d.contact_channels || []).length > 0 && (
              <div className="bg-white rounded-xl border border-zinc-100 p-3" data-testid="prospect-channels">
                <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2 flex items-center gap-1.5"><Share2 className="w-3.5 h-3.5" /> Canaux de contact</p>
                <div className="flex flex-wrap gap-1.5">
                  {d.contact_channels.map((c) => { const ok = c.status === "available" || c.status === "authorized"; return (
                    <span key={c.key} data-testid={`prospect-channel-${c.key}`} className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border ${ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${ok ? "bg-emerald-500" : "bg-amber-500"}`} /> {CHAN_SHORT[c.key] || c.label} · {c.note}
                    </span>
                  ); })}
                </div>
                {d.recommended_channel && <p className="text-[11px] text-zinc-500 mt-2">Canal conseillé : <b className="text-[#A88358]">{CHAN_SHORT[d.recommended_channel] || d.recommended_channel}</b></p>}
                {d.best_match && <button onClick={() => setShowWa(true)} data-testid="prospect-whatsapp-btn" className="mt-2 w-full inline-flex items-center justify-center gap-2 border border-[#C19A6B] bg-[#C19A6B]/10 text-[#A88358] hover:bg-[#C19A6B]/20 text-sm font-semibold rounded-lg py-2 transition-colors"><MessageCircle className="w-4 h-4" /> Préparer le message WhatsApp</button>}
              </div>
            )}

            {/* Recherche */}
            <div className="bg-white rounded-xl border border-zinc-100 p-3" data-testid="prospect-search">
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1.5">Ce que l'IA a détecté</p>
              <p className="text-sm text-zinc-700 leading-relaxed">{d.search.summary || "—"}</p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {d.search.budget && <span className="text-[10px] px-2 py-0.5 bg-[#F1F3F5] rounded-full">Budget {d.search.budget}</span>}
                {(d.search.secteurs || []).map((s, i) => <span key={i} className="text-[10px] px-2 py-0.5 bg-[#F1F3F5] rounded-full">{s}</span>)}
                {(d.search.type_bien || []).map((s, i) => <span key={`t${i}`} className="text-[10px] px-2 py-0.5 bg-[#F1F3F5] rounded-full">{s}</span>)}
              </div>
            </div>

            {/* Source */}
            <div className="bg-white rounded-xl border border-zinc-100 p-3 flex items-center justify-between gap-2" data-testid="prospect-source">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-zinc-400">Source · {fmt(d.detected_at)}</p>
                <p className="text-sm font-medium text-[#111827] truncate">{d.source_label}</p>
              </div>
              {d.source_url ? <a href={d.source_url} target="_blank" rel="noopener noreferrer" className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-[#C19A6B] hover:underline" data-testid="prospect-source-link"><ExternalLink className="w-3.5 h-3.5" /> Voir la publication</a> : <span className="text-[11px] text-zinc-300">—</span>}
            </div>

            {/* Biens matchés */}
            {(d.matches || []).length > 0 && (
              <div data-testid="prospect-matches">
                <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2">{d.matches.length > 1 ? `${d.matches.length} biens compatibles` : "Bien proposé"}</p>
                <div className="space-y-2">
                  {d.matches.map((mm, i) => (
                    <div key={i} className={`rounded-xl border p-2.5 flex items-center gap-3 ${i === 0 ? "border-[#C19A6B]/40 bg-[#C19A6B]/5" : "border-zinc-100 bg-white"}`} data-testid={`prospect-match-${i}`}>
                      {mm.cover ? <img src={mm.cover} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" /> : <div className="w-14 h-14 rounded-lg bg-zinc-100 shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-[#111827] truncate">{mm.title}</p>
                        <p className="text-[11px] text-zinc-500 truncate">{mm.city} {mm.price ? `· ${fmtPrice(mm.price)}` : ""}</p>
                      </div>
                      <div className="text-right shrink-0"><PScore score={mm.score} /><button onClick={() => openBien(mm.slug)} className="block text-[10px] text-[#C19A6B] hover:underline mt-0.5" data-testid={`prospect-view-bien-${i}`}>Voir le bien</button></div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Partenaire */}
            {d.partner && (
              <div className="bg-white rounded-xl border border-zinc-100 p-3 space-y-2" data-testid="prospect-partner">
                <p className="text-[10px] uppercase tracking-wider text-zinc-400">Prospect d'un partenaire</p>
                <p className="text-sm text-[#111827]">Identité protégée — <b>{d.partner.short}</b>{d.partner.company ? ` · ${d.partner.company}` : ""}</p>
                <button onClick={() => setShowPartner(true)} className="w-full inline-flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-sm font-semibold rounded-lg py-2 transition-colors" data-testid="prospect-partner-options"><Handshake className="w-4 h-4" /> Contacter / Ajouter / Proposer le match</button>
              </div>
            )}

            {/* E-mail associé */}
            {!d.partner && (d.latest_email || d.pending_email) && (
              <div className="bg-white rounded-xl border border-zinc-100 p-3" data-testid="prospect-email-block">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-400">E-mail associé</p>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${stageMeta.cls}`}>{stageMeta.dot} {stageMeta.label}</span>
                </div>
                <p className="text-sm text-[#111827] truncate">{(d.latest_email || d.pending_email).subject}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <button onClick={() => viewEmail((d.pending_email || d.latest_email).id)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#C19A6B] hover:underline" data-testid="prospect-view-email"><Eye className="w-3.5 h-3.5" /> Voir l'e-mail</button>
                  {d.pending_email && <button onClick={primary} disabled={!!busy} className="inline-flex items-center gap-1 bg-[#10B981] hover:bg-[#0e9f74] text-white text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors" data-testid="prospect-validate-email"><Check className="w-3.5 h-3.5" /> Valider &amp; envoyer</button>}
                </div>
              </div>
            )}

            {/* Visite : proposer des créneaux ou planifier dans l'agenda */}
            {!d.partner && d.best_match && d.contact && (
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setShowVisite(true)} className="inline-flex items-center justify-center gap-1.5 border border-[#C19A6B] bg-[#C19A6B]/10 text-[#A88358] hover:bg-[#C19A6B]/20 text-[13px] font-semibold rounded-lg py-2.5 transition-colors" data-testid="prospect-propose-visite-btn"><CalendarClock className="w-4 h-4" /> Proposer</button>
                <button onClick={() => setShowSchedule(true)} className="inline-flex items-center justify-center gap-1.5 bg-[#111827] hover:bg-black text-white text-[13px] font-semibold rounded-lg py-2.5 transition-colors" data-testid="prospect-schedule-visite-btn"><CalendarClock className="w-4 h-4" /> Planifier</button>
              </div>
            )}

            {/* Statut commercial */}
            {!d.partner && (d.statuses || []).length > 0 && (
              <div data-testid="prospect-status-picker">
                <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2">Statut commercial</p>
                <div className="flex flex-wrap gap-1.5">
                  {d.statuses.map((s) => (
                    <button key={s.key} onClick={() => setStatus(s.key)} className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${d.status === s.key ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-zinc-600 border-zinc-200 hover:border-[#C19A6B]"}`} data-testid={`prospect-status-${s.key}`}>{s.label}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Timeline */}
            {(d.timeline || []).length > 0 && (
              <div data-testid="prospect-panel-timeline">
                <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2">Historique</p>
                <div className="relative pl-6">
                  <div className="absolute left-[9px] top-1 bottom-1 w-px bg-[#E5E7EB]" />
                  {d.timeline.map((t, i) => { const Ic = tlIcon(t.type); return (
                    <div key={i} className="relative mb-3">
                      <span className="absolute -left-6 top-0 w-[19px] h-[19px] rounded-full bg-white ring-2 ring-[#C19A6B]/40 flex items-center justify-center"><Ic className="w-3 h-3 text-[#C19A6B]" /></span>
                      <p className="text-[13px] text-[#111827]">{t.text}</p>
                      <p className="text-[10px] text-zinc-400">{fmt(t.at)}</p>
                    </div>
                  ); })}
                </div>
              </div>
            )}

            {/* Vue super admin */}
            {d.full && (
              <div className="bg-[#111827] rounded-xl p-3 space-y-1 text-xs text-zinc-300" data-testid="prospect-super">
                <p className="text-[10px] uppercase tracking-wider text-[#C19A6B] font-semibold flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Vue super admin (complète)</p>
                <p>Nom : <span className="text-white">{d.full.name || "—"}</span></p>
                <p>E-mail : <span className="text-white">{d.full.email || "—"}</span></p>
                <p>Téléphone : <span className="text-white">{d.full.phone || "—"}</span></p>
                {d.full.advisor && d.full.advisor.display_name && <p>Conseiller : <span className="text-white">{d.full.advisor.display_name}</span></p>}
              </div>
            )}
          </div>
        )}

        {/* Barre d'actions fixe mobile */}
        {d && !d.error && (
          <div className="sm:hidden shrink-0 grid grid-cols-4 gap-1 border-t border-zinc-200 bg-white p-2" data-testid="prospect-mobile-bar">
            <button onClick={() => (d.pending_email || d.latest_email) && viewEmail((d.pending_email || d.latest_email).id)} className="flex flex-col items-center gap-0.5 py-1 text-[10px] text-zinc-600"><Mail className="w-4 h-4 text-[#C19A6B]" /> Email</button>
            <button onClick={() => d.source_url && window.open(d.source_url, "_blank")} className="flex flex-col items-center gap-0.5 py-1 text-[10px] text-zinc-600"><ExternalLink className="w-4 h-4 text-[#C19A6B]" /> Source</button>
            <button onClick={() => openBien((d.best_match || {}).slug)} className="flex flex-col items-center gap-0.5 py-1 text-[10px] text-zinc-600"><Home className="w-4 h-4 text-[#C19A6B]" /> Bien</button>
            <button onClick={() => d.contact && d.contact.phone && (window.location.href = `tel:${d.contact.phone}`)} className="flex flex-col items-center gap-0.5 py-1 text-[10px] text-zinc-600"><Phone className="w-4 h-4 text-[#C19A6B]" /> Contact</button>
          </div>
        )}
      </motion.div>

      {/* Overlay e-mail */}
      <AnimatePresence>
        {emailView && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => { e.stopPropagation(); setEmailView(null); }} data-testid="prospect-email-overlay">
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-white rounded-[10px] shadow-2xl overflow-hidden flex flex-col" style={{ maxHeight: "90vh" }}>
              <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between gap-2 shrink-0"><p className="text-sm font-semibold text-[#111827] truncate flex items-center gap-2"><Mail className="w-4 h-4 text-[#C19A6B]" /> {emailView.subject}</p><button onClick={() => setEmailView(null)} className="text-zinc-400 hover:text-zinc-700"><X className="w-5 h-5" /></button></div>
              <div className="p-4 overflow-auto flex-1"><iframe title="email" srcDoc={emailView.html} className="w-full rounded-md border border-zinc-100" style={{ height: "62vh" }} /></div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {showPartner && d && d.partner && (
        <PartnerMiniFiche partner={d.partner} slug={d.partner.slug || d.matched_slug} acqId={acqId} onClose={() => setShowPartner(false)} onCollab={() => setShowPartner(false)} onRel={() => {}} />
      )}
      {showVisite && d && (
        <VisiteModal acqId={acqId} bien={d.best_match} onClose={() => setShowVisite(false)} onSent={(msg) => { setShowVisite(false); setToast(msg); load(); onChanged && onChanged(); }} />
      )}
      {showSchedule && d && (
        <ScheduleVisiteModal acqId={acqId} bien={d.best_match} onClose={() => setShowSchedule(false)} onSent={(msg) => { setShowSchedule(false); setStatus("visite_demandee"); setToast(msg); load(); onChanged && onChanged(); }} />
      )}
      <AnimatePresence>
        {showWa && <WhatsAppDraftModal acqId={acqId} onClose={() => setShowWa(false)} />}
      </AnimatePresence>
    </motion.div>
  );
}

// Modale « Planifier la visite » — date + heure précises => crée un RDV agenda + rappel auto la veille (acquéreur + conseiller).
function ScheduleVisiteModal({ acqId, bien, onClose, onSent }) {
  const tomorrow = () => { const dt = new Date(); dt.setDate(dt.getDate() + 1); const pad = (n) => String(n).padStart(2, "0"); return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`; };
  const [date, setDate] = useState(tomorrow());
  const [time, setTime] = useState("14:00");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    if (!date) { setErr("Choisissez une date."); return; }
    setBusy(true); setErr("");
    try {
      await authApi.createVisit({ kind: "acquereur", acquereur_id: acqId, slug: bien && bien.slug, date, time, title: `Visite — ${(bien && bien.title) || "bien"}` });
      onSent("Visite planifiée ✓ — rappel automatique la veille (acquéreur + vous).");
    } catch (e) { setErr((e && e.message) || "Impossible de planifier."); setBusy(false); }
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[95] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="schedule-visite-modal">
      <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-sm bg-white rounded-[12px] shadow-2xl overflow-hidden">
        <div className="px-5 py-4 bg-[#111827] bg-gradient-to-br from-[#0d0d10] to-[#1a1712] flex items-center justify-between">
          <p className="text-white font-semibold flex items-center gap-2"><CalendarClock className="w-4 h-4 text-[#C19A6B]" /> Planifier la visite</p>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          {bien && <p className="text-sm text-zinc-500">Bien : <span className="font-semibold text-[#111827]">{bien.title}</span></p>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 bg-white outline-none focus:border-[#C19A6B]" data-testid="schedule-date" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">Heure</label>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 bg-white outline-none focus:border-[#C19A6B]" data-testid="schedule-time" />
            </div>
          </div>
          <p className="text-[11px] text-zinc-400 flex items-center gap-1.5"><Bell className="w-3.5 h-3.5 text-[#C19A6B]" /> Un rappel automatique partira la veille à l'acquéreur et à vous.</p>
          {err && <p className="text-xs text-red-500" data-testid="schedule-error">{err}</p>}
          <div className="flex gap-2 pt-1">
            <button onClick={onClose} className="flex-1 py-2.5 text-sm font-semibold text-zinc-600 bg-[#F1F3F5] hover:bg-[#E9ECEF] rounded-lg transition-colors">Annuler</button>
            <button onClick={save} disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-white bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 rounded-lg transition-colors" data-testid="schedule-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />} Planifier</button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Modale « Proposer une visite » — le conseiller choisit jusqu'à 3 créneaux + un message.
function VisiteModal({ acqId, bien, onClose, onSent }) {
  const defaults = () => {
    const out = [];
    for (let i = 1; i <= 3; i++) {
      const dt = new Date(); dt.setDate(dt.getDate() + i); dt.setHours(14, 0, 0, 0);
      const pad = (n) => String(n).padStart(2, "0");
      out.push(`${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`);
    }
    return out;
  };
  const [slots, setSlots] = useState(defaults());
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const setSlot = (i, v) => setSlots((s) => s.map((x, j) => (j === i ? v : x)));
  const send = async () => {
    const clean = slots.filter(Boolean);
    if (!clean.length) { setErr("Ajoutez au moins un créneau."); return; }
    setBusy(true); setErr("");
    try {
      const r = await authApi.proposeVisite(acqId, { slots: clean, message, slug: bien && bien.slug });
      onSent(r.sent ? "Proposition de visite envoyée à l'acquéreur." : "Visite enregistrée (e-mail non envoyé — config).");
    } catch (e) { setErr((e && e.message) || "Envoi impossible."); setBusy(false); }
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[95] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="visite-modal">
      <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white rounded-[12px] shadow-2xl overflow-hidden">
        <div className="px-5 py-4 bg-[#111827] bg-gradient-to-br from-[#0d0d10] to-[#1a1712] flex items-center justify-between">
          <p className="text-white font-semibold flex items-center gap-2"><CalendarClock className="w-4 h-4 text-[#C19A6B]" /> Proposer une visite</p>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          {bien && <p className="text-sm text-zinc-500">Bien proposé : <span className="font-semibold text-[#111827]">{bien.title}</span></p>}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">Créneaux proposés</label>
            <div className="space-y-2 mt-1.5">
              {slots.map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input type="datetime-local" value={s} onChange={(e) => setSlot(i, e.target.value)} className="flex-1 px-3 py-2 text-sm rounded-lg border border-zinc-200 bg-white outline-none focus:border-[#C19A6B]" data-testid={`visite-slot-${i}`} />
                  {slots.length > 1 && <button onClick={() => setSlots((arr) => arr.filter((_, j) => j !== i))} className="text-zinc-300 hover:text-red-500"><X className="w-4 h-4" /></button>}
                </div>
              ))}
            </div>
            {slots.length < 4 && <button onClick={() => setSlots((s) => [...s, ""])} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#C19A6B] hover:underline" data-testid="visite-add-slot"><Plus className="w-3.5 h-3.5" /> Ajouter un créneau</button>}
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">Message (optionnel)</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} placeholder="Je peux aussi m'adapter à vos disponibilités…" className="mt-1.5 w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 bg-white outline-none focus:border-[#C19A6B] resize-none" data-testid="visite-message" />
          </div>
          {err && <p className="text-xs text-red-500" data-testid="visite-error">{err}</p>}
          <div className="flex gap-2 pt-1">
            <button onClick={onClose} className="flex-1 py-2.5 text-sm font-semibold text-zinc-600 bg-[#F1F3F5] hover:bg-[#E9ECEF] rounded-lg transition-colors">Annuler</button>
            <button onClick={send} disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-white bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 rounded-lg transition-colors" data-testid="visite-send">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Envoyer</button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Vue Kanban : colonnes par statut commercial (Détecté → Vendu).
function ProspectKanban({ prospects, statuses, onOpen, onMove }) {
  const cols = (statuses || []).map((s) => ({ ...s, items: prospects.filter((p) => p.crm_status === s.key) }));
  return (
    <div className="flex gap-3 overflow-x-auto pb-3 -mx-1 px-1" data-testid="prospects-kanban">
      {cols.map((col) => (
        <div key={col.key} className="shrink-0 w-[230px] bg-[#F6F7F9] rounded-xl p-2" data-testid={`kanban-col-${col.key}`}>
          <div className="flex items-center justify-between px-1.5 py-1 mb-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">{col.label}</span>
            <span className="text-[10px] font-semibold text-zinc-400 bg-white rounded-full px-1.5 py-0.5">{col.items.length}</span>
          </div>
          <div className="space-y-2">
            {col.items.map((p) => { const sm = EMAIL_STAGE_META[(p.email || {}).stage] || EMAIL_STAGE_META.none; return (
              <div key={p.acq_id} className="bg-white rounded-lg border border-zinc-100 p-2.5 hover:border-[#C19A6B]/40 transition-colors" data-testid={`kanban-card-${p.acq_id}`}>
                <div className="flex items-center gap-2 cursor-pointer" onClick={() => onOpen(p.acq_id)}>
                  <div className="w-8 h-8 rounded-full bg-[#C19A6B]/12 flex items-center justify-center text-[#C19A6B] font-bold text-[11px] shrink-0">{p.initials}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1">
                      <p className="text-[13px] font-semibold text-[#111827] truncate">{p.name}</p>
                      {p.hot && <Flame className="w-3 h-3 text-[#EF6C3B] shrink-0" />}
                    </div>
                    {p.partner && <p className="text-[9px] text-zinc-400 truncate">via {p.partner.short}</p>}
                  </div>
                  <PScore score={p.score} />
                </div>
                <p className="text-[11px] text-zinc-500 truncate mt-1.5">{p.search_short || "—"}</p>
                {p.best_match && <p className="text-[10px] text-zinc-400 truncate mt-0.5">🏡 {p.best_match.title}</p>}
                <div className="flex items-center justify-between gap-1 mt-1.5">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${sm.cls}`}>{sm.dot} {sm.label}</span>
                  {p.mine && (
                    <select value={p.crm_status} onChange={(e) => onMove(p.acq_id, e.target.value)} onClick={(e) => e.stopPropagation()} className="text-[9px] border border-zinc-200 rounded px-1 py-0.5 bg-white text-zinc-500 outline-none focus:border-[#C19A6B] max-w-[92px]" data-testid={`kanban-move-${p.acq_id}`}>
                      {(statuses || []).map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                  )}
                </div>
              </div>
            ); })}
            {col.items.length === 0 && <p className="text-[11px] text-zinc-300 text-center py-3">—</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

function ProspectsCRM({ setTab }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [source, setSource] = useState("");
  const [city, setCity] = useState("");
  const [open, setOpen] = useState(null); // acq_id
  const [autoAction, setAutoAction] = useState(null);
  const [sp, setSp] = useSearchParams();
  useEffect(() => {
    const pr = sp.get("prospect");
    if (pr) {
      setOpen(pr);
      setAutoAction(sp.get("action") || null);
      const next = new URLSearchParams(sp); next.delete("prospect"); next.delete("action");
      setSp(next, { replace: true });
    }
  }, [sp]); // eslint-disable-line react-hooks/exhaustive-deps
  const [view, setView] = useState("list"); // list | kanban
  const reload = useCallback(() => { authApi.agentProspects({ q, filter, source, city }).then(setData).catch(() => setData({ prospects: [], counts: {}, sources: [], cities: [] })); }, [q, filter, source, city]);
  useEffect(() => { const t = setTimeout(reload, q ? 300 : 0); return () => clearTimeout(t); }, [reload, q]);
  const counts = (data && data.counts) || {};
  const chips = [
    ["", "Tous", counts.all], ["hot", "🔥 Plus chauds", counts.hot], ["new", "Nouveaux", counts.new],
    ["email_pret", "Email prêt", counts.email_pret], ["email_sent", "Email envoyé", counts.email_sent],
    ["email_opened", "Email ouvert", counts.email_opened], ["inscrit", "Inscrits", counts.inscrit], ["visite", "Visite", counts.visite],
  ];
  const moveStatus = async (acqId, status) => { try { await authApi.prospectSetStatus(acqId, status); reload(); } catch (_) {} };
  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }); } catch (_) { return ""; } };
  return (
    <div data-testid="prospects-crm">
      <EmmaWorkBlock setTab={setTab} onRescan={reload} />
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
          <h3 className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] flex items-center gap-2"><Contact className="w-5 h-5" /> Prospects</h3>
          <p className="text-sm text-zinc-400 mt-1">Chaque recherche détectée par votre Agent IA devient une fiche client actionnable : qui, quoi, quel bien, quel e-mail, quelle action.</p>
        </div>
        <div className="inline-flex rounded-lg bg-[#F1F3F5] p-1 shrink-0" data-testid="prospects-view-toggle">
          <button onClick={() => setView("list")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${view === "list" ? "bg-white text-[#111827] shadow-sm" : "text-zinc-500 hover:text-zinc-700"}`} data-testid="prospects-view-list"><ListIcon className="w-3.5 h-3.5" /> Liste</button>
          <button onClick={() => setView("kanban")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${view === "kanban" ? "bg-white text-[#111827] shadow-sm" : "text-zinc-500 hover:text-zinc-700"}`} data-testid="prospects-view-kanban"><LayoutGrid className="w-3.5 h-3.5" /> Kanban</button>
        </div>
      </div>

      {/* Recherche + filtres source/ville */}
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un prospect (nom, ville, bien, budget…)" className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-zinc-200 bg-white outline-none focus:border-[#C19A6B]" data-testid="prospects-search" />
        </div>
        <select value={source} onChange={(e) => setSource(e.target.value)} className="px-3 py-2.5 text-sm rounded-lg border border-zinc-200 bg-white outline-none focus:border-[#C19A6B]" data-testid="prospects-source-filter">
          <option value="">Toutes sources</option>
          {(data && data.sources || []).map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select value={city} onChange={(e) => setCity(e.target.value)} className="px-3 py-2.5 text-sm rounded-lg border border-zinc-200 bg-white outline-none focus:border-[#C19A6B]" data-testid="prospects-city-filter">
          <option value="">Toutes villes</option>
          {(data && data.cities || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Chips */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="prospects-filters">
        {chips.map(([k, lbl, n]) => (
          <button key={k || "all"} onClick={() => setFilter(k)} className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${filter === k ? "bg-[#C19A6B] text-white" : "bg-[#F1F3F5] text-zinc-600 hover:bg-[#E9ECEF]"}`} data-testid={`prospects-chip-${k || "all"}`}>{lbl}{typeof n === "number" ? ` (${n})` : ""}</button>
        ))}
      </div>

      {!data ? (
        <div className="py-16 text-center text-zinc-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : (data.prospects || []).length === 0 ? (
        <div className="py-16 text-center" data-testid="prospects-empty">
          <Contact className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-sm text-zinc-500">Aucun prospect pour ces filtres. Votre Agent IA alimentera cette liste dès qu'une recherche compatible est détectée.</p>
        </div>
      ) : view === "kanban" ? (
        <ProspectKanban prospects={data.prospects} statuses={data.statuses || []} onOpen={setOpen} onMove={moveStatus} />
      ) : (
        <div className="space-y-2.5" data-testid="prospects-list">
          {data.prospects.map((p) => { const sm = EMAIL_STAGE_META[(p.email || {}).stage] || EMAIL_STAGE_META.none; return (
            <div key={p.acq_id} className="bg-white rounded-xl border border-zinc-100 hover:border-[#C19A6B]/40 transition-colors p-3 flex items-center gap-3" data-testid={`prospect-card-${p.acq_id}`}>
              <div className="w-11 h-11 rounded-full bg-[#C19A6B]/12 flex items-center justify-center text-[#C19A6B] font-bold text-sm shrink-0">{p.initials}</div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-[#111827] truncate">{p.name}</p>
                  {p.partner && <span className="text-[10px] text-zinc-400 truncate">via {p.partner.short}</span>}
                  {p.hot && <Flame className="w-3.5 h-3.5 text-[#EF6C3B] shrink-0" />}
                </div>
                <p className="text-[12px] text-zinc-500 truncate">{p.search_short || "—"}</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {p.city && <span className="text-[10px] text-zinc-500 inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {p.city}</span>}
                  {p.best_match && <span className="text-[10px] text-zinc-500 truncate max-w-[140px]">🏡 {p.best_match.title}</span>}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${sm.cls}`}>{sm.dot} {sm.label}</span>
                  {p.recommended_channel && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#C19A6B]/10 text-[#A88358] font-medium" data-testid={`prospect-chan-${p.acq_id}`}>→ {CHAN_SHORT[p.recommended_channel] || p.recommended_channel}</span>}
                </div>
              </div>
              <div className="text-right shrink-0 flex flex-col items-end gap-1">
                <PScore score={p.score} />
                <button onClick={() => setOpen(p.acq_id)} className="text-[11px] font-semibold text-white bg-[#111827] hover:bg-black rounded-lg px-3 py-1.5 transition-colors" data-testid={`prospect-open-${p.acq_id}`}>Ouvrir</button>
              </div>
            </div>
          ); })}
        </div>
      )}

      <AnimatePresence>
        {open && <ProspectPanel acqId={open} autoAction={autoAction} onClose={() => { setOpen(null); setAutoAction(null); }} onChanged={reload} />}
      </AnimatePresence>
    </div>
  );
}

// Petit champ « tags » (villes, secteurs, types de clients)
function ChipInput({ value, onChange, placeholder, testid }) {
  const [t, setT] = useState("");
  const add = () => { const v = t.trim(); if (v && !(value || []).includes(v)) { onChange([...(value || []), v]); } setT(""); };
  return (
    <div>
      <div className="flex gap-2">
        <input value={t} onChange={(e) => setT(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder={placeholder} className="flex-1 px-3 py-2 text-sm rounded-lg border border-zinc-200 bg-white outline-none focus:border-[#C19A6B]" data-testid={`${testid}-input`} />
        <button onClick={add} className="px-3 py-2 rounded-lg bg-[#F1F3F5] text-zinc-600 hover:bg-[#E9ECEF] transition-colors" data-testid={`${testid}-add`}><Plus className="w-4 h-4" /></button>
      </div>
      {(value || []).length > 0 && <div className="flex flex-wrap gap-1.5 mt-2">{(value || []).map((v, i) => (
        <span key={i} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 bg-[#C19A6B]/10 text-[#A88358] rounded-full">{v}<button onClick={() => onChange(value.filter((x) => x !== v))} className="hover:text-red-500"><X className="w-3 h-3" /></button></span>
      ))}</div>}
    </div>
  );
}

function AgentSettingsTab() {
  const [cfg, setCfg] = useState(null);
  const [listings, setListings] = useState(null);
  const [boostTarget, setBoostTarget] = useState(null);
  const [saved, setSaved] = useState(false);
  const [digestMsg, setDigestMsg] = useState("");
  const [digestBusy, setDigestBusy] = useState(false);
  useEffect(() => {
    authApi.agentGetSettings().then(setCfg).catch(() => setCfg({ agent_mode: "validation", agent_name: "", search_cities: [], search_sectors: [], client_types: [], digest_frequency: "daily", digest_hour: 8 }));
    authApi.myListings().then((d) => setListings(d.listings || d || [])).catch(() => setListings([]));
  }, []);
  const patch = (p) => setCfg((c) => ({ ...c, ...p }));
  const save = async (extra = {}) => { const body = { ...cfg, ...extra }; setCfg(body); try { const r = await authApi.agentSetSettings(body); setCfg(r); setSaved(true); setTimeout(() => setSaved(false), 2000); } catch (_) {} };
  const setMode = (mode) => { patch({ agent_mode: mode }); save({ agent_mode: mode }); };
  const sendPreview = async () => { setDigestBusy(true); setDigestMsg(""); try { const r = await authApi.agentDigestPreview(true); setDigestMsg(r.sent ? "Aperçu envoyé sur votre e-mail ✓" : r.would_send ? "Envoi indisponible (config e-mail)." : "Aucun prospect chaud à inclure pour le moment."); } catch (_) { setDigestMsg("Aperçu indisponible."); } setDigestBusy(false); };
  if (!cfg) return <div className="py-16 text-center text-zinc-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  const published = (listings || []).filter((l) => l.published !== false);
  return (
    <div data-testid="agent-settings-tab" className="space-y-6 max-w-2xl">
      <div>
        <h3 className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] flex items-center gap-2"><Wand2 className="w-5 h-5" /> Paramètres de mon Agent IA</h3>
        <p className="text-sm text-zinc-400 mt-1">Personnalisez votre agent, son mode d'envoi et sa mission de prospection.</p>
      </div>

      {/* Mode d'envoi */}
      <div className="border border-[#C19A6B]/30 bg-[#0d0d10] rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="agent-settings-mode">
        <div>
          <p className="text-sm font-semibold text-white flex items-center gap-2"><Send className="w-4 h-4 text-[#C19A6B]" /> Mode d'envoi</p>
          <p className="text-xs text-zinc-400 mt-0.5">{cfg.agent_mode === "auto" ? "Automatique — l'agent envoie les e-mails tout seul." : "Manuel — chaque e-mail attend votre validation."}</p>
        </div>
        <div className="inline-flex rounded-full bg-[#1a1712] p-1 shrink-0">
          <button onClick={() => setMode("validation")} className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-colors ${cfg.agent_mode === "validation" ? "bg-[#C19A6B] text-white" : "text-zinc-400 hover:text-white"}`} data-testid="agent-settings-mode-validation">Manuel (validation)</button>
          <button onClick={() => setMode("auto")} className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-colors ${cfg.agent_mode === "auto" ? "bg-[#C19A6B] text-white" : "text-zinc-400 hover:text-white"}`} data-testid="agent-settings-mode-auto">Automatique</button>
        </div>
      </div>

      {/* Personnalisation */}
      <div className="bg-white rounded-xl border border-zinc-100 p-4 space-y-4">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">Nom de mon agent</label>
          <input value={cfg.agent_name === "Votre Agent IA" ? "" : (cfg.agent_name || "")} onChange={(e) => patch({ agent_name: e.target.value })} placeholder="Ex : Léa, mon assistante IA" className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 bg-white outline-none focus:border-[#C19A6B]" data-testid="agent-settings-name" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">Villes / secteurs de recherche</label>
          <div className="mt-1"><ChipInput value={cfg.search_cities} onChange={(v) => patch({ search_cities: v })} placeholder="Ajouter une ville (Entrée)" testid="agent-settings-cities" /></div>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">Types de clients recherchés</label>
          <div className="mt-1"><ChipInput value={cfg.client_types} onChange={(v) => patch({ client_types: v })} placeholder="Ex : Familles, Investisseurs, Primo-accédants" testid="agent-settings-clients" /></div>
        </div>
        <button onClick={() => save()} className="inline-flex items-center gap-2 bg-[#111827] hover:bg-black text-white text-sm font-semibold rounded-lg px-4 py-2 transition-colors" data-testid="agent-settings-save">{saved ? <><Check className="w-4 h-4 text-[#10B981]" /> Enregistré</> : "Enregistrer"}</button>
      </div>

      {/* Récap quotidien par e-mail */}
      <div className="bg-white rounded-xl border border-zinc-100 p-4" data-testid="agent-settings-digest">
        <p className="text-sm font-semibold text-[#111827] flex items-center gap-2 mb-1"><Bell className="w-4 h-4 text-[#C19A6B]" /> Récap par e-mail</p>
        <p className="text-xs text-zinc-400 mb-3">Recevez « vos prospects les plus chauds + l'action à faire » directement dans votre boîte, à l'heure qui vous convient.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">Fréquence</label>
            <select value={cfg.digest_frequency || "daily"} onChange={(e) => save({ digest_frequency: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 bg-white outline-none focus:border-[#C19A6B]" data-testid="agent-digest-frequency">
              <option value="daily">Tous les jours</option>
              <option value="weekdays">En semaine (lun → ven)</option>
              <option value="weekly">Une fois par semaine (lundi)</option>
              <option value="off">Désactivé</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">Heure d'envoi (Paris)</label>
            <select value={cfg.digest_hour ?? 8} onChange={(e) => save({ digest_hour: Number(e.target.value) })} disabled={cfg.digest_frequency === "off"} className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 bg-white outline-none focus:border-[#C19A6B] disabled:opacity-50" data-testid="agent-digest-hour">
              {Array.from({ length: 18 }, (_, i) => i + 5).map((h) => { const label = `${String(h).padStart(2, "0")}h00`; return <option key={h} value={h}>{label}</option>; })}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-3">
          <button onClick={sendPreview} disabled={digestBusy} className="inline-flex items-center gap-2 border border-[#C19A6B] bg-[#C19A6B]/10 text-[#A88358] hover:bg-[#C19A6B]/20 disabled:opacity-50 text-sm font-semibold rounded-lg px-3 py-1.5 transition-colors" data-testid="agent-digest-preview-btn">{digestBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Recevoir un aperçu maintenant</button>
          {digestMsg && <span className="text-xs text-zinc-500" data-testid="agent-digest-msg">{digestMsg}</span>}
        </div>
      </div>

      {/* Boost par annonce */}
      <div className="bg-white rounded-xl border border-zinc-100 p-4" data-testid="agent-settings-boost">
        <p className="text-sm font-semibold text-[#111827] flex items-center gap-2 mb-1"><Rocket className="w-4 h-4 text-[#C19A6B]" /> Boost de mes annonces</p>
        <p className="text-xs text-zinc-400 mb-3">Placez une annonce en tête du matching et de la recherche. Statut confidentiel (visible par vous seul).</p>
        {listings === null ? <Loader2 className="w-5 h-5 animate-spin text-[#C19A6B]" /> : published.length === 0 ? (
          <p className="text-sm text-zinc-400">Aucune annonce publiée à booster pour l'instant.</p>
        ) : (
          <div className="space-y-2">
            {published.map((l) => { const boosted = l.boost && l.boost.expires_at && new Date(l.boost.expires_at) > new Date(); return (
              <div key={l.slug} className="flex items-center gap-3 border border-zinc-100 rounded-lg p-2.5" data-testid={`boost-row-${l.slug}`}>
                {(l.cover || (l.images || [])[0]) ? <img src={l.cover || l.images[0]} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" /> : <div className="w-12 h-12 rounded-lg bg-zinc-100 shrink-0" />}
                <div className="min-w-0 flex-1"><p className="text-sm font-medium text-[#111827] truncate">{l.title}</p>{boosted ? <p className="text-[11px] text-[#C19A6B] font-semibold flex items-center gap-1"><Rocket className="w-3 h-3" /> Boosté jusqu'au {new Date(l.boost.expires_at).toLocaleDateString("fr-FR")}</p> : <p className="text-[11px] text-zinc-400">Non boosté</p>}</div>
                <button onClick={() => setBoostTarget(l)} className="shrink-0 inline-flex items-center gap-1.5 border border-[#C19A6B] bg-[#C19A6B]/10 text-[#C19A6B] hover:bg-[#C19A6B]/20 text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors" data-testid={`agent-boost-btn-${l.slug}`}><Rocket className="w-3.5 h-3.5" /> {boosted ? "Prolonger" : "Booster"}</button>
              </div>
            ); })}
          </div>
        )}
      </div>

      {boostTarget && <BoostModal listing={boostTarget} onClose={() => setBoostTarget(null)} />}
    </div>
  );
}


function ProspectFicheModal({ acqId, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { authApi.prospectFiche(acqId).then(setD).catch(() => setD({ error: true })); }, [acqId]);
  const ic = (t) => t === "detection" ? Search : t === "analysis" ? Wand2 : t === "email" ? Mail : t === "open" ? Eye : t === "click" ? MousePointerClick : Clock;
  const fmt = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch (_) { return ""; } };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="prospect-fiche-modal">
      <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-white rounded-[10px] shadow-2xl overflow-hidden flex flex-col" style={{ maxHeight: "90vh" }}>
        <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between gap-2 shrink-0">
          <p className="text-sm font-semibold text-[#111827] flex items-center gap-2"><Contact className="w-4 h-4 text-[#C19A6B]" /> Fiche prospect{d && d.name ? ` — ${d.name}` : ""}</p>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700"><X className="w-5 h-5" /></button>
        </div>
        {!d ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#C19A6B]" /></div> : d.error ? <div className="p-8 text-center text-sm text-zinc-500">Fiche indisponible.</div> : (
          <div className="p-5 overflow-auto space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-[10px] uppercase tracking-wider text-zinc-400">Statut</p><p className="text-[#111827] font-medium">🟢 {d.status}</p></div>
              <div><p className="text-[10px] uppercase tracking-wider text-zinc-400">Source de détection</p><p className="text-[#111827] font-medium">{d.source_label}</p></div>
              <div><p className="text-[10px] uppercase tracking-wider text-zinc-400">Détecté le</p><p className="text-[#111827]">{fmt(d.detected_at)}</p></div>
              <div><p className="text-[10px] uppercase tracking-wider text-zinc-400">Publication source</p>{d.source_url ? <a href={d.source_url} target="_blank" rel="noopener noreferrer" className="text-[#C19A6B] hover:underline text-sm" data-testid="prospect-source-link">Voir la source</a> : <span className="text-zinc-400 text-sm">—</span>}</div>
            </div>
            <div><p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Recherche détectée</p>
              <p className="text-sm text-zinc-700">{d.search.summary || "—"}</p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {d.search.budget && <span className="text-[10px] px-2 py-0.5 bg-[#F1F3F5] rounded-full">Budget {d.search.budget}</span>}
                {(d.search.secteurs || []).map((s, i) => <span key={i} className="text-[10px] px-2 py-0.5 bg-[#F1F3F5] rounded-full">{s}</span>)}
                {(d.search.type_bien || []).map((s, i) => <span key={`t${i}`} className="text-[10px] px-2 py-0.5 bg-[#F1F3F5] rounded-full">{s}</span>)}
              </div>
            </div>
            {d.best_match && <div className="border border-[#C19A6B]/25 bg-[#C19A6B]/5 rounded-lg p-3 flex items-center justify-between">
              <div><p className="text-[10px] uppercase tracking-wider text-[#A88358]">Bien proposé</p><p className="text-sm font-medium text-[#111827]">{d.best_match.title}</p></div>
              <span className="text-lg font-bold text-[#C19A6B]">{d.best_match.score}%</span>
            </div>}
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2">Timeline</p>
              {(d.timeline || []).length === 0 ? <p className="text-sm text-zinc-400">Aucun évènement.</p> : (
                <div className="relative pl-6" data-testid="prospect-timeline">
                  <div className="absolute left-[9px] top-1 bottom-1 w-px bg-[#E5E7EB]" />
                  {d.timeline.map((t, i) => { const Ic = ic(t.type); return (
                    <div key={i} className="relative mb-3">
                      <span className="absolute -left-6 top-0 w-[19px] h-[19px] rounded-full bg-white ring-2 ring-[#C19A6B]/40 flex items-center justify-center"><Ic className="w-3 h-3 text-[#C19A6B]" /></span>
                      <p className="text-[13px] text-[#111827]">{t.text}</p>
                      <p className="text-[10px] text-zinc-400">{fmt(t.at)}</p>
                    </div>
                  ); })}
                </div>
              )}
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

function AgentEmailsTab() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState(null);
  const [range, setRange] = useState("30d");
  const [status, setStatus] = useState("");
  const [detail, setDetail] = useState(null); // full email
  const [fiche, setFiche] = useState(null); // acq_id for prospect fiche
  const [mode, setMode] = useState(null); // auto | validation
  const [pending, setPending] = useState([]);
  const reloadStats = () => authApi.agentEmailStats().then(setStats).catch(() => setStats(null));
  const reloadList = () => authApi.agentEmails(range, status).then((d) => setItems(d.items || [])).catch(() => setItems([]));
  const reloadPending = () => authApi.agentPending().then((d) => setPending(d.items || [])).catch(() => setPending([]));
  useEffect(() => { reloadStats(); authApi.agentGetSettings().then((d) => setMode(d.agent_mode)).catch(() => setMode("validation")); reloadPending(); }, []);
  useEffect(() => { setItems(null); reloadList(); /* eslint-disable-next-line */ }, [range, status]);
  const changeMode = async (nm) => { setMode(nm); try { await authApi.agentSetSettings({ agent_mode: nm }); } catch (_) {} };
  const approve = async (id) => { try { await authApi.agentApprove(id); await reloadPending(); await reloadList(); reloadStats(); } catch (_) {} };
  const reject = async (id) => { try { await authApi.agentReject(id); await reloadPending(); } catch (_) {} };
  const [bulkBusy, setBulkBusy] = useState("");
  const approveAll = async () => {
    if (bulkBusy) return;
    setBulkBusy("approve");
    try { await authApi.agentApproveAll(); await reloadPending(); await reloadList(); reloadStats(); } catch (_) {}
    setBulkBusy("");
  };
  const rejectAll = async () => {
    if (bulkBusy) return;
    setBulkBusy("reject");
    try { await authApi.agentRejectAll(); await reloadPending(); } catch (_) {}
    setBulkBusy("");
  };
  const openDetail = async (id) => { try { setDetail(await authApi.agentEmailDetail(id)); } catch (_) {} };
  const fmt = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch (_) { return "—"; } };
  const kpis = stats ? [
    { l: "Envoyés (total)", v: stats.sent }, { l: "Aujourd'hui", v: stats.sent_today },
    { l: "Taux d'ouverture", v: `${stats.open_rate}%` }, { l: "Taux de clic", v: `${stats.click_rate}%` },
    { l: "Coût IA cumulé", v: `${stats.ai_cost_total_eur} €` },
  ] : [];
  return (
    <div data-testid="agent-emails-tab">
      <div className="mb-5">
        <h3 className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] flex items-center gap-2"><Mail className="w-5 h-5" /> Agent IA — Emails envoyés</h3>
        <p className="text-sm text-zinc-400 mt-1">Tous les e-mails de prospection envoyés par votre Agent IA : ouverture, clics, source du prospect et coût IA.</p>
      </div>
      <AgentFeed />
      {/* Phase 4 — Mode d'envoi de l'Agent IA (Automatique / Validation) */}
      <div className="mb-5 border border-[#C19A6B]/30 bg-[#0d0d10] rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="agent-mode-bar">
        <div>
          <p className="text-sm font-semibold text-white flex items-center gap-2"><Wand2 className="w-4 h-4 text-[#C19A6B]" /> Mode d'envoi de votre Agent IA</p>
          <p className="text-xs text-zinc-400 mt-0.5">
            {mode === "auto"
              ? "Automatique — l'agent envoie les e-mails de prospection tout seul."
              : "Validation — chaque e-mail attend votre approbation avant d'être envoyé."}
          </p>
        </div>
        <div className="inline-flex rounded-full bg-[#1a1712] p-1 shrink-0" data-testid="agent-mode-toggle">
          <button onClick={() => changeMode("validation")} className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-colors ${mode === "validation" ? "bg-[#C19A6B] text-white" : "text-zinc-400 hover:text-white"}`} data-testid="agent-mode-validation">Validation</button>
          <button onClick={() => changeMode("auto")} className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-colors ${mode === "auto" ? "bg-[#C19A6B] text-white" : "text-zinc-400 hover:text-white"}`} data-testid="agent-mode-auto">Automatique</button>
        </div>
      </div>
      {pending.length > 0 && (
        <div className="mb-5 border border-amber-300/60 bg-amber-50 rounded-xl p-4" data-testid="agent-pending-section">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
            <p className="text-sm font-semibold text-amber-700 flex items-center gap-2"><Clock className="w-4 h-4" /> {pending.length} e-mail(s) en attente de validation</p>
            {pending.length > 1 && (
              <div className="flex items-center gap-2 shrink-0" data-testid="agent-bulk-actions">
                <button onClick={approveAll} disabled={!!bulkBusy} className="inline-flex items-center gap-1 bg-[#10B981] hover:bg-[#0e9f74] disabled:opacity-50 text-white text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors" data-testid="agent-pending-approve-all">{bulkBusy === "approve" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Tout valider &amp; envoyer</button>
                <button onClick={rejectAll} disabled={!!bulkBusy} className="inline-flex items-center gap-1 bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50 disabled:opacity-50 text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors" data-testid="agent-pending-reject-all">{bulkBusy === "reject" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />} Tout rejeter</button>
              </div>
            )}
          </div>
          <div className="space-y-2">
            {pending.map((e) => (
              <div key={e.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-white rounded-lg p-3 border border-amber-100" data-testid={`agent-pending-${e.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#111827] truncate">{e.prospect_label || e.prospect_masked || "Prospect"} — {e.title || "—"}</p>
                  <p className="text-[11px] text-zinc-500 truncate">{e.subject}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => openDetail(e.id)} className="text-[11px] font-semibold text-[#C19A6B] hover:underline" data-testid={`agent-pending-view-${e.id}`}>Aperçu</button>
                  <button onClick={() => approve(e.id)} className="inline-flex items-center gap-1 bg-[#10B981] hover:bg-[#0e9f74] text-white text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors" data-testid={`agent-pending-approve-${e.id}`}><Check className="w-3.5 h-3.5" /> Valider &amp; envoyer</button>
                  <button onClick={() => reject(e.id)} className="inline-flex items-center gap-1 bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50 text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors" data-testid={`agent-pending-reject-${e.id}`}><X className="w-3.5 h-3.5" /> Rejeter</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        {kpis.map((k, i) => (
          <div key={i} className="border border-white/10 bg-[#0d0d10] p-4" data-testid={`agent-kpi-${i}`}>
            <p className="text-2xl font-bold text-white">{k.v}</p>
            <p className="text-[11px] uppercase tracking-wider text-zinc-400 mt-1">{k.l}</p>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {[["today", "Aujourd'hui"], ["7d", "7 jours"], ["30d", "30 jours"], ["all", "Tout"]].map(([k, lbl]) => (
          <button key={k} onClick={() => setRange(k)} className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${range === k ? "bg-[#C19A6B] text-white" : "bg-[#F1F3F5] text-zinc-600 hover:bg-[#E9ECEF]"}`} data-testid={`agent-range-${k}`}>{lbl}</button>
        ))}
        <span className="w-px bg-zinc-200 mx-1" />
        {[["", "Tous"], ["opened", "Ouverts"], ["clicked", "Cliqués"]].map(([k, lbl]) => (
          <button key={k || "all"} onClick={() => setStatus(k)} className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${status === k ? "bg-[#111827] text-white" : "bg-[#F1F3F5] text-zinc-600 hover:bg-[#E9ECEF]"}`} data-testid={`agent-status-${k || "all"}`}>{lbl}</button>
        ))}
      </div>
      {items === null ? (
        <div className="py-16 text-center text-zinc-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-zinc-500" data-testid="agent-emails-empty">Aucun e-mail envoyé sur cette période. Utilisez « E-mail de prospection IA » depuis le Matching IA.</p>
      ) : (
        <div className="overflow-x-auto border border-[#E5E7EB] rounded-xl bg-white" data-testid="agent-emails-table">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500 border-b border-[#F1F3F5]">
              <th className="p-3">Date</th><th className="p-3">Prospect</th><th className="p-3">Bien proposé</th><th className="p-3">Score</th><th className="p-3">Source</th><th className="p-3">Statut</th><th className="p-3">Ouv.</th><th className="p-3">Clic</th><th className="p-3"></th>
            </tr></thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-b border-[#F6F7F9] last:border-0 hover:bg-[#FBFBFC]">
                  <td className="p-3 text-zinc-600 whitespace-nowrap">{fmt(e.sent_at)}</td>
                  <td className="p-3 font-medium text-[#111827]">{e.acq_id ? <button onClick={() => setFiche(e.acq_id)} className="hover:text-[#C19A6B] hover:underline" data-testid={`prospect-open-${e.acq_id}`}>{e.prospect_label || e.prospect_masked || "Prospect"}</button> : (e.prospect_label || e.prospect_masked || "Prospect")}</td>
                  <td className="p-3 text-zinc-700 max-w-[220px] truncate">{e.title || "—"}</td>
                  <td className="p-3"><span className="font-bold text-[#C19A6B]">{e.score ? `${e.score}%` : "—"}</span></td>
                  <td className="p-3 text-zinc-500">{e.source || "—"}</td>
                  <td className="p-3">{e.status === "sent" ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-600">Envoyé</span> : e.status === "rejected" ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500">Rejeté</span> : <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600">Échec</span>}</td>
                  <td className="p-3">{(e.open_count || 0) > 0 ? <span className="text-green-600 font-semibold">✓</span> : <span className="text-zinc-300">—</span>}</td>
                  <td className="p-3">{(e.click_count || 0) > 0 ? <span className="text-green-600 font-semibold">{e.click_count}</span> : <span className="text-zinc-300">—</span>}</td>
                  <td className="p-3"><button onClick={() => openDetail(e.id)} className="text-[11px] font-semibold text-[#C19A6B] hover:underline" data-testid={`agent-email-view-${e.id}`}>Voir l'email</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <AnimatePresence>
        {detail && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDetail(null)} data-testid="agent-email-modal">
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-xl bg-white rounded-[10px] shadow-2xl overflow-hidden flex flex-col" style={{ maxHeight: "90vh" }}>
              <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between gap-2 shrink-0">
                <p className="text-sm font-semibold text-[#111827] flex items-center gap-2"><Mail className="w-4 h-4 text-[#C19A6B]" /> {detail.subject}</p>
                <button onClick={() => setDetail(null)} className="text-zinc-400 hover:text-zinc-700"><X className="w-5 h-5" /></button>
              </div>
              <p className="px-5 pt-3 text-[11px] text-zinc-500 shrink-0">{fmt(detail.sent_at)} · Prospect {detail.prospect_label || detail.prospect_masked} · Source {detail.source} · {(detail.open_count || 0) > 0 ? "Ouvert" : "Non ouvert"} · {detail.click_count || 0} clic(s) · Coût IA {detail.ai_cost_eur} €</p>
              <div className="p-4 overflow-auto flex-1"><iframe title="email" srcDoc={detail.html} className="w-full rounded-md border border-zinc-100" style={{ height: "62vh" }} data-testid="agent-email-iframe" /></div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {fiche && <ProspectPanel acqId={fiche} onClose={() => setFiche(null)} />}
      </AnimatePresence>
    </div>
  );
}

function ActivityTab({ onOpenTab }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  useEffect(() => { authApi.memberActivity(filter).then(setData).catch(() => setData({ items: [], types: {} })); }, [filter]);
  const types = (data && data.types) || {};
  const iconFor = (t) => t === "publish" ? Rocket : t === "offer" ? Inbox : t === "match" ? Sparkles : t === "comandat" ? Handshake : t === "ai" ? Wand2 : Clock;
  const tabFor = (t) => t === "offer" ? "offers" : t === "match" ? "matching_bien" : t === "publish" ? "listings" : null;
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch (_) { return ""; } };
  return (
    <div data-testid="activity-tab">
      <div className="mb-5">
        <h3 className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B] flex items-center gap-2"><Clock className="w-5 h-5" /> Historique d'activité</h3>
        <p className="text-sm text-zinc-400 mt-1">Le journal horodaté de vos publications, offres reçues, matchs réseau et co-mandats.</p>
      </div>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="activity-filters">
        <button onClick={() => setFilter("")} className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${filter === "" ? "bg-[#C19A6B] text-white" : "bg-[#F1F3F5] text-zinc-600 hover:bg-[#E9ECEF]"}`} data-testid="activity-filter-all">Tout</button>
        {Object.entries(types).map(([k, lbl]) => (
          <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${filter === k ? "bg-[#C19A6B] text-white" : "bg-[#F1F3F5] text-zinc-600 hover:bg-[#E9ECEF]"}`} data-testid={`activity-filter-${k}`}>{lbl}</button>
        ))}
      </div>
      {data === null ? (
        <div className="py-16 text-center text-zinc-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : (data.items || []).length === 0 ? (
        <p className="text-sm text-zinc-500" data-testid="activity-empty">Aucune activité enregistrée pour cette catégorie.</p>
      ) : (
        <div className="relative pl-6" data-testid="activity-timeline">
          <div className="absolute left-[9px] top-1 bottom-1 w-px bg-[#E5E7EB]" />
          {data.items.map((a) => { const Ic = iconFor(a.type); const t = tabFor(a.type); return (
            <div key={a.id} className="relative mb-4" data-testid="activity-item">
              <span className="absolute -left-6 top-0.5 w-[19px] h-[19px] rounded-full bg-white ring-2 ring-[#C19A6B]/40 flex items-center justify-center"><Ic className="w-3 h-3 text-[#C19A6B]" /></span>
              <div className={`border border-[#E5E7EB] bg-white rounded-lg p-3 ${t ? "cursor-pointer hover:border-[#C19A6B]" : ""} transition-colors`} onClick={() => t && onOpenTab && onOpenTab(t)}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-[#A88358]">{types[a.type] || a.type}</span>
                  <span className="text-[11px] text-zinc-400">{fmtDate(a.created_at)}</span>
                </div>
                <p className="text-sm text-[#111827] mt-1">{a.text}</p>
              </div>
            </div>
          ); })}
        </div>
      )}
    </div>
  );
}


export default function MemberDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tab: tabParam } = useParams();
  const idToSlug = (id) => (id || "accueil").replace(/_/g, "-");
  const slugToId = (s) => { const id = s ? s.replace(/-/g, "_") : "accueil"; return VALID_TABS.has(id) ? id : "accueil"; };
  const [profile, setProfile] = useState(null); // {user, member}
  const [listings, setListings] = useState([]);
  const [tab, setTabState] = useState(() => slugToId(tabParam));
  const setTab = useCallback((id) => {
    setTabState(id);
    nav(id === "accueil" ? "/mon-compte" : `/mon-compte/${idToSlug(id)}`);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }, [nav]);
  useEffect(() => { const id = slugToId(tabParam); setTabState((cur) => (cur === id ? cur : id)); }, [tabParam]);
  const [editing, setEditing] = useState(null); // listing or {} for new
  const [loaded, setLoaded] = useState(false);
  const [superviseAlert, setSuperviseAlert] = useState(0);
  const [createChoice, setCreateChoice] = useState(false);
  const [comandat, setComandat] = useState(null); // fiche de collaboration co-mandat
  const [comandatSplit, setComandatSplit] = useState({ owner_pct: 75, partner_pct: 25 });
  const [comandatRate, setComandatRate] = useState("");
  const [comandatSaved, setComandatSaved] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [creditConfig, setCreditConfig] = useState(null);
  const [publishTarget, setPublishTarget] = useState(null);  const [previewTarget, setPreviewTarget] = useState(null);
  const [stagingTarget, setStagingTarget] = useState(null);
  const [tierTarget, setTierTarget] = useState(null);
  const [boostTarget, setBoostTarget] = useState(null);
  const [buyModal, setBuyModal] = useState(null); // null | {slug, tier} | {}
  const [contentTarget, setContentTarget] = useState(null); // {slug, title}
  const [listingView, setListingView] = useState("mine"); // mine | network
  const [networkListings, setNetworkListings] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [genJobId, setGenJobId] = useState(null);
  const [metaAuto, setMetaAuto] = useState(false);
  const [wizardSlug, setWizardSlug] = useState(null);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImportingListing] = useState(false);
  const [importMsg, setImportMsg] = useState("");
  const [importStep, setImportStep] = useState(0);
  const [importLeft, setImportLeft] = useState(0);
  const [importPreview, setImportPreview] = useState(null);
  const [importSel, setImportSel] = useState({});
  const [importConfirming, setImportConfirming] = useState(false);
  const IMPORT_STEPS = [
    "Connexion à l'annonce…",
    "Lecture du titre et du prix…",
    "Analyse des caractéristiques (surface, pièces, DPE)…",
    "Récupération de la description…",
    "Téléchargement des photos en haute définition…",
    "Structuration des données…",
    "Finalisation, presque terminé…",
  ];
  useEffect(() => {
    if (!importing) { setImportStep(0); setImportLeft(0); return; }
    const EST = 30; // estimation en secondes
    setImportStep(0); setImportLeft(EST);
    const started = Date.now();
    const t = setInterval(() => {
      const elapsed = (Date.now() - started) / 1000;
      setImportLeft(Math.max(1, Math.ceil(EST - elapsed)));
      setImportStep(Math.min(IMPORT_STEPS.length - 1, Math.floor(elapsed / (EST / IMPORT_STEPS.length))));
    }, 900);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importing]);
  const doImportUrl = async () => {
    if (!importUrl.trim().startsWith("http")) { setImportMsg("Collez une URL valide (https://…)"); return; }
    setImportingListing(true); setImportMsg("");
    try {
      const res = await authApi.importUrlStart(importUrl.trim());
      setImportOpen(false); setImportUrl(""); setImportMsg("");
      setGenJobId(res.job_id);
    } catch (e) {
      setImportMsg(e.detail?.message || e.message || "Import impossible pour ce lien.");
    }
    setImportingListing(false);
  };
  // Reprise : si un import est déjà en cours côté serveur, on réaffiche l'écran de génération.
  useEffect(() => {
    authApi.importJobActive().then((r) => { if (r?.job?.id) setGenJobId(r.job.id); }).catch(() => {});
  }, []);

  const confirmImport = async () => {
    if (!importPreview) return;
    setImportConfirming(true); setImportMsg("");
    try {
      const imgs = (importPreview.images || []).filter((_, i) => importSel[i]);
      const srcAll = importPreview.source_photos || [];
      const srcSel = srcAll.filter((_, i) => importSel[i]);
      const payload = { ...importPreview, images: imgs, source_photos: srcSel, cover: imgs[0] || null };
      delete payload.debug; delete payload.warning; delete payload.images_found;
      const res = await authApi.importUrlConfirm(payload);
      setImportOpen(false); setImportUrl(""); setImportPreview(null); setImportSel({}); setImportMsg("");
      await load();
      if (res.slug) setWizardSlug(res.slug);
    } catch (e) {
      setImportMsg(e.detail?.message || e.message || "Création impossible.");
    }
    setImportConfirming(false);
  };
  const importSelCount = Object.values(importSel).filter(Boolean).length;
  const resetImport = () => { setImportOpen(false); setImportPreview(null); setImportSel({}); setImportMsg(""); setImportUrl(""); };

  const completeStaging = async (l) => {
    const cur = (l.staging || []).filter((s) => s && s.after).length;
    const need = Math.max(1, 6 - cur);
    setListings((prev) => prev.map((x) => x.slug === l.slug ? { ...x, generation_status: "processing" } : x));
    try {
      await authApi.autoStaging(l.slug, { style: "auto", max: need });
    } catch (e) {
      setListings((prev) => prev.map((x) => x.slug === l.slug ? { ...x, generation_status: "" } : x));
      window.alert(e.detail || "Génération impossible (plafond du niveau atteint ?).");
    }
  };
  useEffect(() => {
    if (tab === "listings_network") setListingView("network");
    else if (tab === "listings") setListingView("mine");
  }, [tab]);
  useEffect(() => {
    if ((tab === "listings" || tab === "listings_network") && listingView === "network" && networkListings === null) {
      authApi.netSharedListings().then((d) => setNetworkListings(d || [])).catch(() => setNetworkListings([]));
    }
  }, [tab, listingView, networkListings]);

  const load = useCallback(async () => {
    try {
      const p = await authApi.myProfile();
      setProfile(p);
      if (p.member) {
        try { setListings(await authApi.myListings()); } catch (_) {}
        try { setWallet(await authApi.wallet()); } catch (_) {}
        try { const ms = await authApi.metaGetSettings(); setMetaAuto(!!ms.auto_publish); } catch (_) {}
      }
    } catch (_) { setProfile({ user: null, member: null }); }
    try { setCreditConfig(await authApi.creditsConfig()); } catch (_) {}
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (user === null) return; // still checking auth
    if (user === false) { nav("/connexion", { replace: true }); return; }
    load();
  }, [user, nav, load]);

  // Ouvre automatiquement l'éditeur sur ?edit=slug (ex. retour depuis la Visite IA)
  // — UNE SEULE FOIS : on retire le paramètre d'URL après ouverture pour éviter la réouverture
  // du pop-up à chaque re-render (ex. après validation qui recharge les annonces).
  const editParamHandled = useRef(false);
  useEffect(() => {
    const editSlug = searchParams.get("edit");
    if (loaded && editSlug && listings.length && !editParamHandled.current) {
      const l = listings.find((x) => x.slug === editSlug);
      if (l) {
        editParamHandled.current = true;
        setTab("listings"); setEditing(l);
        setSearchParams((prev) => { const p = new URLSearchParams(prev); p.delete("edit"); return p; }, { replace: true });
      }
    }
  }, [loaded, listings, searchParams, setSearchParams]);

  // Parcours « Proposer un bien » : ?wizard=slug ouvre directement l'Assistant de publication.
  const wizardParamHandled = useRef(false);
  useEffect(() => {
    const w = searchParams.get("wizard");
    if (loaded && w && !wizardParamHandled.current) {
      wizardParamHandled.current = true;
      setTab("listings");
      setWizardSlug(w);
    }
  }, [loaded, searchParams]);

  // Lien direct depuis un e-mail : ?tab=evolution ouvre le panneau « Évolution » dans Mon compte.
  useEffect(() => {
    if (loaded && searchParams.get("tab") === "evolution") setTab("evolution");
  }, [loaded, searchParams]);

  // Rafraîchit les annonces tant que des home stagings offerts se génèrent (badge live).
  const anyStagingProcessing = listings.some((l) => l.generation_status === "processing");
  useEffect(() => {
    if (!anyStagingProcessing) return;
    const t = setInterval(() => { authApi.myListings().then(setListings).catch(() => {}); }, 8000);
    return () => clearInterval(t);
  }, [anyStagingProcessing]);

  // Les admins/super_admins restent sur leur espace « Mon compte » (section Administration intégrée).
  // Ils n'accèdent au back-office noir /admin que via le lien « Back-Office complet ».

  // Phase C — pop-up de supervision à la connexion admin (événements non lus).
  useEffect(() => {
    if (!loaded) return;
    if (user?.role !== "admin" && user?.role !== "super_admin") return;
    adminApi.eventsUnread().then((d) => setSuperviseAlert(d?.unread || 0)).catch(() => {});
  }, [loaded, user]);

  // Phase B — fiche de collaboration co-mandat ouverte depuis l'e-mail (?comandat=token).
  useEffect(() => {
    if (!loaded) return;
    const t = searchParams.get("comandat");
    if (!t) return;
    fetch(`${API}/comandat/${t}/details`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((d) => { setComandat({ token: t, ...d }); setComandatSplit(d.split || { owner_pct: 75, partner_pct: 25 }); setComandatRate(d.commission_rate ? String(d.commission_rate) : ""); })
      .catch(() => {});
  }, [loaded, searchParams]);

  const saveComandatSplit = async () => {
    try {
      await fetch(`${API}/comandat/${comandat.token}/split`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...comandatSplit, commission_rate: comandatRate ? Number(comandatRate) : null }),
      });
      setComandatSaved(true);
      setTimeout(() => setComandatSaved(false), 2500);
    } catch (_) {}
  };

  const signComandat = async () => {
    try {
      // On enregistre d'abord le partage puis la signature en 1 clic, puis on télécharge l'accord PDF.
      await fetch(`${API}/comandat/${comandat.token}/split`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...comandatSplit, commission_rate: comandatRate ? Number(comandatRate) : null }),
      });
      await fetch(`${API}/comandat/${comandat.token}/sign`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role: "partner", name: m?.display_name || user?.name || "" }),
      });
      window.open(`${API}/comandat/${comandat.token}/agreement.pdf`, "_blank");
      setComandatSaved(true);
      setTimeout(() => setComandatSaved(false), 2500);
    } catch (_) {}
  };

  const doDelete = async (slug) => {
    if (!window.confirm("Supprimer définitivement cette annonce ?")) return;
    try { await authApi.deleteListing(slug); setListings((l) => l.filter((x) => x.slug !== slug)); } catch (_) {}
  };  const togglePublish = async (l) => {
    try { const u = await authApi.updateListing(l.slug, { published: !l.published }); setListings((ls) => ls.map((x) => x.slug === l.slug ? u : x)); } catch (_) {}
  };

  if (user === null || !loaded) {
    return (
      <PlatformLayout searchDock={false} solidHeader>
        <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="text-sm text-[#C19A6B] uppercase tracking-[0.25em] animate-pulse">Chargement…</div></div>
      </PlatformLayout>
    );
  }

  // Utilisateur connecté sans profil conseiller (ex. via Google) → complétion
  if (profile && !profile.member) {
    return (
      <PlatformLayout searchDock={false} solidHeader>
        <div className="min-h-screen bg-[#050505] pt-28 pb-20 px-6"><CompleteProfile onDone={load} /></div>
      </PlatformLayout>
    );
  }

  const m = profile.member;
  const sm = STATUS_META[m.status] || STATUS_META.pending;
  const StatusIcon = sm.icon;

  const role = (user?.role === "admin" || user?.role === "super_admin") ? "admin" : "agent";

  return (
    <BOShell m={m} user={user} role={role} tab={tab} setTab={setTab} wallet={wallet} onBuy={() => setBuyModal({})} onLogout={async () => { await logout(); nav("/"); }} nav={nav}>
          {/* Phase C — Pop-up supervision (événements non lus) à la connexion admin */}
          {superviseAlert > 0 && (
            <div className="mb-6 flex items-start gap-3 border border-[#C19A6B]/40 bg-[#C19A6B]/10 rounded-xl px-5 py-4" data-testid="supervision-alert">
              <ShieldAlert className="w-5 h-5 mt-0.5 text-[#C19A6B] shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-white font-medium">{superviseAlert} événement{superviseAlert > 1 ? "s" : ""} de supervision non lu{superviseAlert > 1 ? "s" : ""}</p>
                <p className="text-xs text-zinc-400">Inscriptions, paiements, incidents, erreurs IA… à consulter dans le centre de supervision.</p>
              </div>
              <button data-testid="supervision-alert-open" onClick={() => { setTab("admin_supervision"); setSuperviseAlert(0); }} className="shrink-0 text-xs font-semibold uppercase tracking-wider bg-[#C19A6B] text-white px-4 py-2 rounded-md hover:bg-[#d1af84] transition-colors">Voir</button>
              <button data-testid="supervision-alert-dismiss" onClick={() => setSuperviseAlert(0)} className="shrink-0 text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
          )}

          {/* Phase B — Fiche de collaboration co-mandat (honoraires ajustables + contact partenaire) */}
          {comandat && (
            <div className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="comandat-modal" onClick={() => setComandat(null)}>
              <div className="w-full max-w-lg bg-[#141418] border border-white/12 rounded-[12px] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
                  <div className="flex items-center gap-2"><Handshake className="w-5 h-5 text-[#C19A6B]" /><h3 className="text-lg font-medium text-white">Co-mandat accepté</h3></div>
                  <button onClick={() => setComandat(null)} data-testid="comandat-close" className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-6 space-y-5">
                  {/* Bien concerné */}
                  <div className="flex gap-3 items-center">
                    {comandat.listing?.cover && <img src={comandat.listing.cover} alt="" className="w-16 h-16 object-cover rounded-lg shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-white font-medium truncate">{comandat.listing?.title || comandat.listing?.slug}</p>
                      <p className="text-xs text-zinc-400">{comandat.listing?.city}{comandat.listing?.price ? ` · ${Number(comandat.listing.price).toLocaleString("fr-FR")} €` : ""}</p>
                    </div>
                  </div>
                  {/* Contact du titulaire du mandat */}
                  <div className="rounded-lg border border-white/10 bg-white/[0.03] p-4">
                    <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Titulaire du mandat</p>
                    <p className="text-white text-sm font-medium">{comandat.owner?.name}{comandat.owner?.company ? ` · ${comandat.owner.company}` : ""}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-[#C19A6B]">
                      {comandat.owner?.phone && <a href={`tel:${comandat.owner.phone}`} className="inline-flex items-center gap-1 hover:underline"><Phone className="w-3 h-3" /> {comandat.owner.phone}</a>}
                      {comandat.owner?.email && <a href={`mailto:${comandat.owner.email}`} className="inline-flex items-center gap-1 hover:underline"><Mail className="w-3 h-3" /> {comandat.owner.email}</a>}
                    </div>
                  </div>
                  {/* Partage des honoraires */}
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Partage des honoraires</p>
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <input type="range" min="0" max="100" step="5" value={comandatSplit.owner_pct} data-testid="comandat-split-range"
                          onChange={(e) => { const o = Number(e.target.value); setComandatSplit({ owner_pct: o, partner_pct: 100 - o }); }}
                          className="w-full accent-[#C19A6B]" />
                        <div className="flex justify-between text-xs text-zinc-400 mt-1">
                          <span>Titulaire <b className="text-white">{comandatSplit.owner_pct}%</b></span>
                          <span>Vous <b className="text-[#C19A6B]">{comandatSplit.partner_pct}%</b></span>
                        </div>
                      </div>
                    </div>
                    <p className="text-[10px] text-zinc-500 mt-2">Par défaut 75 / 25. Ajustez puis validez — le titulaire du mandat est informé de votre proposition.</p>
                  </div>
                  {/* Commission d'agence (indiquée par le titulaire) → rétrocommissions calculées automatiquement */}
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Taux de commission d'agence (%)</p>
                    <div className="flex items-center gap-2">
                      <input type="number" min="0" max="15" step="0.5" value={comandatRate} data-testid="comandat-rate-input"
                        onChange={(e) => setComandatRate(e.target.value)} placeholder="ex. 5"
                        className="w-28 bg-white/[0.04] border border-white/12 rounded-[5px] px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#C19A6B]/60" />
                      <span className="text-xs text-zinc-500">du prix de vente{comandat.listing?.price ? ` (${Number(comandat.listing.price).toLocaleString("fr-FR")} €)` : ""}</span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {[4, 5, 6, 7].map((r) => (
                        <button key={r} type="button" onClick={() => setComandatRate(String(r))} data-testid={`comandat-rate-${r}`}
                          className={`text-[11px] px-2 py-1 rounded-[5px] border transition-colors ${Number(comandatRate) === r ? "bg-[#C19A6B] text-white border-[#C19A6B]" : "border-white/15 text-zinc-300 hover:border-[#C19A6B]/50"}`}>{r}%</button>
                      ))}
                    </div>
                    {comandatRate && Number(comandatRate) > 0 && comandat.listing?.price ? (() => {
                      const fees = comandat.listing.price * Number(comandatRate) / 100;
                      const off = Math.round(fees * 0.02);
                      const net = Math.round(fees - off);
                      const ownerA = Math.round(net * comandatSplit.owner_pct / 100);
                      const partnerA = Math.round(net * comandatSplit.partner_pct / 100);
                      const fmt = (v) => Math.round(v).toLocaleString("fr-FR") + " €";
                      return (
                        <div className="mt-2 rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-xs space-y-1" data-testid="comandat-fees-preview">
                          <div className="flex items-center justify-between"><span className="text-zinc-400">Commission d'agence</span><span className="text-white font-semibold">{fmt(fees)}</span></div>
                          <div className="flex items-center justify-between"><span className="text-zinc-400">Part OFF MARKETTE (2%)</span><span className="text-zinc-300">− {fmt(off)}</span></div>
                          <div className="flex items-center justify-between border-t border-white/10 pt-1"><span className="text-zinc-400">Net à partager</span><span className="text-white font-semibold">{fmt(net)}</span></div>
                          <div className="flex items-center justify-between"><span className="text-zinc-400">Part titulaire ({comandatSplit.owner_pct}%)</span><span className="text-white">{fmt(ownerA)}</span></div>
                          <div className="flex items-center justify-between"><span className="text-zinc-400">Votre part ({comandatSplit.partner_pct}%)</span><span className="text-[#C19A6B] font-semibold">{fmt(partnerA)}</span></div>
                        </div>
                      );
                    })() : (
                      <p className="text-[10px] text-zinc-500 mt-2">Le titulaire indique le taux de commission (4 à 7% ou autre). OFF MARKETTE conserve 2% (gestion digitalisée &amp; apport d'affaire), le reste est partagé.</p>
                    )}
                  </div>
                  <button onClick={saveComandatSplit} data-testid="comandat-save" className="w-full bg-[#C19A6B] hover:bg-[#d1af84] text-white font-semibold uppercase tracking-wider py-3 rounded-[5px] transition-colors flex items-center justify-center gap-2">
                    {comandatSaved ? <><Check className="w-4 h-4" /> Proposition enregistrée</> : <>Valider le partage {comandatSplit.owner_pct}/{comandatSplit.partner_pct}</>}
                  </button>
                  <button onClick={signComandat} data-testid="comandat-sign" className="w-full bg-transparent border border-[#C19A6B] text-[#C19A6B] hover:bg-[#C19A6B]/10 font-semibold uppercase tracking-wider py-3 rounded-[5px] transition-colors flex items-center justify-center gap-2">
                    <FileSignature className="w-4 h-4" /> Signer & télécharger l'accord PDF
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Bandeau statut — masqué si compte validé */}
          {m.status === "approved" ? null : (
            <div className={`border ${sm.bg} rounded-xl px-5 py-4 flex items-start gap-3 mb-6`} data-testid="status-banner">
              <StatusIcon className={`w-5 h-5 mt-0.5 ${sm.color}`} />
              <div>
                <p className={`text-sm font-medium ${sm.color}`}>{sm.label}</p>
                <p className="text-sm text-zinc-400 mt-0.5">{sm.text}</p>
              </div>
            </div>
          )}

          {(tab === "listings" || tab === "listings_network") && (
            <div>
              <div className="mb-6">
                <p className="text-sm text-zinc-400 mb-3">{listings.length} annonce{listings.length > 1 ? "s" : ""}</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => setCreateChoice(true)} data-testid="create-listing" className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white font-medium uppercase tracking-wider text-sm px-5 py-2.5 rounded-lg transition-colors"><Plus className="w-4 h-4" /> Créer une annonce</button>
                </div>
              </div>
              {createChoice && (
                <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="create-choice-modal" onClick={() => setCreateChoice(false)}>
                  <div className="w-full max-w-3xl bg-[#141418] border border-white/12 rounded-[12px] p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="calli text-2xl sm:text-3xl text-[#C19A6B]">Créer une annonce</h3>
                      <button onClick={() => setCreateChoice(false)} data-testid="create-choice-close" className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
                    </div>
                    <p className="text-sm text-zinc-400 mb-6">Choisissez votre méthode — toutes aboutissent au même Assistant de publication.</p>
                    <div className="grid sm:grid-cols-3 gap-4">
                      {[
                        { id: "visite", testid: "create-choice-visite", icon: Sparkles, title: "Visite IA", desc: "Photographiez et dictez : l'IA rédige tout, pièce par pièce.", badge: "Le plus rapide", onClick: () => nav("/visite-ia") },
                        { id: "lien", testid: "create-choice-lien", icon: Link2, title: "Par lien", desc: "Collez l'URL d'une annonce existante, on l'importe automatiquement.", onClick: () => { setCreateChoice(false); setImportOpen(true); setImportMsg(""); } },
                        { id: "form", testid: "create-choice-form", icon: PenLine, title: "Par formulaire", desc: "Saisissez les informations du bien à la main, étape par étape.", onClick: () => { setCreateChoice(false); setEditing({}); } },
                      ].map((c) => (
                        <button key={c.id} data-testid={c.testid} onClick={c.onClick} className="group text-left rounded-[10px] border border-white/12 hover:border-[#C19A6B] bg-white/[0.03] hover:bg-[#C19A6B]/5 p-5 transition-colors relative">
                          {c.badge && <span className="absolute top-3 right-3 text-[9px] uppercase tracking-wider bg-[#C19A6B] text-white px-2 py-0.5 rounded-full">{c.badge}</span>}
                          <span className="inline-flex w-11 h-11 items-center justify-center rounded-full bg-[#C19A6B]/15 text-[#C19A6B] mb-3"><c.icon className="w-5 h-5" /></span>
                          <p className="text-white font-medium text-base mb-1">{c.title}</p>
                          <p className="text-xs text-zinc-400 leading-relaxed">{c.desc}</p>
                          <span className="mt-3 inline-flex items-center gap-1 text-xs text-[#C19A6B] font-medium group-hover:gap-2 transition-all">Commencer <ChevronRight className="w-3.5 h-3.5" /></span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {/* Fusion BO — séparation Mes annonces / Réseau (transparent côté client) */}
              <div className="inline-flex items-center gap-1 mb-6 border border-white/10 rounded-full p-1 bg-white/5" data-testid="listing-view-toggle">
                <button onClick={() => setTab("listings")} data-testid="listing-view-mine" className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${listingView === "mine" ? "bg-[#C19A6B] text-white" : "text-zinc-300 hover:text-white"}`}><Home className="w-3.5 h-3.5" /> Mes annonces ({listings.length})</button>
                <button onClick={() => setTab("listings_network")} data-testid="listing-view-network" className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${listingView === "network" ? "bg-[#C19A6B] text-white" : "text-zinc-300 hover:text-white"}`}><Users className="w-3.5 h-3.5" /> {user?.role === "admin" || user?.role === "super_admin" ? "Réseau (global)" : "Mon réseau"}{networkListings ? ` (${networkListings.length})` : ""}</button>
              </div>
              {listingView === "network" ? (
                networkListings === null ? (
                  <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" /></div>
                ) : networkListings.length === 0 ? (
                  <div className="text-center py-16 border border-dashed border-white/10" data-testid="network-listings-empty">
                    <Users className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
                    <p className="text-zinc-400">Aucune annonce partagée par votre réseau pour le moment.</p>
                    <button onClick={() => setTab("network")} className="mt-4 text-[#C19A6B] hover:underline text-sm">Gérer mon réseau de partenaires</button>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="network-listings">
                    {networkListings.map((l) => (
                      <div key={l.slug} className="bg-[#0d0d10] border border-white/10 overflow-hidden group" data-testid={`network-listing-${l.slug}`}>
                        <div className="relative aspect-[4/3] bg-black/40">
                          {(l.cover || l.images?.[0]) && <img src={l.cover || l.images[0]} alt={l.title} className="w-full h-full object-cover" />}
                          {l.owner && (
                            <span className="absolute top-2 left-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2.5 py-1 bg-[#C19A6B] text-white border border-[#C19A6B] shadow-md" data-testid={`network-owner-badge-${l.slug}`}>
                              {l.owner.photo && <img src={l.owner.photo} alt="" className="w-4 h-4 rounded-full object-cover" />}
                              {l.owner.display_name || l.owner.company || "Réseau"}
                            </span>
                          )}
                        </div>
                        <div className="p-4 space-y-2">
                          <h3 className="text-white font-light line-clamp-1">{l.title}</h3>
                          <p className="text-[#C19A6B] text-sm">{fmtPrice(l.price)}</p>
                          <p className="text-xs text-zinc-500 line-clamp-1">{l.city}{l.surface ? ` · ${l.surface} m²` : ""}</p>
                          <a href={`/bien/${l.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`network-view-${l.slug}`} className="w-full inline-flex items-center justify-center gap-1.5 border border-white/15 text-zinc-200 hover:border-[#C19A6B] hover:text-[#C19A6B] text-xs font-medium uppercase tracking-wider py-2 transition-colors mt-1"><ExternalLink className="w-3.5 h-3.5" /> Voir l'annonce</a>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : listings.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-white/10">
                  <Home className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-400">Aucune annonce pour le moment.</p>
                  <button onClick={() => setEditing({})} className="mt-4 text-[#C19A6B] hover:underline text-sm">Créer ma première annonce</button>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="my-listings">
                  {listings.map((l) => (
                    <div key={l.slug} className="bg-[#0d0d10] border border-white/10 overflow-hidden group">
                      <div className="relative aspect-[4/3] bg-black/40">
                        {(l.cover || l.images?.[0]) && <img src={l.cover || l.images[0]} alt={l.title} className="w-full h-full object-cover" />}
                        <span className={`absolute top-2 left-2 text-[10px] uppercase tracking-wider px-2 py-1 ${l.published !== false ? "bg-green-500/80 text-black" : "bg-amber-500/90 text-black"}`}>{l.published !== false ? "En ligne" : "Prévisualisation"}</span>
                        {(() => {
                          const boosted = l.boost && l.boost.expires_at && new Date(l.boost.expires_at) > new Date();
                          if (boosted) return <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 bg-[#C19A6B] text-white font-bold" data-testid={`boost-badge-${l.slug}`}><Rocket className="w-3 h-3" /> Boosté</span>;
                          if (l.tier && l.published !== false) return <span className="absolute top-2 right-2 text-[10px] uppercase tracking-wider px-2 py-1 bg-[#C19A6B]/90 text-white">{l.tier}</span>;
                          return null;
                        })()}
                        {(() => {
                          const vm = l.visibility_mode || "national";
                          const meta = vm === "private"
                            ? { label: "Personnel", cls: "bg-zinc-800/90 text-zinc-100 border border-white/20", Icon: EyeOff }
                            : vm === "network"
                              ? { label: l.network_also_national ? "Réseau + National" : "Réseau", cls: "bg-[#3b4a63]/90 text-white", Icon: Users }
                              : { label: "National", cls: "bg-sky-700/85 text-white", Icon: Globe };
                          const Ic = meta.Icon;
                          return (
                            <span className={`absolute bottom-2 right-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 ${meta.cls}`} data-testid={`vis-badge-${l.slug}`}>
                              <Ic className="w-3 h-3" /> {meta.label}
                            </span>
                          );
                        })()}
                        {l.generation_status === "processing" && (
                          <span className="absolute bottom-2 left-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-1 bg-[#0d0d10]/85 text-[#C19A6B] border border-[#C19A6B]/40" data-testid={`staging-badge-${l.slug}`}>
                            <Loader2 className="w-3 h-3 animate-spin" /> 2 mises en scène IA offertes
                          </span>
                        )}
                      </div>
                      <div className="p-4 space-y-2">
                        <h3 className="text-white font-light line-clamp-1">{l.title}</h3>
                        <p className="text-[#C19A6B] text-sm">{fmtPrice(l.price)}</p>
                        <p className="text-xs text-zinc-500 line-clamp-1">{l.city}{l.surface ? ` · ${l.surface} m²` : ""}</p>
                        {metaAuto && l.published !== false && ((l.staging || []).filter((s) => s && s.after).length) < 6 && (
                          <div className="bg-amber-500/10 border border-amber-500/30 rounded-md px-2.5 py-2 space-y-1.5" data-testid={`autopublish-alert-${l.slug}`}>
                            <div className="flex items-start gap-2">
                              <Info className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                              <p className="text-[11px] text-amber-200 leading-snug">Non publiée sur les réseaux : {(l.staging || []).filter((s) => s && s.after).length}/6 photos home-stagées.</p>
                            </div>
                            {l.generation_status === "processing" ? (
                              <p className="text-[11px] text-[#C19A6B] flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Génération des mises en scène en cours…</p>
                            ) : (
                              <div className="flex flex-col gap-1">
                                <button onClick={() => completeStaging(l)} data-testid={`autopublish-generate-${l.slug}`} className="w-full inline-flex items-center justify-center gap-1.5 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-[11px] font-semibold uppercase tracking-wider py-1.5 rounded transition-colors">
                                  <Wand2 className="w-3.5 h-3.5" /> Générer les {Math.max(1, 6 - (l.staging || []).filter((s) => s && s.after).length)} photos manquantes
                                </button>
                                <button onClick={() => setStagingTarget(l)} data-testid={`autopublish-complete-${l.slug}`} className="text-[11px] text-[#C19A6B] hover:underline">Ou choisir moi-même les styles →</button>
                              </div>
                            )}
                          </div>
                        )}
                        {l.published === false && (
                          <>
                            <button onClick={() => setPreviewTarget(l)} data-testid={`preview-${l.slug}`} className="w-full inline-flex items-center justify-center gap-1.5 border border-[#C19A6B]/50 text-[#C19A6B] hover:bg-[#C19A6B]/10 text-xs font-medium uppercase tracking-wider py-2 transition-colors mt-1">
                              <Sparkles className="w-3.5 h-3.5" /> {l.preview_generated ? "Regénérer l'aperçu IA" : "Aperçu IA gratuit"}
                            </button>
                            <button onClick={() => setPublishTarget(l)} data-testid={`publish-${l.slug}`} className="w-full inline-flex items-center justify-center gap-1.5 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-xs font-medium uppercase tracking-wider py-2 transition-colors">
                              <Rocket className="w-3.5 h-3.5" /> Publier mon annonce
                            </button>
                          </>
                        )}
                        <button onClick={() => setStagingTarget(l)} data-testid={`staging-btn-${l.slug}`} className="w-full inline-flex items-center justify-center gap-1.5 border border-white/15 text-zinc-200 hover:border-[#C19A6B] hover:text-[#C19A6B] text-xs font-medium uppercase tracking-wider py-2 transition-colors">
                          <Wand2 className="w-3.5 h-3.5" /> Home Staging IA
                        </button>
                        {l.published !== false && (
                          <button onClick={() => setTierTarget(l)} data-testid={`change-tier-btn-${l.slug}`} className="w-full inline-flex items-center justify-center gap-1.5 border border-[#C19A6B]/40 text-[#C19A6B] hover:bg-[#C19A6B]/10 text-xs font-medium uppercase tracking-wider py-2 transition-colors">
                            <Sparkles className="w-3.5 h-3.5" /> Changer de formule{l.tier ? ` (${(creditConfig?.tiers?.[l.tier]?.label) || l.tier})` : ""}
                          </button>
                        )}
                        {l.published !== false && (
                          <button onClick={() => setBoostTarget(l)} data-testid={`boost-btn-${l.slug}`} className="w-full inline-flex items-center justify-center gap-1.5 border border-[#C19A6B] bg-[#C19A6B]/10 text-[#C19A6B] hover:bg-[#C19A6B]/20 text-xs font-semibold uppercase tracking-wider py-2 transition-colors">
                            <Rocket className="w-3.5 h-3.5" /> {(l.boost && l.boost.expires_at && new Date(l.boost.expires_at) > new Date()) ? "Prolonger le boost" : "Booster mon bien"}
                          </button>
                        )}
                        {l.published !== false ? (
                          <a href={`/bien/${l.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`view-listing-${l.slug}`} className="w-full inline-flex items-center justify-center gap-1.5 border border-white/15 text-zinc-200 hover:border-[#C19A6B] hover:text-[#C19A6B] text-xs font-medium uppercase tracking-wider py-2 transition-colors">
                            <Eye className="w-3.5 h-3.5" /> Voir l'annonce
                          </a>
                        ) : (
                          <button onClick={() => setPreviewTarget(l)} data-testid={`view-listing-${l.slug}`} className="w-full inline-flex items-center justify-center gap-1.5 border border-white/15 text-zinc-200 hover:border-[#C19A6B] hover:text-[#C19A6B] text-xs font-medium uppercase tracking-wider py-2 transition-colors">
                            <Eye className="w-3.5 h-3.5" /> Voir l'annonce (aperçu)
                          </button>
                        )}
                        <div className="flex items-center gap-2 pt-1">
                          <button onClick={() => setEditing(l)} data-testid={`edit-${l.slug}`} className="flex-1 inline-flex items-center justify-center gap-1.5 border border-white/15 text-zinc-200 text-xs uppercase tracking-wider py-2 hover:border-[#C19A6B] hover:text-[#C19A6B] transition-colors"><Pencil className="w-3.5 h-3.5" /> Modifier</button>
                          {l.published !== false && <button onClick={() => togglePublish(l)} data-testid={`toggle-${l.slug}`} title="Mettre hors ligne" className="p-2 border border-white/15 text-zinc-300 hover:text-[#C19A6B] hover:border-[#C19A6B] transition-colors"><EyeOff className="w-4 h-4" /></button>}
                          <button onClick={() => doDelete(l.slug)} data-testid={`delete-${l.slug}`} className="p-2 border border-white/15 text-zinc-300 hover:text-red-400 hover:border-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        {l.published !== false && (
                          <div className="pt-1 flex flex-wrap items-center gap-2" data-testid={`member-share-${l.slug}`}>
                            <ShareButtons slug={l.slug} title={l.title} price={fmtPrice(l.price)} city={l.city} variant="dark" />
                            {l.share_count > 0 && (
                              <span data-testid={`share-count-${l.slug}`} className="inline-flex items-center gap-1 text-[11px] font-medium text-[#A88358]">
                                <Share2 className="w-3.5 h-3.5" /> {l.share_count} partage{l.share_count > 1 ? "s" : ""}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "commissions" && <CommissionsTab />}
          {tab === "acquereurs" && <AcquereursTab />}
          {tab === "matching" && <MatchingTab />}
          {tab === "matching_bien" && <MatchingBienAcqTab />}
          {tab === "prospection" && <ProspectionTerrain />}
          {tab === "match_annonces" && (
            <div className="space-y-4" data-testid="match-annonces">
              <div className="rounded-xl border border-[#C19A6B]/30 bg-[#FBF8F3] p-4 flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-[#C19A6B] mt-0.5 shrink-0" />
                <p className="text-sm text-[#374151] leading-relaxed">Ces professionnels ont <b>plusieurs biens</b> et des <b>acquéreurs intéressants</b> qui matchent avec les vôtres. Nous vous recommandons de les <b>ajouter à votre réseau</b> : la collaboration est réciproque et multiplie vos opportunités de vente.</p>
              </div>
              <MatchingTab />
            </div>
          )}
          {tab === "accueil" && <AuditBanner onOpen={() => setTab("audit")} />}
          {tab === "accueil" && <CrmHomeTab setTab={setTab} />}
          {tab === "evolution" && <EvolutionPanel />}
          {tab === "apporteur" && (m.account_type || "").trim().toLowerCase() === "apporteur d'affaires" && <ApporteurTab setTab={setTab} />}
          {tab === "filleuls" && <FilleulsTab code={m.referral_code} name={m.display_name} />}
          {tab === "connexions" && <SocialConnectionsTab />}
          {["social_reco", "social_published", "social_connect", "social_report", "social_ads"].includes(tab) && <SocialHubTab sub={tab} member={m} />}
          {tab === "opportunites" && <OpportunitiesTab />}
          {tab === "vendeurs" && <VendeursTab />}
          {tab === "comandat" && <ComandatTab />}
          {tab === "audit" && <AuditTab role={user?.role} onEditGoto={() => setTab("listings")} />}
          {tab === "partenaires" && <SoonTab title="Partenaires locaux" desc="Artisans, courtiers, diagnostiqueurs, décorateurs… affichés automatiquement sur votre site personnalisé. Bientôt disponible." icon={Handshake} />}
          {tab === "documents" && <SoonTab title="Mes documents" desc="Mandats, compromis, diagnostics et documents IA centralisés. Bientôt disponible." icon={FileText} />}
          {tab === "agenda" && <AgendaTab />}
          {tab === "rapports" && <RapportsTab />}
          {tab === "offers" && <OffersTab />}
          {tab === "stats" && <StatsTab onOpenTab={setTab} />}
          {tab === "activity" && <ActivityTab onOpenTab={setTab} />}
          {tab === "agent_decouvrir" && <DecouvrirAgentIA setTab={setTab} member={m} />}
          {tab === "agent_emails" && <AgentEmailsTab />}
          {tab === "agent_prospects" && <ProspectsCRM setTab={setTab} />}
          {tab === "agent_settings" && <AgentSettingsTab />}
          {tab === "network" && <NetworkTab />}
          {tab === "admin_listings" && (user?.role === "admin" || user?.role === "super_admin") && <AdminListingsPage embedded />}
          {tab === "admin_submissions" && (user?.role === "admin" || user?.role === "super_admin") && <AdminSubmissionsPage embedded />}
          {tab === "admin_affiliations" && (user?.role === "admin" || user?.role === "super_admin") && <AffiliationsPage embedded />}
          {tab === "admin_supervision" && (user?.role === "admin" || user?.role === "super_admin") && <AdminSupervisionPage embedded />}
          {tab === "admin_access" && (user?.role === "admin" || user?.role === "super_admin") && <AdminAccessRequests />}
          {tab === "admin_acquereurs" && (user?.role === "admin" || user?.role === "super_admin") && <AdminAcquereursPage embedded />}
          {tab === "admin_offers" && (user?.role === "admin" || user?.role === "super_admin") && <AdminOffersPage embedded />}
          {tab === "admin_stats" && (user?.role === "admin" || user?.role === "super_admin") && <AdminStatsPage embedded />}
          {tab === "admin_roi" && (user?.role === "admin" || user?.role === "super_admin") && <AdminROIPage embedded />}
          {tab === "admin_finance" && (user?.role === "admin" || user?.role === "super_admin") && <AdminFinancePage embedded />}
          {tab === "admin_credits" && (user?.role === "admin" || user?.role === "super_admin") && <AdminCreditsPage embedded />}
          {tab === "admin_emails" && (user?.role === "admin" || user?.role === "super_admin") && <AdminEmailPreview />}
          {tab === "admin_agent" && (user?.role === "admin" || user?.role === "super_admin") && <AdminAgentPage embedded />}
          {tab === "site" && <SiteEditorTab member={m} />}
          {tab === "profile" && <ProfileTab member={m} onSaved={load} />}

      <AnimatePresence>
        {editing !== null && <ListingEditor listing={editing.slug ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} onCreated={(newSlug) => { setEditing(null); load(); if (newSlug) setWizardSlug(newSlug); }} />}
        {wizardSlug && <PublishWizard slug={wizardSlug} onClose={() => { setWizardSlug(null); load(); }} onEdit={(l) => setEditing(l)} onGoSocial={(s) => { setWizardSlug(null); load(); setTab("social_reco"); }} />}
        {genJobId && (
          <GenerationProgress
            jobId={genJobId}
            onView={(slug) => { setGenJobId(null); load(); if (slug && typeof window !== "undefined") window.open(`/bien/${slug}`, "_blank"); }}
            onEdit={(slug) => { setGenJobId(null); load(); if (slug) setWizardSlug(slug); }}
            onClose={() => { setGenJobId(null); load(); }}
          />
        )}
        {importOpen && (
          <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur flex items-center justify-center p-4" data-testid="import-modal" onClick={() => !importing && !importConfirming && resetImport()}>
            <div className={`bg-[#0d0d10] border border-white/10 w-full ${importPreview ? "max-w-3xl" : "max-w-lg"} p-6 space-y-4 max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-2"><Link2 className="w-6 h-6 text-[#C19A6B]" /><h3 className="calli text-2xl sm:text-3xl text-[#C19A6B] leading-tight">Importer une annonce depuis un lien</h3></div>

              {!importPreview && <>
              <p className="text-xs text-zinc-400">Collez l'URL d'une annonce (SAFTI, SeLoger, Bien'ici…). Nous analysons la page, récupérons les caractéristiques et la <b className="text-zinc-300">galerie complète du bien</b> (en excluant les biens similaires), puis vous validez les photos avant création.</p>
              <input autoFocus value={importUrl} onChange={(e) => setImportUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !importing && doImportUrl()} placeholder="https://www.safti.fr/annonces/..." disabled={importing} className="w-full bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#C19A6B] disabled:opacity-50" data-testid="import-url-input" />
              {importing && (
                <div className="space-y-3 py-1" data-testid="import-progress">
                  <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-[#C19A6B] transition-all duration-700 ease-out rounded-full" style={{ width: `${((importStep + 1) / IMPORT_STEPS.length) * 100}%` }} />
                  </div>
                  <div className="flex items-center gap-2 text-sm text-white">
                    <Loader2 className="w-4 h-4 animate-spin text-[#C19A6B]" />
                    <span data-testid="import-current-step">{IMPORT_STEPS[importStep]}</span>
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-zinc-400">
                    <span>Étape {importStep + 1} / {IMPORT_STEPS.length}</span>
                    <span data-testid="import-time-left">Temps restant estimé : ~{importLeft}s</span>
                  </div>
                  <p className="text-[11px] text-zinc-500 italic">Merci de patienter, nous récupérons tout automatiquement — ne fermez pas cette fenêtre.</p>
                </div>
              )}
              {importMsg && <p className="text-xs text-zinc-300" data-testid="import-msg">{importMsg}</p>}
              <div className="flex items-center gap-2 justify-end">
                <button onClick={resetImport} disabled={importing} className="text-sm text-zinc-400 hover:text-white px-3 py-2">Annuler</button>
                <button onClick={doImportUrl} disabled={importing} data-testid="import-url-submit" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 text-white font-semibold uppercase tracking-wider text-sm px-5 py-2.5 transition-colors">
                  {importing ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyse…</> : <><Link2 className="w-4 h-4" /> Analyser l'annonce</>}
                </button>
              </div>
              </>}

              {importPreview && (() => {
                const P = importPreview; const imgs = P.images || [];
                const dbg = P.debug;
                return (
                  <div className="space-y-4" data-testid="import-preview">
                    <div className="border border-white/10 bg-white/[0.03] p-3 rounded-[6px]">
                      <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Annonce détectée</p>
                      <p className="text-white text-sm font-medium" data-testid="import-preview-title">{P.title || "(titre à compléter)"}</p>
                      <p className="text-xs text-zinc-400 mt-0.5">
                        {[P.type, P.rooms ? `${P.rooms} pièces` : "", P.surface ? `${P.surface} m²` : "", [P.city, P.postal_code].filter(Boolean).join(" ")].filter(Boolean).join(" · ")}
                        {P.price ? ` · ${Number(P.price).toLocaleString("fr-FR")} €` : ""}
                      </p>
                    </div>

                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <p className="text-sm text-white" data-testid="import-photos-count"><b>{imgs.length}</b> photo{imgs.length > 1 ? "s" : ""} du bien détectée{imgs.length > 1 ? "s" : ""} · <span className="text-[#C19A6B]">{importSelCount} sélectionnée{importSelCount > 1 ? "s" : ""}</span></p>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setImportSel(Object.fromEntries(imgs.map((_, i) => [i, true])))} data-testid="import-select-all" className="text-[11px] border border-white/15 text-zinc-300 hover:bg-white/5 px-2.5 py-1">Tout sélectionner</button>
                        <button onClick={() => setImportSel({})} data-testid="import-select-none" className="text-[11px] border border-white/15 text-zinc-300 hover:bg-white/5 px-2.5 py-1">Tout désélectionner</button>
                      </div>
                    </div>

                    {imgs.length === 0 ? (
                      <p className="text-xs text-amber-300 border border-amber-500/30 bg-amber-500/[0.06] p-3">Aucune photo n'a pu être récupérée automatiquement. Vous pourrez en ajouter manuellement après création.</p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[42vh] overflow-y-auto pr-1" data-testid="import-photos-grid">
                        {imgs.map((u, i) => (
                          <button key={i} type="button" onClick={() => setImportSel((s) => ({ ...s, [i]: !s[i] }))} data-testid={`import-photo-${i}`}
                            className={`relative aspect-[4/3] overflow-hidden rounded-[6px] border-2 transition-all ${importSel[i] ? "border-[#C19A6B]" : "border-white/10 opacity-50"}`}>
                            <img src={u} alt={`Photo ${i + 1}`} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                            <span className={`absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center ${importSel[i] ? "bg-[#C19A6B] text-black" : "bg-black/60 text-white/70"}`}>{importSel[i] ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}</span>
                            {i === 0 && importSel[i] && <span className="absolute bottom-1 left-1 bg-[#C19A6B] text-black text-[8px] font-bold uppercase px-1">Couv.</span>}
                          </button>
                        ))}
                      </div>
                    )}

                    {dbg && (
                      <details className="border border-white/10 bg-black/30 rounded-[6px] p-3" data-testid="import-debug">
                        <summary className="text-[11px] uppercase tracking-wider text-[#C19A6B] cursor-pointer flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Débogage import (Super Admin)</summary>
                        <div className="text-[11px] text-zinc-400 mt-2 space-y-1">
                          <p>Moteur : <span className="text-white">{dbg.source}</span>{dbg.chosen_by ? ` · groupe choisi par ${dbg.chosen_by === "url_id" ? "ID d'URL" : "plus gros groupe"}` : ""}</p>
                          <p><span className="text-emerald-400">{dbg.retained}</span> photo(s) retenue(s) pour le bien{dbg.main_id ? ` (ID ${dbg.main_id})` : ""}.</p>
                          {dbg.ignored_similar_groups && Object.keys(dbg.ignored_similar_groups).length > 0 && (
                            <p><span className="text-red-400">{Object.values(dbg.ignored_similar_groups).reduce((a, b) => a + b, 0)}</span> photo(s) de <span className="text-red-400">{Object.keys(dbg.ignored_similar_groups).length}</span> bien(s) similaire(s) ignorée(s).</p>
                          )}
                        </div>
                      </details>
                    )}

                    {importMsg && <p className="text-xs text-amber-300" data-testid="import-preview-msg">{importMsg}</p>}
                    <div className="flex items-center gap-2 justify-between">
                      <button onClick={() => { setImportPreview(null); setImportMsg(""); }} className="text-sm text-zinc-400 hover:text-white px-3 py-2 inline-flex items-center gap-1.5"><ArrowLeft className="w-4 h-4" /> Retour</button>
                      <button onClick={confirmImport} disabled={importConfirming} data-testid="import-confirm-btn" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-50 text-white font-semibold uppercase tracking-wider text-sm px-5 py-2.5 transition-colors">
                        {importConfirming ? <><Loader2 className="w-4 h-4 animate-spin" /> Création…</> : <><Check className="w-4 h-4" /> Importer l'annonce ({importSelCount})</>}
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
        {publishTarget && creditConfig && (
          <PublishModal
            listing={publishTarget}
            config={creditConfig}
            wallet={wallet}
            onClose={() => setPublishTarget(null)}
            onPublished={() => { setPublishTarget(null); load(); }}
            onNeedCredits={(slug, tier) => { setPublishTarget(null); setBuyModal({ slug, tier }); }}
          />
        )}
        {previewTarget && (
          <PreviewModal
            listing={previewTarget}
            wallet={wallet}
            onClose={() => setPreviewTarget(null)}
            onDone={() => { setPreviewTarget(null); load(); }}
          />
        )}
        {stagingTarget && creditConfig && (
          <StagingStudioModal
            listing={stagingTarget}
            config={creditConfig}
            onClose={() => { setStagingTarget(null); load(); }}
            onDone={() => load()}
            onChangeTier={(l) => { setStagingTarget(null); setTierTarget(l); }}
          />
        )}
        {buyModal && creditConfig && (
          <BuyCreditsModal
            config={creditConfig}
            pendingSlug={buyModal.slug || ""}
            pendingTier={buyModal.tier || "classique"}
            onCredited={() => load()}
            onClose={() => setBuyModal(null)}
          />
        )}
        {tierTarget && creditConfig && (
          <ChangeTierModal
            listing={tierTarget}
            config={creditConfig}
            wallet={wallet}
            onClose={() => setTierTarget(null)}
            onChanged={() => { setTierTarget(null); load(); }}
            onNeedCredits={(slug, tier) => { setTierTarget(null); setBuyModal({ slug, tier }); }}
          />
        )}
        {contentTarget && <ContentModal slug={contentTarget.slug} title={contentTarget.title} onClose={() => setContentTarget(null)} />}
        {boostTarget && <BoostModal listing={boostTarget} onClose={() => setBoostTarget(null)} />}
      </AnimatePresence>
    </BOShell>
  );
}

// ---------- Agent IA Recherche d'opportunités (analyse interne, RGPD) ----------
function OpportunitiesTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { authApi.opportunities().then(setData).catch(() => setErr(true)); }, []);
  const origin = PUBLIC_SITE;
  const refLink = data?.referral_code ? `${origin}/inscription?ref=${data.referral_code}` : `${origin}/inscription`;
  if (err) return <div className="text-sm text-zinc-500 py-10 text-center">Impossible de charger les opportunités.</div>;
  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C19A6B]" /></div>;
  return (
    <div className="space-y-6" data-testid="opportunities-tab">
      <div className="rounded-xl border border-[#C19A6B]/25 bg-gradient-to-r from-[#FBF8F4] to-transparent p-5 flex items-start gap-3">
        <span className="w-10 h-10 rounded-lg bg-[#C19A6B]/15 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-[#C19A6B]" /></span>
        <div>
          <h3 className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B]">Analyse de la demande — {data.total_acquereurs} acquéreur{data.total_acquereurs > 1 ? "s" : ""}</h3>
          <p className="text-sm text-zinc-500 mt-0.5">L'IA analyse en continu les besoins de vos acquéreurs pour révéler les biens à rechercher en priorité. Collecte : biens classiques {data.cadence?.classique}, pro/premium {data.cadence?.pro_premium}.</p>
        </div>
      </div>

      {data.opportunities?.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-[0.08em] text-[#9CA3AF] font-semibold mb-2">Biens manquants (forte demande, peu d'offre)</p>
          <div className="grid sm:grid-cols-2 gap-3" data-testid="opportunity-gaps">
            {data.opportunities.map((o, i) => (
              <div key={i} className="rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-[0_2px_8px_rgba(17,24,39,0.04)]" data-testid={`opp-gap-${i}`}>
                <p className="text-sm text-[#111827] font-medium">{o.demand} acquéreur{o.demand > 1 ? "s" : ""} recherche{o.demand > 1 ? "nt" : ""} <span className="text-[#A88358]">{o.type}</span> à <span className="text-[#A88358]">{o.secteur}</span></p>
                <p className="text-xs text-zinc-500 mt-1">{o.supply} bien{o.supply > 1 ? "s" : ""} disponible{o.supply > 1 ? "s" : ""} — <b className="text-[#C19A6B]">{o.gap} opportunité{o.gap > 1 ? "s" : ""}</b> à sourcer</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.listing_demand?.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-[0.08em] text-[#9CA3AF] font-semibold mb-2">Vos annonces les plus demandées</p>
          <div className="rounded-xl border border-[#E5E7EB] bg-white divide-y divide-[#F3F4F6]" data-testid="listing-demand">
            {data.listing_demand.map((l) => (
              <div key={l.slug} className="flex items-center justify-between px-4 py-3">
                <div className="min-w-0"><p className="text-sm text-[#111827] font-medium truncate">{l.title}</p><p className="text-xs text-zinc-500 truncate">{l.city}</p></div>
                <span className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold text-[#A88358] bg-[#FBF8F4] rounded-full px-3 py-1"><Users className="w-3.5 h-3.5" /> {l.matches}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.total_acquereurs === 0 && (!data.opportunities || data.opportunities.length === 0) && (
        <div className="text-center py-10 border border-dashed border-[#E5E7EB] rounded-xl"><Contact className="w-8 h-8 text-[#C19A6B]/50 mx-auto mb-2" /><p className="text-sm text-zinc-500">Ajoutez des acquéreurs pour révéler vos opportunités de marché.</p></div>
      )}

      <div className="rounded-xl border border-[#E5E7EB] bg-white p-5">
        <p className="text-sm font-medium text-[#111827] mb-1">Élargir votre vivier d'acquéreurs</p>
        <p className="text-xs text-zinc-500 mb-3">Partagez votre lien tracké : chaque inscription devient automatiquement votre filleul.</p>
        <div className="flex flex-col sm:flex-row items-stretch gap-2">
          <input readOnly value={refLink} data-testid="opp-referral-link" className="flex-1 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm text-[#374151]" />
          <button onClick={() => { navigator.clipboard?.writeText(refLink); }} data-testid="opp-referral-copy" className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#C19A6B] hover:bg-[#A88358] text-white text-sm font-semibold px-4 py-2 transition-colors"><Copy className="w-4 h-4" /> Copier</button>
        </div>
      </div>
    </div>
  );
}

// ---------- Agent IA Création de contenu (modale) ----------
function ContentModal({ slug, title, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState("");
  useEffect(() => { authApi.generateListingContent(slug).then(setData).catch((e) => setErr(e.message || "Échec de génération")); }, [slug]);
  const copy = (key, text) => { navigator.clipboard?.writeText(text); setCopied(key); setTimeout(() => setCopied(""), 1500); };
  const url = `${typeof window !== "undefined" ? window.location.origin : ""}/bien/${slug}`;
  const shareUrl = (net, text) => {
    const t = encodeURIComponent(text || ""); const u = encodeURIComponent(url);
    if (net === "facebook") return `https://www.facebook.com/sharer/sharer.php?u=${u}&quote=${t}`;
    if (net === "linkedin") return `https://www.linkedin.com/sharing/share-offsite/?url=${u}`;
    if (net === "twitter") return `https://twitter.com/intent/tweet?text=${t}&url=${u}`;
    return null;
  };
  const Block = ({ k, label, text, net }) => (
    <div className="rounded-xl border border-[#E5E7EB] bg-white p-4">
      <div className="flex items-center justify-between mb-2"><p className="text-[11px] uppercase tracking-[0.08em] text-[#9CA3AF] font-semibold">{label}</p>
        <div className="flex items-center gap-3">
          {net && shareUrl(net, text) && <a href={shareUrl(net, text)} target="_blank" rel="noopener noreferrer" data-testid={`content-share-${k}`} className="inline-flex items-center gap-1 text-xs text-[#A88358] hover:underline"><Send className="w-3.5 h-3.5" /> Publier</a>}
          {net === "ig" && <span className="text-[11px] text-[#9CA3AF]">Copier + coller dans l'app</span>}
          <button onClick={() => copy(k, text)} data-testid={`content-copy-${k}`} className="inline-flex items-center gap-1 text-xs text-[#A88358] hover:underline">{copied === k ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied === k ? "Copié" : "Copier"}</button>
        </div>
      </div>
      <p className="text-sm text-[#374151] whitespace-pre-wrap leading-relaxed">{text}</p>
    </div>
  );
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="content-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bo w-full max-w-lg max-h-[85vh] overflow-y-auto bg-[#FAFAFA] rounded-2xl border border-[#E5E7EB] shadow-2xl">
        <div className="sticky top-0 bg-white/90 backdrop-blur-md border-b border-[#E5E7EB] px-5 py-3.5 flex items-center justify-between z-10">
          <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#C19A6B]" /><h3 className="font-semibold text-[#111827] text-sm truncate">Contenu IA — {title}</h3></div>
          <button onClick={onClose} data-testid="content-close" className="text-[#6B7280] hover:text-[#111827]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          {err && <p className="text-sm text-red-500 text-center py-6">{err}</p>}
          {!data && !err && <div className="py-16 flex flex-col items-center gap-3 text-zinc-500"><Loader2 className="w-6 h-6 animate-spin text-[#C19A6B]" /><span className="text-sm">Rédaction en cours…</span></div>}
          {data && (
            <>
              {data.short_summary && <Block k="summary" label="Accroche" text={data.short_summary} />}
              {data.commercial_text && <Block k="commercial" label="Texte commercial" text={data.commercial_text} />}
              {Array.isArray(data.seller_arguments) && data.seller_arguments.length > 0 && <Block k="args" label="Arguments vendeur" text={data.seller_arguments.map((a) => `• ${a}`).join("\n")} />}
              {data.social?.instagram && <Block k="ig" label="Instagram" text={data.social.instagram} net="ig" />}
              {data.social?.facebook && <Block k="fb" label="Facebook" text={data.social.facebook} net="facebook" />}
              {data.social?.linkedin && <Block k="li" label="LinkedIn" text={data.social.linkedin} net="linkedin" />}
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ---------- Tableau de Bord IA — page d'accueil CRM ----------
function ApporteurRevenuePanel({ rs }) {
  const pro = rs.apporteur_status === "professionnel";
  const rev = rs.revenue || 0;
  const cap = rs.cap || 2500;
  const pct = pro ? 0 : Math.min(100, Math.round((rev / cap) * 100));
  const warn = rs.over_threshold;
  const full = rs.near_cap;
  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-white p-4" data-testid="apporteur-revenue-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <p className="text-[11px] uppercase tracking-[0.08em] text-[#9CA3AF] font-semibold flex items-center gap-1.5"><Coins className="w-3.5 h-3.5 text-[#C19A6B]" /> Revenus d'apport générés</p>
        <span className={`text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full ${pro ? "bg-emerald-50 text-emerald-700" : "bg-[#C19A6B]/10 text-[#A88358]"}`} data-testid="apporteur-status-badge">
          {pro ? "Statut professionnel" : "Apporteur particulier"}
        </span>
      </div>
      <div className="flex items-end gap-2">
        <p className="text-3xl font-bold text-[#111827]" data-testid="apporteur-revenue-value">{fmtPrice(rev)}</p>
        {!pro && <p className="text-sm text-zinc-400 mb-1">/ plafond {fmtPrice(cap)} par an</p>}
      </div>
      {!pro && (
        <div className="mt-3">
          <div className="h-2.5 w-full rounded-full bg-[#F1F3F5] overflow-hidden">
            <div className={`h-full rounded-full transition-all ${full ? "bg-red-500" : warn ? "bg-amber-500" : "bg-[#C19A6B]"}`} style={{ width: `${pct}%` }} data-testid="apporteur-revenue-bar" />
          </div>
          <p className="text-[11px] text-zinc-500 mt-1.5">{full ? "Plafond atteint" : `Il vous reste ${fmtPrice(rs.remaining || 0)} avant le plafond`}</p>
        </div>
      )}
      {pro && (
        <p className="text-sm text-zinc-500 mt-2">Aucune limite de revenus — développement commercial complet {rs.siret ? <>· SIRET {rs.siret}</> : null}.</p>
      )}
      {warn && !pro && (
        <div className="mt-4 rounded-[5px] border border-amber-300 bg-amber-50 p-3.5" data-testid="apporteur-threshold-alert">
          <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5"><Info className="w-4 h-4" /> Pensez à professionnaliser votre activité</p>
          <p className="text-xs text-amber-800/90 mt-1.5 leading-relaxed">
            Vous avez dépassé 2 000 €. Au-delà de 2 500 €/an, un statut professionnel est requis. Créer un statut <b>auto-entrepreneur</b> se fait en ligne en quelques minutes et vous permet de développer davantage votre réseau OFF MARKETTE sans limite de revenus.
          </p>
          <a href="https://www.autoentrepreneur.urssaf.fr/portail/accueil/creer-mon-auto-entreprise.html" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-white rounded-[5px] px-3 py-1.5 mt-2.5" style={{ background: "#C19A6B" }} data-testid="apporteur-autoentrepreneur-link">
            <ArrowRight className="w-3.5 h-3.5" /> Créer mon statut auto-entrepreneur
          </a>
        </div>
      )}
    </div>
  );
}

// ---------- Panneau Évolutions (timeline sur fond blanc, 2ᵉ accès depuis la sidebar) ----------
function EvolutionPanel() {
  const [openId, setOpenId] = useState(EVO_TIMELINE[0]?.id || null);
  const latestId = EVO_TIMELINE[0]?.id;
  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 sm:p-7" data-testid="evolution-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="calli text-3xl text-[#C19A6B] leading-none">Les évolutions</p>
          <p className="text-sm text-zinc-500 mt-1">Le journal des nouveautés OFF MARKETTE — depuis juin 2026.</p>
        </div>
        <span className="inline-flex items-center gap-2 text-xs font-semibold text-[#A88358] bg-[#FBF8F3] border border-[#C19A6B]/25 rounded-full px-3 py-1.5">
          <Sparkles className="w-3.5 h-3.5" /> Version {EVO_LAST.version} · {EVO_LAST.date}
        </span>
      </div>
      <div className="relative">
        {EVO_TIMELINE.map((it, idx) => {
          const st = EVO_STATUS[it.status] || EVO_STATUS.available;
          const open = openId === it.id;
          const isLatest = it.id === latestId;
          const last = idx === EVO_TIMELINE.length - 1;
          const d = (() => { try { return new Date(it.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); } catch (_) { return it.date; } })();
          return (
            <div key={it.id} className="relative pl-10 sm:pl-12 pb-6" data-testid={`evolution-item-${it.id}`}>
              {/* ligne verticale */}
              {!last && <span className="absolute left-[13px] sm:left-[15px] top-6 bottom-0 w-px bg-[#E5E7EB]" />}
              {/* point */}
              <span className="absolute left-1 sm:left-1.5 top-1.5 w-6 h-6 rounded-full border-2 border-white flex items-center justify-center shadow-sm" style={{ background: st.color }}>
                <span className="w-2 h-2 rounded-full bg-white/90" />
              </span>
              <button onClick={() => setOpenId(open ? null : it.id)}
                className={`w-full text-left rounded-xl border transition-colors ${open ? "border-[#C19A6B]/60 bg-[#FDFBF7]" : "border-[#E5E7EB] bg-white hover:border-[#C19A6B]/40"} ${isLatest ? "ring-1 ring-[#C19A6B]/40" : ""}`}>
                <div className="p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 space-y-1.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ color: st.color, background: st.color + "1a" }}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: st.color }} /> {st.label}
                        </span>
                        {it.category && <span className="text-[10px] uppercase tracking-wider text-[#C19A6B] bg-[#C19A6B]/10 px-2 py-0.5 rounded-full">{it.category}</span>}
                        {isLatest && <span className="text-[10px] uppercase tracking-widest text-[#C19A6B] font-semibold">● Nouveauté</span>}
                      </div>
                      <h3 className="text-[15px] font-semibold text-[#111827]">{it.icon ? `${it.icon} ` : ""}{it.title}</h3>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-400">
                        <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-[#C19A6B]" /> {d}</span>
                        {it.version && <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" /> v{it.version}</span>}
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-[#C19A6B] shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
                  </div>
                  {open && (
                    <div className="mt-3 pt-3 border-t border-[#F3F4F6]">
                      <p className="text-sm text-[#374151] leading-relaxed">{it.description}</p>
                      {it.benefits?.length > 0 && (
                        <ul className="mt-3 space-y-1.5">
                          {it.benefits.map((b, i) => <li key={i} className="text-sm text-[#374151] flex items-start gap-2"><Check className="w-4 h-4 text-[#C19A6B] mt-0.5 shrink-0" /> {b}</li>)}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Réseaux Sociaux (hub 5 sous-onglets) ----------
function SocialHubTab({ sub, member }) {
  const [data, setData] = useState(null);
  const load = () => authApi.socialOverview().then(setData).catch(() => setData(null));
  useEffect(() => { load(); }, []); // eslint-disable-line
  if (!data) return <div className="p-10 text-center text-zinc-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  if (sub === "social_reco") return <SocialReco data={data} member={member} />;
  if (sub === "social_published") return <SocialPublished data={data} member={member} />;
  if (sub === "social_connect") return <SocialConnect data={data} onSaved={load} />;
  if (sub === "social_report") return <SocialReport data={data} />;
  if (sub === "social_ads") return <SocialAds member={member} />;
  return null;
}

const relDate = (iso) => { if (!iso) return "jamais partagée"; try { const d = new Date(iso); return "le " + d.toLocaleDateString("fr-FR"); } catch { return ""; } };

const PUB_TONES = ["Prestige", "Professionnel", "Familial", "Investisseur", "Luxe"];
const PUB_NETS = [
  { key: "facebook", label: "Facebook", Icon: Facebook },
  { key: "linkedin", label: "LinkedIn", Icon: Linkedin },
  { key: "instagram", label: "Instagram", Icon: Instagram },
  { key: "whatsapp", label: "WhatsApp", Icon: WhatsAppIcon },
  { key: "messenger", label: "Messenger", Icon: MessageCircle },
  { key: "twitter", label: "X (Twitter)", Icon: Twitter },
  { key: "email", label: "E-mail", Icon: Mail },
];

// Rendu d'un texte de publication : titre (l.1) + lien (l.2) + corps aéré avec puces carrées dorées.
function PubText({ text }) {
  const lines = (text || "").split("\n");
  const title = lines[0] || "";
  const link = (lines[1] || "").trim();
  const body = lines.slice(2);
  return (
    <div className="text-[13px] leading-relaxed">
      <p className="font-semibold text-[#111827]">{title}</p>
      {/^https?:\/\/|^\//.test(link) ? <a href={link} target="_blank" rel="noopener noreferrer" className="text-[#C19A6B] underline break-all">{link}</a> : link && <p className="text-[#374151]">{link}</p>}
      <div className="mt-1 text-[#374151] whitespace-pre-wrap">
        {body.map((ln, i) => {
          const t = ln.trimStart();
          if (t.startsWith("▪")) return <p key={i} className="flex gap-2"><span className="text-[#C19A6B] mt-0.5">▪</span><span>{t.replace(/^▪\s*/, "")}</span></p>;
          return <p key={i}>{ln || "\u00A0"}</p>;
        })}
      </div>
    </div>
  );
}

// ---------- Éditeur de visuel de pub (fond + phrase dorée + slogan blanc déplaçables) ----------
const SLOGAN_PRESETS = ["À vendre en off market", "À saisir avant tout le monde", "Exclusivité off market", "Rare sur le marché", "Nouveauté confidentielle", "Coup de cœur assuré"];
const PARRAINAGE_SLOGANS = ["Rejoins mon réseau", "Deviens partenaire", "Entreprends avec moi", "Rejoins l'aventure OFF MARKETTE", "Développe ton activité", "Libère ton potentiel immobilier"];
const VENTE_ACCROCHES = ["Coup de cœur", "Rare sur le marché", "Exclusivité", "Bien d'exception", "Nouveauté", "Vue imprenable", "À découvrir"];
const SLOGAN_COLOR_OPTS = [{ key: "white", label: "Blanc", css: "#ffffff" }, { key: "gold", label: "Doré", css: "#D4AF78" }, { key: "black", label: "Noir", css: "#111111" }];
function PubVisualEditor({ galleryPhotos = [], initialBg = "", initialPhrase = "", initialSlogan = "", sloganPresets = SLOGAN_PRESETS, phrasePresets = [], onBake, onBaked = null, onAiBg = null, testid = "pubvisual" }) {
  const [bg, setBg] = useState(initialBg || galleryPhotos[0] || "");
  const [phrase, setPhrase] = useState(initialPhrase);
  const [slogan, setSlogan] = useState(initialSlogan);
  const [pos, setPos] = useState({ x: 0.5, y: 0.68 });
  const [logoPos, setLogoPos] = useState({ x: 0.5, y: 0.16 });
  const [phraseScale, setPhraseScale] = useState(1);
  const [sloganScale, setSloganScale] = useState(1);
  const [fmt, setFmt] = useState("square");
  const [sloganColor, setSloganColor] = useState("white");
  const [baked, setBaked] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [copied, setCopied] = useState(false);
  const boxRef = useRef(null);
  const dragTarget = useRef(null);

  const startDrag = (t) => (e) => { dragTarget.current = t; e.stopPropagation(); };
  const onMove = (e) => {
    if (!dragTarget.current || !boxRef.current) return;
    const r = boxRef.current.getBoundingClientRect();
    const x = Math.min(0.92, Math.max(0.08, (e.clientX - r.left) / r.width));
    const y = Math.min(0.94, Math.max(0.06, (e.clientY - r.top) / r.height));
    if (dragTarget.current === "logo") setLogoPos({ x, y }); else setPos({ x, y });
  };
  const endDrag = () => { dragTarget.current = null; };

  const importPhoto = async (file) => {
    if (!file) return; setImporting(true);
    try { const d = await uploadMemberFile(file); setBg(d.url); setBaked(""); } catch (_) { alert("Import impossible."); }
    setImporting(false);
  };
  const aiBg = async () => {
    if (!onAiBg || aiBusy) return; setAiBusy(true);
    try { const url = await onAiBg(); if (url) { setBg(url); setBaked(""); } } catch (_) { alert("Image IA indisponible."); }
    setAiBusy(false);
  };
  const bake = async () => {
    if (!bg || busy) return; setBusy(true); setBaked("");
    try { const url = await onBake({ image_url: bg, tagline: phrase, slogan, pos, phrase_scale: phraseScale, slogan_scale: sloganScale, fmt, slogan_color: sloganColor, logo_pos: logoPos }); setBaked(url); if (onBaked) onBaked(url); }
    catch (_) { alert("Génération du visuel impossible."); }
    setBusy(false);
  };
  const download = async () => {
    if (!baked) return;
    try {
      const res = await fetch(baked); const blob = await res.blob();
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
      a.download = "off-markette-visuel.jpg"; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (_) { window.open(baked, "_blank"); }
  };
  const copyImg = async () => {
    if (!baked) return;
    try {
      const res = await fetch(baked); const blob = await res.blob();
      await navigator.clipboard.write([new window.ClipboardItem({ [blob.type || "image/jpeg"]: blob })]);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch (_) { window.open(baked, "_blank"); }
  };

  const inp = "w-full bg-white border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm text-[#111827] outline-none focus:border-[#C19A6B]";
  return (
    <div className="space-y-4" data-testid={testid}>
      {/* Sélecteur de photo de fond */}
      <div>
        <p className="text-[11px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-1.5">Photo de fond</p>
        <div className="flex flex-wrap gap-2 items-center">
          {galleryPhotos.map((src, i) => (
            <button key={i} onClick={() => { setBg(src); setBaked(""); }} data-testid={`${testid}-bg-${i}`}
              className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${bg === src ? "border-[#C19A6B] ring-2 ring-[#C19A6B]/30" : "border-[#E5E7EB] hover:border-[#C19A6B]/60"}`}>
              <img src={src} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
          <label className="w-16 h-16 rounded-lg border-2 border-dashed border-[#C19A6B]/50 text-[#C19A6B] flex flex-col items-center justify-center cursor-pointer hover:bg-[#C19A6B]/5 text-[10px]" data-testid={`${testid}-import`}>
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4" /><span>Importer</span></>}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => importPhoto(e.target.files?.[0])} />
          </label>
          {onAiBg && (
            <button onClick={aiBg} disabled={aiBusy} data-testid={`${testid}-aibg`}
              className="w-16 h-16 rounded-lg border-2 border-dashed border-[#C19A6B]/50 text-[#C19A6B] flex flex-col items-center justify-center hover:bg-[#C19A6B]/5 text-[10px] disabled:opacity-50">
              {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wand2 className="w-4 h-4" /><span>Image IA</span></>}
            </button>
          )}
        </div>
        {galleryPhotos.length === 0 && <p className="text-[11px] text-zinc-400 mt-1">Aucune photo dans votre galerie — importez-en une ou générez un fond IA.</p>}
      </div>

      {/* Format + Aperçu déplaçable */}
      <div>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-1.5">
          <p className="text-[11px] uppercase tracking-wider text-[#9CA3AF] font-semibold flex items-center gap-1"><Move className="w-3.5 h-3.5" /> Aperçu — glissez le texte ET le logo pour les positionner</p>
          <div className="inline-flex rounded-lg border border-[#E5E7EB] overflow-hidden" data-testid={`${testid}-format`}>
            <button type="button" onClick={() => { setFmt("square"); setBaked(""); }} data-testid={`${testid}-format-square`} className={`text-[11px] px-3 py-1.5 font-medium transition-colors ${fmt === "square" ? "bg-[#C19A6B] text-white" : "text-[#374151] hover:bg-[#F9FAFB]"}`}>Carré 1:1</button>
            <button type="button" onClick={() => { setFmt("story"); setBaked(""); }} data-testid={`${testid}-format-story`} className={`text-[11px] px-3 py-1.5 font-medium transition-colors ${fmt === "story" ? "bg-[#C19A6B] text-white" : "text-[#374151] hover:bg-[#F9FAFB]"}`}>Story 9:16</button>
          </div>
        </div>
        <div ref={boxRef} onPointerMove={onMove} onPointerUp={endDrag} onPointerLeave={endDrag} className={`relative w-full mx-auto ${fmt === "story" ? "max-w-[200px] aspect-[9/16]" : "max-w-xs aspect-square"} rounded-xl overflow-hidden bg-[#0d0d10] select-none touch-none`} data-testid={`${testid}-preview`}>
          {bg ? <img src={bg} alt="" className="absolute inset-0 w-full h-full object-cover pointer-events-none" /> : <div className="absolute inset-0 flex items-center justify-center text-zinc-500"><ImageIcon className="w-8 h-8" /></div>}
          <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to top, rgba(5,5,6,.72) 0%, rgba(5,5,6,0) 45%, rgba(5,5,6,0) 82%, rgba(5,5,6,.5) 100%)" }} />
          <img src={LOGO_URL} alt="" onPointerDown={startDrag("logo")} data-testid={`${testid}-logo`} className="absolute w-1/2 cursor-move" style={{ left: `${logoPos.x * 100}%`, top: `${logoPos.y * 100}%`, transform: "translate(-50%,-50%)" }} />
          {(phrase || slogan) && (
            <div onPointerDown={startDrag("text")}
              className="absolute cursor-move text-center px-2"
              style={{ left: `${pos.x * 100}%`, top: `${pos.y * 100}%`, transform: "translate(-50%,-50%)", width: "90%" }}
              data-testid={`${testid}-textblock`}>
              {phrase && <div className="calli leading-tight" style={{ color: "#D4AF78", fontSize: `${33 * phraseScale}px`, textShadow: "0 2px 4px rgba(0,0,0,.6)" }}>{phrase}</div>}
              {slogan && <div className="font-bold uppercase leading-tight mt-0.5" style={{ color: (SLOGAN_COLOR_OPTS.find((c) => c.key === sloganColor) || {}).css || "#fff", fontSize: `${16 * sloganScale}px`, letterSpacing: ".04em", textShadow: sloganColor === "black" ? "0 1px 3px rgba(255,255,255,.5)" : "0 2px 4px rgba(0,0,0,.7)" }}>{slogan}</div>}
            </div>
          )}
        </div>
      </div>

      {/* Champs éditables */}
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[11px] uppercase tracking-wider text-[#9CA3AF] font-semibold">Phrase (dorée)</label>
            <span className="inline-flex items-center gap-1">
              <button type="button" onClick={() => setPhraseScale((s) => Math.max(0.6, +(s - 0.1).toFixed(2)))} data-testid={`${testid}-phrase-smaller`} className="w-6 h-6 rounded-md border border-[#E5E7EB] text-[#374151] hover:border-[#C19A6B] flex items-center justify-center text-sm leading-none">−</button>
              <span className="text-[10px] text-zinc-400 w-8 text-center">{Math.round(phraseScale * 100)}%</span>
              <button type="button" onClick={() => setPhraseScale((s) => Math.min(2.2, +(s + 0.1).toFixed(2)))} data-testid={`${testid}-phrase-bigger`} className="w-6 h-6 rounded-md border border-[#E5E7EB] text-[#374151] hover:border-[#C19A6B] flex items-center justify-center text-sm leading-none">+</button>
            </span>
          </div>
          <input value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder="Votre accroche" className={inp} data-testid={`${testid}-phrase`} />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[11px] uppercase tracking-wider text-[#9CA3AF] font-semibold">Slogan (blanc, en majuscules)</label>
            <span className="inline-flex items-center gap-1">
              <button type="button" onClick={() => setSloganScale((s) => Math.max(0.6, +(s - 0.1).toFixed(2)))} data-testid={`${testid}-slogan-smaller`} className="w-6 h-6 rounded-md border border-[#E5E7EB] text-[#374151] hover:border-[#C19A6B] flex items-center justify-center text-sm leading-none">−</button>
              <span className="text-[10px] text-zinc-400 w-8 text-center">{Math.round(sloganScale * 100)}%</span>
              <button type="button" onClick={() => setSloganScale((s) => Math.min(2.2, +(s + 0.1).toFixed(2)))} data-testid={`${testid}-slogan-bigger`} className="w-6 h-6 rounded-md border border-[#E5E7EB] text-[#374151] hover:border-[#C19A6B] flex items-center justify-center text-sm leading-none">+</button>
            </span>
          </div>
          <input value={slogan} onChange={(e) => setSlogan(e.target.value)} placeholder="Votre slogan" className={inp} data-testid={`${testid}-slogan`} />
        </div>
      </div>

      {/* Accroches dorées prêtes */}
      {phrasePresets.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-1.5">Accroches dorées prêtes</p>
          <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-phrase-presets`}>
            {phrasePresets.map((s) => (
              <button key={s} type="button" onClick={() => setPhrase(s)} data-testid={`${testid}-phrase-preset`} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${phrase === s ? "bg-[#C19A6B] text-white border-[#C19A6B]" : "border-[#E5E7EB] text-[#374151] hover:border-[#C19A6B]"}`}>{s}</button>
            ))}
          </div>
        </div>
      )}

      {/* Modèles de slogans prêts */}
      <div>
        <p className="text-[11px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-1.5">Slogans prêts à l'emploi</p>
        <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-slogan-presets`}>
          {sloganPresets.map((s) => (
            <button key={s} type="button" onClick={() => setSlogan(s)} data-testid={`${testid}-slogan-preset`} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${slogan === s ? "bg-[#C19A6B] text-white border-[#C19A6B]" : "border-[#E5E7EB] text-[#374151] hover:border-[#C19A6B]"}`}>{s}</button>
          ))}
        </div>
      </div>

      {/* Couleur du slogan */}
      <div>
        <p className="text-[11px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-1.5">Couleur du slogan</p>
        <div className="flex items-center gap-2" data-testid={`${testid}-slogan-color`}>
          {SLOGAN_COLOR_OPTS.map((c) => (
            <button key={c.key} type="button" onClick={() => setSloganColor(c.key)} data-testid={`${testid}-slogan-color-${c.key}`}
              className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${sloganColor === c.key ? "border-[#C19A6B] ring-2 ring-[#C19A6B]/25 text-[#111827]" : "border-[#E5E7EB] text-[#374151] hover:border-[#C19A6B]"}`}>
              <span className="w-3.5 h-3.5 rounded-full border border-black/20" style={{ background: c.css }} /> {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={bake} disabled={!bg || busy} data-testid={`${testid}-generate`} className="inline-flex items-center gap-1.5 text-sm text-white bg-[#C19A6B] hover:bg-[#A88358] px-5 py-2.5 rounded-lg font-semibold disabled:opacity-50">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Génération…</> : <><Wand2 className="w-4 h-4" /> Générer le visuel</>}
        </button>
        {baked && <>
          <button onClick={download} data-testid={`${testid}-download`} className="inline-flex items-center gap-1.5 text-sm text-[#C19A6B] border border-[#C19A6B]/40 hover:bg-[#C19A6B]/10 px-4 py-2.5 rounded-lg"><Download className="w-4 h-4" /> Télécharger</button>
          <button onClick={copyImg} data-testid={`${testid}-copy`} className="inline-flex items-center gap-1.5 text-sm text-[#C19A6B] border border-[#C19A6B]/40 hover:bg-[#C19A6B]/10 px-4 py-2.5 rounded-lg">{copied ? <><Check className="w-4 h-4" /> Copié</> : <><Copy className="w-4 h-4" /> Copier l'image</>}</button>
        </>}
      </div>
      {baked && <div><p className="text-[11px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-1.5">Visuel final</p><img src={baked} alt="Visuel de pub" className="w-full max-w-xs mx-auto rounded-xl border border-[#E5E7EB]" data-testid={`${testid}-result`} /></div>}
    </div>
  );
}

function ListingPublisher({ listing, onClose }) {
  const [tone, setTone] = useState("Professionnel");
  const [sel, setSel] = useState(["facebook", "linkedin"]);
  const [texts, setTexts] = useState({});
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");
  const [photo, setPhoto] = useState(listing?.cover || (listing?.images || [])[0] || "");
  const [imgBusy, setImgBusy] = useState(false);
  const [pubImg, setPubImg] = useState("");
  const [includePhone, setIncludePhone] = useState(false);
  const [extraImgs, setExtraImgs] = useState([]);
  const [photoCopied, setPhotoCopied] = useState(false);
  const [metaImg, setMetaImg] = useState("");
  const imgs = [...((listing?.images && listing.images.length ? listing.images : (listing?.cover ? [listing.cover] : []))), ...extraImgs];
  const toggle = (k) => setSel((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);

  const generate = async () => {
    if (!sel.length || busy) return;
    setBusy(true);
    const out = {};
    for (const net of sel) {
      try { const d = await authApi.shareText(listing.slug, { network: net, tone, include_phone: includePhone }); out[net] = d; }
      catch (_) { out[net] = { text: `${listing.title}\n/bien/${listing.slug}`, url: `/bien/${listing.slug}` }; }
    }
    setTexts(out); setBusy(false);
  };
  const enc = encodeURIComponent;
  const shareOn = async (net) => {
    const d = texts[net]; if (!d) return;
    try { await navigator.clipboard.writeText(d.text); } catch (_) {}
    authApi.trackShare(listing.slug, net).catch(() => {});
    // Page Open Graph (photo principale + titre + prix) pour l'aperçu des réseaux.
    const ogUrl = `${PUBLIC_SITE}/api/listings/${listing.slug}/share`;
    const waText = d.url ? d.text.split(d.url).join(ogUrl) : d.text;
    const links = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(ogUrl)}&quote=${enc(d.text)}`,
      messenger: `https://www.facebook.com/sharer/sharer.php?u=${enc(ogUrl)}&quote=${enc(d.text)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(ogUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${enc(d.text)}&url=${enc(ogUrl)}`,
      whatsapp: `https://wa.me/?text=${enc(waText)}`,
      email: `mailto:?subject=${enc(d.accroche || listing.title)}&body=${enc(d.text)}`,
    };
    if (net === "instagram") { setCopied("instagram"); setTimeout(() => setCopied(""), 2000); window.open("https://www.instagram.com/", "_blank"); return; }
    if (links[net]) window.open(links[net], "_blank");
  };
  const genImage = async () => {
    if (imgBusy) return; setImgBusy(true);
    const accroche = (texts[sel[0]] && texts[sel[0]].accroche) || listing.title;
    try { const d = await authApi.shareImage(listing.slug, { image_url: photo, tagline: accroche }); setPubImg(d.url); } catch (_) {}
    setImgBusy(false);
  };
  const importPhoto = async (file) => {
    if (!file) return; setImgBusy(true);
    try { const d = await uploadMemberFile(file); setExtraImgs((p) => [...p, d.url]); setPhoto(d.url); } catch (_) {}
    setImgBusy(false);
  };
  const copyPhoto = async () => {
    if (!pubImg) return;
    try {
      const res = await fetch(pubImg); const blob = await res.blob();
      await navigator.clipboard.write([new window.ClipboardItem({ [blob.type || "image/jpeg"]: blob })]);
      setPhotoCopied(true); setTimeout(() => setPhotoCopied(false), 2000);
    } catch (_) { window.open(pubImg, "_blank"); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm overflow-y-auto py-8 px-4" onClick={onClose} data-testid="listing-publisher">
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB]">
          <div className="min-w-0"><p className="calli text-2xl text-[#C19A6B] leading-none">Partager sur mes réseaux</p><p className="text-xs text-zinc-500 truncate mt-0.5">{listing?.title}</p></div>
          <button onClick={onClose} data-testid="publisher-close" className="text-zinc-400 hover:text-[#111827]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-5">
          {/* Opt-in téléphone dans la signature */}
          <label className="flex items-start gap-2.5 text-sm text-[#374151] bg-[#C19A6B]/5 border border-[#C19A6B]/25 rounded-lg px-3 py-2.5 cursor-pointer" data-testid="publisher-phone-optin">
            <input type="checkbox" checked={includePhone} onChange={(e) => { setIncludePhone(e.target.checked); setTexts({}); }} className="mt-0.5 accent-[#C19A6B]" data-testid="publisher-phone-checkbox" />
            <span>J'accepte d'afficher mon <b>numéro de téléphone</b> dans les publications réseaux. <span className="text-zinc-500">Si coché, chaque publication se termine par Nom Prénom + e-mail + téléphone ; sinon Nom Prénom + e-mail uniquement.</span></span>
          </label>
          {/* Ton */}
          <div>
            <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Ton de communication</p>
            <div className="flex flex-wrap gap-1.5">
              {PUB_TONES.map((t) => <button key={t} onClick={() => setTone(t)} data-testid={`publisher-tone-${t}`} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${tone === t ? "bg-[#C19A6B] text-white border-[#C19A6B]" : "border-[#E5E7EB] text-[#374151] hover:border-[#C19A6B]"}`}>{t}</button>)}
            </div>
          </div>
          {/* Réseaux */}
          <div>
            <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Réseaux (sélection multiple)</p>
            <div className="flex flex-wrap gap-2" data-testid="publisher-networks">
              {PUB_NETS.map(({ key, label, Icon }) => (
                <button key={key} onClick={() => toggle(key)} data-testid={`publisher-net-${key}`} className={`inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border transition-colors ${sel.includes(key) ? "bg-[#C19A6B]/10 border-[#C19A6B] text-[#C19A6B]" : "border-[#E5E7EB] text-[#374151] hover:border-[#C19A6B]"}`}><Icon className="w-4 h-4" /> {label}{sel.includes(key) && <Check className="w-3 h-3" />}</button>
              ))}
            </div>
          </div>
          <button onClick={generate} disabled={!sel.length || busy} data-testid="publisher-generate" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#A88358] disabled:opacity-50 text-white text-sm font-semibold uppercase tracking-wider px-5 py-2.5 rounded-lg transition-colors">{busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Génération…</> : <><Sparkles className="w-4 h-4" /> Générer les textes adaptés</>}</button>

          {/* Textes par réseau */}
          {sel.filter((n) => texts[n]).map((net) => {
            const meta = PUB_NETS.find((x) => x.key === net); const Icon = meta.Icon;
            return (
              <div key={net} className="border border-[#E5E7EB] rounded-xl p-4 space-y-3" data-testid={`publisher-text-${net}`}>
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#111827]"><Icon className="w-4 h-4 text-[#C19A6B]" /> {meta.label}</span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => { navigator.clipboard.writeText(texts[net].text); setCopied(net); setTimeout(() => setCopied(""), 1500); }} data-testid={`publisher-copy-${net}`} className="text-xs text-[#C19A6B] inline-flex items-center gap-1">{copied === net ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} Copier</button>
                    <button onClick={() => shareOn(net)} data-testid={`publisher-share-${net}`} className="inline-flex items-center gap-1.5 text-xs text-white bg-[#C19A6B] hover:bg-[#A88358] px-3 py-1.5 rounded-md"><Share2 className="w-3.5 h-3.5" /> Partager l'annonce</button>
                  </div>
                </div>
                <PubText text={texts[net].text} />
                {copied === "instagram" && net === "instagram" && <p className="text-[11px] text-[#C19A6B]">Texte copié — collez-le dans votre publication Instagram.</p>}
              </div>
            );
          })}

          {/* Visuel de pub */}
          <div className="border border-[#E5E7EB] rounded-xl p-4 space-y-3" data-testid="publisher-image-block">
            <p className="text-sm font-semibold text-[#111827] flex items-center gap-1.5"><Megaphone className="w-4 h-4 text-[#C19A6B]" /> Visuel de publicité (photo + logo + phrase + slogan)</p>
            <PubVisualEditor
              galleryPhotos={imgs}
              initialBg={photo || imgs[0] || ""}
              initialPhrase={(texts[sel[0]] && texts[sel[0]].accroche) || listing?.title || ""}
              initialSlogan="À vendre en off market"
              phrasePresets={VENTE_ACCROCHES}
              onBaked={setMetaImg}
              onBake={(payload) => authApi.shareImage(listing.slug, payload).then((d) => d.url)}
              testid="publisher-visual"
            />
          </div>
          <MetaPublishBlock slug={listing.slug} imageUrl={metaImg} caption={(texts[sel[0]] && texts[sel[0]].text) || ""} />
        </div>
      </div>
    </div>
  );
}


function SocialReco({ data, member }) {
  const list = data.unpublished || [];
  const [pub, setPub] = useState(null);
  return (
    <div className="space-y-4" data-testid="social-reco">
      <SocialHeader icon={Sparkles} title="Annonces à publier" desc="Vos biens non publiés : recommandez-les à votre réseau pour créer des opportunités off market." />
      {list.length === 0 ? <EmptySocial text="Aucun bien non publié pour le moment. Importez ou créez une annonce depuis « Mes annonces »." />
        : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {list.map((l) => (
              <div key={l.slug} className="rounded-xl border border-[#E5E7EB] bg-white overflow-hidden" data-testid={`social-reco-${l.slug}`}>
                <div className="aspect-[16/10] bg-zinc-100">{l.cover && <img src={l.cover} alt="" className="w-full h-full object-cover" />}</div>
                <div className="p-3">
                  <h4 className="text-sm font-medium text-[#111827] truncate">{l.title}</h4>
                  <p className="text-[#C19A6B] text-sm">{fmtPrice(l.price)}{l.city ? ` · ${l.city}` : ""}</p>
                  <button onClick={() => setPub(l)} data-testid={`social-reco-publish-${l.slug}`} className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-white bg-[#C19A6B] hover:bg-[#A88358] px-3 py-2 rounded-lg transition-colors"><Share2 className="w-3.5 h-3.5" /> Partager sur mes réseaux</button>
                </div>
              </div>
            ))}
          </div>}
      {pub && <ListingPublisher listing={pub} onClose={() => setPub(null)} />}
    </div>
  );
}

function SocialPublished({ data, member }) {
  const list = data.published || [];
  const [pub, setPub] = useState(null);
  return (
    <div className="space-y-4" data-testid="social-published">
      <SocialHeader icon={Share2} title="Annonces publiées" desc="Vos biens en ligne, leur diffusion réseau et la date du dernier partage." />
      {list.length === 0 ? <EmptySocial text="Aucune annonce publiée pour le moment." />
        : <div className="space-y-3">
            {list.map((l) => {
              const nets = Object.entries(l.share_by || {});
              return (
                <div key={l.slug} className="rounded-xl border border-[#E5E7EB] bg-white p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`social-pub-${l.slug}`}>
                  <div className="w-full sm:w-24 h-20 rounded-lg overflow-hidden bg-zinc-100 shrink-0">{l.cover && <img src={l.cover} alt="" className="w-full h-full object-cover" />}</div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-[#111827] truncate">{l.title}</h4>
                    <p className="text-[#C19A6B] text-sm">{fmtPrice(l.price)}{l.city ? ` · ${l.city}` : ""}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-1.5 text-[11px] text-zinc-500">
                      <span className="inline-flex items-center gap-1"><Share2 className="w-3 h-3 text-[#C19A6B]" /> {l.share_count} partage{l.share_count > 1 ? "s" : ""}</span>
                      <span>· Dernier partage {relDate(l.last_shared_at)}{l.last_shared_network ? ` (${l.last_shared_network})` : ""}</span>
                      {nets.map(([n, c]) => <span key={n} className="px-1.5 py-0.5 rounded-full bg-[#F1F3F5] text-[#374151]">{n}: {c}</span>)}
                    </div>
                  </div>
                  <div className="shrink-0"><button onClick={() => setPub(l)} data-testid={`social-pub-share-${l.slug}`} className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-white bg-[#C19A6B] hover:bg-[#A88358] px-3 py-2 rounded-lg transition-colors"><Share2 className="w-3.5 h-3.5" /> Partager</button></div>
                </div>
              );
            })}
          </div>}
      {pub && <ListingPublisher listing={pub} onClose={() => setPub(null)} />}
    </div>
  );
}

// ---------- Meta (Facebook/Instagram) : connexion OAuth + guide + auto-publication ----------
function CopyField({ value, testid }) {
  const [ok, setOk] = useState(false);
  return (
    <div className="flex items-center gap-2 bg-white border border-[#E5E7EB] rounded-lg px-2.5 py-1.5">
      <code className="text-[11px] text-[#374151] truncate flex-1">{value}</code>
      <button type="button" onClick={() => { navigator.clipboard.writeText(value); setOk(true); setTimeout(() => setOk(false), 1500); }} data-testid={testid} className="shrink-0 text-[#C19A6B] hover:text-[#A88358]">{ok ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}</button>
    </div>
  );
}

function MetaGuide() {
  const [open, setOpen] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return (
    <div className="mt-3 border-t border-[#1877F2]/15 pt-3">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="meta-guide-toggle" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#1877F2]">
        <HelpCircle className="w-4 h-4" /> Guide de connexion (à faire une seule fois)
        <ChevronRight className={`w-4 h-4 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <ol className="mt-2 space-y-3 text-[13px] text-[#374151]" data-testid="meta-guide">
          <li><span className="font-semibold">1.</span> Ouvrez le tableau de bord Meta : <a href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer" className="text-[#1877F2] underline">developers.facebook.com/apps</a> et sélectionnez votre application.</li>
          <li><span className="font-semibold">2.</span> Menu de gauche → <b>« + Ajouter un produit »</b> → activez <b>« Connexion Facebook » (Facebook Login)</b>.</li>
          <li>
            <span className="font-semibold">3.</span> Dans <b>Connexion Facebook → Paramètres</b>, champ <b>« URI de redirection OAuth valides »</b>, collez ces deux adresses (bouton copier) :
            <div className="mt-1.5 space-y-1.5">
              <CopyField value={`${origin}/api/meta/callback`} testid="meta-guide-copy-preview" />
              <CopyField value="https://off-markette.com/api/meta/callback" testid="meta-guide-copy-prod" />
            </div>
          </li>
          <li>
            <span className="font-semibold">4.</span> Dans <b>Paramètres → Général</b>, champ <b>« Domaines de l'app »</b>, ajoutez :
            <div className="mt-1.5 space-y-1.5">
              <CopyField value={origin.replace("https://", "").replace("http://", "")} testid="meta-guide-copy-domain-preview" />
              <CopyField value="off-markette.com" testid="meta-guide-copy-domain-prod" />
            </div>
          </li>
          <li><span className="font-semibold">5.</span> Enregistrez. Tant que l'app est en <b>mode Développement</b>, ajoutez votre compte Facebook comme <b>Testeur/Administrateur</b> (menu <b>Rôles</b>), ou passez l'app en <b>Live</b>.</li>
          <li><span className="font-semibold">6.</span> Revenez ici et cliquez <b>« Connecter Facebook / Instagram »</b>. C'est tout ! 🎉</li>
          <li className="text-[12px] text-zinc-400">Astuce : pour publier au nom d'une Page au-delà des testeurs, Meta demandera une <b>App Review</b> (permissions pages_manage_posts, instagram_content_publish).</li>
        </ol>
      )}
    </div>
  );
}

function MetaConnectBox() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [autoPub, setAutoPub] = useState(false);
  const [autoPage, setAutoPage] = useState("");
  const [savedMsg, setSavedMsg] = useState(false);
  const load = () => authApi.metaStatus().then((d) => { setSt(d); if (d?.page_id) setAutoPage(d.page_id); }).catch(() => setSt({ configured: false }));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (st?.connected) authApi.metaGetSettings().then((s) => { setAutoPub(!!s.auto_publish); if (s.auto_page_id) setAutoPage(s.auto_page_id); }).catch(() => {}); }, [st?.connected]);
  const connect = async () => { setBusy(true); try { const d = await authApi.metaLoginUrl(); if (d.url) window.location.href = d.url; else setBusy(false); } catch (_) { alert("Connexion Meta indisponible."); setBusy(false); } };
  const disconnect = async () => { setBusy(true); try { await authApi.metaDisconnect(); await load(); } catch (_) {} setBusy(false); };
  const saveAuto = async (next, page) => {
    setAutoPub(next); if (page !== undefined) setAutoPage(page);
    try { await authApi.metaSetSettings({ auto_publish: next, auto_page_id: page !== undefined ? page : autoPage, auto_targets: ["facebook", "instagram"] }); setSavedMsg(true); setTimeout(() => setSavedMsg(false), 1800); } catch (_) {}
  };
  if (!st) return null;
  const pages = st.pages || [];
  return (
    <div className="rounded-xl border border-[#1877F2]/25 bg-[#F5F8FF] p-4" data-testid="meta-connect-box">
      <div className="flex items-start gap-3">
        <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-lg bg-white border border-[#1877F2]/25 text-[#1877F2]"><Facebook className="w-5 h-5" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[#111827]">Publication en 1 clic — Facebook & Instagram</p>
          {!st.configured ? (
            <p className="text-[13px] text-zinc-500 mt-0.5">Intégration Meta non configurée pour le moment.</p>
          ) : st.connected ? (
            <div className="mt-1 space-y-3">
              <p className="text-[13px] text-emerald-600 flex items-center gap-1.5"><Check className="w-4 h-4" /> Connecté{st.page_name ? ` — Page « ${st.page_name} »` : ""}{st.ig_connected ? " · Instagram lié" : ""}</p>
              {/* Auto-publication */}
              <div className="bg-white rounded-lg border border-[#E5E7EB] p-3">
                <label className="flex items-start gap-2.5 cursor-pointer" data-testid="meta-autopublish-toggle">
                  <input type="checkbox" checked={autoPub} onChange={(e) => saveAuto(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#1877F2]" />
                  <span>
                    <span className="block text-[13px] font-medium text-[#111827]">Publier automatiquement chaque nouvelle annonce</span>
                    <span className="block text-[12px] text-zinc-500">Dès qu'une annonce ayant <b>au moins 6 photos home-stagées</b> passe en ligne, elle est postée sur votre Page{st.ig_connected ? " et Instagram" : ""}. Plus rien à faire !</span>
                  </span>
                </label>
                {autoPub && pages.length > 1 && (
                  <select value={autoPage} onChange={(e) => saveAuto(true, e.target.value)} data-testid="meta-autopublish-page" className="mt-2 w-full bg-white border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm">
                    {pages.map((p) => <option key={p.id} value={p.id}>{p.name}{p.ig_connected ? " (+ Instagram)" : ""}</option>)}
                  </select>
                )}
                {savedMsg && <p className="text-[11px] text-emerald-600 mt-1.5">Préférence enregistrée ✓</p>}
              </div>
              <button onClick={disconnect} disabled={busy} data-testid="meta-disconnect" className="text-xs text-red-500 hover:underline">Déconnecter Meta</button>
            </div>
          ) : (
            <div className="mt-1.5">
              <p className="text-[13px] text-zinc-500 mb-2">Connectez votre Page Facebook et votre compte Instagram Professionnel pour publier vos annonces directement, sans copier-coller.</p>
              <button onClick={connect} disabled={busy} data-testid="meta-connect" className="inline-flex items-center gap-2 text-sm text-white bg-[#1877F2] hover:bg-[#166fe0] px-4 py-2 rounded-lg disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Facebook className="w-4 h-4" />} Connecter Facebook / Instagram</button>
              <MetaGuide />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MetaPublishBlock({ slug, imageUrl, caption }) {
  const [st, setSt] = useState(null);
  const [targets, setTargets] = useState(["facebook", "instagram"]);
  const [pageId, setPageId] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [stats, setStats] = useState(null);
  const [statsBusy, setStatsBusy] = useState(false);
  useEffect(() => { authApi.metaStatus().then((d) => { setSt(d); if (d?.page_id) setPageId(d.page_id); }).catch(() => setSt({ configured: false })); }, []);
  const toggle = (k) => setTargets((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);
  const publish = async () => {
    if (!targets.length || busy) return; setBusy(true); setErr(""); setDone(false);
    try { await authApi.metaPublish({ slug, caption, targets, image_url: imageUrl || "", page_id: pageId }); setDone(true); loadStats(); }
    catch (e) { setErr("Publication impossible. Vérifiez votre connexion Meta."); }
    setBusy(false);
  };
  const loadStats = async () => { setStatsBusy(true); try { const d = await authApi.metaInsights(slug); setStats(d); } catch (_) {} setStatsBusy(false); };
  useEffect(() => { if (st?.connected) loadStats(); /* eslint-disable-next-line */ }, [st?.connected]);
  if (!st || !st.configured) return null;
  const pages = st.pages || [];
  const hasStats = stats && stats.posts && stats.posts.length > 0;
  return (
    <div className="border border-[#1877F2]/25 bg-[#F5F8FF] rounded-xl p-4 space-y-3" data-testid="meta-publish-block">
      <p className="text-sm font-semibold text-[#111827] flex items-center gap-1.5"><Facebook className="w-4 h-4 text-[#1877F2]" /> Publier en 1 clic sur Facebook / Instagram</p>
      {!st.connected ? (
        <p className="text-[13px] text-zinc-500">Connectez d'abord vos comptes dans « Connecter mes réseaux » pour activer la publication automatique.</p>
      ) : (
        <>
          {pages.length > 1 && (
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#9CA3AF] font-semibold block mb-1">Publier sur la page</label>
              <select value={pageId} onChange={(e) => setPageId(e.target.value)} data-testid="meta-page-select" className="w-full sm:w-auto bg-white border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm text-[#111827] outline-none focus:border-[#1877F2]">
                {pages.map((p) => <option key={p.id} value={p.id}>{p.name}{p.ig_connected ? " (+ Instagram)" : ""}</option>)}
              </select>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {[{ k: "facebook", l: "Facebook", I: Facebook }, { k: "instagram", l: "Instagram", I: Instagram }, { k: "story", l: "Story Instagram", I: Instagram }].map(({ k, l, I }) => (
              <button key={k} onClick={() => toggle(k)} data-testid={`meta-target-${k}`} className={`inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border transition-colors ${targets.includes(k) ? "bg-[#1877F2]/10 border-[#1877F2] text-[#1877F2]" : "border-[#E5E7EB] text-[#374151] hover:border-[#1877F2]"}`}><I className="w-4 h-4" /> {l}{targets.includes(k) && <Check className="w-3 h-3" />}</button>
            ))}
          </div>
          {targets.includes("story") && <p className="text-[11px] text-zinc-400">Pour une Story nette, générez d'abord le visuel au format <b>Story 9:16</b> ci-dessus.</p>}
          <button onClick={publish} disabled={busy || !targets.length} data-testid="meta-publish-now" className="inline-flex items-center gap-2 text-sm text-white bg-[#1877F2] hover:bg-[#166fe0] px-4 py-2.5 rounded-lg font-semibold disabled:opacity-50">{busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Publication…</> : <><Send className="w-4 h-4" /> Publier maintenant</>}</button>
          {done && <p className="text-[13px] text-emerald-600 flex items-center gap-1.5" data-testid="meta-publish-done"><Check className="w-4 h-4" /> Publié ! Retrouvez la publication sur votre Page{targets.includes("instagram") || targets.includes("story") ? " et Instagram" : ""}.</p>}
          {err && <p className="text-[13px] text-red-500">{err}</p>}
          <p className="text-[11px] text-zinc-400">Le visuel généré ci-dessus est utilisé s'il existe, sinon la photo principale. Le lien de l'annonce (avec code d'accès) est ajouté à la légende.</p>

          {/* Statistiques Meta */}
          {hasStats && (
            <div className="border-t border-[#1877F2]/15 pt-3" data-testid="meta-stats">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[13px] font-semibold text-[#111827] flex items-center gap-1.5"><BarChart3 className="w-4 h-4 text-[#1877F2]" /> Statistiques des publications</p>
                <button onClick={loadStats} disabled={statsBusy} data-testid="meta-stats-refresh" className="text-[11px] text-[#1877F2] inline-flex items-center gap-1">{statsBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Actualiser</button>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {[{ l: "Vues", v: stats.totals.impressions }, { l: "Portée", v: stats.totals.reach }, { l: "Clics", v: stats.totals.clicks }, { l: "Engagement", v: stats.totals.engagement }].map((s) => (
                  <div key={s.l} className="bg-white rounded-lg border border-[#E5E7EB] p-2.5 text-center">
                    <p className="text-lg font-bold text-[#111827]">{(s.v || 0).toLocaleString("fr-FR")}</p>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-400">{s.l}</p>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-zinc-400 mt-1.5">{stats.posts.length} publication{stats.posts.length > 1 ? "s" : ""} · les <strong>clics</strong> = internautes ayant cliqué sur votre publication (Facebook). Les chiffres se mettent à jour progressivement côté Meta.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SocialConnect({ data, onSaved }) {
  const [rows, setRows] = useState(data.accounts && data.accounts.length ? data.accounts.map((a) => ({ ...a })) : [{ network: "Facebook", profile_url: "", page_url: "", followers: 0 }]);
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(false);
  const upd = (i, k, v) => setRows(rows.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const add = () => setRows([...rows, { network: "Instagram", profile_url: "", page_url: "", followers: 0 }]);
  const del = (i) => setRows(rows.filter((_, j) => j !== i));
  const save = async () => { setSaving(true); setOk(false); try { await authApi.saveSocialAccounts(rows); setOk(true); onSaved && onSaved(); setTimeout(() => setOk(false), 2500); } catch (_) {} setSaving(false); };
  const inp = "w-full bg-white border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm text-[#111827] outline-none focus:border-[#C19A6B]";
  return (
    <div className="space-y-4" data-testid="social-connect">
      <SocialHeader icon={LinkIcon} title="Connecter mes réseaux" desc="Ajoutez vos profils et pages (Facebook, Instagram, LinkedIn…) et renseignez votre nombre d'abonnés pour suivre votre audience." />
      <MetaConnectBox />
      <div className="space-y-3">
        {rows.map((r, i) => (
          <div key={i} className="rounded-xl border border-[#E5E7EB] bg-white p-3 grid sm:grid-cols-12 gap-2 items-center" data-testid={`social-account-${i}`}>
            <select value={r.network} onChange={(e) => upd(i, "network", e.target.value)} className={`${inp} sm:col-span-2`} data-testid={`social-account-network-${i}`}>
              {(data.networks || []).map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <input value={r.profile_url} onChange={(e) => upd(i, "profile_url", e.target.value)} placeholder="URL du profil" className={`${inp} sm:col-span-4`} data-testid={`social-account-profile-${i}`} />
            <input value={r.page_url} onChange={(e) => upd(i, "page_url", e.target.value)} placeholder="URL de la page (optionnel)" className={`${inp} sm:col-span-3`} />
            <input type="number" min="0" value={r.followers} onChange={(e) => upd(i, "followers", e.target.value)} placeholder="Abonnés" className={`${inp} sm:col-span-2`} data-testid={`social-account-followers-${i}`} />
            <button onClick={() => del(i)} className="sm:col-span-1 text-zinc-400 hover:text-red-500 flex justify-center"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3">
        <button onClick={add} className="inline-flex items-center gap-1.5 text-sm text-[#C19A6B] border border-[#C19A6B]/40 hover:bg-[#C19A6B]/10 px-4 py-2 rounded-lg" data-testid="social-add-account"><Plus className="w-4 h-4" /> Ajouter un réseau</button>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 text-sm text-white bg-[#C19A6B] hover:bg-[#A88358] px-5 py-2 rounded-lg disabled:opacity-60" data-testid="social-save-accounts">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Enregistrer</button>
        {ok && <span className="text-sm text-emerald-600 flex items-center gap-1"><Check className="w-4 h-4" /> Enregistré</span>}
      </div>
      <p className="text-[11px] text-zinc-400">La connexion automatique (comptage en temps réel via l'API Facebook/Instagram) sera activée prochainement. En attendant, actualisez vos abonnés manuellement.</p>
    </div>
  );
}

function MetaNetworkDashboard() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => { setBusy(true); authApi.metaDashboard().then(setD).catch(() => setD({ connected: false })).finally(() => setBusy(false)); };
  useEffect(() => { load(); }, []);
  if (!d || !d.connected) return null;
  const t = d.totals || {};
  return (
    <div className="rounded-xl border border-[#1877F2]/25 bg-[#F5F8FF] p-4" data-testid="meta-network-dashboard">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-[#111827] flex items-center gap-1.5"><Facebook className="w-4 h-4 text-[#1877F2]" /> Performance sur Facebook & Instagram</p>
        <button onClick={load} disabled={busy} data-testid="meta-dashboard-refresh" className="text-[11px] text-[#1877F2] inline-flex items-center gap-1">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Actualiser</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        {[{ l: "Vues cumulées", v: t.impressions }, { l: "Portée", v: t.reach }, { l: "Clics", v: t.clicks }, { l: "Engagement", v: t.engagement }].map((s) => (
          <div key={s.l} className="bg-white rounded-lg border border-[#E5E7EB] p-3 text-center">
            <p className="text-xl font-bold text-[#111827]">{(s.v || 0).toLocaleString("fr-FR")}</p>
            <p className="text-[10px] uppercase tracking-wider text-zinc-400">{s.l}</p>
          </div>
        ))}
      </div>
      <p className="text-[12px] text-zinc-500 mb-2">{d.listings_count} annonce{d.listings_count > 1 ? "s" : ""} publiée{d.listings_count > 1 ? "s" : ""} · {d.posts_count} publication{d.posts_count > 1 ? "s" : ""}</p>
      {d.top && d.top.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-1.5">Top annonces sur les réseaux</p>
          <div className="space-y-1.5">
            {d.top.map((l, i) => (
              <div key={l.slug} className="flex items-center gap-2.5 bg-white rounded-lg border border-[#E5E7EB] px-2.5 py-2" data-testid={`meta-top-${i}`}>
                {l.cover ? <img src={l.cover} alt="" className="w-9 h-9 rounded object-cover shrink-0" /> : <div className="w-9 h-9 rounded bg-[#F1F3F5] shrink-0" />}
                <span className="text-[13px] text-[#111827] font-medium truncate flex-1">{l.title}</span>
                <span className="text-[12px] text-[#1877F2] font-semibold shrink-0 inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {(l.impressions || 0).toLocaleString("fr-FR")}</span>
                <span className="text-[12px] text-[#C19A6B] font-semibold shrink-0 inline-flex items-center gap-1" title="Clics"><MousePointerClick className="w-3.5 h-3.5" /> {(l.clicks || 0).toLocaleString("fr-FR")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SocialReport({ data }) {
  const rep = data.report || {}; const an = data.analysis || {};
  const per = rep.per_network || [];
  const max = Math.max(1, ...per.map((p) => p.followers));
  return (
    <div className="space-y-4" data-testid="social-report">
      <SocialHeader icon={BarChart3} title="Rapport de mon réseau" desc="Synthèse de votre audience et de votre présence sur les réseaux sociaux." />
      <MetaNetworkDashboard />
      <div className="grid sm:grid-cols-3 gap-3">
        <KpiCard label="Abonnés cumulés" value={(rep.total_followers || 0).toLocaleString("fr-FR")} icon={Users} />
        <KpiCard label="Réseaux connectés" value={rep.total_networks || 0} icon={LinkIcon} />
        <KpiCard label="Partages d'annonces" value={rep.total_shares || 0} icon={Share2} />
      </div>
      <div className="rounded-xl border border-[#E5E7EB] bg-white p-4">
        <p className="text-[11px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-3">Abonnés par réseau</p>
        {per.length === 0 ? <p className="text-sm text-zinc-400">Aucun réseau connecté. Ajoutez vos réseaux dans « Connecter mes réseaux ».</p>
          : <div className="space-y-2.5">{per.map((p, i) => (
              <div key={i} data-testid={`social-report-net-${i}`}>
                <div className="flex justify-between text-sm mb-1"><span className="text-[#374151]">{p.network}</span><span className="text-[#111827] font-medium">{(p.followers || 0).toLocaleString("fr-FR")}</span></div>
                <div className="h-2 rounded-full bg-[#F1F3F5] overflow-hidden"><div className="h-full rounded-full bg-[#C19A6B]" style={{ width: `${Math.round((p.followers / max) * 100)}%` }} /></div>
              </div>
            ))}</div>}
      </div>
      <div className="rounded-xl border border-[#C19A6B]/30 bg-[#FBF8F3] p-4" data-testid="social-report-analysis">
        <div className="flex items-center gap-3 mb-2">
          <div className="relative w-12 h-12 shrink-0"><svg viewBox="0 0 36 36" className="w-12 h-12"><path d="M18 2a16 16 0 110 32 16 16 0 010-32" fill="none" stroke="#Eee" strokeWidth="3" /><path d="M18 2a16 16 0 110 32 16 16 0 010-32" fill="none" stroke="#C19A6B" strokeWidth="3" strokeDasharray={`${an.score || 0}, 100`} /></svg><span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-[#A88358]">{an.score || 0}</span></div>
          <div><p className="text-sm font-semibold text-[#111827]">{an.headline}</p><p className="text-[11px] text-zinc-500">Score de présence sociale</p></div>
        </div>
        <ul className="space-y-1.5 mt-2">
          {(an.tips || []).map((t, i) => <li key={i} className="text-sm text-[#374151] flex items-start gap-2"><Sparkles className="w-3.5 h-3.5 text-[#C19A6B] mt-0.5 shrink-0" /> {t}</li>)}
        </ul>
      </div>
    </div>
  );
}

function SocialAds({ member }) {
  const [targets, setTargets] = useState([]);
  const [target, setTarget] = useState("agent");
  const [tone, setTone] = useState("classique");
  const [ad, setAd] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  useEffect(() => { authApi.socialOverview().then((d) => setTargets(d.referral_targets || [])).catch(() => {}); }, []);
  const gen = async () => { setBusy(true); setAd(null); try { const r = await authApi.generateReferralAd(target, tone); setAd({ ...r, image: "", img_edits: 0 }); } catch (_) { alert("Génération impossible."); } setBusy(false); };
  const copy = (k, txt) => { navigator.clipboard.writeText(txt); setCopied(k); setTimeout(() => setCopied(""), 1800); };
  const exportAd = async () => {
    if (!ad) return;
    const full = `${(ad.post || "").trim()}\n\n👉 ${ad.referral_link || ""}`.trim();
    try { await navigator.clipboard.writeText(full); } catch (_) {}
    setCopied("export"); setTimeout(() => setCopied(""), 2200);
    if (ad.image) {
      try {
        const res = await fetch(ad.image);
        const blob = await res.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `off-markette-pub-${target}.jpg`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      } catch (_) {}
    }
  };
  const TONES = [["classique", "Classique conseiller"], ["juridique", "Juridique"], ["dynamique", "Dynamique"]];
  const [shareMsg, setShareMsg] = useState("");
  const [shareBusy, setShareBusy] = useState("");
  const [shareNote, setShareNote] = useState("");
  const [shareCopied, setShareCopied] = useState(false);
  const enc = encodeURIComponent;
  const openShare = () => {
    if (!ad) return;
    const initial = `${(ad.post || "").trim()}\n\n👉 ${ad.referral_link || ""}`.trim();
    setShareMsg(initial); setShareNote(""); setShareCopied(false); setShareOpen(true);
  };
  const buildShareLink = (net, msg) => {
    const refUrl = ad?.referral_link || "";
    // OG « nous rejoindre » : aperçu photo par défaut sur les réseaux (extrait le code de parrainage).
    const refCode = (refUrl.match(/[?&]ref=([^&]+)/) || [])[1] || "";
    const ogUrl = refCode ? `${PUBLIC_SITE}/api/referral/${refCode}/share` : refUrl;
    const waMsg = refUrl && ogUrl !== refUrl ? msg.split(refUrl).join(ogUrl) : msg;
    return {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(ogUrl)}&quote=${enc(msg)}`,
      messenger: `https://www.facebook.com/sharer/sharer.php?u=${enc(ogUrl)}&quote=${enc(msg)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(ogUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${enc(msg)}&url=${enc(ogUrl)}`,
      whatsapp: `https://wa.me/?text=${enc(waMsg)}`,
      email: `mailto:?subject=${enc("Rejoins OFF MARKETTE")}&body=${enc(msg)}`,
    }[net];
  };
  const shareOnNet = async (net) => {
    if (shareBusy) return;
    const external = net !== "email";
    const win = external ? window.open("", "_blank") : null;
    setShareBusy(net); setShareNote("");
    try { await navigator.clipboard.writeText(shareMsg); } catch (_) {}
    const link = buildShareLink(net, shareMsg);
    if (net === "email") window.location.href = link;
    else if (win) win.location.href = link; else window.open(link, "_blank");
    setShareBusy("");
    setShareNote(net === "linkedin" || net === "facebook" || net === "messenger"
      ? "✅ Texte copié — collez-le (Ctrl/Cmd+V) dans votre publication."
      : "✅ Texte pré-rempli dans le partage.");
  };
  const copyShareMsg = async () => { try { await navigator.clipboard.writeText(shareMsg); setShareCopied(true); setTimeout(() => setShareCopied(false), 1800); } catch (_) {} };
  const SHARE_GRID = [
    { key: "facebook", label: "Facebook", Icon: Facebook },
    { key: "linkedin", label: "LinkedIn", Icon: Linkedin },
    { key: "twitter", label: "X (Twitter)", Icon: Twitter },
    { key: "whatsapp", label: "WhatsApp", Icon: WhatsAppIcon },
    { key: "email", label: "E-mail", Icon: Mail },
    { key: "messenger", label: "Messenger", Icon: MessageCircle },
  ];
  return (
    <div className="space-y-4" data-testid="social-ads">
      <SocialHeader icon={Megaphone} title="Mes pubs parrainages" desc="Générez une publication prête à poster pour recruter des professionnels et gagner des parrainages. Chaque nouveau membre reçoit 10 € de crédits d'annonces." />
      <div className="rounded-xl border border-[#E5E7EB] bg-white p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[#9CA3AF] font-semibold block mb-1">Cible à parrainer</label>
          <select value={target} onChange={(e) => setTarget(e.target.value)} className="bg-white border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm text-[#111827] outline-none focus:border-[#C19A6B]" data-testid="social-ads-target">
            {targets.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[#9CA3AF] font-semibold block mb-1">Ton</label>
          <select value={tone} onChange={(e) => setTone(e.target.value)} className="bg-white border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm text-[#111827] outline-none focus:border-[#C19A6B]" data-testid="social-ads-tone">
            {TONES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <button onClick={gen} disabled={busy} className="inline-flex items-center gap-1.5 text-sm text-white bg-[#C19A6B] hover:bg-[#A88358] px-5 py-2 rounded-lg disabled:opacity-60" data-testid="social-ads-generate">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Générer la publication</button>
      </div>
      {ad && (
        <>
        <div className="flex items-center justify-between flex-wrap gap-2 rounded-xl border border-[#C19A6B]/30 bg-[#C19A6B]/5 px-4 py-3">
          <p className="text-sm text-[#374151]">Votre publication est prête. <span className="text-[#9CA3AF]">Le texte (avec votre lien de parrainage) est copié{ad.image ? " et le visuel téléchargé" : ""} en un clic.</span></p>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={openShare} data-testid="social-ads-share" className="inline-flex items-center gap-1.5 text-sm text-white bg-[#C19A6B] hover:bg-[#A88358] px-4 py-2.5 rounded-lg font-medium">
              <Share2 className="w-4 h-4" /> Partager sur mes réseaux
            </button>
            <button onClick={exportAd} data-testid="social-ads-export" className="inline-flex items-center gap-1.5 text-sm text-white bg-[#C19A6B] hover:bg-[#A88358] px-5 py-2.5 rounded-lg font-medium">
              {copied === "export" ? <><Check className="w-4 h-4" /> Copié{ad.image ? " + téléchargé" : ""} !</> : <><Download className="w-4 h-4" /> Exporter la pub</>}
            </button>
          </div>
        </div>
        <div className="grid lg:grid-cols-2 gap-4" data-testid="social-ads-result">
          <div className="rounded-xl border border-[#E5E7EB] bg-white p-4 space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1"><p className="text-[11px] uppercase tracking-wider text-[#9CA3AF] font-semibold">Publication réseaux sociaux</p><button onClick={() => copy("post", ad.post)} className="text-xs text-[#C19A6B] inline-flex items-center gap-1">{copied === "post" ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} Copier</button></div>
              <textarea readOnly value={ad.post} rows={7} className="w-full bg-[#FBFBFB] border border-[#E5E7EB] rounded-lg p-3 text-sm text-[#374151]" data-testid="social-ads-post" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1"><p className="text-[11px] uppercase tracking-wider text-[#9CA3AF] font-semibold">Texte d'email d'invitation</p><button onClick={() => copy("email", ad.email)} className="text-xs text-[#C19A6B] inline-flex items-center gap-1">{copied === "email" ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} Copier</button></div>
              <textarea readOnly value={ad.email} rows={3} className="w-full bg-[#FBFBFB] border border-[#E5E7EB] rounded-lg p-3 text-sm text-[#374151]" data-testid="social-ads-email" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-1">Mon lien de parrainage</p>
              <div className="flex items-center gap-2"><input readOnly value={ad.referral_link} className="flex-1 bg-[#FBFBFB] border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm text-[#374151]" data-testid="social-ads-link" /><button onClick={() => copy("link", ad.referral_link)} className="text-white bg-[#C19A6B] hover:bg-[#A88358] px-3 py-2 rounded-lg text-xs inline-flex items-center gap-1">{copied === "link" ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}</button></div>
            </div>
          </div>
          <div className="rounded-xl border border-[#E5E7EB] bg-white p-4 space-y-3">
            <p className="text-[11px] uppercase tracking-wider text-[#9CA3AF] font-semibold flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-[#C19A6B]" /> Visuel de la publication</p>
            <PubVisualEditor
              galleryPhotos={[...(member?.gallery || []), member?.photo].filter(Boolean)}
              initialPhrase={ad.tagline || ""}
              initialSlogan="Rejoins mon réseau"
              sloganPresets={PARRAINAGE_SLOGANS}
              onAiBg={() => authApi.generateReferralAdImage({ target, tone, raw: true }).then((d) => d.url)}
              onBake={(payload) => authApi.generateReferralAdImage({ ...payload, target, tone }).then((d) => d.url)}
              testid="social-ads-visual"
            />
          </div>
        </div>
        </>
      )}
      {shareOpen && ad && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setShareOpen(false)} data-testid="social-ads-share-modal">
          <div className="relative w-full max-w-xl rounded-[5px] border border-[#C19A6B]/30 bg-[#0d0d10] shadow-2xl p-6 sm:p-7 text-white" onClick={(e) => e.stopPropagation()}>
            <div aria-hidden="true" className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-[#C19A6B]/20 blur-3xl pointer-events-none" />
            <button onClick={() => setShareOpen(false)} aria-label="Fermer" data-testid="social-ads-share-close" className="absolute top-3 right-3 text-zinc-400 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
            <div className="flex items-center gap-3">
              <span className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-[5px] bg-[#C19A6B]/15 border border-[#C19A6B]/40 text-[#C19A6B]"><Share2 className="w-5 h-5" /></span>
              <div className="min-w-0">
                <p className="calli text-2xl text-[#C19A6B] leading-none">Partager ma publication sur vos réseaux</p>
                <p className="text-sm text-zinc-400 mt-1">Diffusez votre invitation de parrainage en un clic.</p>
              </div>
            </div>
            <div className="mt-5">
              <p className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1 flex items-center justify-between">
                <span>Votre message (modifiable)</span>
                <button onClick={copyShareMsg} className="inline-flex items-center gap-1 text-[#C19A6B] hover:text-[#d1af84] normal-case tracking-normal" data-testid="social-ads-share-copy">
                  {shareCopied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} Copier
                </button>
              </p>
              <textarea value={shareMsg} onChange={(e) => setShareMsg(e.target.value)} data-testid="social-ads-share-text"
                className="w-full h-36 bg-white/[0.04] border border-white/12 rounded-[5px] px-3 py-2 text-sm text-white outline-none focus:border-[#C19A6B] resize-none" />
            </div>
            <div className="mt-4">
              <p className="text-[11px] uppercase tracking-wider text-zinc-400 mb-2">Partagez sur le réseau de votre choix</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {SHARE_GRID.map(({ key, label, Icon }) => (
                  <button key={key} onClick={() => shareOnNet(key)} disabled={!!shareBusy} data-testid={`social-ads-share-${key}`}
                    className="relative flex flex-col items-center justify-center gap-1.5 py-3.5 px-2 rounded-[5px] border border-white/12 bg-white/[0.04] hover:bg-[#C19A6B]/15 hover:border-[#C19A6B]/50 hover:-translate-y-0.5 transition-all disabled:opacity-50">
                    {shareBusy === key ? <Loader2 className="w-5 h-5 text-[#C19A6B] animate-spin" /> : <Icon className="w-5 h-5 text-[#C19A6B]" />}
                    <span className="text-[10px] uppercase tracking-wide text-zinc-300 text-center leading-tight">Partager sur<br />{label}</span>
                  </button>
                ))}
                <button onClick={copyShareMsg} data-testid="social-ads-share-copylink" className="flex flex-col items-center justify-center gap-1.5 py-3.5 px-2 rounded-[5px] border border-white/12 bg-white/[0.04] hover:bg-[#C19A6B]/15 hover:border-[#C19A6B]/50 hover:-translate-y-0.5 transition-all">
                  {shareCopied ? <Check className="w-5 h-5 text-green-400" /> : <Link2 className="w-5 h-5 text-[#C19A6B]" />}
                  <span className="text-[10px] uppercase tracking-wide text-zinc-300 text-center leading-tight">{shareCopied ? "Copié !" : "Copier le texte"}</span>
                </button>
              </div>
              {shareNote && <p className="text-[11px] text-[#C19A6B] mt-2" data-testid="social-ads-share-note">{shareNote}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const SocialHeader = ({ icon: Ic, title, desc }) => (
  <div className="flex items-start gap-3">
    <div className="w-10 h-10 rounded-lg bg-[#C19A6B]/10 flex items-center justify-center shrink-0"><Ic className="w-5 h-5 text-[#C19A6B]" /></div>
    <div><h2 className="calli text-2xl sm:text-3xl leading-none text-[#C19A6B]">{title}</h2><p className="text-sm text-zinc-500 max-w-2xl mt-1.5">{desc}</p></div>
  </div>
);
const EmptySocial = ({ text }) => <div className="rounded-xl border border-dashed border-[#E5E7EB] bg-white p-10 text-center text-sm text-zinc-400">{text}</div>;
const KpiCard = ({ label, value, icon: Ic }) => (
  <div className="rounded-xl border border-[#E5E7EB] bg-white p-4"><div className="flex items-center justify-between"><p className="text-[11px] uppercase tracking-wider text-[#9CA3AF] font-semibold">{label}</p><Ic className="w-4 h-4 text-[#C19A6B]" /></div><p className="text-2xl font-bold text-[#111827] mt-1">{value}</p></div>
);

function ApporteurTab({ setTab }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    fetch(`${API}/member/apporteur/dashboard`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : Promise.reject()).then(setD).catch(() => setD(false));
  }, []);
  if (d === false) return <div className="text-sm text-zinc-500 py-10 text-center">Tableau de bord indisponible.</div>;
  if (!d) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C19A6B]" /></div>;
  const M = d.metrics;
  const cards = [
    { l: "Biens proposés", v: M.listings, i: Home },
    { l: "Acquéreurs", v: M.acquereurs, i: Contact },
    { l: "Mises en relation", v: M.relations, i: Handshake },
    { l: "Collaborations", v: M.collaborations, i: Users },
    { l: "Co-mandats", v: M.co_mandats, i: FileText },
    { l: "Commission potentielle", v: fmtPrice(M.potential_commission || 0), i: Coins },
  ];
  return (
    <div className="space-y-6" data-testid="apporteur-tab">
      <div>
        <h2 className="calli text-3xl sm:text-4xl leading-none text-[#C19A6B] flex items-center gap-2"><Handshake className="w-6 h-6 text-[#C19A6B]" /> Tableau apporteur d'affaires</h2>
        <p className="text-sm text-zinc-500 mt-0.5">Vous détectez les opportunités, le réseau les commercialise. Suivez vos apports et collaborations.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {cards.map((c, i) => { const Ic = c.i; return (
          <div key={i} data-testid={`apporteur-kpi-${i}`} className="rounded-xl border border-[#E5E7EB] bg-white p-4">
            <Ic className="w-5 h-5 text-[#C19A6B] mb-2" strokeWidth={1.75} />
            <p className="text-2xl font-bold text-[#111827]">{c.v}</p>
            <p className="text-xs text-zinc-500 mt-0.5">{c.l}</p>
          </div>
        ); })}
      </div>

      {d.revenue_status && <ApporteurRevenuePanel rs={d.revenue_status} />}

      <div className="rounded-xl border border-[#E5E7EB] bg-white p-4" data-testid="apporteur-listings">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] uppercase tracking-[0.08em] text-[#9CA3AF] font-semibold flex items-center gap-1.5"><Home className="w-3.5 h-3.5 text-[#C19A6B]" /> Mes biens proposés au réseau</p>
          <button onClick={() => setTab("listings")} data-testid="apporteur-add-listing" className="inline-flex items-center gap-1.5 text-white text-xs font-semibold uppercase tracking-wider rounded-[5px] px-3 py-1.5" style={{ background: "#C19A6B" }}><ArrowRight className="w-3.5 h-3.5" /> Ajouter / gérer</button>
        </div>
        {d.listings.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {d.listings.map((l) => (
              <div key={l.slug} className="rounded-[5px] border border-[#E5E7EB] bg-[#F1F3F5] overflow-hidden" data-testid={`apporteur-listing-${l.slug}`}>
                <div className="aspect-[4/3] bg-[#F3F4F6]">{l.cover && <img src={l.cover} alt="" className="w-full h-full object-cover" />}</div>
                <div className="p-2.5">
                  <p className="text-xs font-semibold text-[#111827] line-clamp-1">{l.title}</p>
                  <p className="text-[11px] text-[#A88358]">{l.price ? fmtPrice(l.price) : ""} · {l.city || ""}</p>
                  <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mt-1">Proposé au réseau</p>
                </div>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-zinc-400 py-3">Aucun bien encore proposé. Ajoutez une opportunité pour la partager au réseau.</p>}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-[#E5E7EB] bg-white p-4" data-testid="apporteur-relations">
          <p className="text-[11px] uppercase tracking-[0.08em] text-[#9CA3AF] font-semibold mb-3 flex items-center gap-1.5"><Handshake className="w-3.5 h-3.5 text-[#C19A6B]" /> Demandes de mise en relation</p>
          {d.relations?.length ? d.relations.map((r) => (
            <div key={r.id} className="flex items-center justify-between py-2 border-b border-[#F3F4F6] last:border-0">
              <div className="min-w-0"><p className="text-sm text-[#111827] truncate">{r.name || r.email || "Contact"}</p><p className="text-xs text-zinc-500 truncate">{r.listing_slug}</p></div>
              <span className="text-[11px] text-[#A88358] font-medium shrink-0 uppercase">{r.kind}</span>
            </div>
          )) : <p className="text-sm text-zinc-400 py-3">Aucune demande pour le moment.</p>}
        </div>
        <div className="rounded-xl border border-[#E5E7EB] bg-white p-4" data-testid="apporteur-collaborations">
          <p className="text-[11px] uppercase tracking-[0.08em] text-[#9CA3AF] font-semibold mb-3 flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-[#C19A6B]" /> Collaborations & co-mandats</p>
          {d.collaborations?.length ? d.collaborations.map((c) => (
            <div key={c.id} className="flex items-center justify-between py-2 border-b border-[#F3F4F6] last:border-0">
              <div className="min-w-0"><p className="text-sm text-[#111827] truncate">{c.listing_slug}</p><p className="text-xs text-zinc-500">{c.match_count} acquéreur(s) correspondant(s)</p></div>
              <span className={`text-[11px] font-medium shrink-0 uppercase ${c.status === "accepted" ? "text-emerald-600" : "text-[#A88358]"}`}>{c.status}</span>
            </div>
          )) : <p className="text-sm text-zinc-400 py-3">Aucune collaboration proposée pour le moment.</p>}
        </div>
      </div>
    </div>
  );
}

function CrmHomeTab({ setTab }) {
  const [d, setD] = useState(null);
  useEffect(() => { authApi.crmHome().then(setD).catch(() => setD(false)); }, []);
  if (d === false) return <div className="text-sm text-zinc-500 py-10 text-center">Tableau de bord indisponible.</div>;
  if (!d) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C19A6B]" /></div>;
  const M = d.metrics;
  const cards = [
    { l: "Annonces", v: M.listings, s: `${M.online} en ligne`, i: Home, tab: "listings" },
    { l: "Vues cumulées", v: M.views, s: "toutes annonces", i: Eye, tab: "stats" },
    { l: "Acquéreurs", v: M.acquereurs, s: `${M.leads_ia} via l'IA`, i: Contact, tab: "acquereurs" },
    { l: "Visites à venir", v: M.visits_upcoming, s: "prochains RDV", i: Clock, tab: "agenda" },
  ];
  const dt = (s) => { try { const x = new Date(s); return x.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }); } catch { return s; } };
  return (
    <div className="space-y-6" data-testid="crm-home">
      <div>
        <h2 className="calli text-3xl sm:text-4xl leading-none text-[#C19A6B]">Bonjour {d.member?.first_name || ""}</h2>
        <p className="text-sm text-zinc-500 mt-0.5">Voici l'essentiel de votre activité aujourd'hui.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((c, i) => { const Ic = c.i; return (
          <button key={i} onClick={() => setTab(c.tab)} data-testid={`crm-metric-${c.tab}`} className="text-left rounded-xl border border-[#E5E7EB] bg-white p-4 hover:shadow-[0_4px_16px_rgba(17,24,39,0.06)] transition-shadow">
            <Ic className="w-5 h-5 text-[#C19A6B] mb-2" strokeWidth={1.75} />
            <p className="text-2xl font-bold text-[#111827]">{c.v}</p>
            <p className="text-xs text-zinc-500 mt-0.5">{c.l}</p>
            <p className="text-[11px] text-[#9CA3AF]">{c.s}</p>
          </button>
        ); })}
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-[#E5E7EB] bg-white p-4">
          <p className="text-[11px] uppercase tracking-[0.08em] text-[#9CA3AF] font-semibold mb-3 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[#C19A6B]" /> Prochaines visites</p>
          {d.upcoming_visits?.length ? d.upcoming_visits.map((v) => (
            <div key={v.id} className="flex items-center justify-between py-2 border-b border-[#F3F4F6] last:border-0">
              <div className="min-w-0"><p className="text-sm text-[#111827] truncate">{v.title}</p><p className="text-xs text-zinc-500">{v.acquereur_name || v.contact || ""}</p></div>
              <span className="text-xs text-[#A88358] font-medium shrink-0">{dt(v.date)} {v.time}</span>
            </div>
          )) : <p className="text-sm text-zinc-400 py-3">Aucune visite planifiée.</p>}
        </div>
        <div className="rounded-xl border border-[#E5E7EB] bg-white p-4">
          <p className="text-[11px] uppercase tracking-[0.08em] text-[#9CA3AF] font-semibold mb-3 flex items-center gap-1.5"><Contact className="w-3.5 h-3.5 text-[#C19A6B]" /> Prospects récents</p>
          {d.recent_prospects?.length ? d.recent_prospects.map((p) => (
            <div key={p.id} className="flex items-center justify-between py-2 border-b border-[#F3F4F6] last:border-0">
              <p className="text-sm text-[#111827] truncate">{p.name}</p>
              <span className="text-[11px] text-zinc-500 shrink-0">{p.origin}</span>
            </div>
          )) : <p className="text-sm text-zinc-400 py-3">Pas encore de prospect.</p>}
        </div>
      </div>
      {d.opportunities?.length > 0 && (
        <div className="rounded-xl border border-[#C19A6B]/25 bg-gradient-to-r from-[#FBF8F4] to-transparent p-4">
          <p className="text-[11px] uppercase tracking-[0.08em] text-[#A88358] font-semibold mb-2 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Opportunités du jour</p>
          <div className="flex flex-wrap gap-2">
            {d.opportunities.map((o, i) => (
              <button key={i} onClick={() => setTab("opportunites")} className="text-sm text-[#374151] bg-white border border-[#E5E7EB] rounded-lg px-3 py-1.5 hover:border-[#C19A6B]">{o.demand} · {o.type} à {o.secteur}</button>
            ))}
          </div>
        </div>
      )}
      {d.top_listings?.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-[0.08em] text-[#9CA3AF] font-semibold mb-2">Biens les plus consultés</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {d.top_listings.map((l) => (
              <div key={l.slug} className="rounded-xl border border-[#E5E7EB] bg-white overflow-hidden">
                <div className="aspect-[4/3] bg-[#F3F4F6]">{l.cover && <img src={l.cover} alt="" className="w-full h-full object-cover" />}</div>
                <div className="p-2.5"><p className="text-xs text-[#111827] font-medium line-clamp-1">{l.title}</p><p className="text-[11px] text-[#A88358] flex items-center gap-1 mt-0.5"><Eye className="w-3 h-3" /> {l.views} vues</p></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Connexions réseaux sociaux (self-service) ----------
const SOCIAL_META = {
  instagram: { label: "Instagram", hint: "@votrecompte" },
  facebook: { label: "Facebook", hint: "Page ou profil" },
  linkedin: { label: "LinkedIn", hint: "votre-nom" },
  twitter: { label: "X (Twitter)", hint: "@votrecompte" },
};
function SocialConnectionsTab() {
  const [conns, setConns] = useState(null);
  const [inputs, setInputs] = useState({});
  const [busy, setBusy] = useState("");
  const load = () => authApi.socialConnections().then((d) => setConns(d.connections || {})).catch(() => setConns({}));
  useEffect(() => { load(); }, []);
  const connect = async (net) => {
    const h = (inputs[net] || "").trim(); if (!h) return; setBusy(net);
    try { const r = await authApi.connectSocial(net, h); setConns((c) => ({ ...c, [net]: r.connection })); setInputs((i) => ({ ...i, [net]: "" })); } catch (_) {}
    setBusy("");
  };
  const disconnect = async (net) => { setBusy(net); try { await authApi.disconnectSocial(net); setConns((c) => { const n = { ...c }; delete n[net]; return n; }); } catch (_) {} setBusy(""); };
  if (!conns) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C19A6B]" /></div>;
  return (
    <div className="space-y-4" data-testid="social-connections">
      <div className="rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-4 text-sm text-zinc-600">
        Connectez vos comptes pour préparer la publication de vos annonces. <b className="text-[#111827]">Publication automatique</b> : disponible une fois l'app OAuth de la plateforme activée — en attendant, le partage en 1 clic est déjà actif depuis « Contenu IA ».
      </div>
      {Object.keys(SOCIAL_META).map((net) => {
        const meta = SOCIAL_META[net]; const c = conns[net];
        return (
          <div key={net} className="rounded-xl border border-[#E5E7EB] bg-white p-4 flex items-center gap-3 flex-wrap" data-testid={`social-${net}`}>
            <div className="flex items-center gap-2 w-32"><Send className="w-4 h-4 text-[#C19A6B]" /> <span className="font-medium text-[#111827]">{meta.label}</span></div>
            {c ? (
              <>
                <span className="flex items-center gap-1.5 text-sm text-emerald-600" data-testid={`social-connected-${net}`}><Check className="w-4 h-4" /> Connecté : {c.handle}</span>
                <button onClick={() => disconnect(net)} disabled={busy === net} data-testid={`social-disconnect-${net}`} className="ml-auto text-xs text-red-500 hover:underline">Déconnecter</button>
              </>
            ) : (
              <>
                <input value={inputs[net] || ""} onChange={(e) => setInputs((i) => ({ ...i, [net]: e.target.value }))} placeholder={meta.hint} data-testid={`social-input-${net}`} className="flex-1 min-w-[140px] bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm text-[#374151]" />
                <button onClick={() => connect(net)} disabled={busy === net || !(inputs[net] || "").trim()} data-testid={`social-connect-${net}`} className="rounded-lg bg-[#C19A6B] hover:bg-[#A88358] text-white text-sm font-semibold px-4 py-2 disabled:opacity-50">Connecter</button>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
